import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import type { Etapa, Card, CampoConfig, Etiqueta, CamposCotacaoEtapa, FornecedorCotacao, SolicitacaoReenvio } from '../../types';
import { XMarkIcon, PencilIcon, TrashIcon, ChevronDownIcon, ChevronUpIcon, DocumentTextIcon, ArrowsPointingOutIcon, ArrowsPointingInIcon, ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline';
import { automationEngine } from '../../utils/automationEngine';
import { useToast } from '../../contexts/ToastContext';
import { apiUrl } from '../../utils/apiBase';
import { useIntegracoes } from '../../hooks/useIntegracoes';
import { generateShortCode, getShareableUrl } from '../../utils/shortCodeUtils';
import { compressImageIfNeeded, compressImagesIfNeeded } from '../../utils/imageCompression';
import ModalResumo from './ModalResumo';
import html2pdf from 'html2pdf.js';
import AIChart, { extractCharts, stripChartBlocks } from '../AIChart';
import GixCriarOcForm, { type CriarOcConfig } from '../Automacoes/GixCriarOcForm';
import CriarOcPelaNota from '../Automacoes/CriarOcPelaNota';
import PortalMensagensInternas from '../Portais/PortalMensagensInternas';
import { EtiquetaBadges, EtiquetasPicker } from '../Etiquetas';
import ModalFatiarCard from '../FatiarCard/ModalFatiarCard';
import { ehAdminMaster } from '../../utils/adminMaster';
import ModalPedirCotacao from '../Cotacao/ModalPedirCotacao';
import CampoParcelas, { textoParcelas } from '../CampoParcelas';
import CampoItens, { textoItens, ItensResumo } from '../CampoItens';
import CampoCotacoes, { ehMapaCotacao, textoCotacoes } from '../CampoCotacoes';
import CampoNdVinculada, { ComprasDaNd, ehNdVinculada, textoNd } from '../CampoNdVinculada';
import PainelEstoqueGix from './PainelEstoqueGix';
import PedidoGix, { PedidoGixDados } from './PedidoGix';
import CampoNumerosNf, { ehCampoNumeroNf } from './CampoNumerosNf';
import { RotasField } from '../common/AeroportoSearch';
import { CidadeSearch } from '../common/RotaTerrestreField';
import { ViajantesField, ViajantesResumo, QuadroVoosField, ehListaDeViajantes, textoViajantes, ehListaDeVoos, textoVoos, problemaViajantes, diariasDaViagem, pedePluxee, viagemUrgente } from '../common/ViagensCampos';
import {
  SeloFornecedorNovo, AvisoFornecedorNovo, ModalCienciaFornecedorNovo,
  cienciaValePara, textoLimpoFornecedor, verificarFornecedor, type CienciaFornecedorNovo,
} from '../AvisoFornecedorNovo';

// Cache para nomes de usuários já buscados
const userNamesCache: { [userId: string]: string } = {};
// Set para rastrear requisições em andamento (evita duplicatas)
const pendingUserFetches = new Set<string>();

// ──────────────────────────────────────────────────────────────
// Sugestão automática de progresso a partir de texto (regras locais)
// Usado para estimar o % do campo "Progresso" com base no que o
// usuário escreve em campos de texto (ex: "Ação Realizada").
// ──────────────────────────────────────────────────────────────
const normalizarTextoProgresso = (texto: string): string =>
  (texto || '')
    .replace(/<[^>]+>/g, ' ') // remove HTML do rich text
    .replace(/&nbsp;/gi, ' ')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // remove acentos
    .toLowerCase();

// Regras ordenadas: a maior % entre as que casarem vence.
const REGRAS_PROGRESSO_TEXTO: { palavras: string[]; percent: number }[] = [
  { palavras: ['conclu', 'finaliz', 'resolvid', 'encerrad', 'entregue', 'pronto', 'feito', 'deu certo', '100%'], percent: 100 },
  { palavras: ['homologa', 'test', 'valida', 'revis', 'aprovacao', 'quase'], percent: 80 },
  { palavras: ['em andamento', 'desenvolv', 'implementa', 'executa', 'fazendo', 'trabalhando', 'andamento'], percent: 50 },
  { palavras: ['inicia', 'comec', 'analis', 'levantamento', 'planeja', 'iniciado'], percent: 25 },
  { palavras: ['aguard', 'pendente', 'a espera', 'parado', 'em aberto', 'nao iniciad', 'sem inicio'], percent: 10 },
];

// Retorna a % sugerida (0-100) ou null se nenhum termo casar.
const calcularSugestaoProgresso = (texto: string): number | null => {
  const t = normalizarTextoProgresso(texto);
  if (!t.trim()) return null;
  let best: number | null = null;
  for (const regra of REGRAS_PROGRESSO_TEXTO) {
    if (regra.palavras.some((p) => t.includes(normalizarTextoProgresso(p)))) {
      best = best === null ? regra.percent : Math.max(best, regra.percent);
    }
  }
  return best;
};

/**
 * Campos a mostrar no histórico para uma passagem do ciclo de reenvio de anexo.
 * Etapas como Reprovados/Reanalise não têm campos próprios, e sem este filtro o
 * histórico despejava o card inteiro nelas. Aqui:
 * - passagem de reprovação → só os anexos pedidos de novo
 * - volta pelo portal      → só os anexos que o solicitante reenviou
 * Retorna null quando a passagem não é do ciclo (mantém o comportamento antigo).
 */
const camposDoCicloReenvio = (entry: any, cardFull: any): string[] | null => {
  if (!entry) return null;
  if (Array.isArray(entry.camposReenviados)) return entry.camposReenviados.map(String);
  if (Array.isArray(entry.camposSolicitados)) return entry.camposSolicitados.map((c: any) => String(c.campoId));

  // Cards gravados antes das marcações acima: deduz pelo que já existe no card.
  const ts = (d: any) => new Date(d?.$date || d || 0).getTime();
  if (entry.motivo === 'reenvio_anexo_portal') {
    const ids = Object.entries(cardFull?.fieldHistory || {})
      .filter(([, hist]) => Array.isArray(hist) && hist.some((h: any) =>
        h?.motivo === 'reenvio_portal' && (h.editedAt === entry.movedAt || ts(h.editedAt) === ts(entry.movedAt))))
      .map(([id]) => id);
    return ids.length > 0 ? ids : null;
  }
  // Só a última solicitação fica no card: vale para a passagem em que ela foi feita.
  const sol = cardFull?.solicitacaoReenvio;
  if (sol?.solicitadoEm && Array.isArray(sol.campos) && sol.campos.length > 0) {
    const pedidoEm = ts(sol.solicitadoEm);
    const inicio = ts(entry.movedAt);
    const fim = entry.leftAt ? ts(entry.leftAt) : Infinity;
    // folga para relógio do navegador (movedAt) x servidor (solicitadoEm)
    if (pedidoEm >= inicio - 60000 && pedidoEm <= fim) return sol.campos.map((c: any) => String(c.campoId));
  }
  return null;
};

interface DetalhesCartaoProps {
  card: Card;
  etapa: Etapa;
  etapasAnteriores: Etapa[];
  modo: 'visualizar' | 'editar';
  onSave: (cardAtualizado: Card) => void;
  onDelete: (cardId: string) => void;
  onClose: () => void;
  userPermissions?: {
    canEdit: boolean;
    canAdmin: boolean;
    currentUser?: any;
  };
  checkCardEditPermission?: (etapa: any, card?: any) => boolean;
  automacoes?: any[];
  flowId?: string; // ID do fluxo para buscar documentos
  flowName?: string; // Nome do fluxo para templates
  // IMPORTANTE: empresaId é necessário para buscar templates de documentos.
  // O card.empresaId pode estar undefined em alguns casos, então usamos o empresaId do flow como fallback.
  // Sem isso, a API /api/documentos/templates não retorna os templates corretamente.
  // Corrigido em: 22/01/2026
  empresaId?: string;
  // Callbacks para hierarquia
  todosCards?: Card[]; // Lista de todos os cards para encontrar pai/filhos
  onDesvincularFilho?: (cardPaiId: string, filhoId: string) => void;
  onSelecionarCard?: (card: Card) => void; // Para abrir detalhes de pai/filho
  colaboradores?: any[]; // Lista de colaboradores para resolver nomes
  todasEtapas?: Etapa[]; // Todas as etapas do fluxo (para resolver nomes na timeline)
  cotacaoHabilitada?: boolean; // 💰 vem da configuração do fluxo, não da etapa
  cotacaoRotulo?: string;
  // 💰 O modelo de campos vem na própria etapa (etapa.cotacaoCampos); isto só
  // avisa o board quando ele é atualizado pelo modal do card.
  onCotacaoPadraoSalvo?: (
    etapaId: string,
    padrao: { campos: CamposCotacaoEtapa | null; fornecedores: FornecedorCotacao[] | null }
  ) => void;
  etiquetas?: Etiqueta[]; // Biblioteca de etiquetas do fluxo (para vincular ao card)
  // 🔪 Avisa o board que uma fatia nasceu. O servidor já persistiu tudo — este
  // callback existe só para o Kanban refletir na hora, sem novo PUT.
  onCardFatiado?: (novoCard: any, cardOrigemId: string, camposRestantes: Record<string, any>) => void;
  // ➡️ Movimentação de etapa pelo modal (mesmas regras do drag-and-drop).
  // A lista já chega filtrada pelo Kanban: permissões do usuário + permiteMoverPara da etapa.
  etapasDisponiveisParaMover?: Etapa[];
  onMoverParaEtapa?: (etapaDestinoId: string) => void | Promise<void>;
}

// Sub-componente para mostrar histórico de etapas do fluxo de origem (card conectado)
const HistoricoFluxoOrigem = ({ cardId, userEmail, empresaId, onAbrirGaleria }: {
  cardId: string,
  userEmail?: string,
  empresaId?: string,
  onAbrirGaleria?: (itens: any[], index: number) => void,
}) => {
  const [dados, setDados] = useState<{
    flowNome: string;
    cardOrigemTitulo: string;
    etapas: { id: string; nome: string; campos: any[] }[];
    historico: any[];
    camposCardOrigem: Record<string, any>;
    etapaAtualId: string;
    cadeiaOrigens?: Array<{
      flowNome: string;
      flowId: string;
      cardOrigemTitulo: string;
      cardOrigemId: string;
      etapas: { id: string; nome: string; campos: any[] }[];
      historico: any[];
      camposCardOrigem: Record<string, any>;
      etapaAtualId: string;
    }>;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [expandido, setExpandido] = useState(false);

  useEffect(() => {
    const buscar = async () => {
      try {
        const params = new URLSearchParams();
        if (userEmail) params.set('userEmail', userEmail);
        if (empresaId) params.set('empresaId', empresaId);
        
        const res = await fetch(apiUrl(`/cards-conectados/historico-origem/${cardId}`) + '?' + params.toString());
        if (res.ok) {
          const data = await res.json();
          if (data.success && ((data.historico && data.historico.length > 0) || (data.cadeiaOrigens && data.cadeiaOrigens.length > 0))) {
            setDados({
              flowNome: data.flowNome || '',
              cardOrigemTitulo: data.cardOrigemTitulo || '',
              etapas: data.etapas || [],
              historico: data.historico || [],
              camposCardOrigem: data.camposCardOrigem || {},
              etapaAtualId: data.etapaAtualId || '',
              cadeiaOrigens: data.cadeiaOrigens || []
            });
          }
        }
      } catch (err) {
        console.warn('Erro ao buscar histórico de origem:', err);
      } finally {
        setLoading(false);
      }
    };
    buscar();
  }, [cardId]);

  if (loading) {
    return (
      <div className="text-xs text-gray-400 py-2 text-center">Carregando histórico do fluxo de origem...</div>
    );
  }

  if (!dados || (dados.historico.length === 0 && (!dados.cadeiaOrigens || dados.cadeiaOrigens.length === 0))) return null;

  // Usar cadeiaOrigens se disponível, senão cair no formato antigo (1 nível)
  const niveisOrigem = dados.cadeiaOrigens && dados.cadeiaOrigens.length > 0
    ? dados.cadeiaOrigens
    : [{
        flowNome: dados.flowNome,
        flowId: '',
        cardOrigemTitulo: dados.cardOrigemTitulo,
        cardOrigemId: '',
        etapas: dados.etapas,
        historico: dados.historico,
        camposCardOrigem: dados.camposCardOrigem,
        etapaAtualId: dados.etapaAtualId
      }];

  // Mapear etapas únicas por nível
  const niveisProcessados = niveisOrigem.map((nivel: any) => {
    const unicos: string[] = [];
    const etapasHist: { id: string; nome: string; campos: any[]; camposSnapshot: Record<string, any> }[] = [];
    
    // Criar mapa de todas as definições de campos do fluxo (para resolver nomes)
    const mapaCampos: Record<string, { nome: string; tipo: string }> = {};
    (nivel.etapas || []).forEach((et: any) => {
      (et.campos || []).forEach((c: any) => {
        mapaCampos[String(c.id)] = { nome: c.nome || c.name || String(c.id), tipo: c.tipo || 'texto' };
      });
    });
    
    (nivel.historico || []).forEach((h: any) => {
      const id = String(h.etapaId);
      if (!unicos.includes(id)) {
        const etapaDef = (nivel.etapas || []).find((e: any) => String(e.id) === id);
        unicos.push(id);
        etapasHist.push({
          id,
          nome: etapaDef?.nome || h.etapaNome || `Etapa desconhecida`,
          campos: etapaDef?.campos || [],
          camposSnapshot: h.camposSnapshot || {}
        });
      }
    });
    
    return {
      flowNome: nivel.flowNome || 'Fluxo de Origem',
      cardOrigemTitulo: nivel.cardOrigemTitulo || '',
      etapasHist,
      camposCardOrigem: nivel.camposCardOrigem || {},
      mapaCampos
    };
  });

  const totalEtapas = niveisProcessados.reduce((acc: number, n: any) => acc + n.etapasHist.length, 0);

  return (
    <div className="border border-purple-200/80 rounded-xl overflow-hidden bg-white shadow-sm mb-3 ring-1 ring-purple-900/5">
      {/* Cabeçalho */}
      <button
        onClick={() => setExpandido(!expandido)}
        className="w-full flex items-center justify-between px-3 py-2 bg-gradient-to-r from-purple-50 via-indigo-50 to-purple-50 border-b border-purple-200 hover:from-purple-100 hover:to-indigo-100 transition-colors"
      >
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-purple-500 to-indigo-500 flex items-center justify-center shadow-sm">
            <span className="text-white text-[10px]">🔗</span>
          </div>
          <div className="text-left">
            <h3 className="font-bold text-gray-900 text-xs">
              Histórico do Fluxo de Origem
            </h3>
            <span className="text-[10px] text-purple-600 font-medium">
              {niveisProcessados.map((n: any) => n.flowNome).join(' → ')} • {niveisProcessados[niveisProcessados.length - 1]?.cardOrigemTitulo} • {totalEtapas} etapa{totalEtapas > 1 ? 's' : ''}
            </span>
          </div>
        </div>
        <span className="text-[10px] font-semibold text-purple-700 border border-purple-200 bg-white rounded-md px-2 py-0.5 whitespace-nowrap">Abrir ↗</span>
      </button>
      
      {/* 27/10 André: o histórico da origem abre numa janela separada, por cima do card, em vez de
          espremer a coluna do Histórico de Etapas */}
      {expandido && createPortal(
        <div className="fixed inset-0 z-[10050] bg-black/50 flex items-center justify-center p-3" onClick={() => setExpandido(false)}>
        <div className="w-full max-w-4xl max-h-[90vh] flex flex-col bg-white rounded-2xl shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between gap-3 px-4 py-3 bg-gradient-to-r from-purple-50 via-indigo-50 to-purple-50 border-b border-purple-200">
          <div className="min-w-0">
            <h3 className="font-bold text-gray-900 text-sm">Histórico do Fluxo de Origem</h3>
            <p className="text-xs text-purple-600 font-medium truncate">
              {niveisProcessados.map((n: any) => n.flowNome).join(' → ')} • {niveisProcessados[niveisProcessados.length - 1]?.cardOrigemTitulo}
            </p>
          </div>
          <button onClick={() => setExpandido(false)} className="p-1.5 text-gray-500 hover:text-gray-800 hover:bg-white rounded-lg" aria-label="Fechar">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
        <div className="p-3 space-y-3 flex-1 overflow-y-auto bg-gray-50/50">
          {niveisProcessados.map((nivel: any, nivelIdx: number) => (
            <div key={`nivel-${nivelIdx}`}>
              {/* Separador de nível (se múltiplos) */}
              {niveisProcessados.length > 1 && (
                <div className="flex items-center gap-1.5 mb-1.5">
                  <div className={`w-2 h-2 rounded-full ${nivelIdx === 0 ? 'bg-indigo-400' : 'bg-purple-400'}`}></div>
                  <span className="text-[10px] font-semibold text-purple-700">{nivel.flowNome}</span>
                  {nivelIdx < niveisProcessados.length - 1 && (
                    <span className="text-[9px] text-gray-400 ml-1">→ gerou subprocesso</span>
                  )}
                </div>
              )}
              
              <div className="space-y-2">
                {nivel.etapasHist.map((etapaOrig: any, index: number) => (
                  <div key={`orig-${nivelIdx}-${etapaOrig.id}-${index}`} className="bg-white rounded-md border border-purple-100 overflow-hidden">
                    {/* Cabeçalho da etapa */}
                    <div className="flex items-center gap-2 px-2 py-1.5 bg-gradient-to-r from-purple-50 to-white border-b border-purple-50">
                      <div className={`w-5 h-5 rounded-full ${nivelIdx === 0 && niveisProcessados.length > 1 ? 'bg-indigo-500' : 'bg-purple-500'} text-white flex items-center justify-center text-[10px] font-bold`}>
                        {index + 1}
                      </div>
                      <span className="font-medium text-gray-800 text-xs">{etapaOrig.nome}</span>
                    </div>
                    
                    {/* Campos da etapa */}
                    <div className="px-2 py-1.5">
                      {(() => {
                        const snapshot = etapaOrig.camposSnapshot;
                        const camposCard = nivel.camposCardOrigem;
                        
                        // Gerar lista de campos para exibir
                        const camposParaExibir: { id: string; nome: string; tipo: string }[] = [];
                        
                        if (etapaOrig.campos && etapaOrig.campos.length > 0) {
                          camposParaExibir.push(...etapaOrig.campos.map((c: any) => ({
                            id: String(c.id),
                            nome: c.nome || c.name || nivel.mapaCampos[String(c.id)]?.nome || String(c.id),
                            tipo: c.tipo || 'texto'
                          })));
                        } else {
                          // Sem definição de campos: usar as chaves do snapshot, resolvendo nomes via mapaCampos
                          const todasChaves = new Set([...Object.keys(snapshot)]);
                          todasChaves.forEach(campoId => {
                            const def = nivel.mapaCampos[campoId];
                            camposParaExibir.push({
                              id: campoId,
                              nome: def?.nome || campoId,
                              tipo: def?.tipo || 'texto'
                            });
                          });
                        }
                        
                        if (camposParaExibir.length === 0) {
                          return (
                            <div className="text-[10px] text-gray-400 italic text-center py-1">
                              Nenhum campo
                            </div>
                          );
                        }
                        
                        return (
                          <div className="space-y-1">
                            {camposParaExibir.map(campo => {
                              let valorFinal = snapshot[campo.id];
                              if (valorFinal === undefined || valorFinal === null || valorFinal === '') {
                                valorFinal = camposCard[campo.id];
                              }
                              if (valorFinal === undefined || valorFinal === null || valorFinal === '') {
                                valorFinal = '-';
                              }

                              // 📎 Anexos: array de objetos {name, url, ...} ou objeto único
                              const ehObjArquivo = (v: any) => v && typeof v === 'object' && (v.name || v.fileName || v.url || v.dataUrl);
                              const arquivos: any[] = Array.isArray(valorFinal)
                                ? valorFinal.filter(ehObjArquivo)
                                : (ehObjArquivo(valorFinal) ? [valorFinal] : []);
                              if (arquivos.length > 0) {
                                return (
                                  <div key={campo.id} className="flex items-start gap-1">
                                    <span className="text-[10px] text-gray-500 flex-shrink-0">{campo.nome}:</span>
                                    <div className="flex flex-wrap gap-1 min-w-0">
                                      {arquivos.map((arq: any, i: number) => {
                                        const nome = arq.name || arq.fileName || arq.originalName || `Anexo ${i + 1}`;
                                        const url = arq.url || arq.webViewLink || arq.webContentLink || arq.dataUrl;
                                        const abrirNaGaleria = (e: React.MouseEvent) => {
                                          if (!onAbrirGaleria) return;
                                          e.preventDefault();
                                          // 🔄 Agregar TODOS os anexos da etapa (de todos os campos),
                                          // igual o histórico de etapas normal faz, e posicionar a galeria
                                          // no índice global correspondente ao arquivo clicado.
                                          const itensGlobais: any[] = [];
                                          let targetGlobalIndex = 0;
                                          camposParaExibir.forEach((c2) => {
                                            let v: any = snapshot[c2.id];
                                            if (v === undefined || v === null || v === '') v = camposCard[c2.id];
                                            const arqs: any[] = Array.isArray(v)
                                              ? v.filter(ehObjArquivo)
                                              : (ehObjArquivo(v) ? [v] : []);
                                            arqs.forEach((a: any, idx: number) => {
                                              if (c2.id === campo.id && idx === i) {
                                                targetGlobalIndex = itensGlobais.length;
                                              }
                                              itensGlobais.push({
                                                arquivo: a,
                                                campoId: c2.id,
                                                campoNome: c2.nome,
                                                etapaId: etapaOrig.id,
                                                indexInCampo: idx,
                                              });
                                            });
                                          });
                                          onAbrirGaleria(itensGlobais, targetGlobalIndex);
                                        };
                                        return url ? (
                                          <a
                                            key={i}
                                            href={url}
                                            onClick={onAbrirGaleria ? abrirNaGaleria : undefined}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-[10px] text-indigo-600 hover:text-indigo-800 underline break-all cursor-pointer"
                                            title={nome}
                                          >
                                            📎 {nome}
                                          </a>
                                        ) : (
                                          <span
                                            key={i}
                                            onClick={onAbrirGaleria ? abrirNaGaleria : undefined}
                                            className={`text-[10px] text-gray-700 break-all ${onAbrirGaleria ? 'cursor-pointer hover:text-indigo-700 hover:underline' : ''}`}
                                            title={nome}
                                          >
                                            📎 {nome}
                                          </span>
                                        );
                                      })}
                                    </div>
                                  </div>
                                );
                              }

                              // itens da solicitação: painel de cotação com os anexos de cada item
                              if (ehListaDeItens(valorFinal)) {
                                return (
                                  <div key={campo.id} className="flex flex-col gap-0.5">
                                    <span className="text-[10px] text-gray-500">{campo.nome}:</span>
                                    <ItensResumo valor={valorFinal} />
                                  </div>
                                );
                              }

                              let valorExibir = valorFinal;
                              if (Array.isArray(valorFinal) && valorFinal.some((v: any) => v && typeof v === 'object' && 'paga' in v && 'nome' in v)) {
                                valorExibir = textoParcelas(valorFinal);
                              } else if (ehListaDeViajantes(valorFinal)) {
                                valorExibir = textoViajantes(valorFinal);
                              } else if (ehListaDeVoos(valorFinal)) {
                                valorExibir = textoVoos(valorFinal);
                              } else if (ehListaDeTrechos(valorFinal)) {
                                valorExibir = textoTrechos(valorFinal);
                              } else if (ehListaDeItens(valorFinal)) {
                                valorExibir = textoItens(valorFinal);
                              } else if (ehMapaCotacao(valorFinal)) {
                                valorExibir = textoCotacoes(valorFinal);
                              } else if (ehNdVinculada(valorFinal)) {
                                valorExibir = textoNd(valorFinal);
                              } else if (Array.isArray(valorFinal)) {
                                valorExibir = valorFinal
                                  .map((v: any) => (v && typeof v === 'object' ? (v.name || v.fileName || JSON.stringify(v)) : String(v)))
                                  .join(', ');
                              } else if (typeof valorFinal === 'object' && valorFinal !== null) {
                                if (valorFinal.name || valorFinal.fileName) {
                                  valorExibir = valorFinal.name || valorFinal.fileName;
                                } else {
                                  valorExibir = JSON.stringify(valorFinal);
                                }
                              }
                              
                              // Formatar datas
                              if ((campo.tipo === 'data' || campo.tipo === 'data_vencimento') && valorFinal && valorFinal !== '-') {
                                try {
                                  const d = new Date(String(valorFinal));
                                  if (!isNaN(d.getTime())) {
                                    valorExibir = d.toLocaleDateString('pt-BR') + ', ' + d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
                                  }
                                } catch {}
                              }
                              
                              if (ehListaDeTrechos(valorFinal) || ((campo.tipo === 'rotas' || campo.tipo === 'rota_terrestre') && Array.isArray(valorFinal))) {
                                return <TrechosHistorico key={campo.id} nome={campo.nome} trechos={valorFinal} terrestre={campo.tipo === 'rota_terrestre'} />;
                              }

                              return (
                                <div key={campo.id} className="flex items-start gap-1">
                                  <span className="text-[10px] text-gray-500 min-w-0 flex-shrink-0">{campo.nome}:</span>
                                  <TextoComHtml valor={valorExibir} className="text-[10px] text-gray-800 font-medium break-words" />
                                </div>
                              );
                            })}
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        </div>
        </div>,
        document.body
      )}
    </div>
  );
};

// Sub-componente para mostrar origem do card conectado com título e fluxo reais
const CardConectadoOrigem = ({ cardOrigemId, cor, userEmail, empresaId }: { cardOrigemId: string, flowOrigemId?: string, cor: string, userEmail?: string, empresaId?: string }) => {
  const [info, setInfo] = useState<{ titulo: string, flowNome: string } | null>(null);

  useEffect(() => {
    const buscar = async () => {
      try {
        const params = new URLSearchParams();
        if (userEmail) params.set('userEmail', userEmail);
        if (empresaId) params.set('empresaId', empresaId);
        params.set('cardId', cardOrigemId);
        
        const res = await fetch(apiUrl('/cards-conectados/relacoes') + '?' + params.toString());
        if (res.ok) {
          const data = await res.json();
          if (data.success) {
            const relacoes = data.relacoes || [];
            // Procurar relação onde este card é o destino (ou seja, o card atual foi gerado a partir do cardOrigem)
            const rel = relacoes.find((r: any) => r.cardOrigemId === cardOrigemId);
            if (rel) {
              setInfo({ titulo: rel.cardOrigemTitulo || `Card #${cardOrigemId.substring(0, 7)}`, flowNome: rel.flowOrigemNome || '' });
              return;
            }
          }
        }
        setInfo({ titulo: `Card #${cardOrigemId.substring(0, 7)}`, flowNome: '' });
      } catch {
        setInfo({ titulo: `Card #${cardOrigemId.substring(0, 7)}`, flowNome: '' });
      }
    };
    buscar();
  }, [cardOrigemId]);

  if (!info) {
    return <span className="text-gray-400 text-xs">Carregando...</span>;
  }

  return (
    <>
      <span className="font-medium" style={{ color: cor }}>
        {info.titulo}
      </span>
      {info.flowNome && (
        <span className="text-gray-400 ml-1">(Fluxo: {info.flowNome})</span>
      )}
    </>
  );
};

// Sub-componente para listar cards conectados com títulos reais
const CardsConectadosLista = ({ cardIds, cor, userEmail, empresaId }: { cardIds: string[], cor: string, userEmail?: string, empresaId?: string }) => {
  const [cardsInfo, setCardsInfo] = useState<{ id: string, titulo: string, flowNome: string }[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const buscarTitulos = async () => {
      try {
        const params = new URLSearchParams();
        if (userEmail) params.set('userEmail', userEmail);
        if (empresaId) params.set('empresaId', empresaId);
        
        const res = await fetch(apiUrl('/cards-conectados/relacoes') + '?' + params.toString());
        if (res.ok) {
          const data = await res.json();
          if (data.success) {
            const relacoes = data.relacoes || [];
            // Mapear IDs para títulos usando as relações
            const info = cardIds.map(cId => {
              const rel = relacoes.find((r: any) => r.cardDestinoId === cId);
              return {
                id: cId,
                titulo: rel?.cardDestinoTitulo || `Card #${cId.substring(0, 7)}`,
                flowNome: rel?.flowDestinoNome || ''
              };
            });
            setCardsInfo(info);
          }
        }
      } catch (err) {
        console.warn('Erro ao buscar títulos dos cards conectados:', err);
        setCardsInfo(cardIds.map(cId => ({ id: cId, titulo: `Card #${cId.substring(0, 7)}`, flowNome: '' })));
      } finally {
        setLoading(false);
      }
    };
    buscarTitulos();
  }, [cardIds.join(',')]);

  if (loading) {
    return (
      <div className="text-xs text-gray-400 py-1">Carregando subprocessos...</div>
    );
  }

  return (
    <div className="space-y-1">
      <p className="text-xs text-gray-500 mb-1">
        {cardsInfo.length} subprocesso{cardsInfo.length > 1 ? 's' : ''} gerado{cardsInfo.length > 1 ? 's' : ''}:
      </p>
      {cardsInfo.map(info => (
        <div key={info.id} className="flex items-center gap-1.5 px-2 py-1.5 bg-white rounded border border-gray-200 text-xs">
          <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: cor }} />
          <span className="font-medium text-gray-700 truncate">{info.titulo}</span>
          {info.flowNome && (
            <span className="text-gray-400 text-[10px] ml-auto flex-shrink-0">→ {info.flowNome}</span>
          )}
        </div>
      ))}
    </div>
  );
};

// Detecta se um texto parece conter HTML (tags básicas ou entidades).
const pareceHtml = (s: string): boolean => /<\/?[a-z][\s\S]*?>|&[a-z#0-9]+;/i.test(s);

// Sanitização mínima: remove tags perigosas e atributos inline `on*=`/`javascript:`.
// Usado apenas para conteúdo gerado por usuário em campos de texto longo
// (ex: rich-text que veio com <div>, <br>, &nbsp;).
const sanitizarHtmlBasico = (html: string): string => {
  return String(html)
    .replace(/<\s*(script|style|iframe|object|embed|link|meta)[\s\S]*?<\s*\/\s*\1\s*>/gi, '')
    .replace(/<\s*(script|style|iframe|object|embed|link|meta)\b[^>]*>/gi, '')
    .replace(/\son\w+\s*=\s*"[^"]*"/gi, '')
    .replace(/\son\w+\s*=\s*'[^']*'/gi, '')
    .replace(/\son\w+\s*=\s*[^\s>]+/gi, '')
    .replace(/javascript\s*:/gi, '');
};

// Texto plano com URLs viráveis em link clicável (ex.: campo com o link do
// chamado no portal do fornecedor).
const linkificar = (texto: string) =>
  texto.split(/(https?:\/\/[^\s<]+)/g).map((parte, i) =>
    /^https?:\/\//.test(parte)
      ? <a key={i} href={parte} target="_blank" rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="text-blue-600 underline break-all hover:text-blue-800">{parte}</a>
      : parte
  );

// Mesma ideia do linkificar, mas para conteúdo que já é HTML. Valor vindo de
// e-mail quase sempre traz &nbsp; ou alguma tag, o que jogava o campo no ramo
// HTML e deixava o link do chamado como texto morto.
// Mexe só em nós de texto: href/src nunca são tocados e link que já existe fica
// como está.
const linkificarHtml = (html: string): string => {
  try {
    const doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html');
    const raiz = doc.body.firstElementChild;
    if (!raiz) return html;

    const textos: Text[] = [];
    const walker = doc.createTreeWalker(raiz, NodeFilter.SHOW_TEXT);
    for (let no = walker.nextNode(); no; no = walker.nextNode()) {
      textos.push(no as Text);
    }

    textos.forEach((no) => {
      if (no.parentElement?.closest('a')) return;
      const partes = no.data.split(/(https?:\/\/[^\s<]+)/g);
      if (partes.length < 2) return;

      const frag = doc.createDocumentFragment();
      partes.forEach((parte) => {
        if (!parte) return;
        if (/^https?:\/\//.test(parte)) {
          const a = doc.createElement('a');
          a.href = parte;
          a.target = '_blank';
          a.rel = 'noopener noreferrer';
          a.className = 'text-blue-600 underline break-all hover:text-blue-800';
          a.textContent = parte;
          frag.appendChild(a);
        } else {
          frag.appendChild(doc.createTextNode(parte));
        }
      });
      no.replaceWith(frag);
    });

    return raiz.innerHTML;
  } catch {
    return html;
  }
};

// Gera o PDF de um documento do card no servidor (texto vetorial, arquivo
// pequeno, sem travar a aba). O html2pdf do navegador continua aqui só como
// rede de segurança: se o servidor não tiver a lib (503) ou estiver fora,
// o botão ainda funciona como funcionava antes.
const gerarPdfDocumento = async (htmlPreenchido: string, nomeArquivo: string) => {
  const baixarBlob = (blob: Blob, nome: string) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = nome;
    document.body.appendChild(a);
    a.click();
    a.remove();
    // revoga depois do clique pra não cancelar o download em andamento
    setTimeout(() => URL.revokeObjectURL(url), 10000);
  };

  try {
    const sessaoRaw = localStorage.getItem('session');
    const sessao = sessaoRaw ? JSON.parse(sessaoRaw) : null;

    const resp = await fetch(apiUrl('/documentos/gerar-pdf'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(sessao?.usuario ? {
          'X-User-Data': JSON.stringify({
            email: sessao.usuario.email,
            id: sessao.usuario.id,
            role: sessao.usuario.role,
            empresaId: sessao?.empresa?.id,
          })
        } : {}),
      },
      body: JSON.stringify({ html: htmlPreenchido, nome: nomeArquivo }),
    });

    if (resp.ok) {
      baixarBlob(await resp.blob(), `${nomeArquivo}.pdf`);
      return;
    }

    if (resp.status !== 503) {
      const erro = await resp.json().catch(() => ({}));
      throw new Error(erro?.error || `Erro HTTP ${resp.status}`);
    }
    console.warn('⚠️ PDF no servidor indisponível — caindo no html2pdf local');
  } catch (err) {
    console.warn('⚠️ Falha no PDF do servidor, tentando local:', err);
  }

  // ---- fallback: o caminho antigo, no navegador ----
  const element = document.createElement('div');
  element.innerHTML = htmlPreenchido;
  element.style.padding = '20px';
  element.style.fontFamily = 'Arial, sans-serif';
  element.style.width = '170mm';
  const style = document.createElement('style');
  style.textContent = `
    * { box-sizing: border-box; }
    p, li, tr, img, table, blockquote, h1, h2, h3, h4, h5, h6 {
      page-break-inside: avoid;
      break-inside: avoid;
    }
    table { border-collapse: collapse; width: 100%; }
  `;
  element.prepend(style);

  // Acima de ~65000px de altura o canvas estoura o limite do Chrome e o PDF sai
  // com as páginas em branco, sem erro. Baixar a escala evita isso.
  document.body.appendChild(element);
  const alturaPx = element.offsetHeight || 0;
  element.remove();
  const escala = alturaPx > 0 ? Math.max(1, Math.min(2, 60000 / alturaPx)) : 2;

  const opt = {
    margin: [10, 15, 10, 15] as [number, number, number, number],
    filename: `${nomeArquivo}.pdf`,
    image: { type: 'jpeg' as const, quality: 0.9 },
    html2canvas: { scale: escala, useCORS: true },
    jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' as const },
    pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
  };
  await html2pdf().set(opt).from(element).save();
};

// Componente para renderizar texto que pode ou não conter HTML.
// Se for HTML, sanitiza e usa dangerouslySetInnerHTML; senão, renderiza como texto plano
// (com URLs viráveis em link). Nos dois caminhos as URLs saem clicáveis.
const TextoComHtml: React.FC<{ valor: any; className?: string }> = ({ valor, className }) => {
  const s = String(valor ?? '');
  if (pareceHtml(s)) {
    return <span className={className} dangerouslySetInnerHTML={{ __html: linkificarHtml(sanitizarHtmlBasico(s)) }} />;
  }
  return <span className={className}>{linkificar(s)}</span>;
};

// Campos 'rotas' e 'rota_terrestre' guardam uma lista de trechos (objetos);
// sem isso o histórico mostrava "[object Object]".
// 23/10/2026 (Ednelson): campo "Itens da solicitação" aparecia como [object Object] no histórico
// 26/10/2026: item salvo só com item/nome + quantidade/unidade (sem fornecedor e sem valor)
// caía no join(', ') e aparecia "[object Object]" no Histórico de Etapas (card #5DXXLQI).
const ehListaDeItens = (v: any) =>
  Array.isArray(v) && v.some((x: any) => x && typeof x === 'object' && !('paga' in x)
    && ('item' in x || 'nome' in x)
    && ('fornecedor' in x || 'valorUnitario' in x || 'quantidade' in x || 'unidade' in x));

const ehListaDeTrechos = (v: any) =>
  Array.isArray(v) && v.some((t: any) => t && typeof t === 'object' && ('origem' in t || 'destino' in t));

const textoTrechos = (trechos: any[]) => trechos
  .filter((t: any) => t && typeof t === 'object')
  .map((t: any, i: number) => {
    const seta = t.tipo === 'volta' ? '←' : '→';
    const extras: string[] = [];
    if (t.data) {
      const d = new Date(String(t.data).length === 10 ? `${t.data}T00:00:00` : t.data);
      extras.push(isNaN(d.getTime()) ? String(t.data) : d.toLocaleDateString('pt-BR'));
    }
    if (t.distancia_km) extras.push(`${Math.round(Number(t.distancia_km))} km`);
    return `${i + 1}. ${t.origem || '?'} ${seta} ${t.destino || '?'}${extras.length ? ` (${extras.join(', ')})` : ''}`;
  })
  .join(' | ');

// Mesmo layout de cartões do preenchimento (Trecho de Ida/Volta), só leitura,
// para o Histórico de Etapas. Aéreo em azul/âmbar, terrestre em verde/laranja.
const TrechosHistorico: React.FC<{ nome: string; trechos: any[]; terrestre?: boolean }> = ({ nome, trechos, terrestre }) => {
  const lista = trechos.filter((t: any) => t && typeof t === 'object');
  const dataBr = (s: any) => {
    if (!s) return '-';
    const d = new Date(String(s).length === 10 ? `${s}T00:00:00` : s);
    return isNaN(d.getTime()) ? String(s) : d.toLocaleDateString('pt-BR');
  };
  const reais = (n: any) => Number(n).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  return (
    <div className="flex flex-col gap-1">
      <span className="text-[10px] text-gray-500">{nome}:</span>
      {lista.length === 0 && <span className="text-[10px] text-gray-400 italic">-</span>}
      {lista.map((t: any, idx: number) => {
        const volta = t.tipo === 'volta';
        const cor = terrestre
          ? (volta ? { borda: 'border-orange-200', fundo: 'from-orange-50', topo: 'bg-orange-100 text-orange-800' } : { borda: 'border-green-200', fundo: 'from-green-50', topo: 'bg-green-100 text-green-800' })
          : (volta ? { borda: 'border-amber-200', fundo: 'from-amber-50', topo: 'bg-amber-100 text-amber-800' } : { borda: 'border-blue-200', fundo: 'from-blue-50', topo: 'bg-blue-100 text-blue-800' });
        const icone = volta ? '🔙' : (terrestre ? '🚗' : '✈️');
        return (
          <div key={t.id || idx} className={`rounded-lg border ${cor.borda} bg-gradient-to-r ${cor.fundo} to-white overflow-hidden`}>
            <div className={`px-2 py-1 text-[9px] font-bold uppercase tracking-wider ${cor.topo}`}>
              {icone} Trecho de {volta ? 'Volta' : 'Ida'} #{idx + 1}
            </div>
            <div className="px-2 py-1.5 grid grid-cols-2 gap-x-2 gap-y-1 text-[10px]">
              <div className="min-w-0"><div className="text-gray-500">📍 Origem</div><div className="font-medium text-gray-800 break-words">{t.origem || '-'}</div></div>
              <div className="min-w-0"><div className="text-gray-500">📍 Destino</div><div className="font-medium text-gray-800 break-words">{t.destino || '-'}</div></div>
              <div><div className="text-gray-500">📅 Data</div><div className="font-medium text-gray-800">{dataBr(t.data)}</div></div>
              <div><div className="text-gray-500">🔄 Tipo</div><div className="font-medium text-gray-800">{volta ? 'Volta' : 'Ida'}</div></div>
              {terrestre && (t.distancia_km || t.pedagio_estimado || t.combustivel_estimado) ? (
                <div className="col-span-2 text-gray-600">
                  {[
                    t.distancia_km ? `${Math.round(Number(t.distancia_km))} km` : '',
                    t.combustivel_estimado ? `combustível ${reais(t.combustivel_estimado)}` : '',
                    t.pedagio_estimado ? `pedágio ${reais(t.pedagio_estimado)}` : '',
                  ].filter(Boolean).join(' · ')}
                </div>
              ) : null}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// =====================================================================
// Galeria de Anexos: lightbox com swipe para navegar entre múltiplos
// anexos da mesma etapa/campo. Funciona em desktop (setas) e mobile (swipe).
// =====================================================================
type GaleriaItem = {
  arquivo: any;
  campoId: string;
  campoNome: string;
  etapaId?: string;
  indexInCampo: number;
};

type AnexoGaleriaProps = {
  itens: GaleriaItem[];
  initialIndex: number;
  flowId?: string;
  cardId: string;
  onClose: () => void;
};

const AnexosGaleria = ({ itens, initialIndex, flowId, cardId, onClose }: AnexoGaleriaProps) => {
  const [index, setIndex] = useState(initialIndex);
  const [resolvedUrls, setResolvedUrls] = useState<Record<number, { url: string; mime: string } | 'loading' | 'error'>>({});
  const touchStartX = useRef<number | null>(null);
  const touchEndX = useRef<number | null>(null);

  const total = itens.length;
  const goPrev = () => setIndex(i => (i - 1 + total) % total);
  const goNext = () => setIndex(i => (i + 1) % total);

  // Extrair metadados do anexo
  const getMeta = useCallback((arq: any) => {
    let nome = 'Arquivo';
    let fileUrl: string | null = null;
    let dataUrl: string | null = null;
    let fileType = '';
    if (typeof arq === 'string') {
      nome = arq;
      if (arq.startsWith('http')) fileUrl = arq;
    } else if (arq && typeof arq === 'object') {
      nome = arq.name || arq.fileName || arq.originalName || 'Arquivo';
      fileUrl = arq.webViewLink || arq.webContentLink || arq.url || null;
      dataUrl = arq.dataUrl || null;
      fileType = arq.type || arq.mimeType || '';
    }
    return { nome, fileUrl, dataUrl, fileType };
  }, []);

  // Carregar dataUrl do anexo atual sob demanda
  useEffect(() => {
    const idxs = [index, (index + 1) % total, (index - 1 + total) % total];
    idxs.forEach(async (i) => {
      if (resolvedUrls[i]) return;
      const item = itens[i];
      const arq = item.arquivo;
      const meta = getMeta(arq);
      // Se já tem URL externa, usar direto
      if (meta.fileUrl) {
        setResolvedUrls(prev => ({ ...prev, [i]: { url: meta.fileUrl!, mime: meta.fileType } }));
        return;
      }
      // Se tem dataUrl local, converter para blob
      if (meta.dataUrl && meta.dataUrl.includes('data:')) {
        try {
          const parts = meta.dataUrl.split(',');
          const base64 = parts[1];
          const mimeMatch = parts[0].match(/data:([^;]+)/);
          const mime = mimeMatch ? mimeMatch[1] : (meta.fileType || 'application/octet-stream');
          const bs = atob(base64);
          const ab = new ArrayBuffer(bs.length);
          const ia = new Uint8Array(ab);
          for (let j = 0; j < bs.length; j++) ia[j] = bs.charCodeAt(j);
          const blob = new Blob([ab], { type: mime });
          const url = URL.createObjectURL(blob);
          setResolvedUrls(prev => ({ ...prev, [i]: { url, mime } }));
          return;
        } catch {
          setResolvedUrls(prev => ({ ...prev, [i]: 'error' }));
          return;
        }
      }
      // Buscar do servidor
      if (!flowId) {
        setResolvedUrls(prev => ({ ...prev, [i]: 'error' }));
        return;
      }
      setResolvedUrls(prev => ({ ...prev, [i]: 'loading' }));
      try {
        let url = `/api/flows/${flowId}/cards/${cardId}/anexo/${item.campoId}/${item.indexInCampo}`;
        if (item.etapaId) url += `?etapaId=${item.etapaId}`;
        const resp = await fetch(url);
        const result = await resp.json();
        if (result.success && result.anexo?.dataUrl) {
          const parts = String(result.anexo.dataUrl).split(',');
          const base64 = parts[1];
          const mimeMatch = parts[0].match(/data:([^;]+)/);
          const mime = mimeMatch ? mimeMatch[1] : (meta.fileType || 'application/octet-stream');
          const bs = atob(base64);
          const ab = new ArrayBuffer(bs.length);
          const ia = new Uint8Array(ab);
          for (let j = 0; j < bs.length; j++) ia[j] = bs.charCodeAt(j);
          const blob = new Blob([ab], { type: mime });
          const blobUrl = URL.createObjectURL(blob);
          setResolvedUrls(prev => ({ ...prev, [i]: { url: blobUrl, mime } }));
        } else {
          setResolvedUrls(prev => ({ ...prev, [i]: 'error' }));
        }
      } catch {
        setResolvedUrls(prev => ({ ...prev, [i]: 'error' }));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, total]);

  // Liberar blob URLs ao desmontar
  useEffect(() => {
    return () => {
      Object.values(resolvedUrls).forEach(v => {
        if (v && typeof v === 'object' && v.url.startsWith('blob:')) URL.revokeObjectURL(v.url);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Atalhos de teclado
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') goPrev();
      else if (e.key === 'ArrowRight') goNext();
      else if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [total]);

  const item = itens[index];
  const meta = getMeta(item.arquivo);
  const resolved = resolvedUrls[index];

  const renderPreview = () => {
    if (resolved === 'loading' || resolved === undefined) {
      return (
        <div className="flex flex-col items-center gap-3 text-white">
          <div className="animate-spin w-10 h-10 border-4 border-white/30 border-t-white rounded-full"></div>
          <span className="text-sm">Carregando...</span>
        </div>
      );
    }
    if (resolved === 'error') {
      return (
        <div className="text-white text-center px-4">
          <p className="text-base font-semibold mb-1">Não foi possível carregar este anexo</p>
          <p className="text-xs opacity-70">{meta.nome}</p>
        </div>
      );
    }
    const { url, mime } = resolved;
    const isImage = mime.startsWith('image/') || /\.(png|jpe?g|gif|webp|svg|bmp)$/i.test(meta.nome);
    const isPdf = mime.includes('pdf') || /\.pdf$/i.test(meta.nome);
    if (isImage) {
      return <img src={url} alt={meta.nome} className="max-h-full max-w-full object-contain select-none" draggable={false} />;
    }
    if (isPdf) {
      return <iframe src={url} title={meta.nome} className="w-full h-full bg-white rounded" />;
    }
    return (
      <div className="text-white text-center px-4 flex flex-col items-center gap-3">
        <div className="text-5xl">📄</div>
        <p className="text-sm font-semibold break-all max-w-md">{meta.nome}</p>
        <a
          href={url}
          download={meta.nome}
          className="px-4 py-2 bg-white text-gray-900 rounded-lg font-semibold text-sm hover:bg-gray-100"
        >
          Baixar arquivo
        </a>
      </div>
    );
  };

  return createPortal(
    <div
      className="fixed inset-0 bg-black/90 z-[10100] flex flex-col"
      onClick={onClose}
      onTouchStart={(e) => { touchStartX.current = e.changedTouches[0].clientX; touchEndX.current = null; }}
      onTouchMove={(e) => { touchEndX.current = e.changedTouches[0].clientX; }}
      onTouchEnd={() => {
        if (touchStartX.current === null || touchEndX.current === null) { touchStartX.current = null; return; }
        const dx = touchEndX.current - touchStartX.current;
        if (Math.abs(dx) > 50) { dx > 0 ? goPrev() : goNext(); }
        touchStartX.current = null; touchEndX.current = null;
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-3 sm:px-5 py-3 text-white" onClick={(e) => e.stopPropagation()}>
        <div className="min-w-0 flex-1 mr-2">
          <p className="text-[11px] uppercase tracking-wider opacity-70">{item.campoNome}</p>
          <p className="text-sm sm:text-base font-semibold truncate">{meta.nome}</p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="text-xs sm:text-sm bg-white/10 rounded-full px-3 py-1">{index + 1} / {total}</span>
          {resolved && typeof resolved === 'object' && (
            <a
              href={resolved.url}
              download={meta.nome}
              target="_blank"
              rel="noreferrer"
              className="hidden sm:inline-flex items-center gap-1 px-3 py-1 bg-white/10 hover:bg-white/20 rounded-full text-xs"
              onClick={(e) => e.stopPropagation()}
            >
              ⬇ Baixar
            </a>
          )}
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center"
            title="Fechar (Esc)"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Preview area */}
      <div className="flex-1 relative flex items-center justify-center px-2 sm:px-12 pb-4 overflow-hidden" onClick={(e) => e.stopPropagation()}>
        {total > 1 && (
          <button
            onClick={(e) => { e.stopPropagation(); goPrev(); }}
            className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white/10 hover:bg-white/25 text-white flex items-center justify-center backdrop-blur-sm"
            title="Anterior (←)"
          >
            <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
          </button>
        )}
        <div className="w-full h-full flex items-center justify-center">
          {renderPreview()}
        </div>
        {total > 1 && (
          <button
            onClick={(e) => { e.stopPropagation(); goNext(); }}
            className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white/10 hover:bg-white/25 text-white flex items-center justify-center backdrop-blur-sm"
            title="Próximo (→)"
          >
            <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
          </button>
        )}
      </div>

      {/* Comentário que o fornecedor escreveu embaixo do print no portal de cotação */}
      {item.arquivo?.descricao && (
        <div className="px-3 sm:px-12 pb-3" onClick={(e) => e.stopPropagation()}>
          <p className="max-w-3xl mx-auto text-sm text-white bg-white/10 rounded-lg px-3 py-2 whitespace-pre-wrap break-words">{item.arquivo.descricao}</p>
        </div>
      )}

      {/* Thumbnails (mobile e desktop) */}
      {total > 1 && (
        <div className="flex gap-2 px-3 pb-3 overflow-x-auto justify-center" onClick={(e) => e.stopPropagation()}>
          {itens.map((_, i) => (
            <button
              key={i}
              onClick={() => setIndex(i)}
              className={`w-2.5 h-2.5 rounded-full transition-all ${i === index ? 'bg-white w-6' : 'bg-white/40 hover:bg-white/70'}`}
              title={`Anexo ${i + 1}`}
            />
          ))}
        </div>
      )}
    </div>,
    document.body
  );
};

// =====================================================================
// Miniaturas de anexos: cada arquivo do campo vira um quadrinho próprio
// (a foto/print em miniatura; PDF e planilha com ícone e nome). Clicar abre
// a galeria acima. Card antigo com um anexo solto no campo (objeto ou string,
// sem array) também passa por aqui.
// =====================================================================
const listaDeAnexos = (valor: any): any[] => {
  if (valor === undefined || valor === null || valor === '' || valor === '-') return [];
  return (Array.isArray(valor) ? valor : [valor]).filter(a => a !== undefined && a !== null && a !== '');
};

const infoAnexo = (arq: any) => {
  const obj = arq && typeof arq === 'object' ? arq : null;
  const nome: string = obj
    ? String(obj.name || obj.originalName || obj.fileName || 'Arquivo')
    : String(arq || 'Arquivo');
  const tipo = obj ? String(obj.type || obj.mimeType || '') : '';
  const ehImagem = tipo.startsWith('image/') || /\.(png|jpe?g|gif|webp|bmp|svg|heic)$/i.test(nome);
  // Só serve de <img> o que aponta para o próprio arquivo (url do servidor ou
  // dataUrl). O webViewLink do Drive é uma página de preview, não uma imagem.
  let src: string | null = null;
  if (obj?.dataUrl && String(obj.dataUrl).startsWith('data:image')) src = obj.dataUrl;
  else if (obj?.url) src = obj.url;
  else if (obj?.thumbnailLink) src = obj.thumbnailLink;
  else if (typeof arq === 'string' && /^https?:/.test(arq)) src = arq;
  const ext = nome.includes('.') ? String(nome.split('.').pop()).toUpperCase().slice(0, 4) : '';
  return { nome, ehImagem, src: ehImagem ? src : null, ext };
};

const MiniaturaAnexo = ({ arquivo, compacto, onAbrir, onRemover }: {
  arquivo: any;
  compacto?: boolean;
  onAbrir: () => void;
  onRemover?: () => void;
}) => {
  const info = infoAnexo(arquivo);
  const [falhou, setFalhou] = useState(false);
  const lado = compacto ? 'w-16 h-16' : 'w-20 h-20 sm:w-24 sm:h-24';
  const icone = info.ehImagem ? '🖼️' : info.ext === 'PDF' ? '📄' : '📎';
  const descricao = arquivo && typeof arquivo === 'object' ? String(arquivo.descricao || '').trim() : '';
  return (
    <div className="flex flex-col gap-1 flex-shrink-0">
    <div className={`relative ${lado} flex-shrink-0`}>
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); onAbrir(); }}
        title={info.nome}
        className="w-full h-full rounded-lg border border-gray-200 bg-gray-50 overflow-hidden hover:ring-2 hover:ring-blue-400 transition-all flex flex-col items-center justify-center gap-0.5"
      >
        {info.src && !falhou ? (
          <img
            src={info.src}
            alt={info.nome}
            loading="lazy"
            draggable={false}
            onError={() => setFalhou(true)}
            className="w-full h-full object-cover"
          />
        ) : (
          <>
            <span className={compacto ? 'text-lg leading-none' : 'text-2xl leading-none'}>{icone}</span>
            {info.ext && <span className="text-[9px] font-semibold text-gray-500 leading-none">{info.ext}</span>}
            <span className="px-1 w-full text-[9px] text-gray-600 truncate text-center leading-tight">{info.nome}</span>
          </>
        )}
      </button>
      {onRemover && (
        <button
          type="button"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onRemover(); }}
          className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-red-500 text-white text-[10px] leading-none flex items-center justify-center shadow hover:bg-red-600"
          title="Remover arquivo"
        >
          ✕
        </button>
      )}
    </div>
      {descricao && (
        <p
          title={descricao}
          className={`${compacto ? 'max-w-[8rem]' : 'max-w-[12rem]'} text-[11px] leading-snug text-gray-700 bg-amber-50 border border-amber-100 rounded px-1.5 py-1 whitespace-pre-wrap break-words line-clamp-4`}
        >
          {descricao}
        </p>
      )}
    </div>
  );
};

const MiniaturasAnexos = ({ arquivos, onAbrir, onRemover, compacto }: {
  arquivos: any[];
  onAbrir: (index: number) => void;
  onRemover?: (index: number) => void;
  compacto?: boolean;
}) => (
  <div className="flex flex-wrap gap-2 pt-1">
    {arquivos.map((arquivo, i) => (
      <MiniaturaAnexo
        key={`${infoAnexo(arquivo).nome}-${i}`}
        arquivo={arquivo}
        compacto={compacto}
        onAbrir={() => onAbrir(i)}
        onRemover={onRemover ? () => onRemover(i) : undefined}
      />
    ))}
  </div>
);

const DetalhesCartao = ({
  card, 
  etapa, 
  etapasAnteriores,
  modo: modoInicial, 
  onSave, 
  onDelete, 
  onClose,
  userPermissions,
  checkCardEditPermission,
  automacoes = [],
  flowId,
  flowName,
  empresaId,
  todosCards = [],
  onDesvincularFilho,
  onSelecionarCard,
  colaboradores = [],
  todasEtapas = [],
  cotacaoHabilitada = false,
  cotacaoRotulo,
  onCotacaoPadraoSalvo,
  etiquetas = [],
  onCardFatiado,
  etapasDisponiveisParaMover = [],
  onMoverParaEtapa
}: DetalhesCartaoProps) => {
  // Log inicial apenas uma vez (controlado por ref para evitar spam)
  const loggedRef = useRef(false);
  if (!loggedRef.current) {
    console.log('🚀 DetalhesCartao montado:', { cardId: card?.id, titulo: card?.titulo, etapaNome: etapa?.nome });
    loggedRef.current = true;
  }
  
  // 💬 Chat do portal vinculado a este card (conversa com o solicitante externo)
  const [submissaoPortal, setSubmissaoPortal] = useState<{
    id: string;
    portalId: string;
    protocolo?: string;
    nomeExterno?: string;
    emailExterno?: string;
    mensagensNaoLidas?: number;
  } | null>(null);
  const [chatNaoLidas, setChatNaoLidas] = useState(0);
  const [chatPortalModalAberto, setChatPortalModalAberto] = useState(false);
  const [chatPortalExpandido, setChatPortalExpandido] = useState(false);

  useEffect(() => {
    let ativo = true;
    if (!card?.id) return;
    (async () => {
      try {
        const res = await fetch(apiUrl(`/portal-submissoes/por-card/${card.id}`));
        const data = await res.json();
        if (ativo && data.success && data.submissao) {
          setSubmissaoPortal(data.submissao);
          setChatNaoLidas(data.submissao.mensagensNaoLidas || 0);
        }
      } catch (err) {
        console.error('Erro ao buscar conversa do portal:', err);
      }
    })();
    return () => { ativo = false; };
  }, [card?.id]);

  const { success, error } = useToast();
  // Verificar se o usuário pode editar este cartão baseado na etapa
  const canEditCard = checkCardEditPermission ? checkCardEditPermission(etapa, card) : 
    (userPermissions?.canEdit || userPermissions?.canAdmin);
  const canDeleteCard = canEditCard && (userPermissions?.canEdit || userPermissions?.canAdmin);

  // Parcelas: marcar como paga é de quem pode editar o card; mexer na lista
  // (adicionar, renomear, remover) é só de quem criou o card ou de admin.
  // Card migrado não tem createdBy — o criador é quem aparece no histórico como 'criado'.
  const criadorDoCard = (card as any).createdBy || (card as any).criadoPor
    || ((card as any).etapaHistory || []).find((h: any) => h?.motivo === 'criado')?.movedBy;
  const podeEditarParcelas = !!userPermissions?.canAdmin
    || (!!criadorDoCard && String(criadorDoCard) === String(userPermissions?.currentUser?.id || ''));
  const usuarioParcelas = {
    id: userPermissions?.currentUser?.id,
    nome: userPermissions?.currentUser?.nome || userPermissions?.currentUser?.email,
  };

  // 🧾 Fluxo de Verbas (tem "Valor da ND"): o card mostra as compras ligadas à ND
  const ehFluxoDeVerbas = useMemo(() => [etapa, ...(todasEtapas || [])].some((e: any) =>
    (e?.campos || []).some((c: any) => /^valor da nd/i.test(String(c?.nome || '')))), [etapa, todasEtapas]);

  // 📊 Mapa de cotação (26/10/2026): os itens cotados são os do campo "Itens da solicitação" (Triagem)
  const itensDaSolicitacao = (): { id: string | null; valor: any } => {
    const etapasDoFluxo: Etapa[] = [etapa, ...etapasAnteriores, ...(todasEtapas || [])];
    const campoItens = etapasDoFluxo.flatMap((e) => e.campos || []).find((c) => (c.tipo as string) === 'itens');
    if (!campoItens) return { id: null, valor: [] };
    return { id: campoItens.id, valor: valores[campoItens.id] ?? (card.campos as any)?.[campoItens.id] ?? [] };
  };

  // Campo "responsavel": as opções são todo mundo que participa de alguma
  // etapa do fluxo (não só desta etapa) — quem está aqui pode passar a ficha
  // pra qualquer pessoa do fluxo. Quem PODE mexer no campo continua sendo só
  // quem tem canEditCard (que já exige ser participante desta etapa).
  const colaboradoresDoFluxo = useMemo(() => {
    const etapasDoFluxo = todasEtapas && todasEtapas.length ? todasEtapas : [etapa];
    const idsParticipantes = new Set<string>();
    etapasDoFluxo.forEach((e: any) => (e.participantes || []).forEach((id: any) => idsParticipantes.add(String(id))));
    return colaboradores.filter((c: any) => idsParticipantes.has(String(c.id)));
  }, [todasEtapas, etapa, colaboradores]);

  // Campo tipo 'responsavel' guarda o id do colaborador; fora do <select> de
  // edição ele caía no render de texto e mostrava o id cru.
  const nomeResponsavelCampo = (id: any): string => {
    if (id === undefined || id === null || id === '') return '-';
    const c = colaboradores.find((c: any) => String(c.id) === String(id));
    return c?.nome || 'Pessoa não encontrada';
  };

  console.log('🔑 DetalhesCartao - Permissões:', { 
    modoInicial, 
    canEditCard, 
    canDeleteCard,
    userPermissions,
    checkCardEditPermissionExists: !!checkCardEditPermission
  });
  
  // ✅ CONFIAR NO MODO QUE VEM COMO PROP - já foi calculado corretamente no KanbanBoard
  const [modo, setModo] = useState(modoInicial);
  const [cardExpandido, setCardExpandido] = useState(false);
  const [titulo, setTitulo] = useState(card.titulo);
  const [destinoAutomacao, setDestinoAutomacao] = useState<string | null>(null);
  const automacaoMetaRef = useRef<{ automacaoId?: string; automacaoNome?: string; campoGatilho?: string } | null>(null);
  // ✈️ Viagens (29/10/2026): checkbox com "mostrarPrimeiras" mostra só as N primeiras opções até clicar "Exibir mais"
  const [opcoesExpandidas, setOpcoesExpandidas] = useState<Record<string, boolean>>({});
  const [mostrarResumo, setMostrarResumo] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<{ campoId: string; files: any[] } | null>(null);
  // 🔒 Lock global: enquanto algum anexo estiver subindo, bloqueia outros uploads
  // (evita que usuário do iPhone dispare 3 câmeras seguidas e só persista a última)
  const [uploadingCampoId, setUploadingCampoId] = useState<string | null>(null);
  const [userNames, setUserNames] = useState<{ [userId: string]: string }>({});
  const [salvando, setSalvando] = useState(false); // Estado de loading
  // 📦 produtos escolhidos no painel de estoque do GIX (guardados em card.campos.__produtosGix ao salvar)
  const produtosGixRef = useRef<number[] | null>(null);
  // 🧾 pedido da OC E01 montado na Negociação (card.campos.__pedidoGix)
  const pedidoGixRef = useRef<PedidoGixDados | null>(null);
  const [responsavelIndividual, setResponsavelIndividual] = useState<string | undefined>(card.responsavelIndividualId);
  // 27/10 André: o card abre direto em edição; antes de fechar/mover com algo não salvo, avisa.
  // A foto é tirada 1,5 s depois de abrir, para não contar as normalizações automáticas dos campos.
  const fotoInicialRef = useRef<string | null>(null);
  const [etiquetasSelecionadas, setEtiquetasSelecionadas] = useState<string[]>(card.etiquetasIds || []);
  const [mostrarWhatsAppMenu, setMostrarWhatsAppMenu] = useState(false); // Menu de compartilhar WhatsApp
  const [whatsAppNumero, setWhatsAppNumero] = useState(''); // Número digitado manualmente
  const [mostrarMoverMenu, setMostrarMoverMenu] = useState(false); // Menu "Mover para" (etapas permitidas)
  const [movendoEtapaId, setMovendoEtapaId] = useState<string | null>(null); // Etapa destino em movimentação

  // ═══ Integração GIX ═══
  // Toggles definidos em Configurações → Integrações → GIX (por empresa).
  // Funciona tanto local quanto na VPS (api_gix conecta via DDNS à rede interna).
  const [gixToggles, setGixToggles] = useState<{
    enabled: boolean;
    criarOc: boolean;
    liberarOc: boolean;
    estornarOc: boolean;
    badgeStatus: boolean;
    allowInsert: boolean;
  }>({ enabled: false, criarOc: true, liberarOc: true, estornarOc: true, badgeStatus: true, allowInsert: false });

  useEffect(() => {
    if (!empresaId) return;
    fetch(apiUrl(`/integracoes/config?empresaId=${encodeURIComponent(empresaId)}`))
      .then(r => r.json())
      .then(j => {
        if (j?.status === 'success' && j?.config?.gix) {
          setGixToggles(t => ({ ...t, ...j.config.gix }));
        }
      })
      .catch(() => { /* mantém defaults */ });
  }, [empresaId]);

  const isGixEnabled = gixToggles.enabled;
  // Status das OCs consultadas (múltiplas - separadas por ;, , ou espaço no campo)
  const [gixOcList, setGixOcList] = useState<any[]>([]);
  // Status "selecionado" para o modal de detalhes (uma OC por vez)
  const [gixOcStatus, setGixOcStatus] = useState<any>(null);
  const [gixOcLoading, setGixOcLoading] = useState(false);
  // Liberar todas
  const [gixLiberarTodasLoading, setGixLiberarTodasLoading] = useState(false);
  // Liberar individual (numero_oc -> bool)
  const [gixLiberarIndividualLoading, setGixLiberarIndividualLoading] = useState<Record<string, boolean>>({});
  // Modal de detalhes da OC (abre ao clicar no badge)
  const [gixOcDetalheAberto, setGixOcDetalheAberto] = useState(false);
  const [gixOcDetalheItens, setGixOcDetalheItens] = useState<any[]>([]);
  const [gixOcDetalheLoading, setGixOcDetalheLoading] = useState(false);
  const [gixOcDetalheErro, setGixOcDetalheErro] = useState<string | null>(null);
  // Liberar OC (toggle "Liberar OC" em Integrações → GIX)
  const [gixLiberarLoading, setGixLiberarLoading] = useState(false);
  const [gixLiberarMsg, setGixLiberarMsg] = useState<string | null>(null);
  // Modal "Criar OC no GIX"
  const [gixCriarOcAberto, setGixCriarOcAberto] = useState(false);
  const [gixCriarOcPrefill, setGixCriarOcPrefill] = useState<Partial<CriarOcConfig> | undefined>(undefined);
  // 30/10/2026: o modal abre na "OC pela nota" (a mesma criação automática do robô); o
  // formulário manual de 1 item fica escondido como opção secundária.
  const [gixCriarOcManual, setGixCriarOcManual] = useState(false);

  const [mostrarFluxoTimeline, setMostrarFluxoTimeline] = useState(false); // Modal de visualizar percurso no fluxo
  const [mostrarTimelineModal, setMostrarTimelineModal] = useState(false); // Modal da Linha do tempo (em vez de inline)
  const [mostrarModalFatiar, setMostrarModalFatiar] = useState(false); // 🔪 Modal de fatiar card
  const [mostrarModalCotacao, setMostrarModalCotacao] = useState(false); // 💰 Pedir cotação
  // 🔒 Fatiar exige ADMINISTRADOR **e** estar na etapa.
  // Note que checkCardEditPermission não serve aqui: ela devolve true direto para
  // admin, ignorando os participantes — exatamente o que precisamos verificar.
  // Etapa sem participantes configurados não restringe ninguém (mesma regra do servidor).
  const ehParticipanteDaEtapa = (et: Etapa): boolean => {
    const participantes = (et.participantes || []).map(p => String(p));
    if (participantes.length === 0) return true;
    return usuarioEstaNaLista(participantes);
  };

  // Compara a pessoa logada com uma lista que pode trazer id de user, id de
  // colaborador ou e-mail (as três collections de pessoa não compartilham id).
  const usuarioEstaNaLista = (lista: string[]): boolean => {
    const participantes = lista.map(p => String(p));
    const user = userPermissions?.currentUser;
    if (!user) return false;

    const email = String(user.email || '').toLowerCase();
    const colaborador = colaboradores.find(
      (c: any) => c?.email && email && String(c.email).toLowerCase() === email
    );

    const candidatos = [
      String(user.id ?? ''),
      String((user as any)._id ?? ''),
      email,
      colaborador ? String(colaborador.id ?? '') : '',
      colaborador ? String((colaborador as any)._id ?? '') : ''
    ].filter(Boolean);

    return candidatos.some(c =>
      participantes.some(p => p === c || p.toLowerCase() === c.toLowerCase())
    );
  };

  // 🔒 Campos internos (visivelSomenteParticipantes): só quem participa de
  // alguma etapa do fluxo, mais os extras do campo, vê o valor.
  const camposOcultosParaMim = useMemo(() => {
    const etapasDoFluxo: Etapa[] = [etapa, ...etapasAnteriores, ...(todasEtapas || [])];
    const participantesDoFluxo = new Set<string>();
    etapasDoFluxo.forEach(e => (e.participantes || []).forEach(p => participantesDoFluxo.add(String(p))));
    const ocultos = new Set<string>();
    // 🔒 visivelSomenteExtras: só a lista do campo vê; participar do fluxo não
    // basta. Administrador e admin master passam sempre.
    const veTudo = Boolean(userPermissions?.canAdmin) || ehAdminMaster(userPermissions?.currentUser?.email);
    etapasDoFluxo.forEach(e => (e.campos || []).forEach(c => {
      if (!c.visivelSomenteExtras || veTudo) return;
      if (usuarioEstaNaLista(c.visivelParaExtras || [])) return;
      ocultos.add(String(c.id));
    }));
    if (participantesDoFluxo.size === 0) return ocultos;
    const noFluxo = usuarioEstaNaLista(Array.from(participantesDoFluxo));
    etapasDoFluxo.forEach(e => (e.campos || []).forEach(c => {
      if (!c.visivelSomenteParticipantes || noFluxo) return;
      if (usuarioEstaNaLista(c.visivelParaExtras || [])) return;
      ocultos.add(String(c.id));
    }));
    return ocultos;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [etapa, etapasAnteriores, todasEtapas, userPermissions?.currentUser, colaboradores]);

  // ✅ Aprovação item a item (Viagens, Aprovação de cotações — André, 27/10/2026): em vez de uma
  // lista de checkboxes no topo, cada cotação fixada ganha o seu "Aprovar". A etapa diz qual campo
  // checkbox_multi guarda as aprovadas e qual opção dele corresponde a cada campo fixado:
  //   aprovacaoPorItem: { campoId, itens: { <campo fixado>: <opção> } }
  // O valor continua no mesmo campo, então as automações que olham "Cotações aprovadas" seguem valendo.
  const aprovacaoPorItem = (etapa as any).aprovacaoPorItem as { campoId?: string; itens?: Record<string, string> } | undefined;
  // gruposFixados: [{ titulo, campos: [ids fixados], aprovar?: <id fixado do aprovacaoPorItem> }]
  const gruposFixados = ((etapa as any).gruposFixados || []) as Array<{ titulo: string; campos: string[]; aprovar?: string }>;
  const campoEmQuadroComAprovar = new Set(gruposFixados.filter((g) => g.aprovar).flatMap((g) => g.campos));
  const renderAprovarItem = (campoFixadoId: string, doQuadro = false) => {
    // dentro de um quadro o "Aprovar" vai no pé do quadro, não embaixo do campo
    if (!doQuadro && campoEmQuadroComAprovar.has(campoFixadoId)) return null;
    const opcao = aprovacaoPorItem?.itens?.[campoFixadoId];
    const campoId = aprovacaoPorItem?.campoId;
    if (!opcao || !campoId) return null;
    const marcadas: string[] = Array.isArray(valores[campoId]) ? valores[campoId] : [];
    const marcada = marcadas.includes(opcao);
    const podeMarcar = canEditCard;
    return (
      <label className={`mt-2 flex items-center gap-2 px-2.5 py-1.5 rounded-md border text-xs font-semibold select-none ${
        marcada ? 'bg-emerald-50 border-emerald-300 text-emerald-800' : 'bg-white border-gray-300 text-gray-700'
      } ${podeMarcar ? 'cursor-pointer' : 'opacity-70'}`}>
        <input
          type="checkbox"
          checked={marcada}
          disabled={!podeMarcar}
          onChange={() => updateFieldValue(campoId, marcada ? marcadas.filter((m) => m !== opcao) : [...marcadas, opcao])}
          className="w-4 h-4 accent-emerald-600"
        />
        {marcada ? `Aprovada: ${opcao}` : `Aprovar ${opcao}`}
      </label>
    );
  };

  // Um card é fatiado uma vez só — o servidor recusa a segunda tentativa (fatiar_card)
  const jaFatiado = (card.fatiasIds?.length || 0) > 0;

  // 👑 O admin master passa por cima da exigência de ser participante da etapa.
  // A mesma exceção vale no servidor, em fatiar_card.
  const podeFatiar = Boolean(
    etapa.configuracaoFatiamento?.habilitado &&
    flowId &&
    (
      ehAdminMaster(userPermissions?.currentUser?.email) ||
      (userPermissions?.canAdmin && ehParticipanteDaEtapa(etapa))
    )
  );

  // Lista estável: um array novo a cada render remontaria a seleção do modal
  const etapasParaCotacao = useMemo(
    () => (todasEtapas && todasEtapas.length ? todasEtapas : [etapa]),
    [todasEtapas, etapa]
  );

  // 💰 Pedir cotação é mais leve que fatiar: não muda o card de origem, só cria
  // links para fora. Por isso basta participar da etapa — admin passa sempre.
  // A mesma regra vale no servidor, em _pode_pedir_cotacao.
  const podePedirCotacao = Boolean(
    cotacaoHabilitada &&
    flowId &&
    (etapa.campos || []).length > 0 &&
    (userPermissions?.canAdmin || ehParticipanteDaEtapa(etapa))
  );
  // Galeria de anexos (lightbox para múltiplos arquivos no histórico de etapas)
  const [galeriaAnexos, setGaleriaAnexos] = useState<null | {
    itens: GaleriaItem[];
    index: number;
  }>(null);

  const [fluxoFullScreen, setFluxoFullScreen] = useState(false); // Fullscreen do modal de fluxo
  const [fluxoZoom, setFluxoZoom] = useState(1); // Zoom do fluxograma
  const [fluxoPan, setFluxoPan] = useState({ x: 0, y: 0 }); // Pan offset
  const [fluxoIsPanning, setFluxoIsPanning] = useState(false); // Arrastando?
  const [fluxoPanStart, setFluxoPanStart] = useState({ x: 0, y: 0 }); // Ponto inicial do arrasto
  const fluxoModalRef = useRef<HTMLDivElement>(null);
  const [dadosFluxoOrigem, setDadosFluxoOrigem] = useState<{
    flowNome: string;
    cardOrigemTitulo: string;
    etapas: { id: string; nome: string; campos: any[] }[];
    historico: any[];
    etapaAtualId: string;
  } | null>(null);
  const [cadeiaOrigens, setCadeiaOrigens] = useState<Array<{
    flowNome: string;
    flowId: string;
    cardOrigemTitulo: string;
    cardOrigemId: string;
    etapas: { id: string; nome: string; campos: any[] }[];
    historico: any[];
    etapaAtualId: string;
  }>>([]);
  const [cadeiaDestinos, setCadeiaDestinos] = useState<Array<{
    flowNome: string;
    flowId: string;
    cardDestinoTitulo: string;
    cardDestinoId: string;
    etapas: { id: string; nome: string; campos: any[] }[];
    historico: any[];
    etapaAtualId: string;
  }>>([]);

  // Buscar dados do fluxo de origem quando o card for conectado (para resolver nomes na timeline)
  useEffect(() => {
    if (card.isCardConectado && card.cardConectadoOrigemId) {
      const buscarOrigemFluxo = async () => {
        try {
          const params = new URLSearchParams();
          const email = userPermissions?.currentUser?.email;
          if (email) params.set('userEmail', email);
          if (empresaId) params.set('empresaId', empresaId);
          const res = await fetch(apiUrl(`/cards-conectados/historico-origem/${card.id}`) + '?' + params.toString());
          if (res.ok) {
            const data = await res.json();
            if (data.success && data.historico && data.historico.length > 0) {
              setDadosFluxoOrigem({
                flowNome: data.flowNome || '',
                cardOrigemTitulo: data.cardOrigemTitulo || '',
                etapas: data.etapas || [],
                historico: data.historico || [],
                etapaAtualId: data.etapaAtualId || ''
              });
              // Salvar cadeia completa de origens (se disponível)
              if (data.cadeiaOrigens && data.cadeiaOrigens.length > 0) {
                setCadeiaOrigens(data.cadeiaOrigens);
              } else {
                // Retrocompatibilidade: montar cadeia com 1 nível
                setCadeiaOrigens([{
                  flowNome: data.flowNome || '',
                  flowId: data.flowId || '',
                  cardOrigemTitulo: data.cardOrigemTitulo || '',
                  cardOrigemId: data.cardOrigemId || '',
                  etapas: data.etapas || [],
                  historico: data.historico || [],
                  etapaAtualId: data.etapaAtualId || ''
                }]);
              }
            }
          }
        } catch (err) {
          console.warn('Erro ao buscar fluxo de origem para percurso:', err);
        }
      };
      buscarOrigemFluxo();
    } else if (!card.isCardConectado) {
      setDadosFluxoOrigem(null);
      setCadeiaOrigens([]);
      setCadeiaDestinos([]);
    }
  }, [card.isCardConectado, card.cardConectadoOrigemId, card.id]);

  // Buscar dados dos fluxos de destino (subprocessos gerados) quando abrir o modal de percurso
  useEffect(() => {
    if (mostrarFluxoTimeline && card.cardsConectadosIds && card.cardsConectadosIds.length > 0) {
      const buscarDestinosFluxo = async () => {
        try {
          const params = new URLSearchParams();
          const email = userPermissions?.currentUser?.email;
          if (email) params.set('userEmail', email);
          if (empresaId) params.set('empresaId', empresaId);
          const res = await fetch(apiUrl(`/cards-conectados/historico-destinos/${card.id}`) + '?' + params.toString());
          if (res.ok) {
            const data = await res.json();
            if (data.success && data.cadeiaDestinos && data.cadeiaDestinos.length > 0) {
              setCadeiaDestinos(data.cadeiaDestinos);
            }
          }
        } catch (err) {
          console.warn('Erro ao buscar fluxos destino para percurso:', err);
        }
      };
      buscarDestinosFluxo();
    } else if (!mostrarFluxoTimeline) {
      setCadeiaDestinos([]);
    }
  }, [mostrarFluxoTimeline, card.cardsConectadosIds]);

  // Fullscreen real via Fullscreen API do navegador
  const toggleFluxoFullScreen = async () => {
    try {
      if (!document.fullscreenElement) {
        await fluxoModalRef.current?.requestFullscreen();
        setFluxoFullScreen(true);
      } else {
        await document.exitFullscreen();
        setFluxoFullScreen(false);
      }
    } catch (e) { console.warn('Fullscreen não suportado', e); }
  };

  // Sincronizar estado quando o usuário sai do fullscreen com ESC
  useEffect(() => {
    const onFsChange = () => {
      if (!document.fullscreenElement) setFluxoFullScreen(false);
    };
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);

  // Ao abrir/fechar o modal de fluxo, esconder/mostrar o menu lateral
  useEffect(() => {
    if (mostrarFluxoTimeline) {
      window.dispatchEvent(new Event('escalone:hideMenu'));
    } else {
      window.dispatchEvent(new Event('escalone:showMenu'));
    }
  }, [mostrarFluxoTimeline]);
  
  // Sincronizar modo quando modoInicial ou canEditCard mudarem
  useEffect(() => {
    const novoModo = canEditCard ? modoInicial : 'visualizar';
    if (novoModo !== modo) {
      setModo(novoModo);
    }
  }, [modoInicial, canEditCard]);
  
  // Inicializar valores diretamente com os campos do card se existirem
  const [valores, setValores] = useState<Record<string, any>>(() => {
    return card.campos || {};
  });
  const [historicoEditavel, setHistoricoEditavel] = useState<boolean>(false);
  const fotoDados = () => { try { return JSON.stringify({ titulo, valores, responsavelIndividual }); } catch { return ''; } };
  useEffect(() => {
    const t = setTimeout(() => { fotoInicialRef.current = fotoDados(); }, 1500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [card.id]);
  const temAlteracaoNaoSalva = () =>
    modo === 'editar' && fotoInicialRef.current !== null && fotoDados() !== fotoInicialRef.current;
  const fecharCard = () => {
    if (temAlteracaoNaoSalva() && !window.confirm('Você alterou o card e não salvou. Fechar e perder as alterações?')) return;
    onClose();
  };
  // 🔧 FIX: Estado para rastrear se o usuário modificou campos da ETAPA ATUAL
  // Se o usuário só editou campos do histórico, não deve validar campos obrigatórios da etapa atual
  const [camposEtapaAtualModificados, setCamposEtapaAtualModificados] = useState<boolean>(false);
  // 🔧 FIX: Estado para rastrear APENAS os campos que o usuário REALMENTE EDITOU no histórico
  // Diferente de valoresHistorico que contém todos os valores, este só contém os editados
  const [camposEditadosNoHistorico, setCamposEditadosNoHistorico] = useState<Record<string, any>>({});
  // ⚠️ Ciência de fornecedor novo dentro do card já criado. O formulário de criação
  // sempre perguntou, mas boa parte dos fornecedores é digitada aqui — numa etapa
  // posterior, ou corrigindo a etapa anterior pelo histórico. Por esse caminho nunca
  // se perguntou nada, e o card acabava com o selo "sem registro de ciência".
  const cienciaRef = useRef<CienciaFornecedorNovo | null>(null);
  const [pedindoCiencia, setPedindoCiencia] = useState<
    { campoId: string; fornecedor: string; parecidos: any[]; noHistorico: boolean } | null>(null);
  // Estado separado para valores editados no histórico de etapas
  const [valoresHistorico, setValoresHistorico] = useState<Record<string, Record<string, any>>>(() => {
    const inicial: Record<string, Record<string, any>> = {};
    card.etapaHistory?.forEach(h => {
      if (h.camposSnapshot) {
        inicial[String(h.etapaId)] = { ...h.camposSnapshot };
      }
    });
    return inicial;
  });
  const didRunInitial = useRef<boolean>(false);
  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  
  // 📄 Estados para documentos da etapa
  const [documentosDisponiveis, setDocumentosDisponiveis] = useState<any[]>([]);
  const [loadingDocumentos, setLoadingDocumentos] = useState(false);
  const [documentoExpandido, setDocumentoExpandido] = useState<string | null>(null);
  const [gerandoPdfId, setGerandoPdfId] = useState<string | null>(null);

  // 🤖 Estados para IA contextual do card
  const [iaAberta, setIaAberta] = useState(false);
  const [iaMensagens, setIaMensagens] = useState<{id: string; role: 'user'|'assistant'; content: string; loading?: boolean}[]>([]);
  const [iaInput, setIaInput] = useState('');
  const [iaLoading, setIaLoading] = useState(false);
  const iaEndRef = useRef<HTMLDivElement>(null);
  const iaInputRef = useRef<HTMLTextAreaElement>(null);
  const [iaExpandedContent, setIaExpandedContent] = useState<{ type: 'table' | 'chart' | 'fullscreen'; html?: string; chart?: any; content?: string } | null>(null);
  
  // Estado para controlar visibilidade dos campos baseado em automações
  const [camposVisiveis, setCamposVisiveis] = useState<Record<string, boolean>>(() => {
    // Inicializar todos os campos como visíveis por padrão
    const inicial: Record<string, boolean> = {};
    etapa.campos?.forEach(campo => {
      inicial[campo.id] = true;
    });
    return inicial;
  });
  
  // Estado para controlar expansão do histórico no mobile
  const [historicoExpandidoMobile, setHistoricoExpandidoMobile] = useState(false);

  // ✈️ Viagens (Dayane 30/10/2026): campo com soViagemUrgente só aparece com a viagem a 20 dias
  // ou menos da solicitação (data_solicitacao gravada pelo servidor, senão a criação do card).
  useEffect(() => {
    const alvos = (etapa.campos || []).filter((c: any) => c.soViagemUrgente);
    if (!alvos.length) return;
    const urgente = viagemUrgente(valores, valores['campo_vg_data_solicitacao'] || (card as any).campos?.campo_vg_data_solicitacao || (card as any).createdAt);
    setCamposVisiveis(prev => {
      if (alvos.every((c: any) => (prev[c.id] !== false) === urgente)) return prev;
      const n = { ...prev };
      alvos.forEach((c: any) => { n[c.id] = urgente; });
      return n;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valores, etapa, camposVisiveis]);
  // Obrigatório condicional (obrigatorioSe: { campoId, valor }): ex. ciência da despesa só com "Sim".
  const ehObrigatorio = (campo: any) => !!campo?.obrigatorio || (!!campo?.obrigatorioSe &&
    String(valores[campo.obrigatorioSe.campoId] ?? '') === String(campo.obrigatorioSe.valor));

  // ✈️ Viagens (Dayane, 29/10/2026) ───────────────────────────────────────────
  // quadrosPorTitulo: cada "texto informativo" da etapa abre um quadro com os campos seguintes
  // (passagens aéreas, veículos, hotel...). Informativo marcado "subtitulo" fica dentro do quadro.
  // Quadro que ficou só com o título (campos escondidos pela automação) some.
  const agruparQuadrosPorTitulo = (lista: CampoConfig[], render: (c: CampoConfig) => React.ReactNode) => {
    const nodes = lista.map(render);
    if (!(etapa as any).quadrosPorTitulo) return nodes;
    const out: React.ReactNode[] = [];
    let atual: { key: string; filhos: React.ReactNode[] } | null = null;
    const fechar = () => {
      if (atual && atual.filhos.length > 1) {
        out.push(
          <div key={`quadro-${atual.key}`} className="rounded-xl border-2 border-blue-100 bg-white p-2.5 space-y-2 shadow-sm">
            {atual.filhos}
          </div>
        );
      }
      atual = null;
    };
    lista.forEach((c, i) => {
      if (c.tipo === 'texto_informativo' && !(c as any).subtitulo) {
        fechar();
        atual = { key: c.id, filhos: [nodes[i]] };
      } else if (atual) {
        atual.filhos.push(nodes[i]);
      } else {
        out.push(nodes[i]);
      }
    });
    fechar();
    return out;
  };
  // gruposFixados[].camposEtapa: campos da PRÓPRIA etapa que aparecem no pé do quadro fixado
  // (ex.: "Cotação aérea aprovada" logo abaixo do quadro das passagens).
  const camposDaEtapaEmGrupos = new Set<string>(
    (((etapa as any).gruposFixados || []) as any[]).flatMap((g: any) => g.camposEtapa || []).map(String)
  );
  // Diárias de alimentação: preenche sozinho pelo período da viagem quando pedem a diária Pluxee.
  // Quem digitou à mão manda: só troca valor vazio ou o que o próprio cálculo pôs.
  const diariasAutoRef = useRef<Record<string, string>>({});
  useEffect(() => {
    if (modo !== 'editar') return;
    const alvos = (etapa.campos || []).filter((c: any) => c.diariasAutomaticas);
    if (!alvos.length || !pedePluxee(valores)) return;
    const dias = diariasDaViagem(valores);
    if (dias == null) return;
    const novo = String(dias);
    const mudar = alvos.filter((c: any) => {
      const atual = String(valores[c.id] ?? '').trim();
      return atual !== novo && (!atual || atual === diariasAutoRef.current[c.id]);
    });
    if (!mudar.length) return;
    mudar.forEach((c: any) => { diariasAutoRef.current[c.id] = novo; });
    setValores(prev => {
      const n = { ...prev };
      mudar.forEach((c: any) => { n[c.id] = novo; });
      return n;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valores, etapa, modo]);
  
  // Estado para controlar visibilidade de opções específicas dentro de campos
  // Estrutura: { campoId: { opcao1: true, opcao2: false, ... } }
  const [opcoesVisiveis, setOpcoesVisiveis] = useState<Record<string, Record<string, boolean>>>({});

  const { getIntegracaoPorId } = useIntegracoes();

  // 🔄 Estado para o card completo (com fieldHistory) buscado do servidor
  const [cardCompleto, setCardCompleto] = useState<Card>(card);
  const [carregandoCardCompleto, setCarregandoCardCompleto] = useState(false);

  // 🚫 Motivo de a NF não ter sido lançada no GIX.
  // A automação escreve o motivo num campo comum do card (o escolhido em "Campo que
  // recebe o motivo"), e ali ele fica com cara de observação qualquer no meio das
  // outras — quem abre o card não vê que aquilo é um erro. O carimbo gixNfLancamento
  // diz exatamente qual texto é esse, então só ele ganha destaque.
  //
  // Nota de serviço NÃO é erro: o contábil abre o anexo e lança à mão, é rotina.
  // Por isso ela sai em azul, não em vermelho — se tudo o que o robô não lança
  // ficasse vermelho, o vermelho pararia de querer dizer "olha aqui".
  const avisoNfLancamento = useMemo<{ texto: string; servico: boolean } | null>(() => {
    const carimbo = (cardCompleto as any)?.gixNfLancamento;
    if (!carimbo || carimbo.ok !== false) return null;
    const texto = String(carimbo.mensagem || '').trim();
    return texto ? { texto, servico: carimbo.tipo === 'servico' } : null;
  }, [cardCompleto]);

  const ehMotivoNfNaoLancada = (valor: any) =>
    !!avisoNfLancamento && String(valor ?? '').trim() === avisoNfLancamento.texto;

  // Classes do destaque, conforme seja problema (vermelho) ou rotina (azul).
  const corAvisoNf = avisoNfLancamento?.servico
    ? { rotulo: 'text-blue-700', caixa: 'text-blue-900 bg-blue-50 border-blue-300',
        borda: 'bg-blue-50 border-blue-300', titulo: 'text-blue-700', icone: '📄 ' }
    : { rotulo: 'text-red-700', caixa: 'text-red-800 bg-red-50 border-red-300',
        borda: 'bg-red-50 border-red-300', titulo: 'text-red-700', icone: '🚫 ' };

  // Helper: coleta TODOS os anexos da etapa (de todos os campos do tipo anexo/arquivo)
  // e abre a galeria já posicionada no anexo clicado.
  const abrirGaleriaDaEtapa = (
    etapaHist: any,
    camposEtapa: { id: string; nome: string; tipo: string }[],
    campoIdClicado: string,
    indexClicado: number,
  ) => {
    const itens: GaleriaItem[] = [];
    let targetGlobalIndex = 0;
    const camposEditadosLocal = camposEditadosNoHistorico;
    const histArrLookup = (cardCompleto as any)?.fieldHistory || {};
    const entradasDestaEtapa = (cardCompleto?.etapaHistory || []).filter(
      (h: any) => String(h.etapaId) === String(etapaHist.id)
    );
    const historicoEntry = entradasDestaEtapa.length > 0
      ? entradasDestaEtapa[entradasDestaEtapa.length - 1]
      : undefined;
    const camposSnapshotLocal = historicoEntry?.camposSnapshot || {};
    const camposDoCardLocal = card.campos || {};

    camposEtapa.forEach((c) => {
      if (c.tipo !== 'anexo' && c.tipo !== 'arquivo') return;
      let valor: any;
      if (c.id in camposEditadosLocal) {
        valor = camposEditadosLocal[c.id];
      } else {
        const histArr = histArrLookup[c.id];
        const ultimaEdicao = Array.isArray(histArr) && histArr.length > 0
          ? histArr[histArr.length - 1]?.value
          : undefined;
        if (ultimaEdicao !== undefined) valor = ultimaEdicao;
        else if (c.id in camposSnapshotLocal) valor = camposSnapshotLocal[c.id];
        else valor = camposDoCardLocal[c.id];
      }
      if (!valor || valor === '-') return;
      const arr = Array.isArray(valor) ? valor : [valor];
      arr.forEach((arq, i) => {
        if (c.id === campoIdClicado && i === indexClicado) {
          targetGlobalIndex = itens.length;
        }
        itens.push({
          arquivo: arq,
          campoId: c.id,
          campoNome: c.nome,
          etapaId: etapaHist.id,
          indexInCampo: i,
        });
      });
    });

    if (itens.length === 0) return;
    setGaleriaAnexos({ itens, index: targetGlobalIndex });
  };

  // Galeria só com os anexos de um campo da etapa atual (a miniatura clicada
  // abre posicionada nela; as setas/swipe passam pelos outros prints do campo).
  const abrirGaleriaDoCampo = (
    campo: { id: string; nome: string },
    arquivos: any[],
    index: number,
  ) => {
    if (arquivos.length === 0) return;
    setGaleriaAnexos({
      itens: arquivos.map((arquivo, i) => ({
        arquivo,
        campoId: campo.id,
        campoNome: campo.nome,
        indexInCampo: i,
      })),
      index,
    });
  };

  // 📡 Buscar card completo (com fieldHistory) do servidor ao abrir
  useEffect(() => {
    const buscarCardCompleto = async () => {
      if (!flowId || !card.id) return;
      
      console.log('📡 [CARD] Buscando card completo com fieldHistory atualizado...');
      setCarregandoCardCompleto(true);
      
      try {
        const response = await fetch(`/api/flows/${flowId}/cards/${card.id}`);
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.card) {
            console.log('✅ [CARD] Card completo recebido:', {
              titulo: data.card.titulo,
              temFieldHistory: !!data.card.fieldHistory,
              fieldsCount: data.card.fieldHistory ? Object.keys(data.card.fieldHistory).length : 0,
              valorTotalHistory: data.card.fieldHistory?.valorTotal?.length || 0
            });
            setCardCompleto(data.card);
          }
        }
      } catch (error) {
        console.error('❌ [CARD] Erro ao buscar card completo:', error);
        // Em caso de erro, usar o card do prop
        setCardCompleto(card);
      } finally {
        setCarregandoCardCompleto(false);
      }
    };
    
    buscarCardCompleto();
  }, [flowId, card.id]);

  // Atualizar valores quando card mudar
  useEffect(() => {
    // Sempre atualizar o estado com os campos do card se eles existirem
    if (card.campos && Object.keys(card.campos).length > 0) {
      setValores(card.campos);
    }
  }, [card]);

  // OC criada pelo Escalone (carimbo gixCriacaoOc / gixOcRevenda): dá a empresa
  // que o campo não guarda e o fornecedor para conferir se a OC do GIX é mesmo esta.
  const ocCarimbo = useMemo(() => {
    const c = (cardCompleto as any)?.gixCriacaoOc || (cardCompleto as any)?.gixOcRevenda;
    if (!c || c.status !== 'criada' || !c.numeroOc) return null;
    // Card com várias NFs (27/10) grava "4186, 4187": uma OC por nota, mesma empresa.
    // Sem isto Number() dava NaN e a OC caía como "ambígua", misturando os itens
    // de outra empresa com o mesmo número (28/10/2026, card #XAGSZAL).
    const numeros = String(c.numeroOc).split(/\D+/).filter(Boolean).map(Number);
    if (numeros.length === 0) return null;
    return {
      numeroOc: numeros[0],
      numeros,
      empresa: c.empresa != null ? Number(c.empresa) : null,
      fornecedor: c.payload?.fornecedor != null ? Number(c.payload.fornecedor) : null,
      // OC recriada (ex.: 4130 apagada no GIX → 4140): o número antigo pode voltar
      // ao campo num salvamento com dados velhos, então ele aponta para a atual.
      anterior: c.anterior?.numeroOc != null ? Number(c.anterior.numeroOc) : null,
    };
  }, [cardCompleto]);
  // Fora das etapas com badge (ex.: Entrega de materiais) a OC aparece num quadro
  // próprio. 19/10/2026, André: "deveria ter um botão pra eu ver as informações da OC".
  const ocForaDaEtapa = !etapa?.gixBadgeCampoId;

  // ═══ GIX: consultar status das OCs ao abrir o card ═══
  // Aceita múltiplos números separados por ;, , ou espaço (ex: "3845;4568;9999")
  // Cada número pode ainda ter sufixo de empresa: "3845-1".
  useEffect(() => {
    if (!isGixEnabled) { setGixOcList([]); return; }
    // Etapa sem badge: usa o campo de OC de outra etapa do fluxo que tenha badge.
    const campoIdRaw = etapa?.gixBadgeCampoId
      || (todasEtapas || []).map((e: any) => e?.gixBadgeCampoId).find((id: any) => {
        if (!id) return false;
        const v = valores[String(id).includes(':') ? String(id).split(':')[1] : id];
        return !!(Array.isArray(v) ? v[0] : v);
      });
    const campoId = campoIdRaw ? (campoIdRaw.includes(':') ? campoIdRaw.split(':')[1] : campoIdRaw) : '';
    const valorRaw = campoId ? valores[campoId] : '';
    let texto = (Array.isArray(valorRaw) ? valorRaw[0] : valorRaw || '').toString().replace(/<[^>]*>/g, '').trim();
    if (!texto && ocCarimbo) {
      texto = ocCarimbo.numeros
        .map(n => `${n}${ocCarimbo.empresa != null ? `-${ocCarimbo.empresa}` : ''}`).join(',');
    }
    if (!texto) { setGixOcList([]); return; }
    // Mesma leitura do servidor (_pares_oc): "4150-2", "4150/2" e "4150 2" = OC 4150 da
    // empresa 2 (empresa tem 1 ou 2 dígitos); número de 3+ dígitos depois do traço é outra
    // OC. 30/10/2026, card #E5P3H87: "4214 - 4213" mostrava 4214 "não encontrada" e 4213
    // "ambígua" porque o servidor lia 4214 da empresa 4213.
    const limpo = texto.replace(/<[^>]*>|&nbsp;/g, ' ');
    const pares: [number, number | null][] = [];
    const reOc = /(\d+)(?:\s*[-/]\s*|[ \t]+)?(\d+)?/g;
    let mOc: RegExpExecArray | null;
    while ((mOc = reOc.exec(limpo)) !== null) {
      const n = parseInt(mOc[1], 10);
      const seg = mOc[2];
      if (seg && seg.length <= 2 && parseInt(seg, 10) > 0) {
        pares.push([n, parseInt(seg, 10)]);
      } else {
        pares.push([n, null]);
        // o 2º número é outra OC: volta para lê-lo na próxima volta
        if (seg) reOc.lastIndex = mOc.index + mOc[0].length - seg.length;
      }
    }
    const parsed = pares.map(([n, emp]) => {
      let numeroOc = n;
      if (ocCarimbo && ocCarimbo.anterior === numeroOc && emp == null) numeroOc = ocCarimbo.numeroOc;
      // Sem "-empresa" no campo: se for a OC que o Escalone criou, a empresa vem do carimbo.
      const empresaOc = emp != null ? emp
        : (ocCarimbo && ocCarimbo.numeros.includes(numeroOc) ? ocCarimbo.empresa : null);
      return isNaN(numeroOc) ? null : { numeroOc, empresaOc };
    }).filter(Boolean) as { numeroOc: number; empresaOc: number | null }[];
    if (parsed.length === 0) { setGixOcList([]); return; }
    // Deduplica por numeroOc
    const uniq = Array.from(new Map(parsed.map(p => [p.numeroOc, p])).values());
    // Evita refetch se a lista de números não mudou
    const numerosAtuais = gixOcList.map(o => Number(o.numero_oc)).sort().join(',');
    const numerosNovos = uniq.map(p => p.numeroOc).sort().join(',');
    if (numerosAtuais === numerosNovos && gixOcList.length === uniq.length) return;

    setGixOcLoading(true);
    const fetchOcComRetry = async (numeroOc: number, empresaOc: number | null, tentativas = 3): Promise<any> => {
      for (let i = 0; i < tentativas; i++) {
        try {
          const r = await fetch(`/api/gix/oc/${numeroOc}${empresaOc ? `?empresa=${empresaOc}` : ''}`);
          const data = await r.json();
          if (data.status === 'ok') {
            if (data.oc) return data.oc;
            const lista = data.ocs || [];
            // O nº da OC é sequencial POR EMPRESA no GIX: o mesmo número pode
            // existir em 2+ empresas. Pegar a primeira mostraria fornecedor,
            // valor e status de uma OC que não é a do card — melhor avisar.
            if (lista.length === 1) return lista[0];
            if (lista.length > 1) {
              return {
                numero_oc: numeroOc,
                status_liberacao: 'AMBÍGUA',
                status_liberacao_cor: 'gray',
                ambigua: true,
                empresas_candidatas: lista.map((o: any) => o.empresa).filter((e: any) => e != null),
              };
            }
            return { numero_oc: numeroOc, status_liberacao: 'DESCONHECIDA', status_liberacao_cor: 'gray' };
          }
          if (data.status === 'not_found') {
            return { numero_oc: numeroOc, status_liberacao: 'NÃO ENCONTRADA', status_liberacao_cor: 'gray' };
          }
          // Erro (503/timeout GIX) — tenta de novo se ainda houver tentativa
          if (i < tentativas - 1) {
            await new Promise(res => setTimeout(res, 1500 * (i + 1)));
            continue;
          }
          return { numero_oc: numeroOc, status_liberacao: 'ERRO', status_liberacao_cor: 'gray' };
        } catch {
          if (i < tentativas - 1) {
            await new Promise(res => setTimeout(res, 1500 * (i + 1)));
            continue;
          }
          return { numero_oc: numeroOc, status_liberacao: 'ERRO', status_liberacao_cor: 'gray' };
        }
      }
      return { numero_oc: numeroOc, status_liberacao: 'ERRO', status_liberacao_cor: 'gray' };
    };
    // OC digitada sem empresa: o servidor resolve pelo que o card tem (filial, fornecedor,
    // valor, data, solicitante) e guarda, em vez de a tela marcar "ambígua" (28/10/2026).
    // Devolve a lista do servidor (número + empresa, na ordem do campo) quando ele
    // responde: é a mesma leitura que a aprovação e o lançamento usam. Sem resposta,
    // fica a leitura local.
    const ocsDoServidor = async (): Promise<{ numeroOc: number; empresaOc: number | null }[] | null> => {
      if (!card?.id || uniq.every(p => p.empresaOc)) return null;
      try {
        // manda o que está na tela: a OC pode ter sido digitada e ainda não salva
        const r = await fetch(`/api/cards/${card.id}/ocs-gix?oc=${encodeURIComponent(texto)}`);
        const d = await r.json();
        const lista = (d.ocs || []).map((o: any) => {
          let numeroOc = Number(o.numero);
          const empresaOc = o.empresa != null ? Number(o.empresa) : null;
          if (ocCarimbo && ocCarimbo.anterior === numeroOc && empresaOc == null) numeroOc = ocCarimbo.numeroOc;
          return { numeroOc, empresaOc };
        }).filter((o: any) => !isNaN(o.numeroOc));
        if (!lista.length) return null;
        // a empresa local (sufixo/carimbo) cobre o que o servidor não cravou
        return lista.map((o: any) => ({
          numeroOc: o.numeroOc,
          empresaOc: o.empresaOc ?? uniq.find(p => p.numeroOc === o.numeroOc)?.empresaOc ?? null,
        }));
      } catch {
        return null;
      }
    };
    ocsDoServidor()
      .then(doServidor => Promise.all((doServidor || uniq).map(({ numeroOc, empresaOc }) =>
        fetchOcComRetry(numeroOc, empresaOc ?? null))))
      .then(list => setGixOcList(list.map((oc: any) => {
        // Caso da OC 4130/2 (lixeiras, 14/10/2026): o GIX apagou a OC do Escalone e
        // reusou o número para outra compra. Sem esta conferência o card mostraria
        // a OC de outro fornecedor como se fosse a dele.
        const outroFornecedor = !!(ocCarimbo && ocCarimbo.fornecedor != null
          && ocCarimbo.numeros.includes(Number(oc?.numero_oc))
          && oc?.codigo_fornecedor != null
          && Number(oc.codigo_fornecedor) !== ocCarimbo.fornecedor);
        return outroFornecedor ? { ...oc, outroFornecedor } : oc;
      })))
      .finally(() => setGixOcLoading(false));
  }, [valores, etapa?.gixBadgeCampoId, isGixEnabled, ocCarimbo, todasEtapas]);

  // Normalizar valores quando o tipo do campo mudar
  useEffect(() => {
    const valoresNormalizados: Record<string, any> = { ...valores };
    let precisaAtualizar = false;
    
    etapa.campos?.forEach(campo => {
      const valorAtual = valores[campo.id];
      
      if ((campo.tipo === 'checkbox' || campo.tipo === 'checkbox_single' || campo.tipo === 'checkbox_multi') && typeof valorAtual === 'string' && valorAtual !== '') {
        valoresNormalizados[campo.id] = [valorAtual];
        precisaAtualizar = true;
      }
      
      if (campo.tipo === 'lista' && Array.isArray(valorAtual) && valorAtual.length > 0) {
        valoresNormalizados[campo.id] = valorAtual[0] || '';
        precisaAtualizar = true;
      }
    });
    
    if (precisaAtualizar) {
      setValores(valoresNormalizados);
    }
  }, [etapa.campos, card.id]);

  // 📄 Carregar documentos habilitados para esta etapa
  useEffect(() => {
    const carregarDocumentos = async () => {
      // Verificar se a etapa tem documentos habilitados
      if (!etapa.documentosHabilitados || etapa.documentosHabilitados.length === 0) {
        setDocumentosDisponiveis([]);
        return;
      }

      try {
        setLoadingDocumentos(true);
        // IMPORTANTE: Usar card.empresaId primeiro, mas se estiver undefined, usar empresaId do flow (passado via props)
        const url = `/api/documentos/templates?flowId=${flowId || ''}&empresaId=${card.empresaId || empresaId || ''}`;
        const response = await fetch(url);
        
        console.log('📄 [DOCUMENTOS] Response status:', response.status);
        
        if (response.ok) {
          const todosTemplates = await response.json();
          console.log('📄 [DOCUMENTOS] Todos templates recebidos:', todosTemplates);
          console.log('📄 [DOCUMENTOS] documentosHabilitados da etapa:', etapa.documentosHabilitados);
          
          // Filtrar apenas os templates habilitados para esta etapa
          const templatesHabilitados = todosTemplates.filter((template: any) => {
            const templateId = template._id || template.id;
            const habilitado = etapa.documentosHabilitados?.includes(templateId);
            console.log(`📄 [DOCUMENTOS] Template "${template.nome}" (ID: ${templateId}): ${habilitado ? 'HABILITADO' : 'não habilitado'}`);
            return habilitado;
          });
          setDocumentosDisponiveis(templatesHabilitados);
          console.log('📄 Documentos carregados para a etapa:', templatesHabilitados);
        } else {
          console.error('📄 [DOCUMENTOS] Erro na resposta:', response.status, response.statusText);
        }
      } catch (error) {
        console.error('Erro ao carregar documentos:', error);
      } finally {
        setLoadingDocumentos(false);
      }
    };

    carregarDocumentos();
  }, [etapa.id, etapa.documentosHabilitados, flowId, card.empresaId]);

  // Executar automações no carregamento (não mover no initial)
  useEffect(() => {
    if (!didRunInitial.current && automacoes.length > 0) {
      didRunInitial.current = true;
      automationEngine.executeAutomations(
        automacoes,
        etapa,
        valores,
        'initial',
        onFieldVisibilityChange,
        () => {},
        card.id
      );
    }
  }, [automacoes, etapa.id]);

  // Limpar justificativas de itens que não estão mais selecionados
  useEffect(() => {
    // Para cada campo de justificativa_checkbox, verificar se há itens não selecionados
    etapa.campos?.forEach(campo => {
      if (campo.tipo === 'justificativa_checkbox' && campo.campoVinculadoId) {
        const campoVinculado = etapa.campos?.find(c => c.id === campo.campoVinculadoId);
        if (campoVinculado && valores[campo.id]) {
          const itensSelecionados = valores[campoVinculado.id] || [];
          const justificativasAtuais = valores[campo.id] || {};
          
          // Verificar se há justificativas para itens não selecionados
          const itensParaRemover = Object.keys(justificativasAtuais).filter(item => 
            !itensSelecionados.includes(item)
          );
          
          if (itensParaRemover.length > 0) {
            console.log(`🧹 Limpando justificativas de itens não selecionados:`, itensParaRemover);
            const novasJustificativas = { ...justificativasAtuais };
            itensParaRemover.forEach(item => {
              delete novasJustificativas[item];
            });
            updateFieldValue(campo.id, novasJustificativas);
          }
        }
      }
    });
  }, [valores, etapa.campos]);

  // Callback para controlar visibilidade dos campos e opções específicas
  const onFieldVisibilityChange = (fieldId: string, visible: boolean, opcoesEspecificas?: string[]) => {
    if (opcoesEspecificas && opcoesEspecificas.length > 0) {
      // Modo: controlar opções específicas dentro do campo
      console.log(`🔧 Campo '${fieldId}' - Opções [${opcoesEspecificas.join(', ')}] definidas como ${visible ? 'VISÍVEIS' : 'OCULTAS'}`);
      setOpcoesVisiveis(prev => {
        const opcoesAtuais = prev[fieldId] || {};
        const novasOpcoes = { ...opcoesAtuais };
        opcoesEspecificas.forEach(opcao => {
          novasOpcoes[opcao] = visible;
        });
        return {
          ...prev,
          [fieldId]: novasOpcoes
        };
      });
    } else {
      // Modo: controlar campo inteiro
      console.log(`🔧 Campo '${fieldId}' definido como ${visible ? 'VISÍVEL' : 'OCULTO'}`);
      setCamposVisiveis(prev => ({
        ...prev,
        [fieldId]: visible
      }));
      // Limpar opções específicas quando o campo inteiro é controlado
      setOpcoesVisiveis(prev => {
        const novoEstado = { ...prev };
        delete novoEstado[fieldId];
        return novoEstado;
      });
    }
  };

  // Atualizador central com automações
  const updateFieldValue = (fieldId: string, value: any) => {
    // 🔧 FIX: Marcar que o usuário modificou campos da etapa atual
    // Isso é importante para a validação de campos obrigatórios
    setCamposEtapaAtualModificados(true);
    setValores(prev => {
      const novos = { ...prev, [fieldId]: value };
      setDestinoAutomacao(null);
      automationEngine.executeAutomations(
        automacoes,
        etapa,
        novos,
        fieldId,
        onFieldVisibilityChange,
        (etapaDestinoId: string, meta?: { automacaoId?: string; automacaoNome?: string; campoGatilho?: string }) => {
          automacaoMetaRef.current = (meta as { automacaoId?: string; automacaoNome?: string; campoGatilho?: string } | null) || null;
          setDestinoAutomacao(etapaDestinoId);
        },
        card.id
      );
      return novos;
    });
  };

  // Função auxiliar para verificar se uma opção específica está visível
  const isOpcaoVisivel = (campoId: string, opcao: string): boolean => {
    const opcoesControladas = opcoesVisiveis[campoId];
    // Se não há controle específico para este campo, todas as opções são visíveis
    if (!opcoesControladas) return true;
    // Se a opção não está no controle, ela é visível por padrão
    if (opcoesControladas[opcao] === undefined) return true;
    // Retorna o valor controlado
    return opcoesControladas[opcao];
  };
  
  // Função auxiliar para filtrar opções visíveis de um campo
  const getOpcoesVisiveis = (campoId: string, opcoes: string[] | undefined): string[] => {
    if (!opcoes) return [];
    return opcoes.filter(opcao => isOpcaoVisivel(campoId, opcao));
  };

  // Atualizador para campos do histórico de etapas anteriores
  const updateHistoricoFieldValue = (etapaId: string, fieldId: string, value: any) => {
    console.log(`📝 updateHistoricoFieldValue chamado: etapaId=${etapaId}, fieldId=${fieldId}, value=`, value);
    
    // 🔧 FIX: Rastrear que este campo foi REALMENTE editado pelo usuário
    // Isso é usado para atualizar card.campos apenas com valores que mudaram
    setCamposEditadosNoHistorico(prev => ({
      ...prev,
      [fieldId]: value
    }));
    console.log(`✅ Campo ${fieldId} marcado como editado pelo usuário com valor:`, value);
    
    setValoresHistorico(prev => {
      const novo = {
        ...prev,
        [etapaId]: {
          ...(prev[etapaId] || {}),
          [fieldId]: value
        }
      };
      console.log('📝 Novo valoresHistorico:', JSON.stringify(novo, null, 2));
      return novo;
    });
  };

  // Utilitários para limpar valores dos campos
  const hasValue = (campo: CampoConfig): boolean => {
    const v = valores[campo.id];
    switch (campo.tipo) {
      case 'checkbox':
      case 'parcelas':
      case 'itens':
      case 'nd_vinculada':
      case 'anexo':
        return Array.isArray(v) && v.length > 0;
      case 'cotacoes':
        return ehMapaCotacao(v) && v.cotacoes.length > 0;
      case 'progresso':
        // Progresso por tarefas: array; progresso numérico (sem tarefas): número > 0
        if (Array.isArray(v)) return v.length > 0;
        return typeof v === 'number' ? v > 0 : (typeof v === 'string' && v.trim() !== '' && v.trim() !== '0');
      case 'valor':
        return v === 0 || v === '0' || (typeof v === 'string' ? v.trim() !== '' : Boolean(v));
      default:
        return typeof v === 'string' ? v.trim() !== '' : Boolean(v);
    }
  };

  const clearField = (campo: CampoConfig) => {
    let empty: any = '';
    switch (campo.tipo) {
      case 'checkbox':
      case 'parcelas':
      case 'itens':
      case 'anexo':
        empty = [];
        break;
      case 'cotacoes':
        empty = { cotacoes: [], escolha: null };
        break;
      case 'progresso':
        empty = progressoTemTarefas(campo) ? [] : 0;
        break;
      default:
        empty = '';
    }
    updateFieldValue(campo.id, empty);
  };

  // ── Helpers do campo Progresso (suporta tarefas OU % numérico) ──
  const progressoTemTarefas = (campo: CampoConfig): boolean =>
    Array.isArray(campo.opcoes) && campo.opcoes.length > 0;

  // % atual do campo progresso (0-100)
  const getProgressoPercent = (campo: CampoConfig): number => {
    const v = valores[campo.id];
    if (progressoTemTarefas(campo)) {
      const visiveis = getOpcoesVisiveis(campo.id, campo.opcoes);
      const marcadas = Array.isArray(v) ? v.filter((x: string) => visiveis.includes(x)).length : 0;
      return visiveis.length > 0 ? Math.round((marcadas / visiveis.length) * 100) : 0;
    }
    const n = typeof v === 'number' ? v : parseInt(String(v || '0').replace(/\D/g, ''), 10);
    return isNaN(n) ? 0 : Math.max(0, Math.min(100, n));
  };

  // Texto-fonte para estimar progresso: junta os campos de texto da etapa
  const getTextoFonteProgresso = (): string => {
    const partes: string[] = [];
    for (const c of etapa.campos || []) {
      if (c.tipo === 'texto' || c.tipo === 'texto_longo') {
        const val = valores[c.id];
        if (typeof val === 'string' && val.trim()) partes.push(val);
      }
    }
    return partes.join('  ');
  };

  // % sugerida pelas regras de palavra-chave (ou null)
  const getSugestaoProgresso = (campo: CampoConfig): number | null => {
    if (progressoTemTarefas(campo)) return null; // só faz sentido no modo numérico
    return calcularSugestaoProgresso(getTextoFonteProgresso());
  };

  // Função para verificar status de vencimento
  const getVencimentoStatus = () => {
    // Procurar campo de data_vencimento nos campos atuais
    const campoVencimento = etapa.campos.find(campo => campo.tipo === 'data_vencimento');
    if (!campoVencimento) return null;

    const dataVencimento = valores[campoVencimento.id];
    if (!dataVencimento) return null;

    try {
      const vencimento = new Date(dataVencimento);
      const agora = new Date();
      
      // Verificar se já venceu
      if (vencimento < agora) {
        return 'vencido';
      }
      
      // Verificar se vence em até 24 horas
      const diferencaHoras = (vencimento.getTime() - agora.getTime()) / (1000 * 60 * 60);
      if (diferencaHoras <= 24) {
        return 'vencendo';
      }
      
      return 'normal';
    } catch (error) {
      return null;
    }
  };

  const statusVencimento = getVencimentoStatus();

  // Derivar histórico real de etapas a partir de card.etapaHistory
  const etapasHistoricas = (() => {
    const hist = card.etapaHistory || [];
    if (!Array.isArray(hist) || hist.length === 0) return [] as Etapa[];
    // Mapear para objetos Etapa existentes no fluxo passado via prop etapasAnteriores + etapa atual
    // Disponíveis: etapasAnteriores (todas anteriores) + etapa atual
    const disponiveis = [etapa, ...etapasAnteriores];
    const unicos: string[] = [];
    const result: Etapa[] = [];
    hist.forEach(h => {
      const id = String(h.etapaId);
      if (!unicos.includes(id)) {
        const e = disponiveis.find(x => String(x.id) === id);
        if (e) {
          unicos.push(id);
          result.push(e);
        }
      }
    });
    return result;
  })();

  // Última movimentação por automação (para badge no cabeçalho)
  const ultimaAutomacaoNome = (() => {
    const hist = card.etapaHistory || [];
    if (!Array.isArray(hist) || hist.length === 0) return null;
    const last = [...hist].reverse().find(m => m?.motivo === 'automacao' && m?.automacaoNome);
    return last?.automacaoNome || null;
  })();

  // 📄 Função para preencher template de documento com dados do card
  const preencherTemplateComDados = (templateHtml: string): string => {
    let htmlPreenchido = templateHtml;
    
    console.log('📄 Preenchendo template com dados do card:', card.titulo);
    console.log('📄 Template original:', templateHtml);
    console.log('📄 Valores disponíveis:', valores);
    console.log('📄 Campos da etapa atual:', etapa.campos);
    
    // Criar um mapa de todos os campos disponíveis (etapa atual + histórico)
    const todosCampos = new Map<string, CampoConfig>();
    
    // Adicionar campos da etapa atual
    etapa.campos?.forEach(campo => {
      todosCampos.set(campo.nome, campo);
      todosCampos.set(campo.id, campo);
    });
    
    // Adicionar campos das etapas anteriores
    etapasAnteriores.forEach(etapaAnt => {
      etapaAnt.campos?.forEach(campo => {
        if (!todosCampos.has(campo.nome)) {
          todosCampos.set(campo.nome, campo);
        }
        if (!todosCampos.has(campo.id)) {
          todosCampos.set(campo.id, campo);
        }
      });
    });
    
    console.log('📄 Todos os campos disponíveis:', Array.from(todosCampos.keys()));

    // Decodifica entidades HTML (&amp; &lt; &gt; &quot; &#39; &nbsp;). O editor
    // salva o conteúdo como HTML, então "&" vira "&amp;". Sem isso, campos com
    // esses caracteres (ex.: "Organização uso & consumo") nunca casam pelo nome.
    const decodeEntidadesHtml = (texto: string): string =>
      texto
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&#0?39;/g, "'")
        .replace(/&apos;/g, "'")
        .replace(/&nbsp;/g, ' ');

    // Formata valores de campos de anexo (array/objeto de arquivos) como links
    // com o nome do arquivo, em vez de "[object Object]" (valor.join) ou JSON cru.
    const escaparHtml = (t: string): string =>
      String(t).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const formatarAnexoOuArray = (valor: any): string => {
      const arr = Array.isArray(valor) ? valor : [valor];
      const ehArquivo = (v: any) => v && typeof v === 'object' &&
        (v.name || v.fileName || v.originalName || v.url || v.dataUrl || v.webViewLink || v.webContentLink);
      if (arr.some(ehArquivo)) {
        return arr
          .filter((v: any) => v !== undefined && v !== null && v !== '')
          .map((arquivo: any) => {
            const nome = typeof arquivo === 'string'
              ? arquivo
              : (arquivo?.name || arquivo?.fileName || arquivo?.originalName || 'Arquivo');
            // Para imagem preferimos uma URL renderável direta (url/dataUrl/webContentLink);
            // webViewLink é uma página de preview (não serve como src de <img>).
            const url = typeof arquivo === 'object'
              ? (arquivo?.url || arquivo?.dataUrl || arquivo?.webContentLink || arquivo?.webViewLink)
              : (typeof arquivo === 'string' && arquivo.startsWith('http') ? arquivo : null);
            const nomeEsc = escaparHtml(nome);
            // Detectar imagem por mime/type ou extensão do arquivo
            const tipo = typeof arquivo === 'object' ? String(arquivo?.type || arquivo?.mimeType || '') : '';
            const ehImagem = tipo.startsWith('image/') ||
              /\.(jpe?g|png|gif|webp|bmp|svg)$/i.test(String(nome));
            if (url && ehImagem) {
              // Renderiza a foto (com link para abrir em tamanho real)
              return `<a href="${url}" target="_blank" rel="noopener noreferrer">` +
                `<img src="${url}" alt="${nomeEsc}" title="${nomeEsc}" ` +
                `style="max-width:240px;max-height:240px;object-fit:contain;border:1px solid #e5e7eb;border-radius:6px;margin:4px;vertical-align:top;" />` +
                `</a>`;
            }
            return url
              ? `<a href="${url}" target="_blank" rel="noopener noreferrer">📎 ${nomeEsc}</a>`
              : `📎 ${nomeEsc}`;
          })
          .join(' ');
      }
      // Não é anexo: comportamento padrão
      if (Array.isArray(valor) && valor.some((v: any) => v && typeof v === 'object' && 'paga' in v && 'nome' in v)) return textoParcelas(valor);
      if (ehListaDeViajantes(valor)) return textoViajantes(valor);
      if (ehListaDeVoos(valor)) return textoVoos(valor);
      if (ehListaDeTrechos(valor)) return textoTrechos(valor);
      if (ehListaDeItens(valor)) return textoItens(valor);
      if (Array.isArray(valor)) return valor.join(', ');
      return typeof valor === 'object' ? JSON.stringify(valor) : String(valor);
    };

    // Substituir campos usando regex para encontrar todos os {{...}}
    const regex = /\{\{([^}]+)\}\}/g;
    htmlPreenchido = htmlPreenchido.replace(regex, (match, fieldName) => {
      const trimmedName = decodeEntidadesHtml(fieldName.trim());
      console.log('📄 Tentando substituir:', match, '| Nome do campo:', trimmedName);
      
      // Verificar se é no formato campo.id
      if (trimmedName.startsWith('campo.')) {
        const campoId = trimmedName.substring(6); // Remove "campo."
        console.log('📄 Formato campo.id detectado, ID:', campoId);
        
        const valor = valores[campoId];
        console.log('📄 Valor direto do ID:', valor);
        
        if (valor !== undefined && valor !== null && valor !== '') {
          // Verificar se este campo é de moeda
          const campoConfig = todosCampos.get(campoId);
          
          let valorFormatado = '';
          if (Array.isArray(valor) || (typeof valor === 'object' && valor !== null)) {
            valorFormatado = formatarAnexoOuArray(valor);
          } else {
            // Formatar como moeda se for campo de moeda
            const isCampoMoeda = campoConfig && (
              campoConfig.tipo === 'valor' ||
              campoConfig.nome?.toLowerCase().includes('valor') ||
              campoConfig.nome?.toLowerCase().includes('r$') ||
              campoConfig.nome?.toLowerCase().includes('preço') ||
              campoConfig.nome?.toLowerCase().includes('preco')
            );
            
            if (isCampoMoeda) {
              // Converter valor para número corretamente
              let numero = 0;
              if (typeof valor === 'string') {
                // Remover apenas símbolos de moeda e espaços, manter ponto e vírgula
                const limpo = valor.replace(/[R$\s]/g, '').trim();
                // Se tem vírgula, assumir formato brasileiro (1.532,00)
                if (limpo.includes(',')) {
                  numero = parseFloat(limpo.replace(/\./g, '').replace(',', '.'));
                } else {
                  // Senão, usar como está (1532.00 ou 1532)
                  numero = parseFloat(limpo);
                }
              } else {
                numero = Number(valor);
              }
              
              if (!isNaN(numero)) {
                valorFormatado = new Intl.NumberFormat('pt-BR', {
                  style: 'currency',
                  currency: 'BRL'
                }).format(numero);
              } else {
                valorFormatado = String(valor);
              }
            } else {
              valorFormatado = String(valor);
            }
          }
          console.log('📄 Valor formatado:', valorFormatado);
          return valorFormatado;
        }
        return ''; // Campo vazio
      }
      
      // Tentar encontrar o campo pelo nome
      const campo = todosCampos.get(trimmedName);
      
      if (campo) {
        const valor = valores[campo.id];
        console.log('📄 Campo encontrado:', campo.nome, '| Tipo:', campo.tipo, '| Valor:', valor);
        
        let valorFormatado = '';
        if (valor !== undefined && valor !== null && valor !== '') {
          if (Array.isArray(valor) || (typeof valor === 'object' && valor !== null)) {
            valorFormatado = formatarAnexoOuArray(valor);
          } else {
            // Verificar se é um campo de moeda/dinheiro e formatar com R$
            const isCampoMoeda = campo.tipo === 'valor' ||
                                campo.nome?.toLowerCase().includes('valor') ||
                                campo.nome?.toLowerCase().includes('r$') ||
                                campo.nome?.toLowerCase().includes('preço') ||
                                campo.nome?.toLowerCase().includes('preco');
            
            if (isCampoMoeda) {
              // Converter valor para número corretamente
              let numero = 0;
              if (typeof valor === 'string') {
                // Remover apenas símbolos de moeda e espaços, manter ponto e vírgula
                const limpo = valor.replace(/[R$\s]/g, '').trim();
                // Se tem vírgula, assumir formato brasileiro (1.532,00)
                if (limpo.includes(',')) {
                  numero = parseFloat(limpo.replace(/\./g, '').replace(',', '.'));
                } else {
                  // Senão, usar como está (1532.00 ou 1532)
                  numero = parseFloat(limpo);
                }
              } else {
                numero = Number(valor);
              }
              
              if (!isNaN(numero)) {
                valorFormatado = new Intl.NumberFormat('pt-BR', {
                  style: 'currency',
                  currency: 'BRL'
                }).format(numero);
              } else {
                valorFormatado = String(valor);
              }
            } else {
              valorFormatado = String(valor);
            }
          }
        }
        
        console.log('📄 Valor formatado:', valorFormatado);
        return valorFormatado;
      }
      
      // Casos especiais
      if (trimmedName === 'fluxo.nome') {
        return flowName || '';
      }
      
      if (trimmedName === 'card.titulo' || trimmedName === 'Título' || trimmedName === 'título') {
        return card.titulo || '';
      }

      if (trimmedName === 'card.id' || trimmedName === 'ID do Card') {
        try {
          return card.id ? `#${generateShortCode(card.id)}` : '';
        } catch {
          return card.id || '';
        }
      }
      
      if (trimmedName === 'data.atual' || trimmedName === 'Data Atual') {
        return new Date().toLocaleDateString('pt-BR');
      }
      
      if (trimmedName === 'hora.atual') {
        return new Date().toLocaleTimeString('pt-BR');
      }
      
      if (trimmedName === 'card.etapa') {
        return etapa?.nome || '';
      }
      
      if (trimmedName === 'card.dataCriacao') {
        return card.createdAt ? new Date(card.createdAt).toLocaleDateString('pt-BR') : '';
      }
      
      console.log('⚠️ Campo não encontrado:', trimmedName);
      // Se não encontrar, deixar o placeholder
      return match;
    });
    
    console.log('📄 Template preenchido:', htmlPreenchido);
    return htmlPreenchido;
  };

  // Função para buscar nome do usuário na API (definida ANTES de getUserNameById)
  const fetchUserName = useCallback(async (userId: string) => {
    // Verificar se já existe no cache (qualquer valor, inclusive "Usuário desconhecido")
    const cachedValue = userNamesCache[userId];
    if (cachedValue && cachedValue !== 'Carregando...') {
      return; // Já resolvido (com sucesso ou falha), não re-buscar
    }
    
    // ⚡ OTIMIZAÇÃO: Verificar se já existe uma requisição pendente para este userId
    if (pendingUserFetches.has(userId)) {
      return; // Já está sendo buscado, não duplicar
    }
    
    // Marcar como em andamento
    pendingUserFetches.add(userId);

    // 🤖 Pseudo-usuários de sistema: nunca vão existir em users/colaboradores.
    // Sem isto viravam "Usuário desconhecido (ID: automacao...)" no histórico.
    const PSEUDO: Record<string, string> = {
      automacao: 'Automação',
      automação: 'Automação',
      portal_automacao: 'Portal (automação)',
      sistema: 'Sistema',
    };
    const chave = String(userId).toLowerCase().trim();
    const pseudo = PSEUDO[chave] || (chave.startsWith('gix_auto') ? 'Integração GIX' : null);
    if (pseudo) {
      userNamesCache[userId] = pseudo;
      setUserNames(prev => ({ ...prev, [userId]: pseudo }));
      pendingUserFetches.delete(userId);
      return;
    }

    // 📧 Parte do histórico grava o E-MAIL no movedBy em vez do id (ex.: a criação
    // de card conectado). As buscas por id/_id/userId nunca casam nesse caso, então
    // resolve pelo e-mail antes de tentar o resto.
    const ehEmail = chave.includes('@');
    if (ehEmail) {
      try {
        const res = await fetch(apiUrl(`/colaboradores`));
        if (res.ok) {
          const data = await res.json();
          const lista = data.data || data.colaboradores || [];
          const achado = lista.find((c: any) => String(c.email || '').toLowerCase().trim() === chave);
          if (achado) {
            const nome = achado.nome || achado.name || userId;
            userNamesCache[userId] = nome;
            setUserNames(prev => ({ ...prev, [userId]: nome }));
            pendingUserFetches.delete(userId);
            return;
          }
        }
      } catch { /* cai no fluxo normal abaixo */ }
      // Não achou o cadastro: mostrar o próprio e-mail identifica melhor que "desconhecido"
      userNamesCache[userId] = userId;
      setUserNames(prev => ({ ...prev, [userId]: userId }));
      pendingUserFetches.delete(userId);
      return;
    }

    try {
      // 1) Tentar buscar direto na API Python de users
      let response = await fetch(`/api/users?id=${userId}`);
      
      if (response.ok) {
        const result = await response.json();
        
        if (result.success && result.data && result.data.length > 0) {
          const userData = result.data[0];
          const nome = userData.nome || userData.name || userData.email || 'Usuário desconhecido';
          
          userNamesCache[userId] = nome;
          setUserNames(prev => ({ ...prev, [userId]: nome }));
          return;
        }
      }
      
      // 2) Tentar buscar em colaboradores
      response = await fetch(apiUrl(`/colaboradores`));
      if (response.ok) {
        const data = await response.json();
        const colaboradoresList = data.data || data.colaboradores || [];
        
        const colab = colaboradoresList.find((c: any) => 
          String(c.userId) === String(userId) ||
          String(c.user_id) === String(userId) ||
          String(c._id) === String(userId) ||
          String(c.id) === String(userId)
        );
        
        if (colab) {
          const nome = colab.nome || colab.name || 'Usuário desconhecido';
          userNamesCache[userId] = nome;
          setUserNames(prev => ({ ...prev, [userId]: nome }));
          return;
        }
      }
      
      // 3) Tentar rota específica
      response = await fetch(apiUrl(`/colaboradores/by-user/${userId}`));
      if (response.ok) {
        const data = await response.json();
        const nome = data.nome || data.name || 'Usuário desconhecido';
        userNamesCache[userId] = nome;
        setUserNames(prev => ({ ...prev, [userId]: nome }));
        return;
      }
      
      // Se não encontrou em nenhum lugar, usar nome genérico
      const nome = `Usuário desconhecido (ID: ${userId.substring(0, 8)}...)`;
      userNamesCache[userId] = nome;
      setUserNames(prev => ({ ...prev, [userId]: nome }));
      
    } catch (err) {
      console.warn(`⚠️ Erro ao buscar usuário ${userId}:`, err);
      const nome = `Usuário desconhecido (ID: ${userId.substring(0, 8)}...)`;
      userNamesCache[userId] = nome;
      setUserNames(prev => ({ ...prev, [userId]: nome }));
    } finally {
      // Remover do Set de pendentes
      pendingUserFetches.delete(userId);
    }
  }, []);

  // Função para obter nome do usuário por ID (otimizada para evitar logs excessivos)
  const getUserNameById = useCallback((userId: string | undefined): string => {
    if (!userId) return 'Sistema';

    // Labels especiais usadas pelo backend para automações e portal
    const ACTOR_LABELS: Record<string, string> = {
      'portal_externo': 'Portal externo',
      'portal_automacao': 'Automação (portal)',
      'automacao': 'Automação',
      'backfill': 'Sistema (backfill)',
      'sistema': 'Sistema',
      'system': 'Sistema',
    };
    if (ACTOR_LABELS[userId]) return ACTOR_LABELS[userId];

    // Verificar cache do componente primeiro (mais rápido)
    if (userNames[userId]) {
      return userNames[userId];
    }
    
    // Verificar cache global
    if (userNamesCache[userId]) {
      return userNamesCache[userId];
    }
    
    // Se for o usuário atual, retornar o nome dele diretamente
    if (userPermissions?.currentUser && String(userPermissions.currentUser.id) === String(userId)) {
      const colabByEmail = colaboradores.find(c => 
        c.email && userPermissions.currentUser.email && 
        c.email.toLowerCase() === userPermissions.currentUser.email.toLowerCase()
      );
      if (colabByEmail) {
        userNamesCache[userId] = colabByEmail.nome;
        return colabByEmail.nome;
      }
      const nome = userPermissions.currentUser.nome || userPermissions.currentUser.email;
      userNamesCache[userId] = nome;
      return nome;
    }
    
    // Tentar múltiplas formas de match por ID
    const colaborador = colaboradores.find(c => 
      String(c._id) === String(userId) || 
      String(c.id) === String(userId) || 
      String(c.colaboradorId) === String(userId) ||
      String(c.userId) === String(userId) ||
      String(c.user_id) === String(userId)
    );
    
    if (colaborador) {
      userNamesCache[userId] = colaborador.nome;
      return colaborador.nome;
    }
    
    // Se não encontrou, buscar na API (de forma assíncrona)
    fetchUserName(userId);
    
    return `Carregando...`;
  }, [userNames, colaboradores, userPermissions, fetchUserName]);

  // Buscar nomes de usuários proativamente ao abrir o card (definido DEPOIS de fetchUserName)
  useEffect(() => {
    const userIdsParaBuscar = new Set<string>();
    
    // NÃO limpar cache de "Usuário desconhecido" — isso causa loop infinito de re-fetches
    
    // Coletar IDs do histórico de campos (usar cardCompleto que tem fieldHistory)
    if (cardCompleto.fieldHistory) {
      Object.values(cardCompleto.fieldHistory).forEach((history: any) => {
        history.forEach((entry: any) => {
          if (entry.editedBy) userIdsParaBuscar.add(String(entry.editedBy));
        });
      });
    }
    
    // Coletar IDs do histórico de etapas
    if (cardCompleto.etapaHistory) {
      cardCompleto.etapaHistory.forEach((mov: any) => {
        if (mov.movedBy) userIdsParaBuscar.add(String(mov.movedBy));
      });
    }
    
    // Buscar apenas os que precisam
    userIdsParaBuscar.forEach(userId => {
      const cachedName = userNamesCache[userId] || userNames[userId];
      const needsFetch = !cachedName || 
                        cachedName.includes('Usuário desconhecido') || 
                        cachedName === 'Carregando...' ||
                        cachedName.includes('ID:');
      
      if (needsFetch) {
        fetchUserName(userId);
      }
    });
  }, [cardCompleto, userNames, fetchUserName]);

  // Linha do tempo unificada (alterações de campos + movimentações)
  const timelineEntries = useMemo(() => {
    console.log('📊 [TIMELINE] Recalculando timeline - fieldHistory:', cardCompleto.fieldHistory ? 'SIM' : 'NÃO', '- etapaHistory:', cardCompleto.etapaHistory?.length || 0, 'entradas');
    type Entry = {
      date: Date;
      kind: 'field' | 'move' | 'hierarchy';
      title: string;
      subtitle?: string;
      meta?: Record<string, any>;
    };
    const list: Entry[] = [];
    
    // Helper: parse date segura (retorna null se inválida)
    const safeParseDate = (val: any): Date | null => {
      if (!val) return null;
      try {
        // Se já for Date
        if (val instanceof Date) return isNaN(val.getTime()) ? null : val;
        // Se for number (timestamp)
        if (typeof val === 'number') {
          const d = new Date(val);
          return isNaN(d.getTime()) ? null : d;
        }
        // Se for string
        if (typeof val === 'string') {
          // Tentar ISO parse direto
          let d = new Date(val);
          if (!isNaN(d.getTime())) return d;
          // Tentar com $date (MongoDB extended JSON)
          if (val.includes('$date')) {
            try {
              const parsed = JSON.parse(val);
              d = new Date(parsed.$date);
              if (!isNaN(d.getTime())) return d;
            } catch {}
          }
        }
        // Se for objeto com $date (MongoDB BSON)
        if (typeof val === 'object' && val.$date) {
          const d = new Date(val.$date);
          return isNaN(d.getTime()) ? null : d;
        }
      } catch {}
      return null;
    };
    
    // Encontrar a data de criação do card (primeira entrada no etapaHistory)
    const primeiraMovimentacao = cardCompleto.etapaHistory && cardCompleto.etapaHistory.length > 0
      ? cardCompleto.etapaHistory.reduce((min: number | null, h: any) => {
          const d = safeParseDate(h.movedAt);
          if (!d) return min;
          const ts = d.getTime();
          return min === null || ts < min ? ts : min;
        }, null as number | null)
      : null;
    
    // Field changes - EXCLUIR preenchimentos iniciais da primeira etapa
    if (cardCompleto.fieldHistory) {
      Object.entries(cardCompleto.fieldHistory).forEach(([fieldId, history]) => {
        if (camposOcultosParaMim.has(String(fieldId))) return;
        history.forEach((entry: any, index: number) => {
          const parsedEditedAt = safeParseDate(entry.editedAt);
          if (!parsedEditedAt) return; // Pular entradas sem data válida
          const editedAt = parsedEditedAt.getTime();
          
          // 🔧 FILTRO: Excluir a PRIMEIRA edição de cada campo se foi feita nos primeiros 30 segundos
          // após a criação do card (preenchimento inicial do formulário)
          const isPreenchimentoInicial = index === 0 && primeiraMovimentacao && 
            Math.abs(editedAt - primeiraMovimentacao) < 30000; // 30 segundos de tolerância
          
          if (isPreenchimentoInicial) {
            console.log(`📋 [TIMELINE] Ignorando preenchimento inicial do campo ${fieldId}`);
            return; // Pular esta entrada
          }
          
          // Buscar nome do campo em TODAS as etapas (não só nas anteriores)
          const todasDisponiveis = todasEtapas.length > 0 ? todasEtapas : [etapa, ...etapasAnteriores];
          const fieldName = fieldId === 'valorTotal' ? 'Valor Total' :
                            todasDisponiveis.flatMap(e => e.campos || []).find(c => c.id === fieldId)?.nome ||
                            fieldId;

          // Formatar o valor para exibição
          let valueText = '(vazio)';
          if (entry.value !== null && entry.value !== undefined) {
            if (fieldId === 'valorTotal' && typeof entry.value === 'number') {
              valueText = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(entry.value);
            } else if (Array.isArray(entry.value)) {
              // Se é array de objetos (anexos), mostrar quantidade
              if (entry.value.length > 0 && typeof entry.value[0] === 'object') {
                valueText = `${entry.value.length} arquivo(s)`;
              } else {
                valueText = entry.value.join('; ') || '(vazio)';
              }
            } else if (typeof entry.value === 'boolean') {
              valueText = entry.value ? 'Sim' : 'Não';
            } else if (typeof entry.value === 'object') {
              valueText = JSON.stringify(entry.value).substring(0, 50) + '...';
            } else {
              valueText = String(entry.value).substring(0, 100);
            }
          }
          
          list.push({
            date: parsedEditedAt,
            kind: 'field',
            title: `Campo "${fieldName}" alterado`,
            subtitle: `Novo valor: ${valueText}`,
            meta: { by: entry.editedBy }
          });
        });
      });
    }
    // Moves
    if (Array.isArray(cardCompleto.etapaHistory)) {
      cardCompleto.etapaHistory.forEach(mov => {
        const parsedMovedAt = safeParseDate(mov.movedAt);
        if (!parsedMovedAt) return; // Pular movimentações sem data válida
        // Buscar nome da etapa em TODAS as etapas (não só nas anteriores)
        const todasDisponiveis = todasEtapas.length > 0 ? todasEtapas : [etapa, ...etapasAnteriores];
        let etapaNome: string | undefined = todasDisponiveis.find(e => String(e.id) === String(mov.etapaId))?.nome;
        // Fallback: card conectado → buscar nas etapas do fluxo de origem
        if (!etapaNome && dadosFluxoOrigem?.etapas?.length) {
          etapaNome = dadosFluxoOrigem.etapas.find(e => String(e.id) === String(mov.etapaId))?.nome;
        }
        // Fallback: nome embutido no próprio entry (backend pode anotar)
        if (!etapaNome) {
          etapaNome = (mov as any).etapaNome || (mov as any)._etapaNome;
        }
        // 🔪 Fatiamento: a movimentação vira o marco de nascimento da fatia, e o que
        // veio antes é a trajetória herdada do card de origem — precisa ficar explícito
        // na linha do tempo, senão parece que o card surgiu do nada.
        const origemTitulo = (mov as any)._cardOrigemTitulo
          || (card as any).fatiadoDe?.titulo
          || 'card de origem';
        const herdado = Boolean((mov as any)._herdadoDaOrigem);
        const nascimentoDaFatia = mov.motivo === 'card_fatiado';

        let tituloMov = `Movido para: ${etapaNome || mov.etapaId}`;
        let subtituloMov = mov.automacaoNome ? `Automação: ${mov.automacaoNome}` : undefined;
        if (nascimentoDaFatia) {
          tituloMov = `🔪 Card criado por fatiamento de "${origemTitulo}"`;
          subtituloMov = `Nasceu em: ${etapaNome || mov.etapaId}`;
        } else if (herdado) {
          tituloMov = `↩︎ Movido para: ${etapaNome || mov.etapaId}`;
          subtituloMov = mov.automacaoNome
            ? `Antes do fatiamento · card "${origemTitulo}" · Automação: ${mov.automacaoNome}`
            : `Antes do fatiamento · card "${origemTitulo}"`;
        }

        list.push({
          date: parsedMovedAt,
          kind: 'move',
          title: tituloMov,
          subtitle: subtituloMov,
          meta: {
            motivo: mov.motivo,
            by: mov.movedBy,
            herdado,
            camposLimpos: (mov as any).camposLimposAutomaticamente // 📋 Campos que foram limpos automaticamente
          }
        });
      });
    }
    // 🔪 Cortes que ESTE card originou (lado da origem)
    const fatiamentoHistory = (cardCompleto as any).fatiamentoHistory;
    if (Array.isArray(fatiamentoHistory)) {
      const todasDisponiveis = todasEtapas.length > 0 ? todasEtapas : [etapa, ...etapasAnteriores];
      fatiamentoHistory.forEach((ev: any) => {
        const parsedAt = safeParseDate(ev.at);
        if (!parsedAt) return;
        const nomesCampos = (ev.camposLevados || [])
          .map((cid: string) => todasDisponiveis.flatMap(e => e.campos || []).find(c => c.id === cid)?.nome || cid);
        list.push({
          date: parsedAt,
          kind: 'hierarchy',
          title: `🔪 Card fatiado: gerou "${ev.fatiaTitulo || ev.fatiaCardId}"`,
          subtitle: nomesCampos.length > 0 ? `Campos levados: ${nomesCampos.join(', ')}` : undefined,
          meta: { by: ev.by },
        });
      });
    }

    // Eventos de hierarquia (virou pai, destravou pai, etc.)
    const hierarchyHistory = (cardCompleto as any).hierarchyHistory;
    if (Array.isArray(hierarchyHistory)) {
      hierarchyHistory.forEach((ev: any) => {
        const parsedAt = safeParseDate(ev.at);
        if (!parsedAt) return;
        let title = '';
        let subtitle: string | undefined;
        switch (ev.event) {
          case 'tornou_pai':
            title = '👑 Card transformado em Card Pai';
            subtitle = 'Agora pode receber cards filhos';
            break;
          case 'destravou_pai':
            title = '🔓 Card deixou de ser Card Pai';
            break;
          case 'adicionou_filho':
            title = '➕ Card filho adicionado';
            subtitle = ev.detalhes;
            break;
          case 'removeu_filho':
            title = '➖ Card filho removido';
            subtitle = ev.detalhes;
            break;
          case 'gix_oc_liberada':
            title = '🔓 OC liberada no GIX';
            subtitle = ev.detalhes;
            break;
          default:
            title = `Evento de hierarquia: ${ev.event}`;
        }
        list.push({
          date: parsedAt,
          kind: 'hierarchy',
          title,
          subtitle,
          meta: { by: ev.by },
        });
      });
    }
    return list.sort((a, b) => b.date.getTime() - a.date.getTime());
  }, [cardCompleto.fieldHistory, cardCompleto.etapaHistory, (cardCompleto as any).hierarchyHistory, (cardCompleto as any).fatiamentoHistory, etapa.campos, etapasAnteriores, todasEtapas, userNames, dadosFluxoOrigem, camposOcultosParaMim]);

  // \u2550\u2550\u2550 GIX: liberar uma OC e atualizar UI/timeline \u2550\u2550\u2550
  const gixLiberarOc = async (numeroOc: number, empresa: number | string | null): Promise<{ ok: boolean; ja_liberada?: boolean; message?: string }> => {
    if (!numeroOc || !empresa) {
      return { ok: false, message: 'Faltam dados (n\u00famero/empresa)' };
    }
    try {
      const userNomeHdr = userPermissions?.currentUser?.nome || userPermissions?.currentUser?.email || 'ESCALONE';
      const userIdHdr = userPermissions?.currentUser?.id || 'unknown';
      const userRoleHdr = (userPermissions?.canAdmin ? 'admin' : 'user');
      const r = await fetch(`/api/gix/oc/${numeroOc}/liberar`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Data': JSON.stringify({ id: userIdHdr, nome: userNomeHdr, role: userRoleHdr }),
          'X-User-Role': userRoleHdr,
        },
        body: JSON.stringify({ empresa, usuario: 'ESCALONE' }),
      });
      const j = await r.json();
      if (j?.status === 'ok') {
        const userNome = userPermissions?.currentUser?.nome || userPermissions?.currentUser?.email || 'ESCALONE';
        const userId = userPermissions?.currentUser?.id || 'unknown';
        // Atualiza a lista de OCs (badge fica verde)
        setGixOcList(prev => prev.map(o => Number(o.numero_oc) === Number(numeroOc)
          ? { ...o, status_liberacao: 'LIBERADA', status_liberacao_cor: 'green', quem_liberou: userNome }
          : o));
        // Atualiza tamb\u00e9m o status \"selecionado\" se for o mesmo
        setGixOcStatus((prev: any) => prev && Number(prev.numero_oc) === Number(numeroOc)
          ? { ...prev, status_liberacao: 'LIBERADA', status_liberacao_cor: 'green', quem_liberou: userNome }
          : prev);
        // Timeline (apenas quando realmente liberada agora)
        if (!j.ja_liberada && flowId && card.id) {
          try {
            const novaEntrada = {
              event: 'gix_oc_liberada',
              at: new Date().toISOString(),
              by: userId,
              detalhes: `OC #${numeroOc} (empresa ${empresa}) liberada no GIX por ${userNome}`,
            };
            const historicoAtual = Array.isArray((cardCompleto as any)?.hierarchyHistory)
              ? (cardCompleto as any).hierarchyHistory
              : (Array.isArray((card as any)?.hierarchyHistory) ? (card as any).hierarchyHistory : []);
            const gixLiberacao = { numeroOc, empresa, por: userNome, porId: userId, em: novaEntrada.at };
            try {
              setCardCompleto((prev: any) => prev ? { ...prev, gixLiberacao, hierarchyHistory: [...historicoAtual, novaEntrada] } : prev);
            } catch { /* ignore */ }
            await fetch(`/api/flows/${flowId}/cards/${card.id}`, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json', 'X-User-Data': JSON.stringify({ id: userId, nome: userNome }) },
              body: JSON.stringify({ hierarchyHistory: [...historicoAtual, novaEntrada], gixLiberacao }),
            });
          } catch (e) { console.warn('\u26a0\ufe0f Falha ao registrar timeline:', e); }
        }
        return { ok: true, ja_liberada: !!j.ja_liberada };
      }
      return { ok: false, message: j?.message || 'Falha ao liberar OC' };
    } catch (e: any) {
      return { ok: false, message: e?.message || 'Erro de conex\u00e3o' };
    }
  };

  // Render reutilizável: badges de OCs do GIX + botão "Liberar todas"
  const renderGixOcBadges = () => {
    if (!isGixEnabled || !gixToggles.badgeStatus) return null;
    if (!gixOcLoading && gixOcList.length === 0) return null;
    const podeAprovarOc = !!userPermissions?.canAdmin;
    const recarregarOc = async (numeroOc: number, empresaOc: number | null | undefined) => {
      try {
        const r = await fetch(`/api/gix/oc/${numeroOc}${empresaOc ? `?empresa=${empresaOc}` : ''}`);
        const data = await r.json();
        let nova: any;
        if (data.status === 'ok') {
          nova = data.oc || data.ocs?.[0] || { numero_oc: numeroOc, status_liberacao: 'DESCONHECIDA', status_liberacao_cor: 'gray' };
        } else if (data.status === 'not_found') {
          nova = { numero_oc: numeroOc, status_liberacao: 'NÃO ENCONTRADA', status_liberacao_cor: 'gray' };
        } else {
          nova = { numero_oc: numeroOc, status_liberacao: 'ERRO', status_liberacao_cor: 'gray' };
        }
        setGixOcList(prev => prev.map(o => Number(o.numero_oc) === Number(numeroOc) ? nova : o));
      } catch {
        // mantém estado atual em caso de falha total
      }
    };
    return (
      <div className="mt-2 space-y-1.5">
        {gixOcLoading && (
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 border border-gray-200">
            <div className="w-3 h-3 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin"></div>
            <span className="text-xs text-gray-500">Consultando OCs no GIX...</span>
          </div>
        )}
        {!gixOcLoading && gixOcList.map((oc: any) => {
          const numeroOc = oc?.numero_oc;
          const liberandoEsta = !!gixLiberarIndividualLoading[String(numeroOc)];
          const isRed = oc.status_liberacao_cor === 'red' && !oc.outroFornecedor;
          const isGreen = oc.status_liberacao_cor === 'green' && !oc.outroFornecedor;
          return (
            <div
              key={`oc-${numeroOc}`}
              className={`flex items-center flex-wrap gap-2 px-3 py-2 rounded-lg border transition-all ${
                isGreen
                  ? 'bg-green-50 border-green-200'
                  : isRed
                    ? 'bg-red-50 border-red-200'
                    : 'bg-gray-50 border-gray-200'
              }`}
            >
              <span
                className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-bold ${
                  isGreen
                    ? 'bg-green-100 text-green-800'
                    : isRed
                      ? 'bg-red-100 text-red-800'
                      : 'bg-gray-100 text-gray-600'
                }`}
              >
                {isGreen ? '✅' : isRed ? '🔒' : '❓'}{' '}#{numeroOc} • {oc.status_liberacao}
              </span>
              {oc.outroFornecedor && (
                <span
                  title="A OC que o Escalone criou para este card não existe mais no GIX; o mesmo número hoje é de outra compra, de outro fornecedor."
                  className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-100 text-red-800"
                >
                  ⚠️ não é a OC deste card · outro fornecedor no GIX
                </span>
              )}
              {oc.ambigua && (
                <span
                  title={`O número ${numeroOc} existe nas empresas ${(oc.empresas_candidatas || []).join(', ')}. `
                    + `A aprovação automática não age sem saber qual é. Escreva "${numeroOc}-EMPRESA" no campo `
                    + `(ex.: "${numeroOc}-${(oc.empresas_candidatas || [])[0] ?? 7}").`}
                  className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-800"
                >
                  ⚠️ ambígua · empresas {(oc.empresas_candidatas || []).join('/')}
                </span>
              )}
              {oc.movimento_96 && (
                <span
                  title={`OC com movimento 96${oc.tipo_movimento ? ` (${oc.tipo_movimento})` : ''}${isGreen ? ' — aprovada' : ' — elegível para aprovação automática'}`}
                  className={`inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-[10px] font-bold ${
                    isGreen ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-700'
                  }`}
                >
                  🧾 mov 96{oc.tipo_movimento ? ` · ${oc.tipo_movimento}` : ''}
                </span>
              )}
              {oc.nome_fornecedor && (
                <span className="text-[10px] text-gray-500 truncate max-w-[180px]" title={oc.nome_fornecedor}>| {oc.nome_fornecedor}</span>
              )}
              {oc.valor_total_fmt && oc.valor_total_fmt !== 'R$ 0,00' && (
                <span className="text-[10px] text-gray-500 font-medium">{oc.valor_total_fmt}</span>
              )}
              {oc.quem_liberou && !oc.realterada_apos_liberacao && (
                <span className="text-[10px] text-gray-400">por {oc.quem_liberou}</span>
              )}
              {/* Liberada e depois alterada no Pyxis: o GIX volta a OC para bloqueada (29/10/2026) */}
              {oc.realterada_apos_liberacao && (
                <div
                  className="basis-full order-last text-[11px] text-red-800 bg-red-100/70 border border-red-200 rounded px-2 py-1"
                  title={oc.realterada_apos_liberacao.resumo || ''}
                >
                  ⚠️ Liberada{oc.quem_liberou ? ` por ${oc.quem_liberou}` : ''}
                  {oc.data_liberacao ? ` em ${String(oc.data_liberacao).slice(0, 10).split('-').reverse().join('/')}` : ''},
                  mas <strong>{oc.realterada_apos_liberacao.alterada_por}</strong> alterou a OC
                  em {oc.realterada_apos_liberacao.alterada_em} e ela voltou a ficar bloqueada.
                </div>
              )}
              <div className="ml-auto flex items-center gap-1">
                <button
                  type="button"
                  onClick={async () => {
                    if (!numeroOc) return;
                    setGixOcStatus(oc);
                    setGixOcDetalheAberto(true);
                    setGixOcDetalheLoading(true);
                    setGixOcDetalheErro(null);
                    setGixOcDetalheItens([]);
                    try {
                      const empresaQs = oc?.empresa ? `?empresa=${oc.empresa}` : '';
                      const r = await fetch(`/api/gix/oc/${numeroOc}/itens${empresaQs}`);
                      const j = await r.json();
                      if (j?.status === 'ok') {
                        setGixOcDetalheItens(j.itens || []);
                      } else {
                        setGixOcDetalheErro(j?.message || 'Sem itens para esta OC');
                      }
                    } catch (e: any) {
                      setGixOcDetalheErro(e?.message || 'Erro ao consultar itens');
                    } finally {
                      setGixOcDetalheLoading(false);
                    }
                  }}
                  title="Ver detalhes desta OC"
                  className="px-2 py-0.5 rounded text-[10px] font-semibold bg-white border border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                >
                  🔍 detalhes
                </button>
                {oc.status_liberacao === 'ERRO' && (
                  <button
                    type="button"
                    onClick={() => recarregarOc(numeroOc, oc?.empresa)}
                    title="Tentar consultar OC novamente"
                    className="px-2 py-0.5 rounded text-[10px] font-semibold bg-white border border-amber-300 text-amber-700 hover:bg-amber-50"
                  >
                    🔄 recarregar
                  </button>
                )}
                {isRed && podeAprovarOc && !ocForaDaEtapa && (
                  <button
                    type="button"
                    disabled={liberandoEsta || gixLiberarTodasLoading}
                    onClick={async () => {
                      const fornecedor = oc.fornecedor_nome || oc.fornecedor || '';
                      const valor = (oc.valor_total != null) ? `R$ ${Number(oc.valor_total).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '';
                      const detalhes = [fornecedor, valor].filter(Boolean).join(' • ');
                      const msg = `Tem certeza que deseja liberar a OC #${numeroOc}?${detalhes ? `\n\n${detalhes}` : ''}\n\nEsta ação não pode ser desfeita.`;
                      if (!confirm(msg)) return;
                      const key = String(numeroOc);
                      setGixLiberarIndividualLoading(prev => ({ ...prev, [key]: true }));
                      const res = await gixLiberarOc(numeroOc, oc.empresa);
                      setGixLiberarIndividualLoading(prev => { const n = { ...prev }; delete n[key]; return n; });
                      if (res.ok) {
                        if (res.ja_liberada) success?.(`OC #${numeroOc} já estava liberada`);
                        else success?.(`OC #${numeroOc} liberada com sucesso`);
                      } else {
                        error?.(res.message || `Falha ao liberar OC #${numeroOc}`);
                      }
                    }}
                    className="px-2 py-0.5 rounded text-[10px] font-semibold bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                  >
                    {liberandoEsta ? (
                      <>
                        <div className="w-2.5 h-2.5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        liberando...
                      </>
                    ) : (
                      <>🔓 liberar</>
                    )}
                  </button>
                )}
                {isRed && !podeAprovarOc && !ocForaDaEtapa && (
                  <span className="text-[10px] text-gray-400 italic" title="Apenas administradores podem liberar OCs">
                    🔒 somente admin
                  </span>
                )}
              </div>
            </div>
          );
        })}
        {!gixOcLoading && podeAprovarOc && !ocForaDaEtapa && gixOcList.filter((o: any) => o.status_liberacao_cor === 'red').length > 1 && (
          <button
            type="button"
            disabled={gixLiberarTodasLoading}
            onClick={async () => {
              const pendentes = gixOcList.filter((o: any) => o.status_liberacao_cor === 'red');
              if (pendentes.length === 0) return;
              if (!confirm(`Liberar ${pendentes.length} OC(s) pendentes deste card?`)) return;
              setGixLiberarTodasLoading(true);
              let okCount = 0; let failCount = 0;
              for (const oc of pendentes) {
                const res = await gixLiberarOc(oc.numero_oc, oc.empresa);
                if (res.ok) okCount++; else failCount++;
              }
              setGixLiberarTodasLoading(false);
              if (failCount === 0) success?.(`${okCount} OC(s) liberada(s) com sucesso`);
              else error?.(`${okCount} OK, ${failCount} falha(s) ao liberar`);
            }}
            className="w-full mt-1 px-3 py-2 rounded-lg text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-sm"
          >
            {gixLiberarTodasLoading ? (
              <>
                <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Liberando todas as OCs...
              </>
            ) : (
              <>🔓 Liberar todas as OCs deste card ({gixOcList.filter((o: any) => o.status_liberacao_cor === 'red').length})</>
            )}
          </button>
        )}
      </div>
    );
  };

  // Vincular/desvincular etiqueta e persistir imediatamente (sem disparar automações
  // nem fechar o modal). Funciona tanto em visualização quanto em edição.
  const toggleEtiqueta = (etiquetaId: string) => {
    const novas = etiquetasSelecionadas.includes(etiquetaId)
      ? etiquetasSelecionadas.filter(id => id !== etiquetaId)
      : [...etiquetasSelecionadas, etiquetaId];
    setEtiquetasSelecionadas(novas);
    onSave({ ...card, etiquetasIds: novas, updatedAt: new Date(), _skipAutomacoes: true } as any);
  };

  // ➡️ Mover o card para outra etapa direto do modal.
  // Toda a regra (permissão, permiteMoverPara, responsável automático, limpeza de
  // campos, automações, histórico) mora no KanbanBoard — é a MESMA rotina do
  // drag-and-drop. Aqui só disparamos e mostramos o feedback.
  const handleMoverParaEtapa = async (etapaDestino: Etapa) => {
    if (!onMoverParaEtapa || movendoEtapaId) return;
    setMostrarMoverMenu(false);
    setMovendoEtapaId(String(etapaDestino.id));
    try {
      await onMoverParaEtapa(String(etapaDestino.id));
      success(`➡️ Card movido para "${etapaDestino.nome}"`);
    } catch (err) {
      console.error('❌ Erro ao mover card de etapa:', err);
      error('❌ Não foi possível mover o card. Tente novamente.');
    } finally {
      setMovendoEtapaId(null);
    }
  };

  // ⚠️ Fornecedor novo — a ciência é de quem digita o fornecedor, não de quem cria
  // o card. Olha o campo da etapa aberta e também o que foi corrigido no histórico.
  const cienciaJaDada = (): CienciaFornecedorNovo | null =>
    cienciaRef.current
    || (cardCompleto as any)?.cienciaFornecedorNovo
    || (card as any)?.cienciaFornecedorNovo
    || null;

  /** Fornecedor inédito que ainda ninguém confirmou, ou null se está tudo certo. */
  const fornecedorPendenteDeCiencia = async () => {
    const candidatos: { campoId: string; valor: any; noHistorico: boolean }[] = [];

    (etapa.campos || []).forEach(c => {
      if ((c as any).validarFornecedorNovo && camposVisiveis[c.id] !== false) {
        candidatos.push({ campoId: c.id, valor: valores[c.id], noHistorico: false });
      }
    });

    const camposDoFluxo = [...(todasEtapas || []), ...(etapasAnteriores || [])]
      .flatMap((e: any) => e?.campos || []);
    Object.keys(camposEditadosNoHistorico).forEach(id => {
      if (candidatos.some(x => x.campoId === id)) return;
      if (camposDoFluxo.some((c: any) => c?.id === id && c?.validarFornecedorNovo)) {
        candidatos.push({ campoId: id, valor: camposEditadosNoHistorico[id], noHistorico: true });
      }
    });

    for (const { campoId, valor, noHistorico } of candidatos) {
      const nome = textoLimpoFornecedor(valor);
      if (!nome || cienciaValePara(cienciaJaDada(), nome)) continue;
      // o próprio card não conta como "já apareceu": o fornecedor que ele mesmo
      // gravou voltaria como conhecido e ninguém seria perguntado
      const verificacao = await verificarFornecedor(nome, String(card.id));
      if (verificacao?.novo) {
        return { campoId, fornecedor: nome, parecidos: verificacao.parecidos || [], noHistorico };
      }
    }
    return null;
  };

  const handleSave = async () => {
    if (!titulo.trim()) {
      error('Digite um título para o cartão');
      return;
    }

    // ⚠️ Antes de gravar: fornecedor inédito tem que passar pela ciência. Sem esta
    // trava o card salvava e fechava calado, e o selo depois dizia que ninguém foi
    // perguntado — era verdade, ninguém tinha sido. Se a consulta falhar,
    // `verificarFornecedor` devolve null e o trabalho segue: o aviso não trava.
    setSalvando(true);
    const pendenteCiencia = await fornecedorPendenteDeCiencia();
    setSalvando(false);
    if (pendenteCiencia) {
      setPedindoCiencia(pendenteCiencia);
      return;
    }

    // Ativar loading
    setSalvando(true);

    // Verificar se o responsável foi alterado
    const responsavelAlterado = responsavelIndividual !== card.responsavelIndividualId;

    // 🔧 FIX: Só validar campos obrigatórios se:
    // 1. O responsável NÃO foi alterado
    // 2. O usuário MODIFICOU campos da ETAPA ATUAL (não apenas histórico)
    // Se o usuário só editou campos do histórico de etapas anteriores, não deve bloquear o salvamento
    const somenteEditouHistorico = !camposEtapaAtualModificados;
    if (!responsavelAlterado && !somenteEditouHistorico) {
      // Campo escondido por automação (Exibir/Ocultar) não é cobrado — igual ao Novo Cartão
      const camposObrigatorios = etapa.campos.filter(campo => ehObrigatorio(campo) && camposVisiveis[campo.id] !== false);
      // ✈️ Viajantes: nome + CPF válido para cada um
      for (const cv of etapa.campos.filter((c: any) => c.tipo === 'viajantes' && camposVisiveis[c.id] !== false)) {
        const preenchidos = Array.isArray(valores[cv.id]) ? valores[cv.id].filter((x: any) => x && (String(x.nome || '').trim() || String(x.cpf || '').trim())) : [];
        if (!cv.obrigatorio && preenchidos.length === 0) continue;
        const problema = problemaViajantes(valores[cv.id]);
        if (problema) {
          error(`${cv.nome}: ${problema}`);
          setSalvando(false);
          return;
        }
      }
      const camposFaltando = camposObrigatorios.filter(campo => {
        const valor = valores[campo.id];
        
        // Validação específica por tipo de campo
        switch (campo.tipo) {
          case 'checkbox':
          case 'parcelas':
          case 'itens':
          case 'nd_vinculada':
          case 'progresso':
            // Para checkboxes, deve ter pelo menos uma opção selecionada
            return !Array.isArray(valor) || valor.length === 0;
          case 'cotacoes':
            return !ehMapaCotacao(valor) || valor.cotacoes.length === 0;
          case 'texto':
          case 'texto_longo':
          case 'data':
          case 'data_vencimento':
          case 'valor':
          case 'lista':
            // Para campos de texto/lista, não pode estar vazio
            return !valor || valor.toString().trim() === '';
          case 'anexo':
            // Para anexos, deve ter pelo menos um arquivo
            return !Array.isArray(valor) || valor.length === 0;
          default:
            return !valor;
        }
      });

      if (camposFaltando.length > 0) {
        error(`Preencha os campos obrigatórios: ${camposFaltando.map(c => c.nome).join(', ')}`);
        setSalvando(false);
        return;
      }
    }

    try {
      // Processar uploads de novos arquivos
      const valoresProcessados = { ...valores };
    
    for (const [campoId, valor] of Object.entries(valores)) {
      const campo = etapa.campos.find(c => c.id === campoId);
      if (campo?.tipo === 'anexo' && valor && Array.isArray(valor) && valor.length > 0) {
        const integracaoId = campo.integracaoId; // Usar integracaoId do campo configurado na etapa
        if (integracaoId) {
          console.log(`📤 DetalhesCartao - Processando upload de ${valor.length} arquivo(s) para integração ${integracaoId}`);
          
          try {
            // Buscar integração já carregada pelo hook
            const integracao = getIntegracaoPorId(integracaoId);
            console.log('🔍 Integração encontrada:', integracao);
            
            if (integracao) {
              console.log(`📁 Enviando arquivos para pasta: ${integracao.pastaNome || integracao.nome}`);
              
              // Processar cada arquivo - apenas os que ainda não foram enviados
              const arquivosProcessados = await Promise.all(
                valor.map(async (arquivo: any) => {
                  // Verificar se o arquivo já foi enviado (tem status 'uploaded')
                  if (arquivo.status === 'uploaded' || arquivo.emailDestino) {
                    console.log(`⏭️ Arquivo ${arquivo.name} já foi enviado, pulando...`);
                    return arquivo; // Manter como está
                  }
                  
                  try {
                    console.log(`⬆️ Enviando arquivo: ${arquivo.name} para ${integracao.nome}`);
                    
                    // Converter para DriveFile (formato esperado pelo serviço)
                    const driveFile = {
                      name: arquivo.name,
                      size: arquivo.size || 0,
                      type: arquivo.type || 'application/octet-stream',
                      dataUrl: arquivo.dataUrl
                    };
                    
                    // Upload real usando a integração configurada
                    console.log(`🔄 Fazendo upload real usando integração: ${integracao.nome}`);
                    
                    // Preparar dados específicos da integração
                    const userInfo = {
                      nome: 'Usuário Escalone',
                      email: 'usuario@escalone.com', 
                      empresa: 'Escalone'
                    };
                    
                    // Para integrações do tipo email, usar os dados da configuração
                    if (integracao.tipo === 'email') {
                      const payload = {
                        file: driveFile,
                        usuario: userInfo,
                        emailDestino: integracao.emailDestino,
                        nomeConfigurador: integracao.nomeConfigurador
                      };
                      
                      console.log('📧 Enviando com configuração personalizada:', payload.emailDestino);
                      
                      const response = await fetch(apiUrl('/upload-arquivo'), {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json',
                        },
                        body: JSON.stringify(payload),
                      });

                      if (!response.ok) {
                        throw new Error(`Erro HTTP: ${response.status}`);
                      }

                      const uploadResult = await response.json();
                      
                      if (uploadResult.status === 'success') {
                        console.log(`✅ Upload realizado com sucesso:`, uploadResult);
                      } else {
                        throw new Error(uploadResult.message || 'Upload falhou');
                      }
                    }
                    
                    console.log(`✅ Upload realizado com sucesso para ${integracao.nome}`);
                    return {
                      ...arquivo,
                      integracaoId,
                      integracaoNome: integracao.nome,
                      pastaNome: integracao.pastaNome || integracao.nomeConfigurador,
                      pastaPath: integracao.pastaPath,
                      uploadedAt: new Date(),
                      status: 'uploaded',
                      emailDestino: integracao.emailDestino || integracao.email
                    };
                  } catch (error) {
                    console.error(`❌ Erro ao enviar arquivo ${arquivo.name}:`, error);
                    return {
                      ...arquivo,
                      integracaoId,
                      status: 'error',
                      error: error instanceof Error ? error.message : String(error)
                    };
                  }
                })
              );
              
              valoresProcessados[campoId] = arquivosProcessados;
              console.log(`✅ Upload concluído para ${arquivosProcessados.length} arquivo(s)`);
            } else {
              console.log('❌ Integração não encontrada para ID:', integracaoId);
            }
          } catch (error) {
            console.error('❌ Erro no processo de upload:', error);
          }
        }
      }
    }

    const currentUser = userPermissions?.currentUser;
    const now = new Date();

    // Criar histórico de mudanças dos campos
    const newFieldHistory = { ...card.fieldHistory };
    
    // Verificar quais campos da ETAPA ATUAL mudaram
    etapa.campos.forEach(campo => {
      const oldValue = card.campos[campo.id];
      const newValue = valores[campo.id];
      
      if (JSON.stringify(oldValue) !== JSON.stringify(newValue)) {
        if (!newFieldHistory[campo.id]) {
          newFieldHistory[campo.id] = [];
        }
        newFieldHistory[campo.id].push({
          value: newValue,
          editedBy: currentUser?.id || 'unknown',
          editedAt: now
        });
      }
    });
    
    // 🔧 FIX: Também incluir campos editados no HISTÓRICO de etapas anteriores
    // Isso garante que anexos adicionados em etapas anteriores também sejam salvos no fieldHistory
    Object.entries(camposEditadosNoHistorico).forEach(([campoId, novoValor]) => {
      const oldValue = card.campos[campoId];
      if (JSON.stringify(oldValue) !== JSON.stringify(novoValor)) {
        if (!newFieldHistory[campoId]) {
          newFieldHistory[campoId] = [];
        }
        newFieldHistory[campoId].push({
          value: novoValor,
          editedBy: currentUser?.id || 'unknown',
          editedAt: now
        });
        console.log(`📝 [fieldHistory] Campo do histórico ${campoId} adicionado ao fieldHistory`);
      }
    });

    const etapaDestinoFinal = card.etapaId; // ← SEMPRE manter na etapa original, automação move depois
    
    // Preparar histórico de etapas COM atualizações dos camposSnapshot
    console.log('🔍 DEBUG - valoresHistorico antes de salvar:', JSON.stringify(valoresHistorico, null, 2));
    console.log('🔍 DEBUG - card.etapaHistory:', JSON.stringify(card.etapaHistory, null, 2));
    
    const historicoAtualizado = ((card as any).etapaHistory || []).map((h: any) => {
      const etapaId = String(h.etapaId);
      // Se há valores editados para esta etapa no histórico, atualizar o camposSnapshot
      if (valoresHistorico[etapaId] && Object.keys(valoresHistorico[etapaId]).length > 0) {
        console.log(`✅ Atualizando camposSnapshot da etapa ${etapaId} com:`, valoresHistorico[etapaId]);
        return {
          ...h,
          camposSnapshot: {
            ...(h.camposSnapshot || {}),
            ...valoresHistorico[etapaId]
          }
        };
      }
      return h;
    });
    
    console.log('🔍 DEBUG - historicoAtualizado final:', JSON.stringify(historicoAtualizado, null, 2));
    
    // 🔧 FIX: Quando o usuário edita valores no histórico de etapas anteriores,
    // esses valores também devem ser refletidos no card.campos para que
    // o mini card no kanban exiba o valor atualizado.
    // IMPORTANTE: Usamos camposEditadosNoHistorico que contém APENAS os campos
    // que o usuário REALMENTE editou, não todos os valores do histórico.
    // 🔧 FIX 10/09/2026: salvar SÓ o que esta tela pode mexer.
    // Antes partíamos de `valoresProcessados`, que carrega os campos do card
    // inteiro — inclusive os de etapas que o usuário nem abriu, e que entram
    // nesse pacote já vazios ([] em checkbox, '' em anexo). Resultado: salvar o
    // formulário de uma etapa apagava valor de outra. Foi o que aconteceu com o
    // "Pagamento duas etapas?": marcado no Financeiro, zerado ao salvar a
    // Finalização do serviço, e o retorno só copiou o vazio.
    // Agora a base é o card gravado e por cima vai apenas o que esta tela edita.
    const idsDaEtapa = new Set<string>((etapa.campos || []).map(c => c.id));
    // campos de outras etapas que ESTA etapa mostra de propósito ("etapaId:campoId")
    ((etapa as any).camposOutrasEtapas || []).forEach((ref: string) => {
      const id = String(ref).includes(':') ? String(ref).split(':')[1] : String(ref);
      if (id) idsDaEtapa.add(id);
    });

    const camposAtualizadosComHistorico: Record<string, any> = { ...(card.campos || {}) };
    Object.entries(valoresProcessados).forEach(([campoId, valor]) => {
      if (idsDaEtapa.has(campoId)) {
        camposAtualizadosComHistorico[campoId] = valor;
      }
    });
    Object.entries(camposEditadosNoHistorico).forEach(([campoId, valor]) => {
      console.log(`🔄 Atualizando card.campos[${campoId}] com valor editado:`, valor);
      camposAtualizadosComHistorico[campoId] = valor;
    });
    if (produtosGixRef.current) camposAtualizadosComHistorico.__produtosGix = produtosGixRef.current;
    if (pedidoGixRef.current) camposAtualizadosComHistorico.__pedidoGix = pedidoGixRef.current;
    console.log('🔍 DEBUG - camposEditadosNoHistorico:', camposEditadosNoHistorico);
    console.log('🔍 DEBUG - camposAtualizadosComHistorico:', camposAtualizadosComHistorico);
    
    // ⚠️ NÃO limpar o campo gatilho aqui!
    // O campo gatilho será limpo pelo KanbanBoard APÓS a automação mover o card
    // Se limparmos aqui, o handleUpdateCard não conseguirá avaliar a condição da automação
    
    // NÃO adicionar mudança de etapa aqui - será feito pela automação no handleUpdateCard
    
  onSave({
      ...card,
      titulo,
      etapaId: etapaDestinoFinal, // ← Sempre a etapa original
      campos: camposAtualizadosComHistorico, // 🔧 FIX: Incluir valores editados do histórico
      responsavelIndividualId: responsavelIndividual, // Salvar o responsável individual
      etiquetasIds: etiquetasSelecionadas, // Salvar etiquetas vinculadas
      lastEditedBy: currentUser?.id || 'unknown',
      lastEditedAt: now,
      fieldHistory: newFieldHistory,
      updatedAt: now,
    // quem confirmou o fornecedor novo agora; a ciência já gravada no card fica
    ...(cienciaRef.current ? { cienciaFornecedorNovo: cienciaRef.current } : {}),
    etapaHistory: historicoAtualizado,
    // 🔧 FIX: sinalizar que durante a edição o motor já decidiu mover.
    // Sem isto, o handleUpdateCard pula automações se os campos não mudaram em JSON
    // (caso clássico: campo já estava "Validado" antes de abrir o modal).
    _pendingAutomacaoMove: destinoAutomacao ? {
      etapaDestinoId: destinoAutomacao,
      automacaoId: automacaoMetaRef.current?.automacaoId,
      automacaoNome: automacaoMetaRef.current?.automacaoNome,
      campoGatilho: automacaoMetaRef.current?.campoGatilho,
    } : undefined,
    } as any);
    
    success(destinoAutomacao ? 'Campos salvos. Cartão será movido por automação.' : 'Campos do cartão salvos com sucesso.');
    
    // Log para debugging
    console.log('💾 Card salvo com campos:', {
      cardId: card.id,
      titulo,
      campos: valores,
      totalCampos: Object.keys(valores).length
    });
    
    // Desativar loading e fechar o card IMEDIATAMENTE (sem delay)
    setSalvando(false);
    onClose();
  } catch (error) {
    setSalvando(false);
  }
};

  // Função helper para renderizar campo com valor específico (para histórico)
  // etapaIdHistorico: se passado, usa valoresHistorico ao invés de valores
  const renderCampoComValor = (campo: CampoConfig, valor: any, disabled: boolean = true, etapaIdHistorico?: string) => {
    // Função auxiliar para converter qualquer valor em string renderizável
    const toSafeString = (val: any): string => {
      if (val === undefined || val === null || val === '') return '-';
      if (typeof val === 'string') return val;
      if (typeof val === 'number' || typeof val === 'boolean') return String(val);
      if (typeof val === 'object') {
        // Se for objeto, tentar extrair propriedades úteis
        if (val.name) return val.name;
        if (val.fileName) return val.fileName;
        if (Array.isArray(val) && val.some((v: any) => v && typeof v === 'object' && 'paga' in v && 'nome' in v)) return textoParcelas(val);
        if (ehListaDeViajantes(val)) return textoViajantes(val);
        if (ehListaDeVoos(val)) return textoVoos(val);
        if (ehListaDeTrechos(val)) return textoTrechos(val);
        if (ehListaDeItens(val)) return textoItens(val);
        if (Array.isArray(val)) return val.join(', ');
        return JSON.stringify(val);
      }
      return String(val);
    };
    
    const valorExibir = campo.tipo === 'responsavel' ? nomeResponsavelCampo(valor) : toSafeString(valor);

    // Determinar qual valor e função de update usar
    const getValor = (campoId: string) => {
      if (etapaIdHistorico) {
        return valoresHistorico[etapaIdHistorico]?.[campoId] ?? valor;
      }
      return valores[campoId];
    };
    
    const setValor = (campoId: string, novoValor: any) => {
      if (etapaIdHistorico) {
        updateHistoricoFieldValue(etapaIdHistorico, campoId, novoValor);
      } else {
        updateFieldValue(campoId, novoValor);
      }
    };
    
    // Se não estiver desabilitado, permitir edição
    if (!disabled) {
      switch (campo.tipo) {
        case 'texto':
          if (ehCampoNumeroNf(campo.nome)) {
            return <CampoNumerosNf valor={getValor(campo.id) || ''} onChange={(v) => setValor(campo.id, v)} />;
          }
          return (
            <input
              type="text"
              value={getValor(campo.id) || ''}
              onChange={(e) => setValor(campo.id, e.target.value)}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
            />
          );
        
        case 'numero':
          return (
            <input
              type="text"
              inputMode="numeric"
              value={getValor(campo.id) || ''}
              onChange={(e) => setValor(campo.id, e.target.value.replace(/\D/g, ''))}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              placeholder="Apenas números"
            />
          );
        
        case 'valor':
          return (
            <div className="relative">
              <span className="absolute left-2 top-1 text-xs text-gray-500">R$</span>
              <input
                type="number"
                value={getValor(campo.id) || ''}
                onChange={(e) => setValor(campo.id, e.target.value)}
                className="w-full pl-7 pr-2 py-1 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                placeholder="0,00"
                step="0.01"
              />
            </div>
          );
        
        case 'lista':
          return (
            <select
              value={getValor(campo.id) || ''}
              onChange={(e) => setValor(campo.id, e.target.value)}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
            >
              <option value="">Selecione...</option>
              {campo.opcoes?.map(opcao => (
                <option key={opcao} value={opcao}>{opcao}</option>
              ))}
            </select>
          );
        
        case 'checkbox':
        case 'checkbox_single':
        case 'checkbox_multi':
          const valoresArrayEdit = Array.isArray(getValor(campo.id)) ? getValor(campo.id) : [];
          return (
            <div className="space-y-1 px-2 py-1.5 border border-gray-300 rounded bg-white">
              {campo.opcoes?.map(opcao => (
                <div key={opcao} className="flex items-center gap-1.5 text-xs">
                  <input
                    type="checkbox"
                    checked={valoresArrayEdit.includes(opcao)}
                    onChange={(e) => {
                      const novosValores = e.target.checked
                        ? [...valoresArrayEdit, opcao]
                        : valoresArrayEdit.filter((v: string) => v !== opcao);
                      setValor(campo.id, novosValores);
                    }}
                    className="h-3 w-3 rounded border-gray-300"
                  />
                  <span className="text-gray-700">{opcao}</span>
                </div>
              )) || <span className="text-gray-500">Sem opções configuradas</span>}
            </div>
          );
        
        case 'data':
        case 'data_vencimento':
          return (
            <input
              type="datetime-local"
              value={getValor(campo.id) || ''}
              onChange={(e) => setValor(campo.id, e.target.value)}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
            />
          );
        
        // IMPORTANTE: Caso de anexo editável no histórico
        // Mostra o arquivo atual (se existir) e permite substituir por outro
        // Corrigido em: 22/01/2026
        case 'anexo':
          const valorAtualAnexo = getValor(campo.id);
          const arquivosAtuais = Array.isArray(valorAtualAnexo) ? valorAtualAnexo : valorAtualAnexo ? [valorAtualAnexo] : [];
          
          return (
            <div className="space-y-1">
              {/* Mostrar arquivos atuais */}
              {arquivosAtuais.length > 0 && (
                <div className="space-y-0.5">
                  <span className="text-[10px] text-gray-500">Arquivo atual:</span>
                  {arquivosAtuais.map((arquivo: any, index: number) => {
                    let nomeArquivo = 'Arquivo';
                    let fileUrl = null;
                    
                    if (typeof arquivo === 'string') {
                      nomeArquivo = arquivo;
                    } else if (typeof arquivo === 'object' && arquivo !== null) {
                      nomeArquivo = arquivo.name || arquivo.fileName || arquivo.originalName || 'Arquivo';
                      fileUrl = arquivo.webViewLink || arquivo.webContentLink || arquivo.url;
                    }
                    
                    return (
                      <div key={index} className="flex items-center justify-between px-2 py-1 bg-blue-50 border border-blue-200 rounded text-xs">
                        <span className="flex items-center gap-1 text-blue-700">
                          📎 {nomeArquivo}
                        </span>
                        <div className="flex items-center gap-1">
                          {fileUrl && (
                            <button
                              type="button"
                              onClick={() => window.open(fileUrl, '_blank')}
                              className="px-1.5 py-0.5 text-[10px] bg-blue-500 text-white rounded hover:bg-blue-600"
                            >
                              Abrir
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => {
                              // Remover este arquivo específico
                              const currentFiles = arquivosAtuais.filter((_, i) => i !== index);
                              setValor(campo.id, currentFiles.length > 0 ? currentFiles : []);
                              console.log('🗑️ Arquivo removido, restantes:', currentFiles.length);
                            }}
                            className="px-1.5 py-0.5 text-[10px] bg-red-500 text-white rounded hover:bg-red-600"
                          >
                            Remover
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              
              {/* Input para novo arquivo - UPLOAD LOCAL */}
              <div className="flex flex-col gap-0.5">
                <span className="text-[10px] text-gray-500">
                  {arquivosAtuais.length >= 6 ? '⚠️ Limite de 6 arquivos atingido' : arquivosAtuais.length > 0 ? `Adicionar mais (${arquivosAtuais.length}/6):` : 'Selecionar arquivo (máx. 20MB):'}
                </span>
                <input
                  type="file"
                  disabled={arquivosAtuais.length >= 6}
                  onChange={async (e) => {
                    let file = e.target.files?.[0];
                    if (file) {
                      // 🖼️ Comprimir imagem (se for foto grande de celular)
                      file = await compressImageIfNeeded(file);
                      // 📏 LIMITE: Máximo 20MB por arquivo
                      const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB
                      if (file.size > MAX_FILE_SIZE) {
                        alert(`⚠️ Arquivo muito grande!\n\nTamanho: ${(file.size / 1024 / 1024).toFixed(2)}MB\nMáximo permitido: 20MB\n\nPor favor, selecione um arquivo menor.`);
                        e.target.value = '';
                        return;
                      }
                      
                      // 📦 LIMITE: Máximo 6 anexos por campo
                      const MAX_ANEXOS = 6;
                      if (arquivosAtuais.length >= MAX_ANEXOS) {
                        alert(`⚠️ Limite de anexos atingido!\n\nMáximo permitido: ${MAX_ANEXOS} arquivos por campo.\n\nRemova um arquivo existente para adicionar outro.`);
                        e.target.value = '';
                        return;
                      }
                      
                      // 🚀 UPLOAD LOCAL: Enviar para o servidor (sem base64!)
                      try {
                        const formData = new FormData();
                        formData.append('file', file);
                        formData.append('flowId', flowId || 'geral');
                        formData.append('cardId', card.id || 'temp');
                        formData.append('campoId', campo.id);
                        // Adicionar nomes para organização em pastas legíveis
                        formData.append('flowNome', flowName || '');
                        formData.append('cardTitulo', card.titulo || card.id || '');
                        
                        const response = await fetch('/api/upload-anexo-local', {
                          method: 'POST',
                          body: formData
                        });
                        
                        if (!response.ok) throw new Error('Erro no upload');
                        const result = await response.json();
                        
                        const novoArquivo = {
                          name: result.originalName || file.name,
                          fileName: result.fileName,
                          size: result.size || file.size,
                          type: result.type || file.type,
                          url: result.url,
                          uploadedAt: result.uploadedAt
                        };
                        // Adicionar ao array existente em vez de substituir
                        setValor(campo.id, [...arquivosAtuais, novoArquivo]);
                      } catch (error) {
                        console.error('Erro upload:', error);
                        alert(`Erro ao enviar ${file.name}`);
                      }
                    }
                  }}
                  className={`w-full px-2 py-1 text-xs border rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all file:mr-2 file:py-0.5 file:px-2 file:rounded file:border-0 file:text-xs file:font-medium ${arquivosAtuais.length >= 6 ? 'border-red-300 bg-red-50 cursor-not-allowed' : 'border-gray-300 file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100'}`}
                />
              </div>
            </div>
          );
        
        default:
          return (
            <textarea
              value={getValor(campo.id) || ''}
              onChange={(e) => setValor(campo.id, e.target.value)}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none overflow-hidden"
              rows={2}
              style={{ minHeight: '40px' }}
              onInput={(e) => {
                const target = e.target as HTMLTextAreaElement;
                target.style.height = 'auto';
                target.style.height = target.scrollHeight + 'px';
              }}
            />
          );
      }
    }
    
    // Modo de visualização (disabled=true)
    switch (campo.tipo) {
      case 'texto':
      case 'texto_longo':
      case 'valor':
        return (
          <div className="px-2 py-1 bg-gray-50 border border-gray-200 rounded text-xs text-gray-700">
            <TextoComHtml valor={valorExibir} />
          </div>
        );
      
      case 'lista':
        return (
          <div className="px-2 py-1 bg-gray-50 border border-gray-200 rounded text-xs text-gray-700">
            {valorExibir}
          </div>
        );
      
      case 'checkbox_multi':
        const valoresArray = Array.isArray(valor) ? valor : [];
        // No histórico (disabled), mostrar apenas as opções selecionadas
        const opcoesSelecionadas = valoresArray.length > 0 
          ? valoresArray 
          : [];
        
        if (opcoesSelecionadas.length === 0) {
          return (
            <div className="px-2 py-1 bg-gray-50 border border-gray-200 rounded text-xs text-gray-500">
              -
            </div>
          );
        }
        
        return (
          <div className="space-y-0.5 px-2 py-1 bg-gray-50 border border-gray-200 rounded">
            {opcoesSelecionadas.map(opcao => (
              <div key={opcao} className="flex items-center gap-1 text-xs">
                <input
                  type="checkbox"
                  checked={true}
                  disabled={true}
                  className="h-3 w-3 rounded border-gray-300"
                />
                <span className="text-gray-700">{opcao}</span>
              </div>
            ))}
          </div>
        );
      
      case 'data':
        const dataFormatada = valor ? new Date(valor).toLocaleDateString('pt-BR') : '-';
        return (
          <div className="px-2 py-1 bg-gray-50 border border-gray-200 rounded text-xs text-gray-700">
            {dataFormatada}
          </div>
        );
      
      case 'anexo':
        const arquivos = Array.isArray(valor) ? valor : valor ? [valor] : [];
        return (
          <div className="space-y-2">
            {arquivos.length > 0 ? (
              arquivos.map((arquivo: any, index: number) => {
                // Extrair dados do arquivo de forma segura
                let nomeArquivo = '-';
                let fileUrl = null;
                let dataUrl = null;
                let fileType = '';
                
                if (typeof arquivo === 'string') {
                  nomeArquivo = arquivo;
                } else if (typeof arquivo === 'object' && arquivo !== null) {
                  nomeArquivo = arquivo.name || arquivo.fileName || arquivo.originalName || 'Arquivo';
                  fileUrl = arquivo.webViewLink || arquivo.webContentLink || arquivo.url;
                  dataUrl = arquivo.dataUrl;
                  fileType = arquivo.type || arquivo.mimeType || '';
                }
                
                const handleOpenFile = async () => {
                  // Priorizar URL do Drive
                  if (fileUrl) {
                    console.log('📥 HISTÓRICO - Abrindo arquivo do Drive:', fileUrl);
                    window.open(fileUrl, '_blank');
                    return;
                  }
                  
                  // Se já temos dataUrl local, usar
                  let dataUrlToUse = dataUrl;
                  
                  // Se não temos dataUrl, buscar do servidor sob demanda
                  if (!dataUrlToUse || typeof dataUrlToUse !== 'string' || !dataUrlToUse.includes('data:')) {
                    console.log('📡 ANEXO - Buscando base64 do servidor para:', nomeArquivo, 'etapaId:', etapaIdHistorico);
                    try {
                      // 🔧 CORREÇÃO 23/01/2026: Passa etapaId para buscar no histórico também
                      let url = `/api/flows/${flowId}/cards/${card.id}/anexo/${campo.id}/${index}`;
                      if (etapaIdHistorico) {
                        url += `?etapaId=${etapaIdHistorico}`;
                      }
                      const response = await fetch(url);
                      const result = await response.json();
                      if (result.success && result.anexo && result.anexo.dataUrl) {
                        dataUrlToUse = result.anexo.dataUrl;
                        console.log('✅ ANEXO - Base64 obtido com sucesso! Fonte:', result.fonte || 'desconhecida');
                      } else {
                        console.error('❌ ANEXO - Não foi possível obter base64:', result);
                        alert('Arquivo não disponível para visualização');
                        return;
                      }
                    } catch (error) {
                      console.error('❌ ANEXO - Erro ao buscar base64:', error);
                      alert('Erro ao buscar arquivo do servidor');
                      return;
                    }
                  }
                  
                  // Agora temos dataUrl, abrir o arquivo
                  if (dataUrlToUse && typeof dataUrlToUse === 'string' && dataUrlToUse.includes('data:')) {
                    try {
                      console.log('👁️ HISTÓRICO - Abrindo arquivo local via dataUrl');
                      const parts = dataUrlToUse.split(',');
                      if (parts.length !== 2) {
                        alert('Formato de arquivo corrompido');
                        return;
                      }

                      const base64Data = parts[1];
                      const mimeMatch = parts[0].match(/data:([^;]+)/);
                      const mimeString = mimeMatch ? mimeMatch[1] : (fileType || 'application/octet-stream');
                      
                      const byteString = atob(base64Data);
                      const ab = new ArrayBuffer(byteString.length);
                      const ia = new Uint8Array(ab);
                      for (let i = 0; i < byteString.length; i++) {
                        ia[i] = byteString.charCodeAt(i);
                      }
                      
                      const blob = new Blob([ab], { type: mimeString });
                      const blobUrl = URL.createObjectURL(blob);
                      
                      // Abrir em nova aba se for PDF ou imagem
                      if (mimeString.includes('pdf') || mimeString.startsWith('image/')) {
                        window.open(blobUrl, '_blank');
                      } else {
                        // Download para outros tipos
                        const a = document.createElement('a');
                        a.href = blobUrl;
                        a.download = nomeArquivo;
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                        URL.revokeObjectURL(blobUrl);
                      }
                    } catch (error) {
                      console.error('❌ Erro ao abrir arquivo:', error);
                      alert('Erro ao abrir arquivo');
                    }
                  } else {
                    alert('Arquivo não disponível para visualização');
                  }
                };
                
                return (
                  <div 
                    key={index} 
                    className="flex items-center justify-between px-2 py-1 bg-gray-50 border border-gray-200 rounded text-xs text-gray-700 hover:bg-gray-100 transition-colors cursor-pointer group"
                    onClick={handleOpenFile}
                  >
                    <span className="flex items-center gap-1">
                      📎 {nomeArquivo}
                    </span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenFile();
                      }}
                      className="opacity-0 group-hover:opacity-100 px-1.5 py-0.5 bg-blue-500 text-white text-[10px] rounded hover:bg-blue-600 transition-all"
                      title="Abrir arquivo"
                    >
                      Abrir
                    </button>
                  </div>
                );
              })
            ) : (
              <div className="px-2 py-1 bg-gray-50 border border-gray-200 rounded text-xs text-gray-500">
                Nenhum arquivo
              </div>
            )}
          </div>
        );
      
      default:
        return (
          <div className="px-2 py-1 bg-gray-50 border border-gray-200 rounded text-xs text-gray-700">
            <TextoComHtml valor={valorExibir} />
          </div>
        );
    }
  };
  void renderCampoComValor; // obsoleta (substituída por renderCampo); mantida para referência

  // 🤖 IA contextual do card - enviar mensagem
  const iaEnviar = useCallback(async (texto?: string) => {
    const msg = texto || iaInput.trim();
    if (!msg || iaLoading) return;
    
    const userMsg = { id: `u-${Date.now()}`, role: 'user' as const, content: msg };
    const loadMsg = { id: `l-${Date.now()}`, role: 'assistant' as const, content: '', loading: true };
    setIaMensagens(prev => [...prev, userMsg, loadMsg]);
    setIaInput('');
    setIaLoading(true);
    
    try {
      // Construir contexto completo do card com todos os campos (histórico + atuais)
      const camposLegíveis: Record<string, any> = {};
      
      // Campos atuais
      etapa.campos.forEach((campo: CampoConfig) => {
        const val = valores[campo.id];
        if (val !== undefined && val !== null && val !== '') {
          camposLegíveis[campo.nome] = { valor: val, tipo: campo.tipo || 'texto' };
        }
      });
      
      // Campos do histórico de etapas (snapshots de campos + datas de movimentação)
      // Usar todasEtapas (todas do fluxo) + etapasAnteriores como fallback para resolver nomes
      const allEtapasForResolve = [...(todasEtapas || []), ...etapasAnteriores];
      const historicoResumo: any[] = [];
      (card.etapaHistory || []).forEach((h: any) => {
        const etapaHist = allEtapasForResolve.find(e => String(e.id) === String(h.etapaId));
        const camposEtapa: Record<string, any> = {};
        if (etapaHist && h.camposSnapshot) {
          etapaHist.campos.forEach((campo: CampoConfig) => {
            const val = h.camposSnapshot[campo.id];
            if (val !== undefined && val !== null && val !== '') {
              camposEtapa[campo.nome] = val;
            }
          });
        }
        historicoResumo.push({
          etapa: etapaHist?.nome || `Etapa ${String(h.etapaId).slice(0, 8)}`,
          etapaId: h.etapaId,
          movedAt: h.movedAt || null,
          leftAt: h.leftAt || null,
          campos: Object.keys(camposEtapa).length > 0 ? camposEtapa : undefined,
        });
      });
      
      // Listar anexos locais existentes
      const anexosInfo: string[] = [];
      Object.entries(valores).forEach(([campoId, val]) => {
        if (Array.isArray(val) && val.length > 0 && typeof val[0] === 'object' && val[0].name) {
          const campoConfig = etapa.campos.find(c => c.id === campoId);
          val.forEach((a: any) => {
            anexosInfo.push(`${campoConfig?.nome || 'Anexo'}: ${a.name} (${a.type || 'arquivo'})`);
          });
        }
      });
      
      const cardContext = [{
        titulo: card.titulo,
        id: card.id,
        etapa: etapa.nome,
        etapaId: (card as any).etapaId || '',
        campos: camposLegíveis,
        responsavel: colaboradores?.find(c => c.id === card.responsavelIndividualId)?.nome || 'Não atribuído',
        criadoEm: card.createdAt,
        atualizadoEm: card.updatedAt,
        historicoEtapas: historicoResumo,
        etapaHistory: (card.etapaHistory || []).map((h: any) => ({
          etapaId: h.etapaId,
          movedAt: h.movedAt || null,
          leftAt: h.leftAt || null,
        })),
        anexos: anexosInfo,
        ehCardPai: card.ehCardPai || false,
        cardsFilhosIds: card.cardsFilhosIds || [],
        cardPaiId: card.cardPaiId || null,
      }];
      
      const etapaContext = [{ id: (etapa as any).id || '', nome: etapa.nome, totalCards: 1 }];
      
      const response = await fetch(apiUrl('/ai/chat'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: `[CONTEXTO: Estou analisando o card "${card.titulo}" na etapa "${etapa.nome}". Responda sobre ESTE card específico.]\n\n${msg}`,
          flowName: flowName || 'Fluxo',
          flowId: flowId || '',
          cardTitle: card.titulo || '',
          cards: cardContext,
          etapas: etapaContext,
          colaboradores: (colaboradores || []).map((c: any) => ({ id: c.id, nome: c.nome, cargo: c.cargo })),
          empresaId: empresaId || '',
          history: iaMensagens.filter(m => !m.loading).slice(-6).map(m => ({ role: m.role, content: m.content })),
        })
      });
      
      const data = await response.json();
      setIaMensagens(prev => prev.map(m =>
        m.loading ? { ...m, content: data.response || 'Sem resposta.', loading: false } : m
      ));
    } catch (err) {
      setIaMensagens(prev => prev.map(m =>
        m.loading ? { ...m, content: '❌ Erro de conexão.', loading: false } : m
      ));
    } finally {
      setIaLoading(false);
      setTimeout(() => iaInputRef.current?.focus(), 100);
    }
  }, [iaInput, iaLoading, card, etapa, etapasAnteriores, todasEtapas, valores, colaboradores, flowName, flowId, empresaId, iaMensagens]);

  // Auto-scroll IA
  useEffect(() => {
    iaEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [iaMensagens]);

  // Focus input IA ao abrir
  useEffect(() => {
    if (iaAberta) setTimeout(() => iaInputRef.current?.focus(), 200);
  }, [iaAberta]);

  // Extrair CSV de tabela markdown
  const iaExtractCsv = useCallback((text: string): string | null => {
    const lines = text.split('\n');
    const tableLines = lines.filter(l => l.trim().startsWith('|') && l.trim().endsWith('|'));
    if (tableLines.length < 2) return null;
    const dataLines = tableLines.filter(l => !/^\|[\s\-:]+\|$/.test(l.trim()));
    if (dataLines.length < 2) return null;
    const csvRows = dataLines.map(line => {
      const cells = line.split('|').filter((_, i, arr) => i > 0 && i < arr.length - 1).map(c => c.trim());
      return cells.map(c => `"${c.replace(/"/g, '""')}"`).join(',');
    });
    return '\uFEFF' + csvRows.join('\n');
  }, []);

  const iaDownloadCsv = useCallback((csvContent: string, filename: string = 'dados_ia.csv') => {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, []);

  // Renderizar markdown simples
  const iaRenderMd = useCallback((text: string) => {
    // Strip chart blocks before rendering
    let result = stripChartBlocks(text);
    // Handle code blocks
    result = result.replace(/```[\s\S]*?```/g, (match) => {
      const code = match.replace(/```\w*\n?/g, '').replace(/```/g, '').trim();
      return `<pre class="bg-gray-100 rounded-lg p-2 text-[10px] font-mono overflow-x-auto my-1 whitespace-pre-wrap">${code.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</pre>`;
    });
    // Formatacao inline (negrito, italico, codigo) + escape do que nao e tag permitida
    const inline = (t: string) => t
      .replace(/&(?!amp;|lt;|gt;|quot;|#)/g, '&amp;')
      .replace(/<(?!\/?(?:strong|em|code|pre|div|table|thead|tbody|tr|th|td|br|span|svg|path|button)\b)/g, '&lt;')
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/(^|[^*])\*([^*\n]+)\*(?!\*)/g, '$1<em>$2</em>')
      .replace(/`(.*?)`/g, '<code class="bg-gray-100 text-violet-600 px-1 py-0.5 rounded text-[10px]">$1</code>');

    // Handle markdown tables - with expand button
    const lines = result.split('\n');
    let inTable = false;
    let tableId = 0;
    const processed: string[] = [];
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      if (line.startsWith('|') && line.endsWith('|')) {
        if (/^\|[\s\-:]+(\|[\s\-:]+)*\|$/.test(line)) continue;
        const cells = line.split('|').filter((_, ci, arr) => ci > 0 && ci < arr.length - 1).map(c => c.trim());
        if (!inTable) {
          inTable = true;
          tableId++;
          processed.push(`<div data-table-group class="relative group/tbl my-1"><button data-expand-table="${tableId}" class="absolute -top-1 -right-1 z-10 opacity-0 group-hover/tbl:opacity-100 transition-opacity bg-violet-600 hover:bg-violet-700 text-white rounded-lg p-0.5 shadow-md" title="Ampliar tabela"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"/></svg></button><div class="overflow-x-auto"><table class="w-full text-[10px] border-collapse">`);
          processed.push('<thead><tr>' + cells.map(c => `<th class="border border-gray-200 bg-violet-50 px-1.5 py-1 text-left font-semibold text-violet-700">${inline(c)}</th>`).join('') + '</tr></thead><tbody>');
        } else {
          processed.push('<tr>' + cells.map(c => `<td class="border border-gray-200 px-1.5 py-0.5 text-gray-600">${inline(c)}</td>`).join('') + '</tr>');
        }
      } else {
        if (inTable) {
          processed.push('</tbody></table></div></div>');
          inTable = false;
        }
        processed.push(line);
      }
    }
    if (inTable) processed.push('</tbody></table></div></div>');
    result = processed.join('\n');

    // Monta blocos de verdade (<p>, <ul>, titulos) em vez de depender de <br/>,
    // que colapsava e deixava paragrafo, titulo e lista grudados numa linha so.
    const out: string[] = [];
    let para: string[] = [];
    let bullets: string[] = [];
    const flushPara = () => {
      if (para.length) { out.push(`<p class="my-1.5">${inline(para.join(' '))}</p>`); para = []; }
    };
    const flushList = () => {
      if (bullets.length) {
        out.push(`<ul class="my-1.5 pl-4 list-disc space-y-1">${bullets.map(b => `<li>${inline(b)}</li>`).join('')}</ul>`);
        bullets = [];
      }
    };
    for (const raw of result.split('\n')) {
      const line = raw.trim();
      if (!line) { flushPara(); flushList(); continue; }
      // HTML ja montado (tabelas, blocos de codigo) passa direto
      if (/^<(div|table|thead|tbody|tr|th|td|pre|\/)/.test(line)) { flushPara(); flushList(); out.push(line); continue; }
      if (/^([-*_])\1{2,}$/.test(line)) { flushPara(); flushList(); out.push('<hr class="my-2 border-gray-200"/>'); continue; }
      const tit = line.match(/^(#{1,6})\s+(.*)$/);
      if (tit) {
        flushPara(); flushList();
        out.push(`<p class="mt-2.5 mb-1 font-semibold text-violet-700">${inline(tit[2])}</p>`);
        continue;
      }
      const cit = line.match(/^>\s?(.*)$/);
      if (cit) {
        flushPara(); flushList();
        out.push(`<p class="my-1.5 pl-2.5 border-l-2 border-violet-200 text-gray-600">${inline(cit[1])}</p>`);
        continue;
      }
      const item = line.match(/^(?:[-*\u2022]|\d+[.)])\s+(.*)$/);
      if (item) { flushPara(); bullets.push(item[1]); continue; }
      flushList();
      para.push(line);
    }
    flushPara();
    flushList();
    return out.join('');
  }, []);

  const renderCampo = (campo: CampoConfig, disabled: boolean = false) => {
    // Campo marcado "Só administradores editam": os demais só veem o valor
    if (campo.somenteAdmin && !userPermissions?.canAdmin) disabled = true;
    // Campo preenchido pelo sistema (ex.: Solicitante, dados de pagamento vindos da triagem)
    if ((campo as any).somenteLeitura || (campo as any).autoSolicitante) disabled = true;
    if ((campo.tipo as string) === 'nd_vinculada') {
      // 🧾 ND vinculada (30/10/2026): mesma caixa na edição e na leitura
      const cfgNd = ((campo as any).ndConfig || {}) as { verbasFlowId?: string; valorUsadoCampoId?: string; valorCardCampoId?: string };
      return (
        <CampoNdVinculada
          valor={valores[campo.id]}
          onChange={(novo) => updateFieldValue(campo.id, novo)}
          podeEditar={modo === 'editar' && !disabled && canEditCard}
          config={cfgNd}
          compraFlowId={flowId}
          compraCardId={card.id}
          valorUsado={cfgNd.valorUsadoCampoId ? valores[cfgNd.valorUsadoCampoId] : undefined}
          valorCard={cfgNd.valorCardCampoId ? valores[cfgNd.valorCardCampoId] : undefined}
          onValorUsado={cfgNd.valorUsadoCampoId ? (v) => updateFieldValue(cfgNd.valorUsadoCampoId as string, v) : undefined} />
      );
    }
    console.log('🎯 Renderizando campo:', campo.tipo, campo.nome, campo.id, 'disabled:', disabled, 'modo atual:', modo, 'modoInicial era:', modoInicial);
    
    try {
      if (modo === 'editar' && !disabled) {
        console.log('✅ Entrando no branch de EDIÇÃO');
      } else {
        console.log('✅ NÃO entrou no branch de edição - modo:', modo, 'disabled:', disabled);
      }
      
      if (modo === 'editar' && !disabled) {
        switch (campo.tipo) {
        case 'texto':
        case 'texto_longo':
          console.log(`🔍 Renderizando campo texto ${campo.nome} (ID: ${campo.id}):`, valores[campo.id]);
          if (campo.tipo === 'texto' && (campo as any).buscaCidade) {
            return <CidadeSearch value={valores[campo.id] || ''} onChange={(v) => updateFieldValue(campo.id, v)} placeholder="🔍 Buscar cidade..." />;
          }
          if (campo.tipo === 'texto' && ehCampoNumeroNf(campo.nome)) {
            return <CampoNumerosNf valor={valores[campo.id] || ''} onChange={(v) => updateFieldValue(campo.id, v)} />;
          }
          return (
            <>
              <textarea
                value={valores[campo.id] || ''}
                onChange={(e) => updateFieldValue(campo.id, e.target.value)}
                className="w-full px-2.5 py-1.5 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-black focus:border-transparent transition-all resize-none overflow-hidden"
                rows={campo.tipo === 'texto_longo' ? 5 : 2}
                style={{ minHeight: campo.tipo === 'texto_longo' ? '110px' : '50px' }}
                onInput={(e) => {
                  const target = e.target as HTMLTextAreaElement;
                  target.style.height = 'auto';
                  target.style.height = target.scrollHeight + 'px';
                }}
              />
              {/* mesmo aviso do formulário de criação: fornecedor inédito e as
                  grafias parecidas, enquanto a pessoa digita */}
              {(campo as any).validarFornecedorNovo && (
                <AvisoFornecedorNovo
                  nome={valores[campo.id] || ''}
                  ignorarCardId={String(card.id)}
                  onUsarNome={(nome) => updateFieldValue(campo.id, nome)}
                />
              )}
            </>
          );

        case 'valor':
          return (
            <div className="relative">
              <span className="absolute left-2 top-1.5 text-gray-500 text-xs">R$</span>
              <input
                type="number"
                value={valores[campo.id] || ''}
                onChange={(e) => updateFieldValue(campo.id, e.target.value)}
                className="w-full pl-7 pr-2.5 py-1.5 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-black focus:border-transparent transition-all"
                placeholder="0,00"
                step="0.01"
              />
            </div>
          );

        case 'numero':
          return (
            <input
              type="text"
              inputMode="numeric"
              value={valores[campo.id] || ''}
              onChange={(e) => updateFieldValue(campo.id, e.target.value.replace(/\D/g, ''))}
              className="w-full px-2.5 py-1.5 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-black focus:border-transparent transition-all"
              placeholder="Apenas números"
            />
          );

        case 'data':
          return (
            <input
              type="datetime-local"
              value={valores[campo.id] || ''}
              onChange={(e) => updateFieldValue(campo.id, e.target.value)}
              className="w-full px-2.5 py-1.5 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-black focus:border-transparent transition-all"
            />
          );

        case 'data_vencimento':
          const formatDateTimeLocalValue = (value: string) => {
            if (!value) return '';
            try {
              // Se já estiver no formato correto (YYYY-MM-DDTHH:mm), retornar como está
              if (value.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/)) {
                return value;
              }
              // Tentar converter outros formatos para datetime-local
              const date = new Date(value);
              if (isNaN(date.getTime())) return '';
              
              // Converter para formato YYYY-MM-DDTHH:mm
              const year = date.getFullYear();
              const month = String(date.getMonth() + 1).padStart(2, '0');
              const day = String(date.getDate()).padStart(2, '0');
              const hours = String(date.getHours()).padStart(2, '0');
              const minutes = String(date.getMinutes()).padStart(2, '0');
              
              return `${year}-${month}-${day}T${hours}:${minutes}`;
            } catch (error) {
              console.error('Erro ao formatar data:', error);
              return '';
            }
          };

          // Verificar se o navegador suporta datetime-local
          const supportsDateTimeLocal = () => {
            const input = document.createElement('input');
            input.type = 'datetime-local';
            return input.type === 'datetime-local';
          };

          if (supportsDateTimeLocal()) {
            return (
              <div className="space-y-1">
                <div className="flex items-center gap-1 text-red-600 text-[10px] font-medium">
                  📅 Data de Vencimento
                </div>
                <input
                  type="datetime-local"
                  value={formatDateTimeLocalValue(valores[campo.id] || '')}
                  onChange={(e) => {
                    console.log('Data selecionada:', e.target.value);
                    updateFieldValue(campo.id, e.target.value);
                  }}
                  className="w-full px-2.5 py-1.5 text-xs border border-red-300 rounded focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all bg-red-50 cursor-pointer"
                  placeholder="Selecione a data de vencimento"
                  style={{ 
                    minHeight: '32px',
                    WebkitAppearance: 'none',
                    MozAppearance: 'textfield'
                  }}
                  onFocus={(e) => {
                    console.log('Campo de data focado');
                    const target = e.target as HTMLInputElement;
                    target.showPicker && target.showPicker();
                  }}
                  onClick={(e) => {
                    console.log('Campo de data clicado');
                    const target = e.target as HTMLInputElement;
                    target.showPicker && target.showPicker();
                  }}
                />
              </div>
            );
          } else {
            // Fallback para navegadores que não suportam datetime-local
            const currentValue = valores[campo.id] || '';
            const currentDate = currentValue ? new Date(currentValue) : new Date();
            
            const dateValue = currentValue ? currentDate.toISOString().split('T')[0] : '';
            const timeValue = currentValue ? currentDate.toTimeString().slice(0, 5) : '';
            
            return (
              <div className="space-y-1">
                <div className="text-red-600 text-[10px] font-medium flex items-center gap-1">
                  📅 Data de Vencimento
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-[10px] text-red-600 font-medium mb-0.5">Data</label>
                    <input
                      type="date"
                      value={dateValue}
                      onChange={(e) => {
                        const date = e.target.value;
                        const time = timeValue || '12:00';
                        const newDateTime = `${date}T${time}`;
                        updateFieldValue(campo.id, newDateTime);
                      }}
                      className="w-full px-2 py-1 text-xs border border-red-300 rounded focus:ring-2 focus:ring-red-500 bg-red-50"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] text-red-600 font-medium mb-0.5">Hora</label>
                    <input
                      type="time"
                      value={timeValue}
                      onChange={(e) => {
                        const time = e.target.value;
                        const date = dateValue || new Date().toISOString().split('T')[0];
                        const newDateTime = `${date}T${time}`;
                        updateFieldValue(campo.id, newDateTime);
                      }}
                      className="w-full px-2 py-1 text-xs border border-red-300 rounded focus:ring-2 focus:ring-red-500 bg-red-50"
                    />
                  </div>
                </div>
              </div>
            );
          }

        case 'lista':
          return (
            <select
              value={valores[campo.id] || ''}
              onChange={(e) => updateFieldValue(campo.id, e.target.value)}
              className="w-full px-2.5 py-1.5 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-black focus:border-transparent transition-all"
            >
              <option value="">Selecione...</option>
              {getOpcoesVisiveis(campo.id, campo.opcoes).filter((opcao) => {
                // ✈️ Viagens: cotação MSX excluída não aparece para aprovar
                const ctrl = (campo as any).ocultarOpcoesMarcadasEm;
                const marcadas = ctrl && Array.isArray(valores[ctrl]) ? valores[ctrl] : [];
                if (marcadas.includes(opcao) && valores[campo.id] !== opcao) return false;
                // opcaoExigeCampo: { <opção>: <campo> } — só oferece a cotação que foi preenchida
                const exige = (campo as any).opcaoExigeCampo?.[opcao];
                if (exige && valores[campo.id] !== opcao) {
                  const v = valores[exige];
                  if (v === undefined || v === null || String(v).trim() === '' || (Array.isArray(v) && v.length === 0)) return false;
                }
                return true;
              }).map((opcao) => (
                <option key={opcao} value={opcao}>{opcao}</option>
              ))}
            </select>
          );

        case 'checkbox_single':
          console.log('🔍 RENDER: Renderizando checkbox_single para campo', campo.nome, 'valor:', valores[campo.id]);
          return (
            <div className="space-y-1 p-2 bg-gray-50 rounded">
              {getOpcoesVisiveis(campo.id, campo.opcoes).map((opcao) => (
                <label key={opcao} className="flex items-center gap-1.5 hover:bg-gray-100 px-1.5 py-1 rounded transition-colors cursor-pointer text-xs">
                  <input
                    type="radio"
                    name={campo.id}
                    checked={valores[campo.id]?.includes(opcao) || false}
                    onChange={() => {
                      updateFieldValue(campo.id, [opcao]);
                    }}
                    className="h-3 w-3 border-gray-300 text-black focus:ring-black transition-colors"
                  />
                  <span>{opcao}</span>
                </label>
              ))}
            </div>
          );

        case 'checkbox_multi':
          console.log('🔍 RENDER: Renderizando checkbox_multi para campo', campo.nome, 'valor:', valores[campo.id]);
          return (
            <div className="space-y-1 p-2 bg-gray-50 rounded">
              {getOpcoesVisiveis(campo.id, campo.opcoes).map((opcao) => (
                <label key={opcao} className="flex items-center gap-1.5 hover:bg-gray-100 px-1.5 py-1 rounded transition-colors cursor-pointer text-xs">
                  <input
                    type="checkbox"
                    checked={valores[campo.id]?.includes(opcao) || false}
                    onChange={(e) => {
                      const currentValues = valores[campo.id] || [];
                      const newValues = e.target.checked
                        ? [...currentValues, opcao]
                        : currentValues.filter((v: string) => v !== opcao);
                      updateFieldValue(campo.id, newValues);
                    }}
                    className="h-3 w-3 rounded border-gray-300 text-black focus:ring-black transition-colors"
                  />
                  <span>{opcao}</span>
                </label>
              ))}
            </div>
          );

        case 'parcelas':
          return (
            <CampoParcelas
              valor={valores[campo.id]}
              opcoesPadrao={campo.opcoes || []}
              onChange={(novo) => updateFieldValue(campo.id, novo)}
              podeEditarLista={podeEditarParcelas}
              podeMarcarPaga={canEditCard}
              usuario={usuarioParcelas} />
          );

        case 'itens':
          return (
            <CampoItens
              valor={valores[campo.id]}
              onChange={(novo) => updateFieldValue(campo.id, novo)}
              podeEditarLista={canEditCard}
              podeAprovar={false}
              usuario={usuarioParcelas}
              contextoUpload={{ flowId, cardId: card.id, campoId: campo.id, flowNome: flowName, cardTitulo: card.titulo || card.id }} />
          );

        case 'cotacoes':
          // 📊 mapa de cotação: o comprador cota os itens da Triagem
          return (
            <CampoCotacoes
              valor={valores[campo.id]}
              onChange={(novo) => updateFieldValue(campo.id, novo)}
              itens={itensDaSolicitacao().valor}
              modo={canEditCard ? 'editar' : 'ver'}
              usuario={usuarioParcelas}
              contextoUpload={{ flowId, cardId: card.id, campoId: campo.id, flowNome: flowName, cardTitulo: card.titulo || card.id }} />
          );

        case 'responsavel':
          // Opções: todo mundo do fluxo. Quem pode escolher: só quem já pode
          // editar o card (canEditCard já exige ser participante desta etapa).
          // Ao escolher, atribui de verdade — não é só um valor de campo,
          // atualiza o responsável do card.
          return (
            <select
              value={responsavelIndividual || ''}
              onChange={(e) => {
                const novo = e.target.value || undefined;
                setResponsavelIndividual(novo);
                updateFieldValue(campo.id, novo || '');
              }}
              disabled={!canEditCard}
              className="w-full px-2.5 py-1.5 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-black focus:border-transparent transition-all disabled:bg-gray-100 disabled:text-gray-500"
            >
              <option value="">Selecione...</option>
              {colaboradoresDoFluxo.map((c: any) => (
                <option key={c.id} value={c.id}>{c.nome}{c.cargo ? ` - ${c.cargo}` : ''}</option>
              ))}
            </select>
          );

        case 'checkbox':
        case 'progresso':
          // Progresso SEM tarefas → barra numérica 0-100% com sugestão por texto
          if (campo.tipo === 'progresso' && !progressoTemTarefas(campo)) {
            const atual = getProgressoPercent(campo);
            const sugestao = getSugestaoProgresso(campo);
            const mostrarSugestao = sugestao !== null && sugestao !== atual;
            return (
              <div className="space-y-2 p-2.5 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <input
                    type="range"
                    min={0}
                    max={100}
                    step={5}
                    value={atual}
                    onChange={(e) => updateFieldValue(campo.id, parseInt(e.target.value, 10))}
                    className="flex-1 accent-black h-1.5 cursor-pointer"
                  />
                  <span className="text-xs font-bold text-gray-800 w-10 text-right tabular-nums">{atual}%</span>
                </div>
                <div className="h-2 w-full rounded-full bg-gray-200 overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-300 ${atual >= 100 ? 'bg-green-500' : atual >= 50 ? 'bg-blue-500' : 'bg-amber-500'}`}
                    style={{ width: `${atual}%` }}
                  />
                </div>
                {mostrarSugestao && (
                  <div className="flex items-center justify-between gap-2 px-2 py-1.5 rounded-md bg-violet-50 border border-violet-200">
                    <span className="text-[11px] text-violet-700 font-medium">
                      💡 Sugestão pelo texto: <strong>{sugestao}%</strong>
                    </span>
                    <button
                      type="button"
                      onClick={() => updateFieldValue(campo.id, sugestao)}
                      className="text-[11px] font-semibold text-white bg-violet-600 hover:bg-violet-700 px-2 py-0.5 rounded transition-colors"
                    >
                      Aplicar
                    </button>
                  </div>
                )}
              </div>
            );
          }
          {
          const todasOpcoesCb = getOpcoesVisiveis(campo.id, campo.opcoes);
          const limiteCb = Number((campo as any).mostrarPrimeiras || 0);
          const marcadasCb: string[] = Array.isArray(valores[campo.id]) ? valores[campo.id] : [];
          const recolhidoCb = limiteCb > 0 && !opcoesExpandidas[campo.id] && todasOpcoesCb.length > limiteCb;
          const opcoesCb = recolhidoCb
            ? todasOpcoesCb.filter((o, i) => i < limiteCb || marcadasCb.includes(o))
            : todasOpcoesCb;
          return (
            <div className="space-y-1 p-2 bg-gray-50 rounded">
              {opcoesCb.map((opcao) => (
                <label key={opcao} className="flex items-center gap-1.5 hover:bg-gray-100 px-1.5 py-1 rounded transition-colors cursor-pointer text-xs">
                  <input
                    type="checkbox"
                    checked={valores[campo.id]?.includes(opcao) || false}
                    onChange={(e) => {
                      const currentValues = valores[campo.id] || [];
                      const newValues = e.target.checked
                        ? [...currentValues, opcao]
                        : currentValues.filter((v: string) => v !== opcao);
                      updateFieldValue(campo.id, newValues);
                    }}
                    className="h-3 w-3 rounded border-gray-300 text-black focus:ring-black transition-colors"
                  />
                  <span>{opcao}</span>
                </label>
              ))}
              {limiteCb > 0 && todasOpcoesCb.length > limiteCb && (
                <button type="button" onClick={() => setOpcoesExpandidas(prev => ({ ...prev, [campo.id]: !prev[campo.id] }))}
                  className="text-[11px] font-semibold text-blue-600 hover:text-blue-800 px-1.5 py-0.5">
                  {recolhidoCb ? `Exibir mais (${todasOpcoesCb.length - opcoesCb.length})` : 'Exibir menos'}
                </button>
              )}
              {campo.tipo === 'progresso' && (
                <div className="text-[10px] text-gray-500 mt-1 pl-1">
                  Progresso: {getOpcoesVisiveis(campo.id, campo.opcoes).length > 0 ? 
                    Math.round((valores[campo.id]?.filter((v: string) => getOpcoesVisiveis(campo.id, campo.opcoes).includes(v)).length || 0) / getOpcoesVisiveis(campo.id, campo.opcoes).length * 100) 
                    : 0}%
                </div>
              )}
            </div>
          );
          }

        case 'justificativa_checkbox':
          // Buscar o campo checkbox vinculado
          console.log('🔍 DEBUG justificativa_checkbox:', { campo, campoVinculadoId: campo.campoVinculadoId, etapaCampos: etapa.campos });
          const campoVinculado = campo.campoVinculadoId ? etapa.campos?.find(c => c.id === campo.campoVinculadoId) : null;
          const itensSelecionados = (campoVinculado && valores[campoVinculado.id] && Array.isArray(valores[campoVinculado.id])) ? valores[campoVinculado.id] : [];
          
          // Garantir que apenas itens selecionados tenham justificativas
          const justificativasAtuais = (valores[campo.id] && typeof valores[campo.id] === 'object') ? valores[campo.id] : {};
          const justificativasFiltradas: Record<string, string> = Object.fromEntries(
            Object.entries(justificativasAtuais).filter(([item]) => itensSelecionados.includes(item))
          ) as Record<string, string>;
          
          // Se há diferença, atualizar silenciosamente
          if (Object.keys(justificativasAtuais).length !== Object.keys(justificativasFiltradas).length) {
            setTimeout(() => updateFieldValue(campo.id, justificativasFiltradas), 0);
          }
          
          return (
            <div className="space-y-2">
              {!campoVinculado ? (
                <div className="text-[10px] text-red-600 bg-red-50 p-2 rounded">
                  ⚠️ Campo checkbox vinculado não encontrado
                </div>
              ) : itensSelecionados.length === 0 ? (
                <div className="text-[10px] text-gray-500 bg-gray-50 p-2 rounded">
                  Nenhum item selecionado em "{campoVinculado.nome}"
                </div>
              ) : (
                <div className="space-y-1.5">
                  <div className="text-[10px] text-gray-600 mb-1">
                    Justifique cada item selecionado em "{campoVinculado.nome}":
                  </div>
                  {itensSelecionados.map((item: string) => (
                    <div key={item} className="space-y-0.5">
                      <label className="block text-xs font-medium text-gray-700">
                        📌 {item}
                      </label>
                      <textarea
                        value={justificativasFiltradas[item] || ''}
                        onChange={(e) => {
                          const newValue = {
                            ...justificativasFiltradas,
                            [item]: e.target.value
                          };
                          updateFieldValue(campo.id, newValue);
                        }}
                        className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-black focus:border-transparent transition-all resize-none"
                        rows={2}
                        placeholder={`Descreva o motivo para "${item}"...`}
                        disabled={disabled}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          );

        case 'anexo':
          // 📏 Contar anexos atuais para validação
          const anexosAtuaisCount = Array.isArray(valores[campo.id]) ? valores[campo.id].length : 0;
          const MAX_ANEXOS = Number((campo as any).maxAnexos || 0) || 6;
          const MIN_ANEXOS = Number((campo as any).minAnexos || 0);
          const MAX_FILE_SIZE_MB = 20;
          const MAX_FILE_SIZE = MAX_FILE_SIZE_MB * 1024 * 1024; // 20MB em bytes
          
          return (
            <div className="space-y-2">
              {/* Mostrar integração configurada (opcional) */}
              {campo.integracaoId && (
                <div className="text-[10px] text-gray-600 bg-blue-50 p-1.5 rounded" title="Integração configurada - arquivos serão enviados automaticamente">
                  📁 Integração configurada para envio automático
                </div>
              )}
              
              {/* Indicador de limite */}
              <div className={`text-[10px] px-2 py-1 rounded ${anexosAtuaisCount >= MAX_ANEXOS ? 'bg-red-50 text-red-600' : (MIN_ANEXOS > 0 && anexosAtuaisCount < MIN_ANEXOS) ? 'bg-amber-50 text-amber-700' : 'bg-gray-50 text-gray-500'}`}>
                📎 {anexosAtuaisCount}/{MAX_ANEXOS} anexos{MIN_ANEXOS > 0 ? ` • mínimo ${MIN_ANEXOS}` : ''} • Máx. {MAX_FILE_SIZE_MB}MB por arquivo
              </div>

              {/* 🔒 Indicador de upload em andamento */}
              {uploadingCampoId && uploadingCampoId !== campo.id && (
                <div className="text-[10px] px-2 py-1 rounded bg-amber-50 text-amber-700 border border-amber-200">
                  ⏳ Aguarde o upload em andamento terminar antes de adicionar outro anexo...
                </div>
              )}
              {uploadingCampoId === campo.id && (
                <div className="text-[10px] px-2 py-1 rounded bg-blue-50 text-blue-700 border border-blue-200 animate-pulse">
                  📤 Enviando arquivo(s)... não feche esta tela.
                </div>
              )}

              {/* Input de arquivo */}
              <input
                ref={(el) => fileInputRefs.current[campo.id] = el}
                type="file"
                multiple
                accept="image/*,video/*,application/pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.csv,.txt,.zip,.rar"
                disabled={disabled || anexosAtuaisCount >= MAX_ANEXOS || (uploadingCampoId !== null && uploadingCampoId !== campo.id)}
                onChange={async (e) => {
                  let files = Array.from(e.target.files || []);
                  if (files.length === 0) return;

                  // 🔒 Lock global: bloqueia novos uploads em outros campos
                  if (uploadingCampoId && uploadingCampoId !== campo.id) {
                    alert('⏳ Aguarde o upload em andamento terminar antes de adicionar outro anexo.');
                    e.target.value = '';
                    return;
                  }
                  setUploadingCampoId(campo.id);
                  try {
                  // 🖼️ Comprimir imagens (se forem fotos grandes de celular)
                  files = await compressImagesIfNeeded(files);
                  // 📦 LIMITE: Verificar quantidade máxima
                  const espacoDisponivel = MAX_ANEXOS - anexosAtuaisCount;
                  if (files.length > espacoDisponivel) {
                    alert(`⚠️ Limite de anexos!\n\nVocê pode adicionar no máximo ${espacoDisponivel} arquivo(s).\nVocê selecionou ${files.length}.\n\nMáximo por campo: ${MAX_ANEXOS} arquivos.`);
                    e.target.value = '';
                    return;
                  }
                  
                  // 📏 LIMITE: Verificar tamanho de cada arquivo
                  const arquivosGrandes = files.filter(f => f.size > MAX_FILE_SIZE);
                  if (arquivosGrandes.length > 0) {
                    const nomes = arquivosGrandes.map(f => `• ${f.name} (${(f.size / 1024 / 1024).toFixed(2)}MB)`).join('\n');
                    alert(`⚠️ Arquivo(s) muito grande(s)!\n\nMáximo permitido: ${MAX_FILE_SIZE_MB}MB por arquivo.\n\nArquivos que excedem o limite:\n${nomes}`);
                    e.target.value = '';
                    return;
                  }
                  
                  console.log('📎 Novos arquivos selecionados:', files.length);
                  console.log('📋 Valores atuais do campo:', valores[campo.id]);
                  
                  // 🚀 UPLOAD LOCAL: Enviar arquivos para o servidor (não base64!)
                  const fileData = await Promise.all(
                    files.map(async (file) => {
                      try {
                        // Criar FormData para upload real
                        const formData = new FormData();
                        formData.append('file', file);
                        formData.append('flowId', flowId || 'geral');
                        formData.append('cardId', card.id || 'temp');
                        formData.append('campoId', campo.id);
                        // Adicionar nomes para organização em pastas legíveis
                        formData.append('flowNome', flowName || '');
                        formData.append('cardTitulo', card.titulo || card.id || '');
                        
                        console.log(`📤 Fazendo upload local de: ${file.name}`);
                        
                        // Upload para o servidor
                        const response = await fetch('/api/upload-anexo-local', {
                          method: 'POST',
                          body: formData // FormData, não JSON!
                        });
                        
                        if (!response.ok) {
                          throw new Error(`Erro no upload: ${response.status}`);
                        }
                        
                        const result = await response.json();
                        console.log(`✅ Upload concluído:`, result);
                        
                        // Retornar dados do arquivo (SEM base64!)
                        return {
                          name: result.originalName || file.name,
                          fileName: result.fileName,
                          size: result.size || file.size,
                          type: result.type || file.type,
                          url: result.url, // URL para acessar o arquivo
                          uploadedAt: result.uploadedAt,
                          integracaoId: campo.integracaoId
                          // SEM dataUrl! O arquivo está no servidor
                        };
                      } catch (error) {
                        console.error(`❌ Erro no upload de ${file.name}:`, error);
                        alert(`Erro ao fazer upload de ${file.name}: ${error}`);
                        return null;
                      }
                    })
                  );
                  
                  // Filtrar uploads que falharam
                  const sucessos = fileData.filter(f => f !== null);
                  if (sucessos.length === 0) {
                    e.target.value = '';
                    return;
                  }
                  
                  // Verificar se já existem anexos
                  const existingFiles = valores[campo.id];
                  const hasExistingFiles = Array.isArray(existingFiles) && existingFiles.length > 0;
                  
                  console.log('🔍 Verificando anexos existentes:', {
                    existingFiles,
                    hasExistingFiles,
                    length: existingFiles?.length
                  });
                  
                  if (hasExistingFiles) {
                    // Mostrar modal de confirmação
                    console.log('✅ Mostrando modal de confirmação');
                    setPendingFiles({ campoId: campo.id, files: sucessos });
                  } else {
                    // Adicionar diretamente se não houver anexos
                    console.log('➕ Adicionando arquivos diretamente (sem anexos anteriores)');
                    console.log('📋 Arquivos a adicionar:', sucessos);
                    updateFieldValue(campo.id, sucessos);

                    // 🔧 FIX iOS/Safari: persistir imediatamente no servidor após cada upload.
                    // No iOS, abrir a câmera pode suspender/recarregar a aba e perder o estado local
                    // do React. Sem auto-save, o usuário acaba só vendo o último anexo enviado.
                    // Aqui usamos os valores ATUAIS do card no servidor (cardCompleto.campos) como
                    // base, mesclamos com o que está em `valores` (que tem alterações desta sessão)
                    // e sobrescrevemos o campo deste anexo. Isso evita race conditions caso
                    // múltiplos uploads disparem em sequência.
                    try {
                      const baseCampos = {
                        ...(cardCompleto?.campos || {}),
                        ...(valores || {}),
                        [campo.id]: sucessos,
                      };
                      onSave({
                        ...card,
                        ...(cardCompleto || {}),
                        campos: baseCampos,
                        updatedAt: new Date(),
                        // Sinaliza ao handler do Kanban que este é um auto-save
                        // disparado pelo upload de anexo — não deve fechar o modal,
                        // não disparar notificação de "card atualizado" e NÃO rodar
                        // automações (mover_card só deve ocorrer no "Salvar Alterações").
                        _skipClose: true,
                        _skipAutomacoes: true,
                      } as any);
                      console.log('💾 Auto-save após upload de anexo concluído');
                    } catch (err) {
                      console.error('⚠️ Falha no auto-save após upload:', err);
                    }
                  }
                  
                  // NÃO limpar o input imediatamente - só depois de processar
                  // Isso evita que o "Nenhum arquivo escolhido" apareça antes do upload
                  setTimeout(() => {
                    if (fileInputRefs.current[campo.id]) {
                      fileInputRefs.current[campo.id]!.value = '';
                    }
                  }, 100);
                  } finally {
                    // 🔓 Libera o lock global em qualquer caminho de saída
                    setUploadingCampoId(null);
                  }
                }}
                className={`w-full px-2 py-1 text-xs border rounded focus:ring-2 focus:ring-black focus:border-transparent transition-all file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-xs file:font-medium ${
                  disabled || anexosAtuaisCount >= MAX_ANEXOS 
                    ? 'border-red-300 bg-red-50 cursor-not-allowed file:bg-gray-400 file:text-white'
                    : 'border-gray-300 file:bg-black file:text-white hover:file:bg-gray-800'
                }`}
              />

              {/* 📋 Área de colar (Ctrl+V): joga o print da área de transferência
                  dentro do <input type="file"> acima e dispara o mesmo fluxo de
                  upload/validação já existente — nada é duplicado aqui. */}
              {!(disabled || anexosAtuaisCount >= MAX_ANEXOS || (uploadingCampoId !== null && uploadingCampoId !== campo.id)) && (
                <div
                  tabIndex={0}
                  role="button"
                  title="Clique aqui e pressione Ctrl+V para colar um print"
                  onPaste={(e) => {
                    const arquivos = Array.from(e.clipboardData?.items || [])
                      .filter(item => item.kind === 'file')
                      .map(item => item.getAsFile())
                      .filter((f): f is File => !!f);
                    if (arquivos.length === 0) return;
                    e.preventDefault();

                    const input = fileInputRefs.current[campo.id];
                    if (!input || input.disabled) return;

                    // Print da área de transferência vem sem nome útil ("image.png").
                    // Renomeia com timestamp pra não colidir no storage.
                    const carimbo = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
                    const dt = new DataTransfer();
                    arquivos.forEach((f, i) => {
                      const ext = (f.name?.includes('.') ? f.name.split('.').pop() : (f.type.split('/')[1] || 'png')) || 'png';
                      const nome = f.name && f.name !== 'image.png'
                        ? f.name
                        : `print-${carimbo}${arquivos.length > 1 ? `-${i + 1}` : ''}.${ext}`;
                      dt.items.add(new File([f], nome, { type: f.type, lastModified: f.lastModified }));
                    });

                    input.files = dt.files;
                    // change nativo: o React escuta 'change' direto em input[type=file],
                    // então o onChange acima roda normalmente.
                    input.dispatchEvent(new Event('change', { bubbles: true }));
                  }}
                  className="group w-full px-2 py-2 text-[10px] text-center text-gray-500 border border-dashed border-gray-300 rounded cursor-pointer outline-none hover:border-gray-400 hover:bg-gray-50 focus:border-black focus:bg-gray-50 focus:text-gray-700 transition-colors"
                >
                  <span className="group-focus:hidden">📋 Clique aqui para colar um print (Ctrl+V)</span>
                  <span className="hidden group-focus:inline">📋 Pronto — pressione Ctrl+V para colar o print</span>
                </div>
              )}

              {/* Cada arquivo/print em uma miniatura própria; clicar abre a galeria */}
              {listaDeAnexos(valores[campo.id]).length > 0 && (
                <MiniaturasAnexos
                  arquivos={listaDeAnexos(valores[campo.id])}
                  onAbrir={(i) => abrirGaleriaDoCampo(campo, listaDeAnexos(valores[campo.id]), i)}
                  onRemover={(userPermissions?.canEdit || userPermissions?.canAdmin) ? (i) => {
                    const newFiles = listaDeAnexos(valores[campo.id]).filter((_: any, idx: number) => idx !== i);
                    // Usar updateFieldValue para persistir a mudança
                    updateFieldValue(campo.id, newFiles);
                  } : undefined}
                />
              )}
            </div>
          );

        case 'rotas':
          return (
            <RotasField
              rotasAtuais={Array.isArray(valores[campo.id]) ? valores[campo.id] : []}
              campoId={campo.id}
              updateFieldValue={updateFieldValue}
              terrestre={!!(campo as any).terrestre}
            />
          );

        case 'viajantes':
          return <ViajantesField valor={valores[campo.id]} onChange={(novo) => updateFieldValue(campo.id, novo)} />;

        case 'quadro_voos': {
          const ctrlExcl = (campo as any).excluidasCampoId;
          return (
            <QuadroVoosField
              valor={valores[campo.id]}
              onChange={(novo) => updateFieldValue(campo.id, novo)}
              opcoesCotacao={campo.opcoes || []}
              excluidas={ctrlExcl && Array.isArray(valores[ctrlExcl]) ? valores[ctrlExcl] : []}
              cardId={card?.id}
              valoresCard={valores}
            />
          );
        }

        case 'telefone':
          // Formatar telefone com máscara brasileira
          const formatarTelefone = (valor: string) => {
            // Remove tudo que não é número
            const numeros = valor.replace(/\D/g, '');
            
            // Limita a 11 dígitos (celular com DDD)
            const numerosLimitados = numeros.slice(0, 11);
            
            // Aplica a máscara
            if (numerosLimitados.length <= 2) {
              return numerosLimitados;
            } else if (numerosLimitados.length <= 6) {
              return `(${numerosLimitados.slice(0, 2)}) ${numerosLimitados.slice(2)}`;
            } else if (numerosLimitados.length <= 10) {
              return `(${numerosLimitados.slice(0, 2)}) ${numerosLimitados.slice(2, 6)}-${numerosLimitados.slice(6)}`;
            } else {
              return `(${numerosLimitados.slice(0, 2)}) ${numerosLimitados.slice(2, 7)}-${numerosLimitados.slice(7)}`;
            }
          };

          return (
            <div className="relative">
              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400">
                🇧🇷
              </span>
              <input
                type="tel"
                value={formatarTelefone(valores[campo.id] || '')}
                onChange={(e) => {
                  const valorFormatado = formatarTelefone(e.target.value);
                  updateFieldValue(campo.id, valorFormatado);
                }}
                className="w-full pl-8 pr-2.5 py-1.5 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-black focus:border-transparent transition-all"
                placeholder="(99) 99999-9999"
              />
            </div>
          );
      }
    }

    return (
      <div className="px-3 py-2 bg-white border border-gray-200 rounded-lg">
        {campo.tipo === 'progresso' ? (
          progressoTemTarefas(campo) ? (
            <div>
              <div className="space-y-1">
                {valores[campo.id]?.map((tarefa: string) => (
                  <div key={tarefa} className="text-sm flex items-center gap-2">
                    <span className="text-green-500">✓</span>
                    {tarefa}
                  </div>
                ))}
              </div>
              <div className="text-sm text-gray-500 mt-2">
                {Math.round((valores[campo.id]?.length || 0) / (campo.opcoes?.length || 1) * 100)}% concluído
              </div>
            </div>
          ) : (
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-gray-500">Progresso</span>
                <span className="text-xs font-bold text-gray-800 tabular-nums">{getProgressoPercent(campo)}%</span>
              </div>
              <div className="h-2 w-full rounded-full bg-gray-200 overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-300 ${getProgressoPercent(campo) >= 100 ? 'bg-green-500' : getProgressoPercent(campo) >= 50 ? 'bg-blue-500' : 'bg-amber-500'}`}
                  style={{ width: `${getProgressoPercent(campo)}%` }}
                />
              </div>
            </div>
          )
        ) : campo.tipo === 'valor' ? (
          `R$ ${valores[campo.id] || '0,00'}`
        ) : campo.tipo === 'responsavel' ? (
          <span title={valores[campo.id] ? String(valores[campo.id]) : undefined}>
            {nomeResponsavelCampo(valores[campo.id])}
          </span>
        ) : campo.tipo === 'telefone' ? (
          <div className="flex items-center gap-2">
            <span>🇧🇷</span>
            <span className="font-medium">{valores[campo.id] || '-'}</span>
            {valores[campo.id] && (
              <a 
                href={`https://wa.me/55${valores[campo.id].replace(/\D/g, '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-green-600 hover:text-green-800 text-xs"
                title="Abrir no WhatsApp"
              >
                📱 WhatsApp
              </a>
            )}
          </div>
        ) : (campo.tipo as string) === 'viajantes' ? (
          <ViajantesResumo valor={valores[campo.id]} />
        ) : (campo.tipo as string) === 'quadro_voos' ? (
          <QuadroVoosField valor={valores[campo.id]} disabled
            excluidas={(campo as any).excluidasCampoId && Array.isArray(valores[(campo as any).excluidasCampoId]) ? valores[(campo as any).excluidasCampoId] : []} />
        ) : campo.tipo === 'rotas' ? (
          <div className="space-y-1.5">
            {Array.isArray(valores[campo.id]) && valores[campo.id].length > 0 ? (
              valores[campo.id].filter((t: any) => t.origem && t.destino).map((t: any, i: number) => (
                <div key={i} className="flex items-center gap-2 text-xs">
                  <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold text-white ${t.tipo === 'ida' ? 'bg-blue-500' : 'bg-amber-500'}`}>{i + 1}</span>
                  <span>{t.origem}</span>
                  <span className="text-gray-400">{t.tipo === 'ida' ? '→' : '←'}</span>
                  <span>{t.destino}</span>
                  {t.data && <span className="text-gray-400 ml-auto text-[10px]">{new Date(t.data + 'T00:00:00').toLocaleDateString('pt-BR')}</span>}
                </div>
              ))
            ) : (
              <span className="text-gray-400 text-xs">Nenhuma rota definida</span>
            )}
          </div>
        ) : campo.tipo === 'data_vencimento' ? (
          <div className="flex items-center gap-2">
            <span className="text-red-500">📅</span>
            <span className={`font-medium ${
              valores[campo.id] ? (() => {
                try {
                  const date = new Date(valores[campo.id]);
                  if (isNaN(date.getTime())) return 'text-gray-500';
                  
                  const now = new Date();
                  const diferencaHoras = (date.getTime() - now.getTime()) / (1000 * 60 * 60);
                  
                  if (date < now) return 'text-red-700'; // Vencido
                  if (diferencaHoras <= 24) return 'text-yellow-700'; // Vence em breve
                  return 'text-green-700'; // Normal
                } catch (error) {
                  return 'text-gray-500';
                }
              })() : 'text-gray-500'
            }`}>
              {valores[campo.id] ? (() => {
                try {
                  const date = new Date(valores[campo.id]);
                  if (isNaN(date.getTime())) return 'Data inválida';
                  
                  // Verificar se a data já passou (vencida)
                  const now = new Date();
                  const diferencaHoras = (date.getTime() - now.getTime()) / (1000 * 60 * 60);
                  const isOverdue = date < now;
                  const isSoon = diferencaHoras <= 24 && diferencaHoras > 0;
                  
                  const dateString = date.toLocaleString('pt-BR', {
                    day: '2-digit',
                    month: '2-digit', 
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  });
                  
                  if (isOverdue) return `${dateString} (VENCIDO)`;
                  if (isSoon) return `${dateString} (VENCE EM BREVE)`;
                  return dateString;
                } catch (error) {
                  return 'Data inválida';
                }
              })() : 'Não definido'}
            </span>
          </div>
        ) : (campo.tipo === 'checkbox' || campo.tipo === 'checkbox_single' || campo.tipo === 'checkbox_multi') ? (
          <div className="space-y-1">
            {(() => {
              const val = valores[campo.id];
              const items = Array.isArray(val) ? val : (val ? [String(val)] : []);
              return items.map((item: string) => (
                <div key={item} className="text-sm flex items-center gap-2">
                  <span className="text-green-500">✓</span>
                  {item}
                </div>
              ));
            })()}
          </div>
        ) : campo.tipo === 'justificativa_checkbox' ? (
          <div className="space-y-2">
            {valores[campo.id] && typeof valores[campo.id] === 'object' && Object.keys(valores[campo.id]).length > 0 ? (
              Object.entries(valores[campo.id]).map(([item, justificativa]: [string, any]) => (
                <div key={item} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex items-start gap-2">
                    <span className="text-blue-600 font-medium text-sm">📌 {item}:</span>
                  </div>
                  <div className="mt-1 text-sm text-gray-700 pl-5">
                    {justificativa || <span className="text-gray-400 italic">Sem justificativa</span>}
                  </div>
                </div>
              ))
            ) : (
              <div className="text-sm text-gray-400 italic">Nenhuma justificativa adicionada</div>
            )}
          </div>
        ) : campo.tipo === 'anexo' ? (
          (() => {
            const arquivosCampo = listaDeAnexos(valores[campo.id]);
            return arquivosCampo.length > 0 ? (
              <MiniaturasAnexos
                arquivos={arquivosCampo}
                onAbrir={(i) => abrirGaleriaDoCampo(campo, arquivosCampo, i)}
              />
            ) : (
              <span className="text-gray-400 text-sm">Nenhum arquivo anexado</span>
            );
          })()
        ) : (
          (() => {
            console.log(`🔍 Campo texto "${campo.nome}" (id: ${campo.id}):`, {
              valorNoCampos: card.campos?.[campo.id],
              valorNoState: valores[campo.id],
              valorFinal: valores[campo.id] || '-',
              todasChavesNoCampos: Object.keys(card.campos || {}),
              todasChavesNoState: Object.keys(valores)
            });
            const valorCampo = valores[campo.id];
            if (valorCampo === undefined || valorCampo === null || valorCampo === '') return '-';
            // Passa pelo TextoComHtml para o link do chamado (e qualquer URL em
            // campo de texto) sair clicável — antes voltava a string crua aqui.
            return <TextoComHtml valor={valorCampo} />;
          })()
        )}
      </div>
    );
    } catch (error) {
      console.error('❌ Erro ao renderizar campo:', campo.tipo, error);
      return <div className="text-red-500 p-2">Erro ao renderizar campo: {campo.nome}</div>;
    }
  };

  return (
    <div className={`fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] overflow-y-auto ${cardExpandido ? 'p-0' : 'p-3 pt-16 md:pt-3'}`} onClick={fecharCard}>
      <div 
        onClick={(e) => e.stopPropagation()}
        className={
        `relative z-10 overflow-hidden bg-white border border-gray-200/80 shadow-[0_25px_60px_-15px_rgba(0,0,0,0.3)] flex flex-col transition-all duration-300 ${cardExpandido ? 'w-screen h-screen max-w-none max-h-none rounded-none my-0' : 'w-full max-w-[min(96vw,1600px)] max-h-[94vh] md:max-h-[94vh] rounded-2xl my-auto'}`
      }>
        {/* Top gradient accent bar */}
        <div
          className={`absolute top-0 left-0 right-0 h-1 ${
            statusVencimento === 'vencido'
              ? 'bg-rose-500'
              : statusVencimento === 'vencendo'
              ? 'bg-amber-400'
              : 'bg-slate-800'
          }`}
        />

        {/* Header — tudo numa faixa só: nome, etiquetas e ações lado a lado.
            Empilhado em três linhas, isto sozinho comia uns 100px de altura e
            jogava o histórico e os cards conectados para baixo da dobra. */}
        <div className={`border-b px-3 py-2 sm:px-5 sm:py-2.5 flex flex-wrap items-center gap-x-3 gap-y-1.5 ${
          statusVencimento === 'vencido'
            ? 'bg-rose-50/60 border-rose-200'
            : statusVencimento === 'vencendo'
            ? 'bg-amber-50/60 border-amber-200'
            : 'bg-white border-gray-200'
        }`}>
          {/* `contents`: os filhos entram direto na faixa acima, e a ordem de
              cada bloco é decidida pelo `order-*`. */}
          <div className="contents">
            <div className="order-1 flex items-center gap-2 flex-1 basis-0 min-w-[180px]">
            {statusVencimento && (
              <div className={`px-2 py-0.5 rounded-lg text-[10px] font-semibold inline-flex items-center gap-1 border shadow-sm shrink-0 ${
                statusVencimento === 'vencido'
                  ? 'bg-rose-600 text-white border-rose-500 shadow-rose-200'
                  : 'bg-amber-500 text-white border-amber-400 shadow-amber-200'
              }`}>
                {statusVencimento === 'vencido' ? '🚨 VENCIDO' : '⚠️ VENCE EM BREVE'}
              </div>
            )}
            {modo === 'editar' ? (
              <input
                value={titulo}
                onChange={(e) => setTitulo(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 bg-white rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all text-base font-bold text-gray-900 placeholder-gray-400"
              />
            ) : (
              <h2 className="min-w-0 text-sm sm:text-base font-bold text-gray-900 break-words line-clamp-2 tracking-tight">{titulo}</h2>
            )}
            </div>

            {/* Lado direito: Responsável + Atraso + Botões */}
            <div className="order-2 ml-auto flex items-center justify-end gap-1 sm:gap-2 shrink-0 flex-wrap">
              {/* Responsável */}
              {modo === 'editar' ? (
                <select
                  value={responsavelIndividual || ''}
                  onChange={(e) => setResponsavelIndividual(e.target.value || undefined)}
                  className="px-2 py-1 text-xs border border-gray-300 bg-white rounded-md focus:ring-2 focus:ring-emerald-500 focus:border-transparent w-auto min-w-[150px] sm:min-w-[240px] sm:max-w-[320px]"
                  title="Responsável"
                >
                  <option value="">👤 Responsável...</option>
                  {colaboradores
                    .filter(colab => etapa.participantes?.includes(colab.id))
                    .map((colab) => (
                      <option key={colab.id} value={colab.id}>
                        {colab.nome} {colab.cargo ? `- ${colab.cargo.substring(0, 25)}` : ''}
                      </option>
                    ))}
                </select>
              ) : responsavelIndividual ? (
                (() => {
                  const responsavel = colaboradores.find(c => String(c.id) === String(responsavelIndividual));
                  if (responsavel) {
                    const initials = responsavel.nome.split(' ').map((n: string) => n[0]).join('').substring(0, 2).toUpperCase();
                    return (
                      <div className="flex items-center gap-2 px-2.5 py-1 bg-slate-50 border border-slate-200 rounded-lg" title={`Responsável: ${responsavel.nome}`}>
                        <div className="w-6 h-6 rounded-full bg-slate-700 flex items-center justify-center text-white font-bold text-[9px]">
                          {initials}
                        </div>
                        <span className="text-xs text-slate-800 font-semibold">{responsavel.nome.split(' ')[0]}</span>
                      </div>
                    );
                  }
                  return null;
                })()
              ) : null}
              
              {/* Indicador de atraso/SLA */}
              {etapa.tempoMaximoHoras && (() => {
                const historicoEtapa = card.etapaHistory?.find(h => String(h.etapaId) === String(etapa.id));
                if (!historicoEtapa) return null;
                
                const entradaNaEtapa = new Date(historicoEtapa.movedAt);
                if (isNaN(entradaNaEtapa.getTime())) return null;
                
                const agora = new Date();
                const horasNaEtapa = (agora.getTime() - entradaNaEtapa.getTime()) / (1000 * 60 * 60);
                const atrasado = horasNaEtapa > etapa.tempoMaximoHoras;
                
                if (!atrasado) return null;
                
                return (
                  <div 
                    className="flex items-center gap-1 px-2 py-0.5 bg-red-50 border border-red-200 rounded-md"
                    title={`Entrada: ${entradaNaEtapa.toLocaleString('pt-BR')}`}
                  >
                    <span className="text-xs">⏰</span>
                    <span className="text-xs font-semibold text-red-700">
                      {Math.floor(horasNaEtapa - etapa.tempoMaximoHoras)}h atrasado
                    </span>
                  </div>
                );
              })()}
              
              {/* Botões de ação */}
              {canEditCard && (
                <>
                  {modo === 'visualizar' && (
                  <button
                    onClick={() => setModo('editar')}
                    className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 border border-transparent hover:border-indigo-200 rounded-lg transition-all"
                    aria-label="Editar cartão"
                  >
                    <PencilIcon className="h-4 w-4" />
                  </button>
                  )}
                  <button
                    onClick={() => setCardExpandido(prev => !prev)}
                    className={`p-1.5 text-indigo-500 hover:text-indigo-700 hover:bg-indigo-50 border border-indigo-200 hover:border-indigo-300 rounded-lg transition-all ${!cardExpandido ? 'animate-pulse' : ''}`}
                    aria-label={cardExpandido ? 'Recolher cartão' : 'Expandir cartão'}
                    title={cardExpandido ? 'Recolher' : 'Expandir'}
                  >
                    {cardExpandido ? (
                      <ArrowsPointingInIcon className="h-4 w-4" />
                    ) : (
                      <ArrowsPointingOutIcon className="h-4 w-4" />
                    )}
                  </button>
                  {canDeleteCard && (
                    <button
                      onClick={() => {
                        if (window.confirm('Tem certeza que deseja excluir este cartão?')) {
                          onDelete(card.id);
                          success('Cartão excluído com sucesso.');
                        }
                      }}
                      className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all border border-transparent hover:border-red-200"
                      aria-label="Excluir cartão"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  )}
                </>
              )}
              <button
                onClick={fecharCard}
                className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 border border-transparent hover:border-gray-200 rounded-lg transition-all"
                aria-label="Fechar"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>

            {/* Quebra a faixa: etiquetas e botões vão para a linha de baixo,
                inteiros, em vez de sobrar um pedaço solto ao lado do nome. */}
            <div className="order-3 basis-full h-0" aria-hidden="true" />
          </div>

          {/* Etiquetas vinculadas + seletor */}
          {(etiquetasSelecionadas.length > 0 || canEditCard) && (
          <div className="order-4 flex flex-wrap items-center gap-2">
            <EtiquetaBadges etiquetasIds={etiquetasSelecionadas} etiquetas={etiquetas} tamanho="sm" />
            {canEditCard && (
              <EtiquetasPicker
                etiquetas={etiquetas}
                selecionadas={etiquetasSelecionadas}
                onToggle={toggleEtiqueta}
              />
            )}
          </div>
          )}

          {/* ID, compartilhamento e ações do card */}
          <div className="order-5 flex flex-wrap items-center gap-x-2 gap-y-1.5">
            {/* Lado esquerdo: ID e ícones de compartilhamento */}
            <div className="flex items-center gap-1.5 flex-wrap">
              <div 
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-slate-50 border border-slate-200 cursor-help hover:bg-slate-100 transition-colors"
                title={`Código do card (clique para copiar)`}
              >
                <span className="text-[10px] font-bold text-indigo-500 uppercase tracking-widest">ID</span>
                <span className="text-xs font-bold font-mono text-indigo-700 tracking-wide">#{generateShortCode(card.id)}</span>
              </div>
              <button
                onClick={async (e) => {
                  e.stopPropagation();
                  try {
                    const shortCode = generateShortCode(card.id);
                    if (navigator.clipboard && navigator.clipboard.writeText) {
                      await navigator.clipboard.writeText(shortCode);
                      success(`✅ Código #${shortCode} copiado!`);
                    } else {
                      const textarea = document.createElement('textarea');
                      textarea.value = shortCode;
                      textarea.style.position = 'fixed';
                      textarea.style.opacity = '0';
                      document.body.appendChild(textarea);
                      textarea.select();
                      document.execCommand('copy');
                      document.body.removeChild(textarea);
                      success(`✅ Código #${shortCode} copiado!`);
                    }
                  } catch (err) {
                    console.error('Erro ao copiar:', err);
                    error('❌ Erro ao copiar código');
                  }
                }}
                className="text-indigo-400 hover:text-indigo-600 transition-colors p-0.5 rounded-full hover:bg-indigo-50 flex-shrink-0"
                title="Copiar código do card"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </button>
              <button
                onClick={async (e) => {
                  e.stopPropagation();
                  try {
                    const shareUrl = getShareableUrl(card.id);
                    const formattedUrl = `\n${shareUrl}\n`;
                    if (navigator.clipboard && navigator.clipboard.writeText) {
                      await navigator.clipboard.writeText(formattedUrl);
                      success('🔗 Link copiado! Cole no WhatsApp');
                    } else {
                      const textarea = document.createElement('textarea');
                      textarea.value = formattedUrl;
                      textarea.style.position = 'fixed';
                      textarea.style.opacity = '0';
                      document.body.appendChild(textarea);
                      textarea.select();
                      document.execCommand('copy');
                      document.body.removeChild(textarea);
                      success('🔗 Link copiado! Cole no WhatsApp');
                    }
                  } catch (err) {
                    console.error('Erro ao copiar link:', err);
                    error('❌ Erro ao copiar link');
                  }
                }}
                className="text-gray-400 hover:text-green-600 transition-colors p-0.5 rounded hover:bg-gray-100 flex-shrink-0"
                title="Copiar link compartilhável do card"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                </svg>
              </button>
              {/* Botão WhatsApp com dropdown */}
              <div className="relative">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setMostrarWhatsAppMenu(!mostrarWhatsAppMenu);
                  }}
                  className="text-gray-400 hover:text-green-500 transition-colors p-0.5 rounded hover:bg-gray-100 flex-shrink-0"
                  title="Enviar card por WhatsApp"
                >
                  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                </button>
                
                {/* Menu dropdown WhatsApp */}
                {mostrarWhatsAppMenu && (
                  <div 
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/20"
                    onClick={() => setMostrarWhatsAppMenu(false)}
                  >
                    <div 
                      className="w-80 bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden"
                      onClick={(e) => e.stopPropagation()}
                    >
                    <div className="p-3 bg-green-500 text-white">
                      <div className="flex items-center gap-2">
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                        </svg>
                        <span className="font-semibold">Enviar por WhatsApp</span>
                        <button 
                          onClick={() => setMostrarWhatsAppMenu(false)}
                          className="ml-auto hover:bg-green-600 rounded p-1"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    </div>
                    
                    {/* Colaboradores com telefone */}
                    <div className="max-h-48 overflow-y-auto">
                      {colaboradores
                        .filter((c: any) => c.telefone && c.telefone.trim() !== '' && c.telefone !== '-')
                        .slice(0, 10)
                        .map((colab: any) => {
                          const telefone = colab.telefone.replace(/\D/g, '');
                          const telefoneFormatado = telefone.startsWith('55') ? telefone : `55${telefone}`;
                          return (
                            <button
                              key={colab.id}
                              onClick={() => {
                                const shareUrl = getShareableUrl(card.id);
                                const titulo = card.titulo || 'Card';
                                const texto = encodeURIComponent(`📋 *${titulo}*\n\nAcesse o card:\n${shareUrl}`);
                                window.open(`https://wa.me/${telefoneFormatado}?text=${texto}`, '_blank');
                                setMostrarWhatsAppMenu(false);
                              }}
                              className="w-full px-3 py-2 text-left hover:bg-green-50 flex items-center gap-3 border-b border-gray-100"
                            >
                              <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center text-green-600 font-semibold text-sm">
                                {colab.nome?.charAt(0)?.toUpperCase() || '?'}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-gray-900 truncate">{colab.nome}</div>
                                <div className="text-xs text-gray-500">{colab.telefone}</div>
                              </div>
                              <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.707l-3-3a1 1 0 00-1.414 1.414L10.586 9H7a1 1 0 100 2h3.586l-1.293 1.293a1 1 0 101.414 1.414l3-3a1 1 0 000-1.414z" clipRule="evenodd" />
                              </svg>
                            </button>
                          );
                        })}
                      {colaboradores.filter((c: any) => c.telefone && c.telefone.trim() !== '' && c.telefone !== '-').length === 0 && (
                        <div className="px-3 py-4 text-center text-gray-500 text-sm">
                          Nenhum colaborador com telefone cadastrado
                        </div>
                      )}
                    </div>
                    
                    {/* Campo para digitar número */}
                    <div className="p-3 border-t border-gray-200 bg-gray-50">
                      <div className="text-xs text-gray-500 mb-2">Ou digite o número:</div>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={whatsAppNumero}
                          onChange={(e) => setWhatsAppNumero(e.target.value.replace(/\D/g, ''))}
                          placeholder="5548999999999"
                          className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        />
                        <button
                          onClick={() => {
                            if (whatsAppNumero.length >= 10) {
                              const shareUrl = getShareableUrl(card.id);
                              const titulo = card.titulo || 'Card';
                              const numero = whatsAppNumero.startsWith('55') ? whatsAppNumero : `55${whatsAppNumero}`;
                              const texto = encodeURIComponent(`📋 *${titulo}*\n\nAcesse o card:\n${shareUrl}`);
                              window.open(`https://wa.me/${numero}?text=${texto}`, '_blank');
                              setMostrarWhatsAppMenu(false);
                              setWhatsAppNumero('');
                            }
                          }}
                          disabled={whatsAppNumero.length < 10}
                          className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                          </svg>
                        </button>
                      </div>
                    </div>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Badge de automação */}
              {modo === 'editar' && automacaoMetaRef.current?.automacaoNome && (
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200 whitespace-nowrap">
                  Auto: {automacaoMetaRef.current.automacaoNome}
                </span>
              )}
              {modo !== 'editar' && ultimaAutomacaoNome && (
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200 whitespace-nowrap">
                  Auto: {ultimaAutomacaoNome}
                </span>
              )}
              {/* ➡️ Mover para — só lista as etapas que a etapa atual permite.
                  A filtragem e a movimentação usam a MESMA rotina do drag-and-drop
                  (permissões do usuário + permiteMoverPara da etapa de origem).
                  Escondido em modo edição para não descartar alterações não salvas. */}
              {onMoverParaEtapa && etapasDisponiveisParaMover.length > 0 && (
                <div className="relative">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!mostrarMoverMenu && temAlteracaoNaoSalva()) {
                        window.alert('Você alterou o card e não salvou. Clique em "Salvar Alterações" antes de mover.');
                        return;
                      }
                      setMostrarMoverMenu(!mostrarMoverMenu);
                    }}
                    disabled={!!movendoEtapaId}
                    className={`flex items-center gap-1.5 px-3 py-1 bg-white border rounded-lg transition-colors text-[11px] font-semibold whitespace-nowrap ${
                      movendoEtapaId
                        ? 'text-gray-400 border-gray-200 cursor-not-allowed opacity-70'
                        : 'text-indigo-700 border-indigo-200 hover:bg-indigo-50 hover:border-indigo-300'
                    }`}
                    title={`Mover este card para outra etapa (${etapasDisponiveisParaMover.length} disponível${etapasDisponiveisParaMover.length > 1 ? 'is' : ''})`}
                  >
                    {movendoEtapaId ? (
                      <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                      </svg>
                    ) : (
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                      </svg>
                    )}
                    {movendoEtapaId ? 'Movendo…' : 'Mover para'}
                    {!movendoEtapaId && (
                      <svg className={`w-3 h-3 transition-transform ${mostrarMoverMenu ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    )}
                  </button>

                  {mostrarMoverMenu && !movendoEtapaId && (
                    <>
                      {/* Clique fora fecha o menu */}
                      <div className="fixed inset-0 z-40" onClick={() => setMostrarMoverMenu(false)} />
                      <div className="absolute left-0 top-full mt-1.5 z-50 w-64 bg-white rounded-xl border border-slate-200 shadow-[0_12px_32px_-8px_rgba(0,0,0,0.25)] overflow-hidden">
                        <div className="px-3 py-2 bg-slate-50 border-b border-slate-100">
                          <p className="text-[9px] font-bold uppercase tracking-widest text-slate-400">Mover para</p>
                          <p className="text-[11px] text-slate-600 truncate">
                            De <span className="font-semibold text-slate-800">{etapa?.nome}</span>
                          </p>
                        </div>
                        <div className="max-h-64 overflow-y-auto py-1">
                          {etapasDisponiveisParaMover.map((etapaDestino) => {
                            // Numeração igual à do "Histórico de Etapas" (posição no fluxo)
                            const posicao = (todasEtapas || []).findIndex(e => String(e.id) === String(etapaDestino.id));
                            return (
                              <button
                                key={etapaDestino.id}
                                onClick={() => handleMoverParaEtapa(etapaDestino)}
                                className="w-full px-3 py-2 flex items-center gap-2.5 text-left hover:bg-indigo-50 transition-colors group"
                              >
                                <span className="w-5 h-5 rounded-md bg-slate-900 text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0">
                                  {posicao >= 0 ? posicao + 1 : '•'}
                                </span>
                                <span className="flex-1 text-xs font-semibold text-slate-800 truncate">
                                  {etapaDestino.nome}
                                </span>
                                <svg className="w-3.5 h-3.5 text-slate-300 group-hover:text-indigo-500 transition-colors flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}
              {/* Botão Ver Fluxo no header */}
              {cardCompleto?.etapaHistory && cardCompleto.etapaHistory.length > 0 && (
                <button
                  onClick={() => { setFluxoZoom(1); setFluxoPan({ x: 0, y: 0 }); setMostrarFluxoTimeline(true); }}
                  className="flex items-center gap-1.5 px-3 py-1 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors text-[11px] font-semibold whitespace-nowrap"
                  title="Visualizar percurso do card no fluxo"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
                  </svg>
                  Ver Fluxo
                </button>
              )}
              {/* Botão Linha do tempo no header (abre modal) */}
              {(timelineEntries.length > 0 || carregandoCardCompleto) && (
                <button
                  onClick={() => setMostrarTimelineModal(true)}
                  className="flex items-center gap-1.5 px-3 py-1 bg-white text-slate-700 border border-slate-200 rounded-lg hover:bg-slate-50 hover:border-slate-300 transition-colors text-[11px] font-semibold whitespace-nowrap"
                  title="Abrir Linha do tempo"
                >
                  <span className="text-sm leading-none">📜</span>
                  Linha do tempo
                </button>
              )}
              {/* 🔪 Fatiar card — só aparece quando a etapa habilita o fatiamento */}
              {podeFatiar && (
                <button
                  onClick={() => { if (!jaFatiado) setMostrarModalFatiar(true); }}
                  disabled={jaFatiado}
                  className={`flex items-center gap-1.5 px-3 py-1 bg-white border rounded-lg transition-colors text-[11px] font-semibold whitespace-nowrap ${
                    jaFatiado
                      ? 'text-gray-400 border-gray-200 cursor-not-allowed opacity-70'
                      : 'text-orange-700 border-orange-200 hover:bg-orange-50 hover:border-orange-300'
                  }`}
                  title={jaFatiado
                    ? 'Este card já foi fatiado e não pode ser fatiado novamente'
                    : 'Gerar um card novo a partir dos campos deste card'}
                >
                  <span className="text-sm leading-none">🔪</span>
                  {etapa.configuracaoFatiamento?.rotulo || 'Fatiar card'}
                  {(card.fatiasIds?.length || 0) > 0 && (
                    <span className="ml-0.5 inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 rounded-full bg-orange-100 text-orange-700 text-[9px] font-bold">
                      {card.fatiasIds!.length}
                    </span>
                  )}
                </button>
              )}
              {/* 💰 Pedir cotação — manda este card para fornecedores cotarem */}
              {podePedirCotacao && (
                <button
                  onClick={() => setMostrarModalCotacao(true)}
                  className="flex items-center gap-1.5 px-3 py-1 bg-white text-emerald-700 border border-emerald-200 rounded-lg hover:bg-emerald-50 hover:border-emerald-300 transition-colors text-[11px] font-semibold whitespace-nowrap"
                  title="Enviar este card para fornecedores cotarem"
                >
                  <span className="text-sm leading-none">💰</span>
                  {cotacaoRotulo || 'Pedir cotação'}
                </button>
              )}
              {submissaoPortal && (
                <button
                  onClick={() => setChatPortalModalAberto(true)}
                  className={`relative flex items-center gap-1.5 px-3 py-1 rounded-lg transition-colors text-[11px] font-semibold whitespace-nowrap border ${
                    chatNaoLidas > 0
                      ? 'bg-blue-600 text-white border-blue-600 hover:bg-blue-700 animate-pulse'
                      : 'bg-white text-blue-700 border-blue-200 hover:bg-blue-50 hover:border-blue-300'
                  }`}
                  title={`Conversar com ${submissaoPortal.nomeExterno || submissaoPortal.emailExterno || 'solicitante'}`}
                >
                  <ChatBubbleLeftRightIcon className="h-4 w-4" />
                  Conversar com solicitante
                  {chatNaoLidas > 0 && (
                    <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-white text-blue-700 text-[10px] font-bold">
                      {chatNaoLidas > 99 ? '99+' : chatNaoLidas}
                    </span>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Conteúdo - Layout de 2 colunas (empilhado verticalmente no mobile) */}
        <div className="p-5 flex-1 overflow-y-auto">
          <div className="flex flex-col-reverse md:flex-row gap-5">
            {/* ════════════════════════════════════════════════════════════════════════════════
                COLUNA ESQUERDA (Desktop) / ACIMA (Mobile) - Histórico de Etapas
                ════════════════════════════════════════════════════════════════════════════════ */}
            {/* Sem histórico (primeira etapa) e sem ser card conectado, a coluna
                nem aparece — os Campos ao lado ocupam o espaço todo */}
            {(etapasHistoricas.length > 0 || (card.isCardConectado && card.cardConectadoOrigemId)) && (
            <div className={`${etapasHistoricas.length > 0 ? 'flex-1' : 'md:w-72 md:flex-none'} min-w-0 overflow-y-auto ${cardExpandido ? 'md:max-h-[calc(100vh-180px)]' : 'md:max-h-[calc(94vh-170px)]'}`}>
              {/* Histórico do Fluxo de Origem (se for card conectado) */}
              {card.isCardConectado && card.cardConectadoOrigemId && (
                <HistoricoFluxoOrigem
                  cardId={card.id}
                  userEmail={userPermissions?.currentUser?.email}
                  empresaId={empresaId}
                  onAbrirGaleria={(itens, index) => setGaleriaAnexos({ itens, index })}
                />
              )}

              {/* Histórico de Etapas (sempre visível) */}
              {etapasHistoricas.length > 0 ? (
                <div className="border border-gray-200/80 rounded-xl overflow-hidden bg-white shadow-sm ring-1 ring-gray-900/5">
                  {/* Cabeçalho */}
                  <div className="flex items-center justify-between px-3 py-2.5 bg-slate-50 border-b border-slate-200">
                    <div className="flex items-center gap-2.5 flex-1">
                      <div className="w-7 h-7 rounded-lg bg-slate-800 flex items-center justify-center">
                        <span className="text-white text-xs">📋</span>
                      </div>
                      <div className="flex-1">
                        <h3 className="font-bold text-gray-900 text-sm tracking-tight">Histórico de Etapas</h3>
                        <span className="text-[10px] text-gray-500 font-medium">
                          {etapasHistoricas.length} etapa{etapasHistoricas.length > 1 ? 's' : ''} percorrida{etapasHistoricas.length > 1 ? 's' : ''}
                        </span>
                      </div>
                      {/* Botão toggle para mobile */}
                      <button
                        onClick={() => setHistoricoExpandidoMobile(!historicoExpandidoMobile)}
                        className="md:hidden p-1 hover:bg-indigo-100 rounded transition-colors"
                        title={historicoExpandidoMobile ? 'Minimizar' : 'Expandir'}
                      >
                        <svg 
                          className={`w-4 h-4 text-indigo-600 transition-transform ${historicoExpandidoMobile ? 'rotate-180' : ''}`}
                          fill="none" 
                          stroke="currentColor" 
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                    </div>
                    {(userPermissions?.canAdmin || canEditCard) && (
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => setHistoricoEditavel(!historicoEditavel)}
                          className={`text-[10px] px-2 py-1 rounded-full transition-all font-medium ${
                            historicoEditavel
                              ? 'bg-blue-600 text-white hover:bg-blue-700'
                              : 'bg-white text-blue-600 hover:bg-blue-50 border border-blue-200'
                          }`}
                        >
                          {historicoEditavel ? '✓ Editando' : '✏️ Editar'}
                        </button>
                        {historicoEditavel && Object.keys(camposEditadosNoHistorico).length > 0 && (
                          <button
                            onClick={handleSave}
                            disabled={salvando}
                            className="text-[10px] px-2 py-1 rounded-full transition-all font-medium bg-green-600 text-white hover:bg-green-700 disabled:bg-green-400 flex items-center gap-1"
                          >
                            {salvando ? (
                              <>
                                <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                </svg>
                                Salvando...
                              </>
                            ) : (
                              '💾 Salvar'
                            )}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  
                  {/* Lista de etapas - visível sempre no desktop, toggle no mobile */}
                  <div className={`p-3 space-y-2.5 ${cardExpandido ? 'max-h-[calc(100vh-260px)]' : 'max-h-[calc(94vh-250px)]'} overflow-y-auto bg-gray-50/30 ${
                    !historicoExpandidoMobile ? 'hidden md:block' : 'block'
                  }`}>
                    {/* 🔪 Linhagem para frente: o que este card gerou ao ser fatiado */}
                    {jaFatiado && (
                      <div className="rounded-lg border border-orange-200 bg-orange-50/70 px-3 py-2">
                        <div className="flex items-center gap-1.5 text-[11px] font-semibold text-orange-800">
                          <span>🔪</span>
                          <span>
                            Este card foi fatiado — gerou {card.fatiasIds!.length} card
                            {card.fatiasIds!.length > 1 ? 's' : ''}
                          </span>
                        </div>
                        <div className="mt-1 space-y-0.5">
                          {(((cardCompleto as any).fatiamentoHistory || []) as any[]).map((f: any, i: number) => {
                            const d = f.at ? new Date(f.at.$date || f.at) : null;
                            const quando = d && !isNaN(d.getTime())
                              ? d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                              : null;
                            const quem = userNames[f.by] || null;
                            return (
                              <div key={f.fatiaCardId || i} className="text-[10px] text-orange-700/90">
                                → <span className="font-semibold">{f.fatiaTitulo || f.fatiaCardId}</span>
                                {[quando, quem ? `por ${quem}` : null].filter(Boolean).length > 0 && (
                                  <span className="text-orange-700/70">
                                    {' '}· {[quando, quem ? `por ${quem}` : null].filter(Boolean).join(' · ')}
                                  </span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                        <div className="text-[10px] text-orange-700/70 mt-1">
                          As etapas abaixo guardam o retrato do card <span className="font-semibold">antes do corte</span>.
                        </div>
                      </div>
                    )}
                    {/* ⚠️ Fornecedor novo: calculado a partir dos cards, então vale também
                        para card criado por automação e para os antigos. Só consulta em
                        fluxo que tem campo com o aviso ligado. */}
                    {(() => {
                      // A ciência vem do card completo do servidor: o card do board
                      // chega pela projeção "leve", que só traz os campos listados —
                      // ali `cienciaFornecedorNovo` vinha undefined e o selo acusava
                      // "sem registro" em card onde a pessoa tinha confirmado.
                      const fonte: any = cardCompleto || card;
                      const ciencia = cienciaJaDada();
                      const temCampoComAviso = [...(todasEtapas || []), ...(etapasAnteriores || [])]
                        .some((e: any) => (e?.campos || []).some((c: any) => c?.validarFornecedorNovo));
                      if (!ciencia && !temCampoComAviso) return null;
                      return (
                        <SeloFornecedorNovo
                          cardId={card.id}
                          ciencia={ciencia}
                          criadoEm={fonte?.createdAt || (card as any).createdAt}
                          criadoPor={fonte?.criadoPor || (card as any).criadoPor}
                        />
                      );
                    })()}
                    {/* 🔪 Linhagem: de onde esta fatia veio. Sem isso o card nasce órfão,
                        sem pista nenhuma de qual card ele era antes do corte. */}
                    {(card as any).fatiadoDe && (
                      <div className="rounded-lg border border-orange-200 bg-orange-50/70 px-3 py-2">
                        <div className="flex items-center gap-1.5 text-[11px] font-semibold text-orange-800">
                          <span>🔪</span>
                          <span>Fatiado de: {(card as any).fatiadoDe.titulo || 'card de origem'}</span>
                        </div>
                        <div className="text-[10px] text-orange-700/80 mt-0.5">
                          {(() => {
                            const d = (card as any).fatiadoDe.fatiadoEm;
                            const data = d ? new Date(d.$date || d) : null;
                            const quando = data && !isNaN(data.getTime())
                              ? data.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                              : null;
                            const quem = userNames[(card as any).fatiadoDe.fatiadoPor] || null;
                            return [quando, quem ? `por ${quem}` : null].filter(Boolean).join(' · ');
                          })()}
                        </div>
                        <div className="text-[10px] text-orange-700/70 mt-1">
                          As etapas marcadas como <span className="font-semibold">antes do corte</span> são a trajetória herdada do card de origem.
                        </div>
                      </div>
                    )}
                    {etapasHistoricas.map((etapaHist, index) => {
                      // Verificar se o usuário pode editar ESTA etapa específica
                      const canEditThisEtapa = checkCardEditPermission ? checkCardEditPermission(etapaHist) : false;
                      // Etapa 100% herdada da origem = o card passou por ela antes de virar fatia
                      const entradasEtapa = (cardCompleto?.etapaHistory || []).filter(
                        (h: any) => String(h.etapaId) === String(etapaHist.id)
                      );
                      const etapaHerdada = entradasEtapa.length > 0
                        && entradasEtapa.every((h: any) => h._herdadoDaOrigem);

                      return (
                      <div key={`${etapaHist.id}-${index}`} className="bg-white rounded-lg border border-gray-200/80 overflow-hidden hover:shadow-sm transition-shadow">
                        {/* Cabeçalho da etapa */}
                        <div className="flex items-center gap-2.5 px-3 py-2 bg-slate-50/60 border-b border-slate-100">
                          <div className="w-6 h-6 rounded-lg bg-slate-700 text-white flex items-center justify-center text-[10px] font-bold">
                            {index + 1}
                          </div>
                          <span className="font-semibold text-gray-800 text-xs">{etapaHist.nome}</span>
                          {etapaHerdada && (
                            <span
                              className="text-[9px] px-1.5 py-0.5 bg-orange-100 text-orange-700 rounded-full font-medium"
                              title={`Etapa percorrida pelo card "${(card as any).fatiadoDe?.titulo || 'de origem'}" antes do fatiamento`}
                            >
                              antes do corte
                            </span>
                          )}
                          {/* Indicador de permissão de edição */}
                          {historicoEditavel && canEditThisEtapa && !userPermissions?.canAdmin && (
                            <span className="ml-auto text-[9px] px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded-full">✓ Pode editar</span>
                          )}
                        </div>
                        
                        {/* Campos da etapa */}
                        <div className="px-2 py-1.5">
                          {(() => {
                            // Buscar snapshot do histórico — usar a ENTRADA MAIS RECENTE
                            // (caso o card tenha revisitado a etapa, queremos o último snapshot salvo)
                            const entradasDestaEtapa = (cardCompleto?.etapaHistory || []).filter(
                              (h: any) => String(h.etapaId) === String(etapaHist.id)
                            );
                            const historicoEntry = entradasDestaEtapa.length > 0
                              ? entradasDestaEtapa[entradasDestaEtapa.length - 1]
                              : undefined;
                            const camposSnapshot = historicoEntry?.camposSnapshot || {};
                            
                            // Também usar campos do card atual como fallback
                            const camposDoCard = card.campos || {};
                            
                            // Gerar lista de campos para exibir
                            const camposParaExibir: { id: string; nome: string; tipo: string; opcoes?: string[] }[] = [];
                            
                            // 📧 NOVO: Adicionar emailContato como primeiro campo se existir (vem do portal)
                            const emailContato = camposDoCard['emailContato'] || camposSnapshot['emailContato'];
                            if (emailContato && index === 0) {
                              // Verificar se já não existe um campo emailContato na lista
                              const jaTemEmailContato = etapaHist.campos?.some((c: any) => c.id === 'emailContato');
                              if (!jaTemEmailContato) {
                                camposParaExibir.push({
                                  id: 'emailContato',
                                  nome: 'E-mail para Contato',
                                  tipo: 'email'
                                });
                              }
                            }
                            
                            if (etapaHist.campos && etapaHist.campos.length > 0) {
                              camposParaExibir.push(...etapaHist.campos);
                            } else {
                              // Construir mapa global de definições de campos a partir de TODAS as etapas conhecidas
                              // (etapa atual + etapasAnteriores) para resolver nome/tipo a partir do ID
                              const defsPorId = new Map<string, any>();
                              [etapa, ...etapasAnteriores].forEach((et: any) => {
                                (et?.campos || []).forEach((c: any) => {
                                  if (c?.id && !defsPorId.has(String(c.id))) defsPorId.set(String(c.id), c);
                                });
                              });
                              // Usar campos do snapshot OU do card — exceto nas etapas do ciclo
                              // de reenvio, que mostram só os anexos pedidos/reenviados.
                              const idsReenvio = camposDoCicloReenvio(historicoEntry, cardCompleto);
                              const todasChaves = idsReenvio
                                ? new Set(idsReenvio)
                                : new Set([...Object.keys(camposSnapshot), ...Object.keys(camposDoCard)]);
                              todasChaves.forEach(campoId => {
                                // Não duplicar emailContato se já foi adicionado
                                if (campoId === 'emailContato' && camposParaExibir.some(c => c.id === 'emailContato')) {
                                  return;
                                }
                                const def = defsPorId.get(String(campoId));
                                camposParaExibir.push({
                                  id: campoId,
                                  nome: def?.nome || campoId,
                                  tipo: def?.tipo || 'text',
                                  opcoes: def?.opcoes,
                                });
                              });
                            }
                            // Fallback adicional: se algum campo está com nome === id (ex: "campo_xxx"),
                            // tentar resolver via lookup global de definições
                            const defsPorIdGlobal = new Map<string, any>();
                            [etapa, ...etapasAnteriores].forEach((et: any) => {
                              (et?.campos || []).forEach((c: any) => {
                                if (c?.id && !defsPorIdGlobal.has(String(c.id))) defsPorIdGlobal.set(String(c.id), c);
                              });
                            });
                            for (let i = 0; i < camposParaExibir.length; i++) {
                              const c = camposParaExibir[i];
                              const looksLikeRawId = !c.nome || c.nome === c.id || /^campo_[a-z0-9]+_[a-z0-9]+$/i.test(String(c.nome));
                              if (looksLikeRawId) {
                                const def = defsPorIdGlobal.get(String(c.id));
                                if (def?.nome) {
                                  camposParaExibir[i] = { ...c, nome: def.nome, tipo: c.tipo || def.tipo || 'text', opcoes: c.opcoes || def.opcoes };
                                }
                              }
                            }
                            
                            if (camposParaExibir.length === 0) {
                              return (
                                <div className="text-[10px] text-gray-400 italic text-center py-1">
                                  Nenhum campo
                                </div>
                              );
                            }
                            
                            return (
                              <div className="space-y-1">
                                {/* Rótulos (texto_informativo) não são dado preenchido — não entram no histórico */}
                                {camposParaExibir.filter(c => c.tipo !== 'texto_informativo' && !camposOcultosParaMim.has(String(c.id))).map(campo => {
                                  const campoId = campo.id;
                                  // Prioridade: editado nesta sessão > última entrada salva no fieldHistory
                                  // > snapshot (se chave existe) > campos do card > vazio.
                                  // O fieldHistory é a fonte mais confiável para "última edição com salvamento",
                                  // porque cada save empurra uma entrada nova com o valor exato daquele momento.
                                  let valorFinal: any;
                                  if (campoId in camposEditadosNoHistorico) {
                                    valorFinal = camposEditadosNoHistorico[campoId];
                                  } else {
                                    const histArr = (cardCompleto as any)?.fieldHistory?.[campoId];
                                    const ultimaEdicao = Array.isArray(histArr) && histArr.length > 0
                                      ? histArr[histArr.length - 1]?.value
                                      : undefined;
                                    if (ultimaEdicao !== undefined) {
                                      valorFinal = ultimaEdicao;
                                    } else if (campoId in camposSnapshot) {
                                      valorFinal = camposSnapshot[campoId];
                                    } else {
                                      valorFinal = camposDoCard[campoId];
                                    }
                                  }
                                  if (valorFinal === undefined || valorFinal === null || valorFinal === '') {
                                    valorFinal = '-';
                                  }
                                  
                                  // Renderização especial para arquivos/anexos
                                  if ((campo.tipo === 'anexo' || campo.tipo === 'arquivo') && (valorFinal && valorFinal !== '-' || (historicoEditavel && (userPermissions?.canAdmin || canEditThisEtapa)))) {
                                    const arquivos = Array.isArray(valorFinal) ? valorFinal : valorFinal && valorFinal !== '-' ? [valorFinal] : [];
                                    const isEditando = historicoEditavel && (userPermissions?.canAdmin || canEditThisEtapa);
                                    
                                    return (
                                      <div key={campoId} className="flex flex-col gap-1">
                                        <div className="flex items-center justify-between gap-2">
                                          <span className="text-[10px] text-gray-500">{campo.nome}:</span>
                                          {arquivos.length > 1 && (
                                            <button
                                              onClick={() => abrirGaleriaDaEtapa(etapaHist, camposParaExibir, campoId, 0)}
                                              className="text-[10px] px-2 py-0.5 bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-full hover:bg-indigo-100 transition-colors font-medium whitespace-nowrap"
                                              title="Abrir galeria com todos os anexos"
                                            >
                                              🖼️ Ver todos ({arquivos.length})
                                            </button>
                                          )}
                                        </div>
                                        
                                        {/* Cada arquivo/print em uma miniatura própria; clicar abre a galeria da etapa */}
                                        <MiniaturasAnexos
                                          compacto
                                          arquivos={arquivos}
                                          onAbrir={(idx) => abrirGaleriaDaEtapa(etapaHist, camposParaExibir, campoId, idx)}
                                          onRemover={isEditando ? (idx) => {
                                            const nomeArquivo = infoAnexo(arquivos[idx]).nome;
                                            if (!confirm(`Remover "${nomeArquivo}"?`)) return;
                                            const novosArquivos = arquivos.filter((_: any, i: number) => i !== idx);
                                            setCamposEditadosNoHistorico(prev => ({
                                              ...prev,
                                              [campoId]: novosArquivos.length > 0 ? novosArquivos : []
                                            }));
                                            // Também atualizar valoresHistorico
                                            setValoresHistorico(prev => ({
                                              ...prev,
                                              [etapaHist.id]: {
                                                ...(prev[etapaHist.id] || {}),
                                                [campoId]: novosArquivos.length > 0 ? novosArquivos : []
                                              }
                                            }));
                                          } : undefined}
                                        />
                                        
                                        {/* Input para adicionar novo arquivo (só no modo edição) */}
                                        {isEditando && arquivos.length < 6 && (
                                          <div className="mt-1">
                                            <input
                                              type="file"
                                              onChange={async (e) => {
                                                let file = e.target.files?.[0];
                                                if (!file) return;
                                                // 🖼️ Comprimir imagem (se for foto grande de celular)
                                                file = await compressImageIfNeeded(file);
                                                
                                                // Limite de 20MB
                                                if (file.size > 20 * 1024 * 1024) {
                                                  alert('Arquivo muito grande! Máximo 20MB.');
                                                  e.target.value = '';
                                                  return;
                                                }
                                                
                                                try {
                                                  // Upload para servidor
                                                  const formData = new FormData();
                                                  formData.append('file', file);
                                                  formData.append('flowId', flowId || 'geral');
                                                  formData.append('cardId', card.id || 'temp');
                                                  formData.append('campoId', campoId);
                                                  formData.append('flowNome', flowName || '');
                                                  formData.append('cardTitulo', card.titulo || '');
                                                  
                                                  const response = await fetch('/api/upload-anexo-local', {
                                                    method: 'POST',
                                                    body: formData
                                                  });
                                                  
                                                  if (!response.ok) throw new Error('Erro no upload');
                                                  const result = await response.json();
                                                  
                                                  const novoArquivo = {
                                                    name: result.originalName || file.name,
                                                    fileName: result.fileName,
                                                    size: result.size || file.size,
                                                    type: result.type || file.type,
                                                    url: result.url,
                                                    uploadedAt: result.uploadedAt
                                                  };
                                                  
                                                  const novosArquivos = [...arquivos, novoArquivo];
                                                  setCamposEditadosNoHistorico(prev => ({
                                                    ...prev,
                                                    [campoId]: novosArquivos
                                                  }));
                                                  setValoresHistorico(prev => ({
                                                    ...prev,
                                                    [etapaHist.id]: {
                                                      ...(prev[etapaHist.id] || {}),
                                                      [campoId]: novosArquivos
                                                    }
                                                  }));
                                                  
                                                  e.target.value = '';
                                                } catch (error) {
                                                  console.error('Erro upload:', error);
                                                  alert('Erro ao enviar arquivo');
                                                }
                                              }}
                                              className="w-full text-[10px] file:mr-1 file:py-0.5 file:px-2 file:rounded file:border-0 file:text-[10px] file:bg-green-500 file:text-white hover:file:bg-green-600 cursor-pointer"
                                            />
                                            <span className="text-[9px] text-gray-400">
                                              {arquivos.length}/3 • Máx 5MB
                                            </span>
                                          </div>
                                        )}
                                        
                                        {arquivos.length === 0 && !isEditando && (
                                          <span className="text-[10px] text-gray-400 italic">Nenhum arquivo</span>
                                        )}
                                      </div>
                                    );
                                  }
                                  
                                  // itens da solicitação: painel de cotação com os anexos de cada item
                                  if (ehListaDeItens(valorFinal)) {
                                    return (
                                      <div key={campoId} className="flex flex-col gap-0.5">
                                        <span className="text-[10px] text-gray-500">{campo.nome}:</span>
                                        <ItensResumo valor={valorFinal} />
                                      </div>
                                    );
                                  }

                                  // Formatar valor para exibição
                                  let valorExibir = valorFinal;
                                  if (Array.isArray(valorFinal) && valorFinal.some((v: any) => v && typeof v === 'object' && 'paga' in v && 'nome' in v)) {
                                    valorExibir = textoParcelas(valorFinal);
                                  } else if (ehListaDeViajantes(valorFinal)) {
                                    valorExibir = textoViajantes(valorFinal);
                                  } else if (ehListaDeVoos(valorFinal)) {
                                    valorExibir = textoVoos(valorFinal);
                                  } else if (ehListaDeTrechos(valorFinal)) {
                                    valorExibir = textoTrechos(valorFinal);
                                  } else if (ehListaDeItens(valorFinal)) {
                                    valorExibir = textoItens(valorFinal);
                                  } else if (ehMapaCotacao(valorFinal)) {
                                    valorExibir = textoCotacoes(valorFinal);
                                  } else if (Array.isArray(valorFinal)) {
                                    valorExibir = valorFinal
                                      .map((v: any) => (v && typeof v === 'object' ? (v.name || v.fileName || v.item || v.nome || JSON.stringify(v)) : String(v)))
                                      .join(', ');
                                  } else if (typeof valorFinal === 'object' && valorFinal !== null) {
                                    valorExibir = JSON.stringify(valorFinal);
                                  } else if (campo.tipo === 'responsavel' && valorFinal !== '-') {
                                    // guarda o id do colaborador: mostrar o nome (30/10/2026, atas)
                                    valorExibir = nomeResponsavelCampo(valorFinal);
                                  }

                                  // Verificar se é texto longo (mais de 30 caracteres)
                                  const textoLongo = String(valorExibir).length > 30;
                                  
                                  // Função helper para atualizar campos no histórico
                                  const updateCampoHistorico = (novoValor: any) => {
                                    // Atualizar camposEditadosNoHistorico (usa apenas campoId como chave)
                                    setCamposEditadosNoHistorico(prev => ({
                                      ...prev,
                                      [campoId]: novoValor
                                    }));
                                    // Também atualizar valoresHistorico para o camposSnapshot
                                    setValoresHistorico(prev => ({
                                      ...prev,
                                      [etapaHist.id]: {
                                        ...(prev[etapaHist.id] || {}),
                                        [campoId]: novoValor
                                      }
                                    }));
                                  };
                                  
                                  // Verificar se está em modo edição
                                  const isEditandoCampo = historicoEditavel && (userPermissions?.canAdmin || canEditThisEtapa);
                                  
                                  // Renderização especial para campos de lista/select em modo edição
                                  if (isEditandoCampo && campo.tipo === 'lista' && campo.opcoes && campo.opcoes.length > 0) {
                                    return (
                                      <div key={campoId} className="flex flex-col gap-0.5">
                                        <span className="text-[10px] text-gray-500">{campo.nome}:</span>
                                        <select
                                          value={String(valorFinal === '-' ? '' : valorFinal) || ''}
                                          onChange={(e) => updateCampoHistorico(e.target.value)}
                                          className="w-full px-1.5 py-1 text-[10px] border border-blue-300 rounded bg-blue-50 focus:ring-1 focus:ring-blue-400"
                                        >
                                          <option value="">Selecione...</option>
                                          {campo.opcoes.map((opcao: string) => (
                                            <option key={opcao} value={opcao}>{opcao}</option>
                                          ))}
                                        </select>
                                      </div>
                                    );
                                  }
                                  
                                  // Helper: normaliza valor/opção para comparação tolerante
                                  // (ignora diferenças de whitespace/tabs/hifens — útil quando o label da opção
                                  // foi alterado depois que o valor já estava salvo no card)
                                  const normOpc = (s: any) => String(s ?? '')
                                    .replace(/\s+/g, ' ')
                                    .replace(/\s*-\s*/g, ' ')
                                    .trim()
                                    .toUpperCase();

                                  // Renderização especial para checkbox_single em modo edição
                                  if (isEditandoCampo && campo.tipo === 'checkbox_single' && campo.opcoes && campo.opcoes.length > 0) {
                                    const valorAtual = Array.isArray(valorFinal) ? valorFinal : (valorFinal && valorFinal !== '-' ? [valorFinal] : []);
                                    return (
                                      <div key={campoId} className="flex flex-col gap-0.5">
                                        <span className="text-[10px] text-gray-500">{campo.nome}:</span>
                                        <div className="space-y-0.5 p-1 bg-blue-50 rounded border border-blue-300">
                                          {campo.opcoes.map((opcao: string) => (
                                            <label key={opcao} className="flex items-center gap-1 hover:bg-blue-100 px-1 py-0.5 rounded cursor-pointer text-[10px]">
                                              <input
                                                type="radio"
                                                name={`hist_${etapaHist.id}_${campoId}`}
                                                checked={valorAtual.some((v: any) => normOpc(v) === normOpc(opcao))}
                                                onChange={() => updateCampoHistorico([opcao])}
                                                className="h-2.5 w-2.5 border-gray-300 text-blue-600 focus:ring-blue-500"
                                              />
                                              <span className="text-gray-800">{opcao}</span>
                                            </label>
                                          ))}
                                        </div>
                                      </div>
                                    );
                                  }
                                  
                                  // Renderização especial para checkbox_multi/checkbox/progresso em modo edição
                                  if (isEditandoCampo && (campo.tipo === 'checkbox_multi' || campo.tipo === 'checkbox' || campo.tipo === 'progresso') && campo.opcoes && campo.opcoes.length > 0) {
                                    const valorAtual = Array.isArray(valorFinal) ? valorFinal : (valorFinal && valorFinal !== '-' ? [valorFinal] : []);
                                    return (
                                      <div key={campoId} className="flex flex-col gap-0.5">
                                        <span className="text-[10px] text-gray-500">{campo.nome}:</span>
                                        <div className="space-y-0.5 p-1 bg-blue-50 rounded border border-blue-300">
                                          {campo.opcoes.map((opcao: string) => (
                                            <label key={opcao} className="flex items-center gap-1 hover:bg-blue-100 px-1 py-0.5 rounded cursor-pointer text-[10px]">
                                              <input
                                                type="checkbox"
                                                checked={valorAtual.some((v: any) => normOpc(v) === normOpc(opcao))}
                                                onChange={(e) => {
                                                  // Remove qualquer valor (canônico OU stale) que normalize para esta opção,
                                                  // depois — se foi marcado — adiciona a string canônica da opção.
                                                  const semEsta = valorAtual.filter((v: any) => normOpc(v) !== normOpc(opcao));
                                                  const newValues = e.target.checked ? [...semEsta, opcao] : semEsta;
                                                  updateCampoHistorico(newValues);
                                                }}
                                                className="h-2.5 w-2.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                              />
                                              <span className="text-gray-800">{opcao}</span>
                                            </label>
                                          ))}
                                        </div>
                                      </div>
                                    );
                                  }
                                  
                                  // Renderização especial para campos de data em modo edição
                                  if (isEditandoCampo && (campo.tipo === 'data' || campo.tipo === 'data_vencimento')) {
                                    // Formatar valor para datetime-local input
                                    let valorData = '';
                                    if (valorFinal && valorFinal !== '-') {
                                      try {
                                        if (String(valorFinal).match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/)) {
                                          valorData = String(valorFinal);
                                        } else {
                                          const d = new Date(String(valorFinal));
                                          if (!isNaN(d.getTime())) {
                                            valorData = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}T${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
                                          }
                                        }
                                      } catch { valorData = ''; }
                                    }
                                    return (
                                      <div key={campoId} className="flex flex-col gap-0.5">
                                        <span className="text-[10px] text-gray-500">{campo.nome}:</span>
                                        <input
                                          type="datetime-local"
                                          value={valorData}
                                          onChange={(e) => updateCampoHistorico(e.target.value)}
                                          className="w-full px-1.5 py-1 text-[10px] border border-blue-300 rounded bg-blue-50 focus:ring-1 focus:ring-blue-400"
                                        />
                                      </div>
                                    );
                                  }
                                  
                                  // Roteiro aéreo / rota terrestre: cartões de trecho (só leitura, também no modo edição)
                                  if (ehListaDeTrechos(valorFinal) || ((campo.tipo === 'rotas' || campo.tipo === 'rota_terrestre') && Array.isArray(valorFinal))) {
                                    return <TrechosHistorico key={campoId} nome={campo.nome} trechos={valorFinal} terrestre={campo.tipo === 'rota_terrestre'} />;
                                  }

                                  // Aviso do lançamento automático de NF: o texto é o mesmo
                                  // do carimbo, mas destacado — senão some no meio dos
                                  // outros campos da etapa.
                                  const ehMotivoNf = ehMotivoNfNaoLancada(valorExibir);

                                  return (
                                    <div key={campoId} className={(textoLongo || ehMotivoNf) ? "flex flex-col gap-0.5" : "flex items-start gap-1"}>
                                      <span className={`text-[10px] min-w-0 flex-shrink-0 ${ehMotivoNf ? `${corAvisoNf.rotulo} font-semibold` : 'text-gray-500'}`}>
                                        {ehMotivoNf && corAvisoNf.icone}{campo.nome}:
                                      </span>
                                      {isEditandoCampo && !ehListaDeTrechos(valorFinal) ? (
                                        <textarea
                                          value={String(valorFinal === '-' ? '' : valorFinal) || ''}
                                          onChange={(e) => updateCampoHistorico(e.target.value)}
                                          className="flex-1 px-1.5 py-0.5 text-[10px] border border-blue-300 rounded bg-blue-50 focus:ring-1 focus:ring-blue-400 resize-none"
                                          rows={textoLongo ? 3 : 1}
                                        />
                                      ) : (
                                        <span className={`text-[10px] font-medium break-words ${
                                          ehMotivoNf
                                            ? `${corAvisoNf.caixa} px-1.5 py-1 rounded border w-full`
                                            : `text-gray-800 ${textoLongo ? 'bg-gray-50 px-1.5 py-1 rounded border border-gray-200 w-full' : ''}`}`}>
                                          <TextoComHtml valor={valorExibir} />
                                        </span>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    );})}
                  </div>
                </div>
              ) : (
                <div className="border border-gray-200/80 rounded-xl p-8 bg-gradient-to-br from-gray-50 via-white to-gray-50 text-center ring-1 ring-gray-900/5">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center mx-auto mb-3 shadow-sm">
                    <span className="text-2xl">📋</span>
                  </div>
                  <p className="text-sm font-semibold text-gray-700">Primeira etapa</p>
                  <p className="text-xs text-gray-400 mt-1">Nenhum histórico anterior disponível</p>
                </div>
              )}
            </div>
            )}

            {/* ════════════════════════════════════════════════════════════════════════════════
                COLUNA DIREITA - Campos Atuais, Linha do Tempo, etc.
                ════════════════════════════════════════════════════════════════════════════════ */}
            <div className="flex-1 md:flex-[1.25] min-w-0 space-y-4">
            {/* Aviso de movimentação por automação pendente */}
            {modo === 'editar' && destinoAutomacao && (
              <div className="p-2 bg-blue-50 border border-blue-200 rounded-md text-blue-800 text-xs">
                Este cartão será movido ao salvar para a etapa selecionada pela automação
                {automacaoMetaRef.current?.automacaoNome ? (
                  <span className="font-medium">{automacaoMetaRef.current?.automacaoNome}</span>
                ) : null}
                .
              </div>
            )}

            {/* Seção de Hierarquia - Card Pai/Filho */}
            {(card.ehCardPai || card.cardPaiId) && (
              <div className="bg-gradient-to-br from-purple-50 to-indigo-50 border-2 border-purple-200 rounded-lg p-3">
                <h3 className="font-semibold text-purple-900 mb-2 flex items-center gap-1.5 text-sm">
                  🎴 Hierarquia de Cards
                </h3>

                {/* Se for um Card Pai */}
                {card.ehCardPai && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-1.5 text-xs">
                        <span className="px-2 py-0.5 bg-purple-600 text-white rounded-full font-medium">
                          Card Pai
                        </span>
                        <span className="text-purple-700">
                          {card.cardsFilhosIds?.length || 0} {card.cardsFilhosIds?.length === 1 ? 'filho' : 'filhos'}
                        </span>
                      </div>
                      
                      {/* Botão Resumo */}
                      {card.cardsFilhos && card.cardsFilhos.length > 0 && (
                        <button
                          onClick={() => setMostrarResumo(true)}
                          className="flex items-center gap-1.5 px-2.5 py-1 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-md hover:from-purple-700 hover:to-indigo-700 transition-all shadow-sm font-medium text-xs"
                        >
                          <DocumentTextIcon className="w-3.5 h-3.5" />
                          Resumo
                        </button>
                      )}
                    </div>

                    {/* Lista de filhos */}
                    {card.cardsFilhos && card.cardsFilhos.length > 0 && (
                      <div className="space-y-1.5">
                        <div className="text-xs font-medium text-purple-800">Cards Filhos:</div>
                        {card.cardsFilhos.map((filho) => (
                          <div
                            key={filho.id}
                            className="flex items-center justify-between bg-white/80 rounded-md p-2 border border-purple-200"
                          >
                            <div 
                              className="flex-1 cursor-pointer hover:text-purple-700"
                              onClick={() => onSelecionarCard?.(filho)}
                            >
                              <div className="font-medium text-gray-900 text-xs">→ {filho.titulo}</div>
                            </div>
                            {onDesvincularFilho && (
                              <button
                                onClick={() => {
                                  if (confirm(`Desvincular "${filho.titulo}" deste card pai?`)) {
                                    onDesvincularFilho(card.id, filho.id);
                                  }
                                }}
                                className="px-3 py-1 text-sm text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors"
                              >
                                Desvincular
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {card.cardsFilhosIds?.length === 0 && (
                      <div className="text-sm text-purple-600 bg-white/60 rounded-lg p-3 border border-purple-200">
                        Este card pai ainda não possui filhos
                      </div>
                    )}
                  </div>
                )}

                {/* Se for um Card Filho */}
                {card.cardPaiId && !card.ehCardPai && (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-sm">
                      <span className="px-3 py-1 bg-indigo-600 text-white rounded-full font-medium">
                        Card Filho
                      </span>
                    </div>

                    {/* Mostrar card pai */}
                    {(() => {
                      const cardPai = todosCards.find(c => String(c.id) === String(card.cardPaiId));
                      return cardPai ? (
                        <div className="space-y-2">
                          <div className="text-sm font-medium text-indigo-800">Pertence ao Card Pai:</div>
                          <div
                            className="flex items-center justify-between bg-white/80 rounded-lg p-3 border border-indigo-200 cursor-pointer hover:bg-indigo-50"
                            onClick={() => onSelecionarCard?.(cardPai)}
                          >
                            <div className="flex-1">
                              <div className="font-medium text-gray-900">🎴 {cardPai.titulo}</div>
                              <div className="text-xs text-gray-500 mt-1">
                                {cardPai.cardsFilhosIds?.length || 0} {cardPai.cardsFilhosIds?.length === 1 ? 'filho' : 'filhos'} no total
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="text-sm text-amber-700 bg-amber-50 rounded-lg p-3 border border-amber-200">
                          ⚠️ Card pai não encontrado
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            )}

            {/* � Cards Conectados (Subprocessos) */}
            {(card.isCardConectado || (card.cardsConectadosIds && card.cardsConectadosIds.length > 0)) && (
              <div className="border border-gray-200/80 rounded-xl p-4 ring-1 ring-gray-900/5" style={{ 
                borderColor: card.corCardConectado || '#8B5CF6',
                backgroundColor: `${card.corCardConectado || '#8B5CF6'}06`
              }}>
                <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2 text-sm">
                  <svg className="w-4 h-4" style={{ color: card.corCardConectado || '#8B5CF6' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                  </svg>
                  Cards Conectados
                </h3>

                {/* Se é um card conectado (veio de outro card) */}
                {card.isCardConectado && card.cardConectadoOrigemId && (
                  <div className="mb-2 px-2 py-1.5 bg-white rounded-md border text-xs" style={{ borderColor: `${card.corCardConectado || '#8B5CF6'}40` }}>
                    <span className="text-gray-500">Origem:</span>{' '}
                    <CardConectadoOrigem
                      cardOrigemId={card.cardConectadoOrigemId}
                      flowOrigemId={card.cardConectadoOrigemFlowId}
                      cor={card.corCardConectado || '#8B5CF6'}
                      userEmail={userPermissions?.currentUser?.email}
                      empresaId={empresaId}
                    />
                  </div>
                )}

                {/* Se tem cards conectados gerados */}
                {card.cardsConectadosIds && card.cardsConectadosIds.length > 0 && (
                  <CardsConectadosLista 
                    cardIds={card.cardsConectadosIds} 
                    cor={card.corCardConectado || '#8B5CF6'}
                    userEmail={userPermissions?.currentUser?.email}
                    empresaId={empresaId}
                  />
                )}
              </div>
            )}

            {/* 💬 Conversa com o solicitante (chat do portal) */}
            {submissaoPortal && (
              <div className="space-y-2.5">
                <button
                  type="button"
                  onClick={() => setChatPortalExpandido(prev => !prev)}
                  className="w-full font-bold text-gray-900 flex items-center gap-2 text-sm hover:bg-gray-50 -mx-1 px-1 py-1 rounded-lg transition-colors"
                  aria-expanded={chatPortalExpandido}
                  title={chatPortalExpandido ? 'Recolher conversa' : 'Expandir conversa'}
                >
                  <div className="w-7 h-7 rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600 flex items-center justify-center">
                    <DocumentTextIcon className="w-3.5 h-3.5 text-white" />
                  </div>
                  Conversa com o solicitante
                  {chatNaoLidas > 0 && (
                    <span className="text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full animate-pulse min-w-[18px] text-center bg-red-500">
                      {chatNaoLidas > 99 ? '99+' : chatNaoLidas}
                    </span>
                  )}
                  {submissaoPortal.protocolo && (
                    <span className="text-xs font-normal text-gray-400 ml-auto">
                      {submissaoPortal.protocolo}
                    </span>
                  )}
                  {chatPortalExpandido ? (
                    <ChevronUpIcon className="h-4 w-4 text-gray-400" />
                  ) : (
                    <ChevronDownIcon className="h-4 w-4 text-gray-400" />
                  )}
                </button>
                {chatPortalExpandido && (
                  <PortalMensagensInternas
                    embedded
                    submissaoId={submissaoPortal.id}
                    portalId={submissaoPortal.portalId}
                    nomeExterno={submissaoPortal.nomeExterno}
                    emailExterno={submissaoPortal.emailExterno}
                    protocolo={submissaoPortal.protocolo}
                    currentUser={userPermissions?.currentUser ? {
                      id: userPermissions.currentUser.id,
                      nome: userPermissions.currentUser.nome
                    } : undefined}
                    onAtualizou={(naoLidas) => setChatNaoLidas(naoLidas)}
                  />
                )}
              </div>
            )}

            {/* �📄 Documentos Disponíveis */}
            {documentosDisponiveis.length > 0 && (
              <div className="space-y-2.5">
                <h3 className="font-bold text-gray-900 flex items-center gap-2 text-sm">
                  <div className="w-7 h-7 rounded-lg bg-slate-800 flex items-center justify-center">
                    <DocumentTextIcon className="w-3.5 h-3.5 text-white" />
                  </div>
                  Documentos
                </h3>
                
                {loadingDocumentos ? (
                  <div className="text-center py-2 bg-gray-50 rounded">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="text-xs text-gray-600 mt-1">Carregando documentos...</p>
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    {documentosDisponiveis.map((doc) => {
                      const docId = doc._id || doc.id;
                      const isExpanded = documentoExpandido === docId;
                      const htmlPreenchido = preencherTemplateComDados(doc.conteudoHtml);
                      
                      return (
                        <div key={docId} className="border border-gray-200 bg-white rounded overflow-hidden">
                          <div className="flex items-center justify-between p-2 bg-slate-50/60">
                            <div className="flex items-center gap-2 flex-1">
                              <DocumentTextIcon className="w-4 h-4 text-slate-600" />
                              <div>
                                <h4 className="font-medium text-gray-900 text-xs">{doc.nome}</h4>
                                {doc.descricao && (
                                  <p className="text-[10px] text-gray-500">{doc.descricao}</p>
                                )}
                              </div>
                            </div>
                            
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => setDocumentoExpandido(isExpanded ? null : docId)}
                                className="px-2 py-1 text-[10px] bg-white border border-slate-200 text-slate-700 rounded hover:bg-slate-50 transition-colors font-medium"
                              >
                                {isExpanded ? 'Fechar' : 'Ver'}
                              </button>
                              <button
                                disabled={gerandoPdfId === docId}
                                onClick={async () => {
                                  setGerandoPdfId(docId);
                                  try {
                                    await gerarPdfDocumento(htmlPreenchido, `${doc.nome}_${card.titulo}`);
                                  } catch (error) {
                                    console.error('Erro ao gerar PDF:', error);
                                    alert('Erro ao gerar PDF. Tente novamente.');
                                  } finally {
                                    setGerandoPdfId(null);
                                  }
                                }}
                                className="px-2 py-1 text-[10px] bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors font-medium flex items-center gap-0.5 disabled:opacity-60 disabled:cursor-wait"
                              >
                                {gerandoPdfId === docId ? '⏳ Gerando...' : '📄 PDF'}
                              </button>
                            </div>
                          </div>
                          
                          {isExpanded && (
                            <div className="p-2 bg-gray-50 border-t border-gray-200">
                              <div className="bg-white p-3 rounded shadow-sm">
                                <div 
                                  className="prose prose-sm max-w-none"
                                  dangerouslySetInnerHTML={{ __html: htmlPreenchido }}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* 📎 Voltou do reenvio pelo portal: o que a equipe pediu e o que chegou.
                Antes isso só aparecia no Histórico de Etapas, sem o motivo e sem destaque.
                Some quando o card sai da etapa de volta (ou quando há novo pedido pendente). */}
            {(() => {
              const sol = ((cardCompleto as any)?.solicitacaoReenvio || (card as any).solicitacaoReenvio) as SolicitacaoReenvio | undefined;
              if (!sol || sol.status !== 'atendida' || !Array.isArray(sol.campos) || sol.campos.length === 0) return null;
              const etapaVolta = String(sol.etapaRetornoId || sol.etapaOrigemId || '');
              if (!etapaVolta || String(card.etapaId) !== etapaVolta) return null;

              const hist: any[] = (cardCompleto as any)?.etapaHistory || (card as any).etapaHistory || [];
              const ultimaVolta = [...hist].reverse().find((h: any) => h?.motivo === 'reenvio_anexo_portal');
              const reenviados = new Set<string>((ultimaVolta?.camposReenviados || []).map(String));
              const camposCard: Record<string, any> = (cardCompleto as any)?.campos || card.campos || {};
              const fmt = (d?: string) => {
                if (!d) return '';
                const x = new Date(d);
                return isNaN(x.getTime()) ? '' : x.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
              };

              return (
                <div className="border border-amber-300 rounded-xl overflow-hidden bg-amber-50 shadow-sm">
                  <div className="px-3 py-2.5 border-b border-amber-200 bg-amber-100/70">
                    <h3 className="font-bold text-amber-900 text-sm tracking-tight">📎 Reanalisar: o solicitante reenviou</h3>
                    <p className="text-[11px] text-amber-800 mt-0.5">
                      Pedido por {sol.solicitadoPorNome || 'Equipe interna'}
                      {fmt(sol.solicitadoEm) && ` em ${fmt(sol.solicitadoEm)}`}
                      {fmt(sol.atendidoEm) && ` · reenviado em ${fmt(sol.atendidoEm)}`}
                    </p>
                  </div>
                  <div className="p-3 space-y-2.5">
                    {sol.campos.map(c => {
                      const valor = camposCard[c.campoId];
                      const arquivos: any[] = Array.isArray(valor) ? valor : (valor ? [valor] : []);
                      // Cards antigos não têm camposReenviados: vale a marca no próprio anexo
                      const veio = reenviados.has(String(c.campoId)) || arquivos.some((a: any) => a?.reenviadoPeloPortal);
                      return (
                        <div key={c.campoId} className="bg-white rounded-lg border border-amber-200 p-2.5">
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-xs font-semibold text-gray-800">{c.nome}</span>
                            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${veio ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                              {veio ? 'Reenviado' : 'Não reenviado'}
                            </span>
                          </div>
                          {c.motivo && (
                            <p className="text-[11px] text-gray-600 mt-1">
                              <span className="font-medium">Motivo da reprovação:</span> {c.motivo}
                            </p>
                          )}
                          {veio && arquivos.length > 0 && (
                            <div className="mt-1.5 space-y-1">
                              {arquivos.map((a: any, i: number) => {
                                const nome = typeof a === 'string' ? a : (a?.name || a?.fileName || a?.originalName || 'Arquivo');
                                const url = typeof a === 'string'
                                  ? (a.startsWith('http') || a.startsWith('/') ? a : '')
                                  : (a?.url || a?.webViewLink || '');
                                return url ? (
                                  <a
                                    key={i}
                                    href={url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center justify-between gap-2 text-[11px] text-indigo-700 bg-indigo-50 border border-indigo-100 rounded px-2 py-1 hover:bg-indigo-100 transition-colors"
                                  >
                                    <span className="truncate">📎 {nome}</span>
                                    <span className="font-semibold shrink-0">Abrir</span>
                                  </a>
                                ) : (
                                  <p key={i} className="text-[11px] text-gray-700 truncate">📎 {nome}</p>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {sol.observacao && (
                      <p className="text-[11px] text-gray-600">
                        <span className="font-medium">Observação do pedido:</span> {sol.observacao}
                      </p>
                    )}
                  </div>
                </div>
              );
            })()}

            {/* 📦 Estoque do GIX (etapas com painelEstoqueGix, ex.: Análise do Compras Revenda) */}
            {(etapa as any).painelEstoqueGix && (
              <PainelEstoqueGix
                codigosIniciais={Array.isArray((card.campos as any)?.__produtosGix) ? (card.campos as any).__produtosGix : []}
                onMudarCodigos={(c) => { produtosGixRef.current = c; }}
              />
            )}

            {/* 🧾 Pedido no GIX: editável na etapa com pedidoGix (Negociação), só leitura nas outras */}
            {((etapa as any).pedidoGix || (card.campos as any)?.__pedidoGix) && (
              <PedidoGix
                inicial={(card.campos as any)?.__pedidoGix || null}
                produtosPainel={Array.isArray((card.campos as any)?.__produtosGix) ? (card.campos as any).__produtosGix : []}
                nomeResponsavel={colaboradores?.find(c => String(c.id) === String(card.responsavelIndividualId))?.nome || ''}
                somenteLeitura={!(etapa as any).pedidoGix || !canEditCard}
                onMudar={(p) => { pedidoGixRef.current = p; }}
              />
            )}

            {/* 🧾 OC do GIX fora das etapas com badge (o badge fica junto do campo nelas): só a situação;
                liberar fica nas etapas de OC (André 29/10/2026: aparecia até em Financeiro/Concluído) */}
            {ocForaDaEtapa && (gixOcLoading || gixOcList.length > 0) && isGixEnabled && gixToggles.badgeStatus && (
              <div className="border border-gray-200/80 rounded-xl bg-white shadow-sm ring-1 ring-gray-900/5 px-3 py-2.5">
                <h3 className="font-bold text-gray-900 text-sm tracking-tight">🧾 Ordem de compra no GIX</h3>
                {renderGixOcBadges()}
              </div>
            )}

            {/* Campos Atuais */}
            <div className="border border-gray-200/80 rounded-xl overflow-hidden bg-white shadow-sm ring-1 ring-gray-900/5">
              {/* Cabeçalho */}
              <div className="flex items-center justify-between px-3 py-2.5 bg-slate-50 border-b border-slate-200">
                <div className="flex items-center gap-2.5">
                  <div className="w-7 h-7 rounded-lg bg-slate-800 flex items-center justify-center">
                    <span className="text-white text-xs">📝</span>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 text-sm tracking-tight">
                      {etapasAnteriores.length > 0 ? 'Campos Atuais' : 'Campos'}
                    </h3>
                    <span className="text-[10px] text-gray-500 font-medium">
                      {etapa.campos.filter((campo) => camposVisiveis[campo.id] !== false && !camposOcultosParaMim.has(String(campo.id))).length} campo{etapa.campos.filter((campo) => camposVisiveis[campo.id] !== false && !camposOcultosParaMim.has(String(campo.id))).length > 1 ? 's' : ''}
                    </span>
                  </div>
                </div>
                {/* Botão IA */}
                <button
                  onClick={() => {
                    if (iaMensagens.length === 0) {
                      setIaMensagens([{
                        id: 'welcome',
                        role: 'assistant',
                        content: `Olá! Sou a IA do card **"${card.titulo}"**.\n\nPosso te ajudar a:\n- 📋 Analisar os dados preenchidos\n- 📑 **Ler e analisar documentos/PDFs** anexados\n- ❓ Responder dúvidas sobre este card\n- 💡 Sugerir preenchimentos\n- 📄 Explicar campos\n\nO que precisa?`
                      }]);
                    }
                    setIaAberta(true);
                  }}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold transition-all
                    bg-white text-violet-600 border border-violet-200 hover:bg-violet-50 hover:border-violet-300 shadow-sm"
                  title="Assistente IA para este card"
                >
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round"
                      d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
                  </svg>
                  IA
                </button>
              </div>

              
              {/* Lista de campos */}
              <div className={`p-3 ${(etapa as any).camposProprioNoFim ? 'flex flex-col gap-2.5' : 'space-y-2.5'} overflow-y-auto bg-gray-50/30 ${cardExpandido ? 'max-h-[calc(100vh-260px)]' : 'max-h-[calc(94vh-250px)]'}`}>
                {agruparQuadrosPorTitulo(etapa.campos
                  .filter((campo) => camposVisiveis[campo.id] !== false && !camposOcultosParaMim.has(String(campo.id)))
                  // a lista de aprovadas vira um "Aprovar" embaixo de cada item fixado
                  .filter((campo) => campo.id !== aprovacaoPorItem?.campoId)
                  // campo que vai no pé de um quadro fixado (gruposFixados[].camposEtapa) não repete aqui
                  .filter((campo) => !camposDaEtapaEmGrupos.has(String(campo.id))),
                  (campo) => campo.tipo === 'texto_informativo' ? (
                    // 🏷️ Rótulo da etapa: não é campo de dado, então não tem caixa de valor.
                    // Respeita a formatação configurada (negrito, itálico, tamanho, cor, alinhamento).
                    <div key={campo.id} className="bg-white rounded-lg border border-gray-200/80 p-3">
                      {!campo.ocultarTitulo && (
                        <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                          {campo.nome}
                        </label>
                      )}
                      <div className={`px-3 py-2 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg ${
                        campo.formatacao?.alinhamento === 'centro' ? 'text-center'
                        : campo.formatacao?.alinhamento === 'direita' ? 'text-right'
                        : 'text-left'
                      }`}>
                        <span style={{
                          fontWeight: campo.formatacao?.negrito ? 'bold' : 'normal',
                          fontStyle: campo.formatacao?.italico ? 'italic' : 'normal',
                          fontSize: campo.formatacao?.tamanho === 'pequeno' ? '11px'
                            : campo.formatacao?.tamanho === 'grande' ? '16px' : '13px',
                          color: campo.formatacao?.cor || '#374151'
                        }}>
                          {campo.textoInformativo || ''}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div key={campo.id} className={`rounded-lg border p-3 hover:shadow-sm transition-shadow ${
                      ehMotivoNfNaoLancada(valores[campo.id] ?? card.campos?.[campo.id])
                        ? corAvisoNf.borda
                        : 'bg-white border-gray-200/80'}`}>
                      <div className="flex items-center justify-between mb-1.5">
                        <label className={`block text-xs font-semibold ${
                          ehMotivoNfNaoLancada(valores[campo.id] ?? card.campos?.[campo.id])
                            ? corAvisoNf.titulo
                            : 'text-gray-700'}`}>
                          {ehMotivoNfNaoLancada(valores[campo.id] ?? card.campos?.[campo.id]) && corAvisoNf.icone}
                          {campo.nome}
                          {ehObrigatorio(campo) && modo === 'editar' && (
                            <span className="text-red-500 ml-0.5">*</span>
                          )}
                        </label>
                        {/* Só aparece quando o campo tem valor — então, quando aparece,
                            tem que ser visto: era um texto cinza de 10px que passava batido. */}
                        {modo === 'editar' && hasValue(campo) && !(campo as any).somenteLeitura && !(campo as any).autoSolicitante && (
                          <button
                            type="button"
                            onClick={() => clearField(campo)}
                            className="flex-shrink-0 inline-flex items-center gap-1 text-[11px] font-semibold text-red-600 bg-red-50 border border-red-200 rounded-md px-2 py-1 hover:bg-red-100 hover:border-red-300 hover:text-red-700 transition-colors"
                            title="Limpar este campo"
                          >
                            <XMarkIcon className="w-3 h-3" />
                            Limpar
                          </button>
                        )}
                      </div>
                      {/* GIX - Botão Criar OC (só se a etapa configurou este campo e estamos em localhost) */}
                      {isGixEnabled && gixToggles.criarOc && etapa.gixCriarOcCampoId === campo.id && !hasValue(campo) && modo === 'editar' && (
                        <div className="mb-2">
                          <button
                            type="button"
                            onClick={async () => {
                              // Pré-preenche o form buscando dados dos campos do card (atual + anteriores)
                              const todasEtapasBusca = [etapa, ...etapasAnteriores];
                              // Coleta todos os valores não-vazios de campos que casam com o regex.
                              // Útil pra escolher entre vários candidatos (ex: "Categoria",
                              // "Obras", "Rh/Dp/Mkt", "Compras Indiretas") e priorizar o que
                              // tem código numérico no início.
                              const coletarValores = (regex: RegExp): string[] => {
                                const out: string[] = [];
                                for (const et of todasEtapasBusca) {
                                  const candidatos = (et.campos || []).filter((x: any) => regex.test(x.nome));
                                  for (const c of candidatos) {
                                    const raw = (et.id === etapa.id ? valores[c.id] : undefined)
                                      ?? valoresHistorico[et.id]?.[c.id]
                                      ?? card.campos?.[c.id];
                                    if (raw == null) continue;
                                    if (Array.isArray(raw)) {
                                      for (const item of raw) {
                                        const s = item == null ? '' : String(item).trim();
                                        if (s && s !== '-') out.push(s);
                                      }
                                      continue;
                                    }
                                    const s = String(raw).trim();
                                    if (s && s !== '-') out.push(s);
                                  }
                                }
                                return out;
                              };
                              // Retorna o primeiro valor encontrado (compat).
                              const getTexto = (regex: RegExp): string | undefined => {
                                const vs = coletarValores(regex);
                                return vs[0];
                              };
                              // Retorna o primeiro valor que começa com código numérico
                              // (ex: "267 MATERIAIS DE PINTURA"). Cai pro primeiro qualquer
                              // se nenhum tiver código.
                              const getTextoComCodigo = (regex: RegExp): string | undefined => {
                                const vs = coletarValores(regex);
                                const comCodigo = vs.find(v => /^\s*0*\d{1,6}\b/.test(v));
                                return comCodigo || vs[0];
                              };
                              // Suprimir warning "unused" caso uma das funções não seja usada.
                              void getTexto;
                              const extrairCodigo = (txt?: string): number | null => {
                                if (!txt) return null;
                                const m = txt.match(/^\s*0*(\d{1,6})\b/);
                                return m ? parseInt(m[1], 10) : null;
                              };
                              const parseValor = (txt?: string): number | null => {
                                if (!txt) return null;
                                const limpo = txt.replace(/[^\d,.-]/g, '');
                                if (!limpo) return null;
                                let norm = limpo;
                                if (limpo.includes(',')) norm = limpo.replace(/\./g, '').replace(',', '.');
                                const n = parseFloat(norm);
                                return isNaN(n) ? null : n;
                              };
                              const lookup = async (endpoint: string, codigo: number | null): Promise<{codigo: number; descricao: string} | null> => {
                                if (!codigo) return null;
                                try {
                                  const r = await fetch(`/api/gix${endpoint}?q=${codigo}&limit=5`);
                                  const j = await r.json();
                                  const hit = (j?.results || []).find((x: any) => Number(x.codigo) === codigo);
                                  return hit || null;
                                } catch { return null; }
                              };
                              const lookupPorTexto = async (endpoint: string, texto: string | undefined): Promise<{codigo: number; descricao: string} | null> => {
                                if (!texto || texto.trim().length < 3) return null;
                                try {
                                  const limpo = texto.replace(/<[^>]+>/g, ' ').replace(/&nbsp;/gi, ' ').replace(/\s+/g, ' ').trim();
                                  if (!limpo) return null;
                                  const r = await fetch(`/api/gix${endpoint}?q=${encodeURIComponent(limpo.slice(0, 40))}&limit=5`);
                                  const j = await r.json();
                                  const results = j?.results || [];
                                  if (results.length === 0) return null;
                                  const norm = (s: string) => s.toUpperCase().replace(/\s+/g, ' ').trim();
                                  const alvo = norm(limpo);
                                  const hit = results.find((x: any) => {
                                    const d = norm(String(x.descricao || ''));
                                    return d === alvo || alvo.startsWith(d) || d.startsWith(alvo);
                                  });
                                  return hit || null;
                                } catch { return null; }
                              };

                              const filialTxt = getTextoComCodigo(/filial|filiais|empresa|loja/i);
                              const ccusTxt = getTextoComCodigo(/centro\s+de\s+cust|c\.?\s*cust|ccusto/i);
                              // Tipo de despesa: cobre nome do campo direto ("Tipo de despesa" / "despensa") e
                              // também os campos auxiliares onde o usuário costuma colocar o código (Obras,
                              // Rh/Dp/Mkt, Compras Indiretas, Categoria de compra/serviço, etc).
                              const tipoDespTxt = getTextoComCodigo(
                                /tipo.*desp[ae]ns?a|desp[ae]ns?a|rh.*dp.*mkt|compras\s*ind|obras|categoria/i
                              );
                              const valorTxt = getTexto(/valor\s*m[eé]dio|valor\s*total|valor/i);
                              const observacao = getTexto(/informa[cç][oõ]es?\s*adicion/i) || '';
                              const fornecedorDesc = getTexto(/fornecedor/i);
                              const condicaoDesc = getTexto(/condi[cç][aã]o.*pag|condi[cç][aã]o\s*de\s*pagamento/i);
                              const formaPagTxt = getTexto(/forma\s*de\s*pagamento|forma\s*pagamento/i);

                              // Detecta a "forma de pagamento" (Cartão, Pix, Boleto, Dinheiro,
                              // Transferência) a partir de qualquer texto onde apareça.
                              // Usado quando a "Condição de pagamento" é um texto livre e não
                              // bate com nenhuma condição cadastrada no GIX.
                              const detectarFormaPag = (txt?: string): string | null => {
                                if (!txt) return null;
                                const t = txt.toLowerCase();
                                if (/\bpix\b/.test(t)) return 'PIX';
                                if (/cart[aã]o/.test(t)) return 'CARTAO';
                                if (/boleto/.test(t)) return 'BOLETO';
                                if (/transfer[eê]ncia|\bted\b|\bdoc\b/.test(t)) return 'TRANSFERENCIA';
                                if (/dinheiro|esp[eé]cie/.test(t)) return 'DINHEIRO';
                                return null;
                              };
                              const formaPag = detectarFormaPag(formaPagTxt) || detectarFormaPag(condicaoDesc);

                              // Lookup tolerante para fornecedor: tenta o texto inteiro, depois
                              // pedaços (primeiras 2 palavras significativas) e aceita match por
                              // substring quando não há match exato.
                              const lookupFornecedor = async (texto?: string): Promise<{codigo: number; descricao: string} | null> => {
                                if (!texto) return null;
                                const limpo = texto.replace(/<[^>]+>/g, ' ').replace(/&nbsp;/gi, ' ').replace(/\s+/g, ' ').trim();
                                if (!limpo || limpo.length < 3) return null;
                                const norm = (s: string) => s.toUpperCase().replace(/[^A-Z0-9 ]/g, ' ').replace(/\s+/g, ' ').trim();
                                const alvo = norm(limpo);
                                // 1) busca pelo texto inteiro
                                const tentar = async (q: string) => {
                                  try {
                                    const r = await fetch(`/api/gix/fornecedores?q=${encodeURIComponent(q)}&limit=10`);
                                    const j = await r.json();
                                    return (j?.results || []) as Array<{codigo: number; descricao: string}>;
                                  } catch { return []; }
                                };
                                let results = await tentar(limpo.slice(0, 40));
                                // 2) se vier vazio, tenta com as 2 primeiras palavras
                                if (results.length === 0) {
                                  const palavras = alvo.split(' ').filter(p => p.length >= 3);
                                  if (palavras.length > 0) results = await tentar(palavras.slice(0, 2).join(' '));
                                }
                                if (results.length === 0) return null;
                                // Match exato → match prefixo → substring → primeiro resultado
                                const exato = results.find(x => norm(String(x.descricao || '')) === alvo);
                                if (exato) return exato;
                                const prefixo = results.find(x => {
                                  const d = norm(String(x.descricao || ''));
                                  return d.startsWith(alvo) || alvo.startsWith(d);
                                });
                                if (prefixo) return prefixo;
                                const substr = results.find(x => {
                                  const d = norm(String(x.descricao || ''));
                                  return d.includes(alvo) || alvo.includes(d);
                                });
                                if (substr) return substr;
                                return results.length === 1 ? results[0] : null;
                              };

                              // Lookup de condição de pagamento: prioriza a forma de pagamento
                              // detectada (PIX/CARTAO/BOLETO/...) e procura uma condição cuja
                              // descrição contenha essa palavra. Se não, faz fallback no texto
                              // livre da condição.
                              const lookupCondicao = async (): Promise<{codigo: number; descricao: string} | null> => {
                                const norm = (s: string) => s.toUpperCase().replace(/[^A-Z0-9 ]/g, ' ').replace(/\s+/g, ' ').trim();
                                const tentar = async (q: string) => {
                                  try {
                                    const r = await fetch(`/api/gix/condicoes?q=${encodeURIComponent(q)}&limit=20`);
                                    const j = await r.json();
                                    return (j?.results || []) as Array<{codigo: number; descricao: string}>;
                                  } catch { return []; }
                                };
                                if (formaPag) {
                                  const results = await tentar(formaPag);
                                  if (results.length > 0) {
                                    // Prefere a condição mais "à vista" / sem parcelamento
                                    // (ex: "PIX A VISTA", "CARTAO DE CREDITO"). Critério: menor
                                    // descrição que contenha a palavra-chave.
                                    const compat = results
                                      .filter(x => norm(String(x.descricao || '')).includes(formaPag))
                                      .sort((a, b) => String(a.descricao).length - String(b.descricao).length);
                                    if (compat.length > 0) return compat[0];
                                    return results[0];
                                  }
                                }
                                // Fallback: tenta com o texto livre da condição
                                if (condicaoDesc) return lookupPorTexto('/condicoes', condicaoDesc);
                                return null;
                              };

                              const empresa = extrairCodigo(filialTxt) || 1;
                              const codCc = extrairCodigo(ccusTxt);
                              const codTdes = extrairCodigo(tipoDespTxt);
                              const preco = parseValor(valorTxt) || 0;

                              const [cc, tdes, forn, cond] = await Promise.all([
                                lookup('/centros-custo', codCc),
                                lookup('/tipos-despesa', codTdes),
                                lookupFornecedor(fornecedorDesc),
                                lookupCondicao(),
                              ]);

                              let empresaValidada = 1;
                              try {
                                const re = await fetch('/api/gix/empresas');
                                const je = await re.json();
                                const lista: Array<{codigo: number}> = je?.results || [];
                                if (lista.some(e => Number(e.codigo) === empresa)) empresaValidada = empresa;
                              } catch { /* ignora */ }

                              setGixCriarOcPrefill({
                                empresa: empresaValidada,
                                centro_custo: cc ? Number(cc.codigo) : null,
                                centro_custo_desc: cc?.descricao,
                                tipo_despesa: tdes ? Number(tdes.codigo) : null,
                                tipo_despesa_desc: tdes?.descricao,
                                preco_unitario: preco,
                                observacao,
                                fornecedor: forn ? Number(forn.codigo) : null,
                                fornecedor_desc: forn?.descricao,
                                condicao: cond ? Number(cond.codigo) : null,
                                condicao_desc: cond?.descricao,
                              });
                              setGixCriarOcManual(false);
                              setGixCriarOcAberto(true);
                            }}
                            className="w-full px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-lg shadow-sm flex items-center justify-center gap-1.5"
                          >
                            <span>🧾</span>
                            <span>Criar OC pela nota</span>
                          </button>
                        </div>
                      )}
                      {/* Etapa "OC automática não criada": o mesmo botão, embaixo do motivo */}
                      {isGixEnabled && campo.id === 'campo_ocauto_motivo' && modo === 'editar' && (
                        <div className="mb-2">
                          <button
                            type="button"
                            onClick={() => { setGixCriarOcPrefill(undefined); setGixCriarOcManual(false); setGixCriarOcAberto(true); }}
                            className="w-full px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-lg shadow-sm flex items-center justify-center gap-1.5"
                          >
                            <span>🧾</span>
                            <span>Criar OC pela nota</span>
                          </button>
                        </div>
                      )}
                      {renderCampo(campo)}
                      {/* GIX - Badges de Status das OCs (uma por número, separados por ;, ou espaço) */}
                      {etapa.gixBadgeCampoId === campo.id && renderGixOcBadges()}
                      {etapa.gixBadgeCampoId === campo.id && (cardCompleto as any)?.gixOcAprovacao?.status === 'mov_nao_aceito' && (
                        <div className="mt-1 rounded-md border border-amber-300 bg-amber-50 px-2 py-1 text-[12px] text-amber-800">
                          ⚠ OC {(cardCompleto as any).gixOcAprovacao.numeroOc} já está liberada no GIX, mas com movimento {(cardCompleto as any).gixOcAprovacao.movimento}.
                          Esta etapa só anda sozinha com {((cardCompleto as any).gixOcAprovacao.aceitos || ['E96']).join(', ')}: mova o card à mão.
                        </div>
                      )}
                    </div>
                  ))}

                {/* 🧾 Card de ND (fluxo de Verbas): compras pagas/abatidas com ela */}
                {ehFluxoDeVerbas && <ComprasDaNd verbaCardId={card.id} verbasFlowId={flowId} empresaId={(card as any).empresaId || empresaId} />}

                {/* 📌 Campos puxados de outras etapas (read-only) */}
                <div className="space-y-2.5" style={(etapa as any).camposProprioNoFim ? { order: -1 } : undefined}>
                {(() => {
                const renderPuxado = (campoKey: string) => {
                  const [outraEtapaId, outroCampoId] = campoKey.split(':');
                  if (camposOcultosParaMim.has(String(outroCampoId))) return null;
                  // ✈️ ocultarFixadosPorOpcao: { campoId, itens: { <campo fixado>: <opção> } } — some quando a
                  // opção está marcada no campo de controle (cotação MSX excluída da aprovação, mantida no histórico)
                  const ocp = (etapa as any).ocultarFixadosPorOpcao as { campoId?: string; itens?: Record<string, string> } | undefined;
                  if (ocp?.campoId && ocp.itens?.[outroCampoId] && Array.isArray(valores[ocp.campoId]) && valores[ocp.campoId].includes(ocp.itens[outroCampoId])) return null;
                  const outraEtapa = [etapa, ...etapasAnteriores, ...(todasEtapas || [])].find(
                    (e: any) => String(e.id) === outraEtapaId
                  );
                  const campoOriginal = outraEtapa?.campos?.find((c: any) => c.id === outroCampoId);
                  if (!outraEtapa || !campoOriginal) return null;
                  const valor = valores[outroCampoId];
                  // Etapa que puxa muitos campos (ex.: Aprovação de cotações do Viagens) pode
                  // esconder os que vieram vazios: só aparece a cotação que foi feita.
                  if ((etapa as any).ocultarFixadosVazios &&
                      (valor === undefined || valor === null || valor === '' || (Array.isArray(valor) && valor.length === 0))) return null;
                  if (campoOriginal.tipo === 'parcelas') {
                    return (
                      <div key={`puxado-${campoKey}`} className="bg-purple-50/40 rounded-lg border border-purple-200/60 p-3">
                        <label className="block text-xs font-semibold text-gray-700 flex items-center gap-1.5 mb-1.5">
                          <span title={`Campo da etapa "${outraEtapa.nome}"`}>📌</span>
                          {campoOriginal.nome}
                          <span className="text-[9px] text-purple-600 font-normal">de {outraEtapa.nome}</span>
                        </label>
                        <CampoParcelas
                          valor={valor}
                          opcoesPadrao={campoOriginal.opcoes || []}
                          onChange={(novo) => updateFieldValue(outroCampoId, novo)}
                          podeEditarLista={podeEditarParcelas}
                          podeMarcarPaga={canEditCard}
                          usuario={usuarioParcelas} />
                      </div>
                    );
                  }
                  if ((campoOriginal.tipo as string) === 'cotacoes') {
                    // 📊 mapa de cotação puxado: comparativo e escolha do vencedor (grava nos itens)
                    const its = itensDaSolicitacao();
                    return (
                      <div key={`puxado-${campoKey}`} className="bg-purple-50/40 rounded-lg border border-purple-200/60 p-3">
                        <label className="block text-xs font-semibold text-gray-700 flex items-center gap-1.5 mb-1.5">
                          <span title={`Campo da etapa "${outraEtapa.nome}"`}>📌</span>
                          {campoOriginal.nome}
                          <span className="text-[9px] text-purple-600 font-normal">de {outraEtapa.nome}</span>
                        </label>
                        <CampoCotacoes
                          valor={valor}
                          onChange={(novo) => updateFieldValue(outroCampoId, novo)}
                          itens={its.valor}
                          modo={canEditCard ? 'aprovar' : 'ver'}
                          onAplicarItens={its.id ? (novos) => updateFieldValue(its.id as string, novos) : undefined}
                          usuario={usuarioParcelas} />
                      </div>
                    );
                  }
                  if ((campoOriginal.tipo as string) === 'nd_vinculada') {
                    const cfgNd = ((campoOriginal as any).ndConfig || {}) as { verbasFlowId?: string; valorUsadoCampoId?: string };
                    return (
                      <div key={`puxado-${campoKey}`} className="bg-purple-50/40 rounded-lg border border-purple-200/60 p-3">
                        <label className="block text-xs font-semibold text-gray-700 flex items-center gap-1.5 mb-1.5">
                          <span title={`Campo da etapa "${outraEtapa.nome}"`}>📌</span>
                          {campoOriginal.nome}
                          <span className="text-[9px] text-purple-600 font-normal">de {outraEtapa.nome}</span>
                        </label>
                        <CampoNdVinculada
                          valor={valor}
                          onChange={(novo) => updateFieldValue(outroCampoId, novo)}
                          podeEditar={false}
                          config={cfgNd}
                          compraFlowId={flowId}
                          compraCardId={card.id}
                          valorUsado={cfgNd.valorUsadoCampoId ? valores[cfgNd.valorUsadoCampoId] : undefined} />
                      </div>
                    );
                  }
                  if ((campoOriginal.tipo as string) === 'itens') {
                    // itens puxados na etapa de aprovação: aprovar/reprovar por fornecedor
                    return (
                      <div key={`puxado-${campoKey}`} className="bg-purple-50/40 rounded-lg border border-purple-200/60 p-3">
                        <label className="block text-xs font-semibold text-gray-700 flex items-center gap-1.5 mb-1.5">
                          <span title={`Campo da etapa "${outraEtapa.nome}"`}>📌</span>
                          {campoOriginal.nome}
                          <span className="text-[9px] text-purple-600 font-normal">de {outraEtapa.nome}</span>
                        </label>
                        <CampoItens
                          valor={valor}
                          onChange={(novo) => updateFieldValue(outroCampoId, novo)}
                          podeEditarLista={false}
                          podeAprovar={canEditCard}
                          podeAnexar={canEditCard}
                          usuario={usuarioParcelas}
                          contextoUpload={{ flowId, cardId: card.id, campoId: outroCampoId, flowNome: flowName, cardTitulo: card.titulo || card.id }} />
                      </div>
                    );
                  }
                  if ((campoOriginal.tipo as string) === 'viajantes' || (campoOriginal.tipo as string) === 'quadro_voos') {
                    const ctrlExcl = (campoOriginal as any).excluidasCampoId;
                    return (
                      <div key={`puxado-${campoKey}`} className="bg-purple-50/40 rounded-lg border border-purple-200/60 p-3">
                        <label className="block text-xs font-semibold text-gray-700 flex items-center gap-1.5 mb-1.5">
                          <span title={`Campo da etapa "${outraEtapa.nome}"`}>📌</span>
                          {campoOriginal.nome}
                          <span className="text-[9px] text-purple-600 font-normal">de {outraEtapa.nome}</span>
                        </label>
                        {(campoOriginal.tipo as string) === 'viajantes'
                          ? <ViajantesResumo valor={valor} />
                          : <QuadroVoosField valor={valor} disabled excluidas={ctrlExcl && Array.isArray(valores[ctrlExcl]) ? valores[ctrlExcl] : []} />}
                      </div>
                    );
                  }
                  if (campoOriginal.tipo === 'anexo' || (campoOriginal.tipo as string) === 'arquivo') {
                    // anexo puxado: miniaturas em vez de "[object Object]"
                    const arquivosPuxados = listaDeAnexos(valor);
                    return (
                      <div key={`puxado-${campoKey}`} className="bg-purple-50/40 rounded-lg border border-purple-200/60 p-3">
                        <label className="block text-xs font-semibold text-gray-700 flex items-center gap-1.5 mb-1.5">
                          <span title={`Campo da etapa "${outraEtapa.nome}"`}>📌</span>
                          {campoOriginal.nome}
                          <span className="text-[9px] text-purple-600 font-normal">de {outraEtapa.nome}</span>
                        </label>
                        {arquivosPuxados.length > 0 ? (
                          <MiniaturasAnexos
                            arquivos={arquivosPuxados}
                            onAbrir={(i) => abrirGaleriaDoCampo({ id: outroCampoId, nome: campoOriginal.nome }, arquivosPuxados, i)}
                          />
                        ) : (
                          <div className="px-3 py-2 bg-white border border-gray-200 rounded text-xs text-gray-400">—</div>
                        )}
                        {renderAprovarItem(outroCampoId)}
                      </div>
                    );
                  }
                  const dataIso = typeof valor === 'string' ? valor.match(/^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}:\d{2}))?$/) : null;
                  const valorTexto = ehListaDeItens(valor)
                    ? textoItens(valor)
                    : ehListaDeTrechos(valor)
                    ? textoTrechos(valor)
                    : ehListaDeViajantes(valor)
                    ? textoViajantes(valor)
                    : dataIso
                    ? `${dataIso[3]}/${dataIso[2]}/${dataIso[1]}${dataIso[4] ? ' ' + dataIso[4] : ''}`
                    : Array.isArray(valor)
                    ? valor.join(', ')
                    : (campoOriginal.tipo as string) === 'responsavel' && valor
                    ? nomeResponsavelCampo(valor)
                    : (valor === null || valor === undefined || valor === '' ? '—' : String(valor));
                  // Esta chave do gixBadgeCampoId pode estar referenciando este campo puxado
                  const matchBadge = etapa.gixBadgeCampoId === campoKey || etapa.gixBadgeCampoId === outroCampoId;
                  return (
                    <div key={`puxado-${campoKey}`} className="bg-purple-50/40 rounded-lg border border-purple-200/60 p-3">
                      <div className="flex items-center justify-between mb-1.5">
                        <label className="block text-xs font-semibold text-gray-700 flex items-center gap-1.5">
                          <span title={`Campo da etapa "${outraEtapa.nome}"`}>📌</span>
                          {campoOriginal.nome}
                          <span className="text-[9px] text-purple-600 font-normal">de {outraEtapa.nome}</span>
                        </label>
                      </div>
                      <div className={`px-3 py-2 bg-white border border-gray-200 rounded text-xs text-gray-800 break-words whitespace-pre-line ${String(valorTexto).length > 600 ? 'max-h-72 overflow-y-auto' : ''}`}>
                        {valorTexto}
                      </div>
                      {matchBadge && renderGixOcBadges()}
                      {renderAprovarItem(outroCampoId)}
                    </div>
                  );
                };
                  // Quadros (gruposFixados): os campos fixados de um mesmo item ficam juntos num quadro
                  // com título, e o "Aprovar" do item vai no pé do quadro.
                  const chaves: string[] = etapa.camposOutrasEtapas || [];
                  if (!gruposFixados.length) return chaves.map(renderPuxado);
                  const feitos = new Set<number>();
                  return chaves.map((campoKey) => {
                    const gi = gruposFixados.findIndex((g) => g.campos.includes(campoKey.split(':')[1]));
                    if (gi < 0) return renderPuxado(campoKey);
                    if (feitos.has(gi)) return null;
                    feitos.add(gi);
                    const g = gruposFixados[gi];
                    const filhos = chaves.filter((k) => g.campos.includes(k.split(':')[1])).map(renderPuxado).filter(Boolean);
                    if (filhos.length === 0) return null;
                    const proprios = (((g as any).camposEtapa || []) as string[])
                      .map((id) => etapa.campos.find((c) => String(c.id) === String(id)))
                      .filter((c): c is CampoConfig => !!c && camposVisiveis[c.id] !== false && !camposOcultosParaMim.has(String(c.id)));
                    return (
                      <div key={`grupo-${gi}`} className="rounded-xl border-2 border-purple-200 bg-white p-2.5 space-y-2 shadow-sm">
                        <div className="text-[13px] font-bold text-gray-800 px-0.5">{g.titulo}</div>
                        {filhos}
                        {g.aprovar && renderAprovarItem(g.aprovar, true)}
                        {proprios.map((campo) => (
                          <div key={`proprio-${campo.id}`} className="rounded-lg border-2 border-emerald-200 bg-emerald-50/40 p-3">
                            <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                              {campo.nome}
                              {ehObrigatorio(campo) && modo === 'editar' && <span className="text-red-500 ml-0.5">*</span>}
                            </label>
                            {renderCampo(campo)}
                          </div>
                        ))}
                      </div>
                    );
                  });
                })()}
                </div>
              </div>
            </div>

            {/* Informações de Auditoria */}
            {(card.lastEditedBy || card.lastEditedAt) && (
              <div className="border border-gray-200/80 rounded-xl p-3 bg-gradient-to-br from-gray-50 to-white ring-1 ring-gray-900/5">
                <h3 className="font-semibold text-gray-700 mb-2 text-xs flex items-center gap-2">
                  <span className="w-5 h-5 rounded bg-gray-200 flex items-center justify-center text-[10px]">📝</span>
                  Última Edição
                </h3>
                <div className="text-xs text-gray-500 space-y-1">
                  {card.lastEditedAt && (
                    <p className="flex items-center gap-1.5">
                      <span className="text-gray-400">📅</span>
                      {new Date(card.lastEditedAt).toLocaleString('pt-BR')}
                    </p>
                  )}
                  {card.lastEditedBy && (
                    <p className="flex items-center gap-1.5">
                      <span className="text-gray-400">👤</span>
                      Editado por: <span className="font-medium text-gray-700">{getUserNameById(card.lastEditedBy)}</span>
                    </p>
                  )}
                </div>
              </div>
            )}
            </div> {/* Fim da coluna direita */}
          </div> {/* Fim do layout de 2 colunas */}

          {/* Linha do tempo agora é exibida em modal (botão no header).
              Veja {mostrarTimelineModal && ...} no final do componente. */}
        </div>

        {/* Footer */}
        {/* Badge de Card Conectado no footer (modo visualização) */}
        {modo === 'visualizar' && card.isCardConectado && (
          <div className="px-4 py-2.5 border-t border-gray-100 flex items-center gap-2.5" style={{ backgroundColor: `${card.corCardConectado || '#8B5CF6'}08` }}>
            <svg className="w-4 h-4" style={{ color: card.corCardConectado || '#8B5CF6' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
            </svg>
            {/* Nomeia a origem em vez de só dizer "subprocesso": quando a cadeia tem
                mais de um nível, mostra o caminho inteiro (Fluxo A › Fluxo B › este). */}
            <div className="text-xs font-medium min-w-0" style={{ color: card.corCardConectado || '#8B5CF6' }}>
              {(() => {
                const cadeia = cadeiaOrigens.length
                  ? cadeiaOrigens
                  : (dadosFluxoOrigem ? [{
                      flowNome: dadosFluxoOrigem.flowNome,
                      cardOrigemTitulo: dadosFluxoOrigem.cardOrigemTitulo,
                    }] : []);
                const nomes = cadeia.map(o => o.flowNome).filter(Boolean);
                if (!nomes.length) return 'Card Conectado - Gerado a partir de subprocesso';
                const origemDireta = cadeia[cadeia.length - 1];
                return (
                  <>
                    <span>Card Conectado - Gerado a partir de </span>
                    <span className="font-semibold">{nomes.join(' › ')}</span>
                    {origemDireta?.cardOrigemTitulo && (
                      <span className="opacity-70"> · {origemDireta.cardOrigemTitulo}</span>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        )}
        {modo === 'editar' && (
          <div className="px-5 py-3.5 border-t border-gray-200 flex justify-end gap-3 bg-gradient-to-r from-gray-50 to-white">
            <button
              onClick={fecharCard}
              className="px-4 py-2 border border-gray-300 bg-white text-gray-700 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-all text-sm font-medium shadow-sm"
              disabled={salvando}
            >
              Cancelar
            </button>
            <button
              onClick={handleSave}
              disabled={salvando}
              className="px-5 py-2 bg-gradient-to-r from-gray-900 to-gray-800 text-white rounded-lg hover:from-gray-800 hover:to-gray-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 text-sm font-semibold shadow-md hover:shadow-lg"
            >
              {salvando ? (
                <>
                  <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Salvando...
                </>
              ) : (
                'Salvar Alterações'
              )}
            </button>
          </div>
        )}
      </div>

      {/* Modal de Confirmação de Anexos */}
      {pendingFiles && (() => {
        console.log('🎯 Renderizando modal de confirmação:', {
          pendingFiles,
          existingCount: valores[pendingFiles.campoId]?.length,
          newCount: pendingFiles.files.length
        });
        return (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100] p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Anexos já existem
              </h3>
              <p className="text-gray-600 mb-6">
                Este campo já possui {valores[pendingFiles.campoId]?.length || 0} arquivo(s) anexado(s). 
                O que você deseja fazer com {pendingFiles.files.length === 1 ? 'o novo arquivo' : 'os novos arquivos'}?
              </p>
              
              <div className="space-y-3 mb-6">
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm font-medium text-blue-900">
                    📎 Arquivos existentes: {valores[pendingFiles.campoId]?.length || 0}
                  </p>
                </div>
                <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                  <p className="text-sm font-medium text-green-900">
                    ➕ Novos arquivos: {pendingFiles.files.length}
                  </p>
                </div>
              </div>
              
              <div className="flex flex-col gap-3">
                <button
                  onClick={() => {
                    console.log('➕ Adicionando aos existentes');
                    // Adicionar aos existentes
                    const existing = valores[pendingFiles.campoId] || [];
                    const novosValores = [...existing, ...pendingFiles.files];
                    setValores({ 
                      ...valores, 
                      [pendingFiles.campoId]: novosValores 
                    });
                    // 🔧 FIX iOS: persistir imediatamente após adicionar (vide explicação no input file)
                    try {
                      onSave({
                        ...card,
                        ...(cardCompleto || {}),
                        campos: {
                          ...(cardCompleto?.campos || {}),
                          ...(valores || {}),
                          [pendingFiles.campoId]: novosValores,
                        },
                        updatedAt: new Date(),
                        _skipAutomacoes: true,
                      } as any);
                    } catch (err) {
                      console.error('⚠️ Falha no auto-save após adicionar anexos:', err);
                    }
                    setPendingFiles(null);
                    success(`${pendingFiles.files.length} arquivo(s) adicionado(s) com sucesso!`);
                  }}
                  className="w-full px-4 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all font-medium shadow-lg"
                >
                  ➕ Adicionar aos existentes
                </button>
                
                <button
                  onClick={() => {
                    console.log('🔄 Sobrescrevendo todos');
                    // Sobrescrever
                    setValores({ 
                      ...valores, 
                      [pendingFiles.campoId]: pendingFiles.files 
                    });
                    // 🔧 FIX iOS: persistir imediatamente após sobrescrever
                    try {
                      onSave({
                        ...card,
                        ...(cardCompleto || {}),
                        campos: {
                          ...(cardCompleto?.campos || {}),
                          ...(valores || {}),
                          [pendingFiles.campoId]: pendingFiles.files,
                        },
                        updatedAt: new Date(),
                        _skipAutomacoes: true,
                      } as any);
                    } catch (err) {
                      console.error('⚠️ Falha no auto-save após sobrescrever anexos:', err);
                    }
                    setPendingFiles(null);
                    success(`${pendingFiles.files.length} arquivo(s) substituído(s) com sucesso!`);
                  }}
                  className="w-full px-4 py-3 bg-gradient-to-r from-orange-600 to-red-600 text-white rounded-xl hover:from-orange-700 hover:to-red-700 transition-all font-medium shadow-lg"
                >
                  🔄 Sobrescrever todos
                </button>
                
                <button
                  onClick={() => {
                    console.log('❌ Cancelando upload');
                    setPendingFiles(null);
                  }}
                  className="w-full px-4 py-3 border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-all font-medium"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Modal de Visualização do Percurso no Fluxo - Estilo BPMN */}
      {mostrarFluxoTimeline && (() => {
        // Construir o percurso ordenado do card
        const hist = cardCompleto.etapaHistory || [];
        // Usar TODAS as etapas do fluxo (incluindo posteriores à atual), não só as anteriores
        const disponiveis = todasEtapas.length > 0 ? todasEtapas : [...etapasAnteriores, etapa];
        
        // Todas as etapas do fluxo ATUAL, ordenadas por ordemFluxo
        const todasEtapasAtual = [...disponiveis].sort((a, b) => {
          if (a.ordemFluxo !== undefined && b.ordemFluxo !== undefined) return a.ordemFluxo - b.ordemFluxo;
          if (a.ordemFluxo !== undefined) return -1;
          if (b.ordemFluxo !== undefined) return 1;
          return 0;
        });
        
        // IDs válidos das etapas do fluxo atual (para filtrar histórico herdado de fluxos pai)
        const idsEtapasFluxoAtual = new Set(todasEtapasAtual.map(e => String(e.id)));
        
        // Ordenar histórico cronologicamente, FILTRANDO entradas que não pertencem ao fluxo atual
        const histOrdenado = [...hist]
          .filter(h => idsEtapasFluxoAtual.has(String(h.etapaId)))
          .sort((a, b) => new Date(a.movedAt).getTime() - new Date(b.movedAt).getTime());
        
        // ===== FLUXO DE ORIGEM (card conectado) - SUPORTE A CADEIA RECURSIVA =====
        const temOrigem = card.isCardConectado && dadosFluxoOrigem && dadosFluxoOrigem.etapas.length > 0;
        
        // Usar cadeiaOrigens se disponível (múltiplos fluxos de origem encadeados)
        const cadeiaCompleta = cadeiaOrigens.length > 0 ? cadeiaOrigens : (temOrigem ? [{
          flowNome: dadosFluxoOrigem!.flowNome,
          flowId: '',
          cardOrigemTitulo: dadosFluxoOrigem!.cardOrigemTitulo,
          cardOrigemId: '',
          etapas: dadosFluxoOrigem!.etapas,
          historico: dadosFluxoOrigem!.historico,
          etapaAtualId: dadosFluxoOrigem!.etapaAtualId
        }] : []);
        
        const temCadeia = cadeiaCompleta.length > 0;
        
        // ===== UNIFICAR tudo como um fluxo contínuo =====
        // Etapas unificadas: primeiro as de TODAS as origens na cadeia, depois as do fluxo atual
        // Cada item tem: id, nome, flowLabel (qual processo pertence), isOrigem, poolIndex
        type EtapaUnificada = {
          id: string;
          nome: string;
          flowLabel: string;
          isOrigem: boolean;
          isDestino?: boolean;
          poolIndex: number; // 0..N-1 = pools de origem, N = pool atual
          permiteCriarCards?: boolean;
          permiteMoverPara?: string[];
          campos?: any[];
          participantes?: string[];
          ordemFluxo?: number;
          etapaOriginal?: any;
        };
        
        const etapasUnificadas: EtapaUnificada[] = [];
        
        // Percursos por nível de origem (para construir conexões entre pools)
        const percursosPorNivel: { etapaId: string; movedAt: Date; movedBy?: string; motivo?: string; automacaoNome?: string; }[][] = [];
        
        // Adicionar etapas de CADA nível de origem na cadeia (APENAS visitadas, na ordem do histórico)
        cadeiaCompleta.forEach((nivel, nivelIdx) => {
          const etapasNivel = nivel.etapas || [];
          const histNivel = [...(nivel.historico || [])].sort((a: any, b: any) => new Date(a.movedAt).getTime() - new Date(b.movedAt).getTime());
          
          // IDs de etapas visitadas neste nível
          const visitadasNesteNivel = new Set(histNivel.map((h: any) => String(h.etapaId)));
          
          // Ordem de primeira visita para layout
          const ordemPrimeiraVisita: Record<string, number> = {};
          let ordemContador = 0;
          for (const h of histNivel) {
            const eid = String(h.etapaId);
            if (!(eid in ordemPrimeiraVisita)) {
              ordemPrimeiraVisita[eid] = ordemContador++;
            }
          }
          
          // Só incluir etapas que foram visitadas, ordenadas pela primeira visita
          const etapasFiltradas = etapasNivel
            .filter((et: any) => visitadasNesteNivel.has(String(et.id)))
            .sort((a: any, b: any) => (ordemPrimeiraVisita[String(a.id)] ?? 999) - (ordemPrimeiraVisita[String(b.id)] ?? 999));
          
          etapasFiltradas.forEach((et: any) => {
            etapasUnificadas.push({
              id: `orig${nivelIdx}_${et.id}`,
              nome: et.nome || 'Sem nome',
              flowLabel: nivel.flowNome || `Fluxo Origem ${nivelIdx + 1}`,
              isOrigem: true,
              poolIndex: nivelIdx,
              campos: et.campos,
              etapaOriginal: et,
            });
          });
          
          // Histórico ordenado deste nível
          percursosPorNivel.push(histNivel.map((h: any) => ({
            etapaId: `orig${nivelIdx}_${h.etapaId}`,
            movedAt: new Date(h.movedAt),
          })));
        });
        
        // Adicionar etapas do fluxo atual
        const poolIndexAtual = cadeiaCompleta.length;
        todasEtapasAtual.forEach((et) => {
          etapasUnificadas.push({
            id: String(et.id),
            nome: et.nome || 'Sem nome',
            flowLabel: flowName || 'Fluxo Atual',
            isOrigem: false,
            poolIndex: poolIndexAtual,
            permiteCriarCards: et.permiteCriarCards,
            permiteMoverPara: et.permiteMoverPara,
            campos: et.campos,
            participantes: et.participantes,
            ordemFluxo: et.ordemFluxo,
            etapaOriginal: et,
          });
        });
        
        // ===== FLUXOS DE DESTINO (subprocessos gerados) =====
        const percursosPorNivelDestino: { etapaId: string; movedAt: Date; movedBy?: string; motivo?: string; automacaoNome?: string; }[][] = [];
        cadeiaDestinos.forEach((nivel, nivelIdx) => {
          const poolIdx = poolIndexAtual + 1 + nivelIdx;
          const etapasNivel = nivel.etapas || [];
          const histNivel = [...(nivel.historico || [])].sort((a: any, b: any) => new Date(a.movedAt).getTime() - new Date(b.movedAt).getTime());
          
          // IDs de etapas visitadas neste nível
          const visitadasNesteNivel = new Set(histNivel.map((h: any) => String(h.etapaId)));
          
          // Ordem de primeira visita para layout
          const ordemPrimeiraVisita: Record<string, number> = {};
          let ordemContador = 0;
          for (const h of histNivel) {
            const eid = String(h.etapaId);
            if (!(eid in ordemPrimeiraVisita)) {
              ordemPrimeiraVisita[eid] = ordemContador++;
            }
          }
          
          // Só incluir etapas que foram visitadas, ordenadas pela primeira visita
          const etapasFiltradas = etapasNivel
            .filter((et: any) => visitadasNesteNivel.has(String(et.id)))
            .sort((a: any, b: any) => (ordemPrimeiraVisita[String(a.id)] ?? 999) - (ordemPrimeiraVisita[String(b.id)] ?? 999));
          
          etapasFiltradas.forEach((et: any) => {
            etapasUnificadas.push({
              id: `dest${nivelIdx}_${et.id}`,
              nome: et.nome || 'Sem nome',
              flowLabel: nivel.flowNome || `Subprocesso ${nivelIdx + 1}`,
              isOrigem: false,
              isDestino: true,
              poolIndex: poolIdx,
              campos: et.campos,
              etapaOriginal: et,
            });
          });
          
          // Histórico ordenado deste nível destino
          percursosPorNivelDestino.push(histNivel.map((h: any) => ({
            etapaId: `dest${nivelIdx}_${h.etapaId}`,
            movedAt: new Date(h.movedAt),
          })));
        });
        
        // Percurso unificado do card (todos os históricos da cadeia de origens + atual + destinos)
        const percursoUnificado: { etapaId: string; movedAt: Date; movedBy?: string; motivo?: string; automacaoNome?: string; }[] = [];
        percursosPorNivel.forEach(percurso => {
          percurso.forEach(p => {
            percursoUnificado.push(p);
          });
        });
        histOrdenado.forEach((h) => {
          percursoUnificado.push({
            etapaId: String(h.etapaId),
            movedAt: new Date(h.movedAt),
            movedBy: h.movedBy,
            motivo: h.motivo,
            automacaoNome: h.automacaoNome,
          });
        });
        percursosPorNivelDestino.forEach(percurso => {
          percurso.forEach(p => {
            percursoUnificado.push(p);
          });
        });
        
        // IDs de etapas visitadas (unificado)
        const etapasVisitadasIds = new Set(percursoUnificado.map(p => p.etapaId));
        
        // Mapa: etapaId → número sequencial da primeira visita no percurso
        const ordemVisita: Record<string, number> = {};
        let contadorVisita = 1;
        for (const p of percursoUnificado) {
          if (!ordemVisita[p.etapaId]) {
            ordemVisita[p.etapaId] = contadorVisita++;
          }
        }
        
        // Conexões percorridas unificadas
        const conexoesPercurso: { origem: string; destino: string; label: string; tipo: string }[] = [];
        for (let i = 0; i < percursoUnificado.length - 1; i++) {
          const origem = percursoUnificado[i].etapaId;
          const destino = percursoUnificado[i + 1].etapaId;
          if (origem !== destino) {
            const motivo = percursoUnificado[i + 1].motivo;
            // Transição entre processos (qualquer nível de origem → próximo nível ou atual)
            const nivelOrigem = origem.match(/^orig(\d+)_/) ? parseInt(origem.match(/^orig(\d+)_/)![1]) : -1;
            const nivelDestino = destino.match(/^orig(\d+)_/) ? parseInt(destino.match(/^orig(\d+)_/)![1]) : -1;
            const isTransicaoProcesso = nivelOrigem !== nivelDestino || (nivelOrigem >= 0 && nivelDestino === -1);
            const label = isTransicaoProcesso
              ? 'Gerou Subprocesso'
              : motivo === 'automacao' 
                ? `⚡ ${percursoUnificado[i + 1].automacaoNome || 'Automação'}`
                : motivo === 'dragdrop' ? 'Movimentação manual'
                : motivo === 'criado' ? 'Criado'
                : `Movimentação`;
            const jaExiste = conexoesPercurso.some(c => c.origem === origem && c.destino === destino);
            if (!jaExiste) {
              conexoesPercurso.push({ origem, destino, label, tipo: isTransicaoProcesso ? 'subprocesso' : motivo === 'automacao' ? 'automacao' : 'permitido' });
            }
          }
        }
        
        // Criar conexões de subprocesso entre cada nível da cadeia → próximo nível
        for (let nivelIdx = 0; nivelIdx < cadeiaCompleta.length; nivelIdx++) {
          const percursoNivel = percursosPorNivel[nivelIdx];
          if (percursoNivel.length === 0) continue;
          
          // Último estágio deste nível → primeiro estágio do próximo nível
          const ultimoEsteNivel = percursoNivel[percursoNivel.length - 1].etapaId;
          
          let primeiroProximoNivel: string | null = null;
          if (nivelIdx < cadeiaCompleta.length - 1) {
            // Próximo nível de origem
            const percursoProximo = percursosPorNivel[nivelIdx + 1];
            if (percursoProximo.length > 0) {
              primeiroProximoNivel = percursoProximo[0].etapaId;
            } else {
              // Sem percurso no próximo, usar primeira etapa definida
              const etapasProximo = cadeiaCompleta[nivelIdx + 1].etapas;
              if (etapasProximo.length > 0) {
                primeiroProximoNivel = `orig${nivelIdx + 1}_${etapasProximo[0].id}`;
              }
            }
          } else {
            // Último nível de origem → primeiro do fluxo atual
            if (todasEtapasAtual.length > 0) {
              primeiroProximoNivel = String(todasEtapasAtual[0].id);
            }
          }
          
          if (primeiroProximoNivel) {
            const jaExiste = conexoesPercurso.some(c => c.origem === ultimoEsteNivel && c.destino === primeiroProximoNivel);
            if (!jaExiste) {
              conexoesPercurso.push({ origem: ultimoEsteNivel, destino: primeiroProximoNivel, label: 'Gerou Subprocesso', tipo: 'subprocesso' });
            }
          }
        }
        
        // Caso legado: se temOrigem e não tem cadeia, mas tem percursoOrigem vazio (garantir link)
        if (temCadeia && percursosPorNivel.every(p => p.length === 0) && todasEtapasAtual.length > 0) {
          // Se não há histórico em nenhum nível da cadeia, mas existem etapas, criar link visual
          for (let nivelIdx = 0; nivelIdx < cadeiaCompleta.length; nivelIdx++) {
            const etapasNivel = cadeiaCompleta[nivelIdx].etapas;
            if (etapasNivel.length === 0) continue;
            const ultimoNivel = `orig${nivelIdx}_${etapasNivel[etapasNivel.length - 1].id}`;
            let primeiroProximo: string | null = null;
            if (nivelIdx < cadeiaCompleta.length - 1) {
              const etapasProximo = cadeiaCompleta[nivelIdx + 1].etapas;
              if (etapasProximo.length > 0) primeiroProximo = `orig${nivelIdx + 1}_${etapasProximo[0].id}`;
            } else {
              if (todasEtapasAtual.length > 0) primeiroProximo = String(todasEtapasAtual[0].id);
            }
            if (primeiroProximo) {
              const jaExiste = conexoesPercurso.some(c => c.origem === ultimoNivel && c.destino === primeiroProximo);
              if (!jaExiste) {
                conexoesPercurso.push({ origem: ultimoNivel, destino: primeiroProximo, label: 'Gerou Subprocesso', tipo: 'subprocesso' });
              }
            }
          }
        }
        
        // Criar conexões de subprocesso do fluxo atual → primeiro destino
        if (cadeiaDestinos.length > 0) {
          // Encontrar a etapa do fluxo atual que gerou o subprocesso (última etapa visitada antes do destino)
          // Usar a etapa atual do card como ponto de saída, ou a última visitada do fluxo atual
          const etapasAtualVisitadas = percursoUnificado.filter(p => !p.etapaId.startsWith('orig') && !p.etapaId.startsWith('dest'));
          const ultimaEtapaAtual = etapasAtualVisitadas.length > 0 ? etapasAtualVisitadas[etapasAtualVisitadas.length - 1].etapaId : (todasEtapasAtual.length > 0 ? String(todasEtapasAtual[todasEtapasAtual.length - 1].id) : null);
          
          if (ultimaEtapaAtual) {
            // Buscar a etapa que tem permiteCriarCards ou que gerou o subprocesso
            // Conectar da etapa que gerou → primeira etapa do primeiro destino
            const primeiroDestinoEtapas = cadeiaDestinos[0].etapas;
            const percursoDestino0 = percursosPorNivelDestino[0];
            let primeiroDestino: string | null = null;
            if (percursoDestino0 && percursoDestino0.length > 0) {
              primeiroDestino = percursoDestino0[0].etapaId;
            } else if (primeiroDestinoEtapas.length > 0) {
              primeiroDestino = `dest0_${primeiroDestinoEtapas[0].id}`;
            }
            
            if (primeiroDestino) {
              // Encontrar qual etapa do fluxo atual realmente gerou o subprocesso
              // (a que tem permiteCriarCards ou a última visitada)
              let etapaGeradora = ultimaEtapaAtual;
              for (const et of todasEtapasAtual) {
                if (et.permiteCriarCards) {
                  // Verificar se essa etapa foi visitada
                  if (etapasVisitadasIds.has(String(et.id))) {
                    etapaGeradora = String(et.id);
                  }
                }
              }
              
              const jaExiste = conexoesPercurso.some(c => c.origem === etapaGeradora && c.destino === primeiroDestino);
              if (!jaExiste) {
                conexoesPercurso.push({ origem: etapaGeradora, destino: primeiroDestino, label: 'Gerou Subprocesso', tipo: 'subprocesso' });
              }
            }
          }
          
          // Conexões entre níveis de destino
          for (let nivelIdx = 0; nivelIdx < cadeiaDestinos.length - 1; nivelIdx++) {
            const percursoNivel = percursosPorNivelDestino[nivelIdx];
            const etapasNivel = cadeiaDestinos[nivelIdx].etapas;
            let ultimoNivel: string | null = null;
            if (percursoNivel && percursoNivel.length > 0) {
              ultimoNivel = percursoNivel[percursoNivel.length - 1].etapaId;
            } else if (etapasNivel.length > 0) {
              ultimoNivel = `dest${nivelIdx}_${etapasNivel[etapasNivel.length - 1].id}`;
            }
            
            const percursoProximo = percursosPorNivelDestino[nivelIdx + 1];
            const etapasProximo = cadeiaDestinos[nivelIdx + 1].etapas;
            let primeiroProximo: string | null = null;
            if (percursoProximo && percursoProximo.length > 0) {
              primeiroProximo = percursoProximo[0].etapaId;
            } else if (etapasProximo.length > 0) {
              primeiroProximo = `dest${nivelIdx + 1}_${etapasProximo[0].id}`;
            }
            
            if (ultimoNivel && primeiroProximo) {
              const jaExiste = conexoesPercurso.some(c => c.origem === ultimoNivel && c.destino === primeiroProximo);
              if (!jaExiste) {
                conexoesPercurso.push({ origem: ultimoNivel, destino: primeiroProximo, label: 'Gerou Subprocesso', tipo: 'subprocesso' });
              }
            }
          }
        }
        
        // Conexões do fluxo atual (permiteMoverPara + automações)
        const conexoesFluxo: { origem: string; destino: string; label: string; tipo: 'permitido' | 'automacao' }[] = [];
        todasEtapasAtual.forEach(et => {
          if (et.permiteMoverPara && et.permiteMoverPara.length > 0) {
            et.permiteMoverPara.forEach(destId => {
              const destEtapa = todasEtapasAtual.find(e => String(e.id) === String(destId));
              if (destEtapa) {
                conexoesFluxo.push({
                  origem: String(et.id),
                  destino: String(destId),
                  tipo: 'permitido',
                  label: `Movimentação: ${destEtapa.nome?.substring(0, 15)}${(destEtapa.nome?.length || 0) > 15 ? '...' : ''}`
                });
              }
            });
          }
        });
        
        // Adicionar conexões das automações
        automacoes?.forEach((automacao: any) => {
          const etapaOrigemId = automacao.etapaOrigemId || automacao.etapaId;
          automacao.acoes?.forEach((acao: any) => {
            if (acao.tipo === 'moverCard' || acao.tipo === 'mover_card' || acao.tipo === 'moveCard') {
              if (acao.configuracao?.regrasMover && acao.configuracao.regrasMover.length > 0) {
                acao.configuracao.regrasMover.forEach((regra: any) => {
                  if (regra.acaoEntao?.tipo === 'mover' && regra.acaoEntao.etapaDestinoId) {
                    const campo = todasEtapasAtual.flatMap(e => e.campos || []).find(c => c.id === regra.condicao?.campoId);
                    const jaExiste = conexoesFluxo.some(c => c.origem === String(etapaOrigemId) && c.destino === String(regra.acaoEntao.etapaDestinoId) && c.tipo === 'automacao');
                    if (!jaExiste) {
                      conexoesFluxo.push({
                        origem: String(etapaOrigemId),
                        destino: String(regra.acaoEntao.etapaDestinoId),
                        tipo: 'automacao',
                        label: `SE ${campo?.nome || 'campo'} ${regra.condicao?.operador === 'preenchido' ? 'preenchido' : regra.condicao?.operador || ''}...`
                      });
                    }
                  }
                  if (regra.acaoSenao?.tipo === 'mover' && regra.acaoSenao.etapaDestinoId) {
                    const jaExiste = conexoesFluxo.some(c => c.origem === String(etapaOrigemId) && c.destino === String(regra.acaoSenao.etapaDestinoId) && c.tipo === 'automacao');
                    if (!jaExiste) {
                      conexoesFluxo.push({
                        origem: String(etapaOrigemId),
                        destino: String(regra.acaoSenao.etapaDestinoId),
                        tipo: 'automacao',
                        label: automacao.nome || 'Automação'
                      });
                    }
                  }
                });
              } else {
                const destino = acao.configuracao?.etapaDestinoId || acao.etapaDestinoId || acao.etapaDestino;
                if (destino) {
                  const jaExiste = conexoesFluxo.some(c => c.origem === String(etapaOrigemId) && c.destino === String(destino));
                  if (!jaExiste) {
                    conexoesFluxo.push({
                      origem: String(etapaOrigemId),
                      destino: String(destino),
                      tipo: 'automacao',
                      label: automacao.nome || 'Automação'
                    });
                  }
                }
              }
            }
          });
        });
        
        // Adicionar conexões de origem → origem (sequencial apenas entre etapas visitadas)
        if (temCadeia) {
          cadeiaCompleta.forEach((_nivel, nivelIdx) => {
            // Pegar apenas as etapas visitadas da pool de origem, na ordem que estão no layout
            const etapasDestaPool = etapasUnificadas.filter(e => e.isOrigem && e.poolIndex === nivelIdx);
            for (let i = 0; i < etapasDestaPool.length - 1; i++) {
              const origId = etapasDestaPool[i].id;
              const destId = etapasDestaPool[i + 1].id;
              const jaExiste = conexoesFluxo.some(c => c.origem === origId && c.destino === destId);
              if (!jaExiste) {
                conexoesFluxo.push({
                  origem: origId,
                  destino: destId,
                  tipo: 'permitido',
                  label: `Movimentação: ${etapasDestaPool[i + 1].nome?.substring(0, 15) || ''}`
                });
              }
            }
          });
        }
        
        // Adicionar conexões sequenciais dentro de cada nível de destino (apenas entre etapas visitadas)
        cadeiaDestinos.forEach((_nivel, nivelIdx) => {
          const poolIdx = poolIndexAtual + 1 + nivelIdx;
          const etapasDestaPool = etapasUnificadas.filter(e => e.isDestino && e.poolIndex === poolIdx);
          for (let i = 0; i < etapasDestaPool.length - 1; i++) {
            const origId = etapasDestaPool[i].id;
            const destId = etapasDestaPool[i + 1].id;
            const jaExiste = conexoesFluxo.some(c => c.origem === origId && c.destino === destId);
            if (!jaExiste) {
              conexoesFluxo.push({
                origem: origId,
                destino: destId,
                tipo: 'permitido',
                label: `Movimentação: ${etapasDestaPool[i + 1].nome?.substring(0, 15) || ''}`
              });
            }
          }
        });
        
        // Layout: Calcular posições de TODAS as etapas unificadas em um grid contínuo
        const cardW = 200;
        const cardH = 100;
        const gapH = 120;
        const gapV = 200;
        const maxPorLinha = 5;
        const margemX = 160;
        const margemY = (temCadeia || cadeiaDestinos.length > 0) ? 90 : 60;
        
        const layoutMap: Record<string, { x: number; y: number }> = {};
        const temMultiplasPools = temCadeia || cadeiaDestinos.length > 0;
        if (temMultiplasPools) {
          // Separar em múltiplas pools: cada nível de origem como pool, depois o fluxo atual, depois destinos
          let currentPoolY = margemY;
          
          // Pools de origem
          for (let nivelIdx = 0; nivelIdx < cadeiaCompleta.length; nivelIdx++) {
            const etapasPool = etapasUnificadas.filter(e => e.poolIndex === nivelIdx);
            etapasPool.forEach((et, idx) => {
              const col = idx % maxPorLinha;
              const row = Math.floor(idx / maxPorLinha);
              layoutMap[et.id] = {
                x: margemX + col * (cardW + gapH),
                y: currentPoolY + row * (cardH + gapV)
              };
            });
            const rowsPool = Math.max(1, Math.ceil(etapasPool.length / maxPorLinha));
            currentPoolY += rowsPool * (cardH + gapV);
          }
          
          // Pool do fluxo atual
          const etapasAtualArr = etapasUnificadas.filter(e => e.poolIndex === poolIndexAtual);
          etapasAtualArr.forEach((et, idx) => {
            const col = idx % maxPorLinha;
            const row = Math.floor(idx / maxPorLinha);
            layoutMap[et.id] = {
              x: margemX + col * (cardW + gapH),
              y: currentPoolY + row * (cardH + gapV)
            };
          });
          const rowsAtual = Math.max(1, Math.ceil(etapasAtualArr.length / maxPorLinha));
          currentPoolY += rowsAtual * (cardH + gapV);
          
          // Pools de destino (subprocessos gerados)
          for (let nivelIdx = 0; nivelIdx < cadeiaDestinos.length; nivelIdx++) {
            const poolIdx = poolIndexAtual + 1 + nivelIdx;
            const etapasPool = etapasUnificadas.filter(e => e.poolIndex === poolIdx);
            etapasPool.forEach((et, idx) => {
              const col = idx % maxPorLinha;
              const row = Math.floor(idx / maxPorLinha);
              layoutMap[et.id] = {
                x: margemX + col * (cardW + gapH),
                y: currentPoolY + row * (cardH + gapV)
              };
            });
            const rowsPool = Math.max(1, Math.ceil(etapasPool.length / maxPorLinha));
            currentPoolY += rowsPool * (cardH + gapV);
          }
        } else {
          // Sem origem: layout normal sequencial
          etapasUnificadas.forEach((et, idx) => {
            const col = idx % maxPorLinha;
            const row = Math.floor(idx / maxPorLinha);
            layoutMap[et.id] = {
              x: margemX + col * (cardW + gapH),
              y: margemY + row * (cardH + gapV)
            };
          });
        }
        
        // Calcular bounding boxes das pools (grupos de fluxo)
        type PoolInfo = { label: string; isOrigem: boolean; poolIndex: number; minX: number; maxX: number; minY: number; maxY: number };
        const poolsMap: Record<string, PoolInfo> = {};
        etapasUnificadas.forEach((et) => {
          const pos = layoutMap[et.id];
          if (!pos) return;
          const key = `pool_${et.poolIndex}`;
          if (!poolsMap[key]) {
            poolsMap[key] = { label: et.flowLabel, isOrigem: et.isOrigem, poolIndex: et.poolIndex, minX: pos.x, maxX: pos.x + cardW, minY: pos.y, maxY: pos.y + cardH };
          } else {
            poolsMap[key].minX = Math.min(poolsMap[key].minX, pos.x);
            poolsMap[key].maxX = Math.max(poolsMap[key].maxX, pos.x + cardW);
            poolsMap[key].minY = Math.min(poolsMap[key].minY, pos.y);
            poolsMap[key].maxY = Math.max(poolsMap[key].maxY, pos.y + cardH);
          }
        });
        const pools = Object.values(poolsMap);

        // Dimensões totais
        const posArr = Object.values(layoutMap);
        const svgW = posArr.length > 0 ? Math.max(...posArr.map(p => p.x)) + cardW + margemX + 50 : 800;
        const svgH = posArr.length > 0 ? Math.max(...posArr.map(p => p.y)) + cardH + margemY + 80 : 400;
        
        // Verificar se conexão foi percorrida
        const conexaoPercorrida = (origem: string, destino: string) => {
          return conexoesPercurso.some(c => c.origem === origem && c.destino === destino);
        };
        
        // Cores por sequência
        const coresSeq = [
          'from-red-500 to-red-600',
          'from-blue-500 to-blue-600',
          'from-green-500 to-green-600',
          'from-yellow-500 to-yellow-600',
          'from-purple-500 to-purple-600',
          'from-pink-500 to-pink-600',
          'from-indigo-500 to-indigo-600',
          'from-teal-500 to-teal-600',
          'from-orange-500 to-orange-600',
          'from-cyan-500 to-cyan-600'
        ];

        // 🎯 Mostrar APENAS conexões que aconteceram de fato (percurso real)
        // Para cada movimento real, herdar o tipo (permitido/automacao) da conexão configurada
        // se existir, mantendo o destaque visual de automações que dispararam.
        const todasConexoesRender: { origem: string; destino: string; label: string; tipo: string }[] = [];
        conexoesPercurso.forEach(cp => {
          if (todasConexoesRender.some(c => c.origem === cp.origem && c.destino === cp.destino)) return;
          // Tenta encontrar conexão configurada pra herdar tipo (automacao/permitido) e label
          const config = conexoesFluxo.find(c => c.origem === cp.origem && c.destino === cp.destino);
          todasConexoesRender.push({
            origem: cp.origem,
            destino: cp.destino,
            tipo: config?.tipo || cp.tipo || 'permitido',
            label: config?.label || cp.label
          });
        });

        return (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[10000] p-0 backdrop-blur-sm" ref={fluxoModalRef} onClick={() => { setMostrarFluxoTimeline(false); if (document.fullscreenElement) document.exitFullscreen().catch(() => {}); setFluxoFullScreen(false); }}>
            <div 
              className="bg-white shadow-2xl overflow-hidden flex flex-col w-full h-full"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between px-5 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white flex-shrink-0">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-white/20 rounded-xl flex items-center justify-center">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-base font-bold">Percurso no Fluxo</h2>
                    <p className="text-blue-100 text-xs">
                      {card.titulo} • {percursoUnificado.length} movimentação{percursoUnificado.length > 1 ? 'ões' : ''} • {etapasVisitadasIds.size}/{etapasUnificadas.length} etapas
                      {temCadeia && <span className="text-purple-200"> • Origem: {cadeiaCompleta.map(c => c.flowNome).join(' → ')}</span>}
                      {cadeiaDestinos.length > 0 && <span className="text-green-200"> • Destino: {cadeiaDestinos.map(c => c.flowNome).join(' → ')}</span>}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  {/* Legenda */}
                  <div className="hidden md:flex items-center gap-3 text-[10px]">
                    <div className="flex items-center gap-1">
                      <div className="w-3 h-3 rounded-full bg-green-400 border-2 border-white shadow ring-2 ring-green-300"></div>
                      <span className="text-blue-100">Atual</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-3 h-3 rounded-full bg-white shadow"></div>
                      <span className="text-blue-100">Visitada</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-3 h-3 rounded bg-white/30"></div>
                      <span className="text-blue-100">Não visitada</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-6 h-0.5 bg-blue-300"></div>
                      <span className="text-blue-100">Permitido</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-6 h-0.5 bg-purple-300 border-dashed" style={{borderTop: '2px dashed #C4B5FD'}}></div>
                      <span className="text-blue-100">Automação</span>
                    </div>
                    {temCadeia && (
                      <div className="flex items-center gap-1">
                        <div className="w-3 h-3 rounded border-2 border-purple-400 bg-purple-500/20"></div>
                        <span className="text-blue-100">Fluxo Origem</span>
                      </div>
                    )}
                    {cadeiaDestinos.length > 0 && (
                      <div className="flex items-center gap-1">
                        <div className="w-3 h-3 rounded border-2 border-green-400 bg-green-500/20"></div>
                        <span className="text-blue-100">Fluxo Destino</span>
                      </div>
                    )}
                  </div>
                  <button
                    onClick={toggleFluxoFullScreen}
                    className="w-8 h-8 bg-white/20 hover:bg-white/30 rounded-lg flex items-center justify-center transition-colors"
                    title={fluxoFullScreen ? 'Sair do modo tela cheia (F11)' : 'Tela cheia (F11)'}
                  >
                    {fluxoFullScreen ? (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 9V4.5M9 9H4.5M9 9L3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5l5.25 5.25" /></svg>
                    ) : (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15" /></svg>
                    )}
                  </button>
                  <button
                    onClick={() => { setMostrarFluxoTimeline(false); if (document.fullscreenElement) document.exitFullscreen().catch(() => {}); setFluxoFullScreen(false); }}
                    className="w-8 h-8 bg-white/20 hover:bg-white/30 rounded-lg flex items-center justify-center transition-colors"
                  >
                    <XMarkIcon className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Fluxograma BPMN com pan e zoom */}
              <div 
                className="flex-1 overflow-hidden bg-gradient-to-br from-blue-50 via-white to-purple-50 relative"
                style={{ cursor: fluxoIsPanning ? 'grabbing' : 'grab' }}
                onMouseDown={(e) => {
                  if (e.button === 0) {
                    setFluxoIsPanning(true);
                    setFluxoPanStart({ x: e.clientX - fluxoPan.x, y: e.clientY - fluxoPan.y });
                  }
                }}
                onMouseMove={(e) => {
                  if (fluxoIsPanning) {
                    setFluxoPan({ x: e.clientX - fluxoPanStart.x, y: e.clientY - fluxoPanStart.y });
                  }
                }}
                onMouseUp={() => setFluxoIsPanning(false)}
                onMouseLeave={() => setFluxoIsPanning(false)}
                onWheel={(e) => {
                  e.preventDefault();
                  const delta = e.deltaY > 0 ? -0.1 : 0.1;
                  setFluxoZoom(z => Math.min(2, Math.max(0.2, z + delta)));
                }}
              >
                {/* Botões de zoom */}
                <div className="absolute top-3 right-3 z-30 flex flex-col gap-1">
                  <button
                    onClick={() => setFluxoZoom(z => Math.min(2, z + 0.15))}
                    className="w-8 h-8 bg-white/90 hover:bg-white border border-gray-300 rounded-lg flex items-center justify-center shadow-sm text-gray-700 font-bold text-lg"
                    title="Zoom in"
                  >+</button>
                  <button
                    onClick={() => setFluxoZoom(z => Math.max(0.2, z - 0.15))}
                    className="w-8 h-8 bg-white/90 hover:bg-white border border-gray-300 rounded-lg flex items-center justify-center shadow-sm text-gray-700 font-bold text-lg"
                    title="Zoom out"
                  >−</button>
                  <button
                    onClick={() => { setFluxoZoom(1); setFluxoPan({ x: 0, y: 0 }); }}
                    className="w-8 h-8 bg-white/90 hover:bg-white border border-gray-300 rounded-lg flex items-center justify-center shadow-sm text-gray-500 text-[10px] font-medium"
                    title="Resetar zoom e posição"
                  >1:1</button>
                  <div className="text-[9px] text-gray-400 text-center mt-0.5">{Math.round(fluxoZoom * 100)}%</div>
                </div>
                <div 
                  style={{ 
                    transform: `translate(${fluxoPan.x}px, ${fluxoPan.y}px) scale(${fluxoZoom})`, 
                    transformOrigin: '0 0',
                    userSelect: 'none',
                  }}
                >
                <div className="relative" style={{ width: `${svgW}px`, minHeight: `${svgH}px` }}>
                  {/* SVG das conexões */}
                  <svg className="absolute inset-0 pointer-events-none" style={{ width: `${svgW}px`, height: `${svgH}px` }}>
                    <defs>
                      <marker id="arrowBlue" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                        <path d="M 0 0 L 10 5 L 0 10 z" fill="#3B82F6" />
                      </marker>
                      <marker id="arrowPurple" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                        <path d="M 0 0 L 10 5 L 0 10 z" fill="#8B5CF6" />
                      </marker>
                      <marker id="arrowGreen" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                        <path d="M 0 0 L 10 5 L 0 10 z" fill="#10B981" />
                      </marker>
                    </defs>
                    
                    {todasConexoesRender.map((conexao, idx) => {
                      const orig = layoutMap[conexao.origem];
                      const dest = layoutMap[conexao.destino];
                      if (!orig || !dest) return null;
                      
                      const isPercorrida = conexaoPercorrida(conexao.origem, conexao.destino);
                      const isSubprocesso = conexao.tipo === 'subprocesso';
                      const cor = isPercorrida ? '#10B981' : (isSubprocesso || conexao.tipo === 'automacao') ? '#8B5CF6' : '#3B82F6';
                      const markerId = isPercorrida ? 'arrowGreen' : (isSubprocesso || conexao.tipo === 'automacao') ? 'arrowPurple' : 'arrowBlue';
                      const dasharray = isSubprocesso ? '8,4' : conexao.tipo === 'automacao' ? '5,5' : 'none';
                      const strokeW = isPercorrida ? 3 : isSubprocesso ? 3 : 2;
                      const opacidade = isPercorrida ? 1 : isSubprocesso ? 0.8 : 0.5;
                      
                      // Calcular path ortogonal
                      const x1c = orig.x + cardW / 2;
                      const y1c = orig.y + cardH / 2;
                      const x2c = dest.x + cardW / 2;
                      const y2c = dest.y + cardH / 2;
                      
                      const mesmaLinha = Math.abs(y1c - y2c) < 50;
                      const mesmaColuna = Math.abs(x1c - x2c) < 50;
                      
                      // Detectar se existe conexão bidirecional (A→B e B→A)
                      const temReversa = todasConexoesRender.some(c => c.origem === conexao.destino && c.destino === conexao.origem);
                      // Se é bidirecional, a conexão "de volta" (onde x da origem > x do destino na mesma linha) vai por baixo
                      const isConexaoReversa = temReversa && mesmaLinha && x1c > x2c;
                      const isConexaoDireta = temReversa && mesmaLinha && x1c < x2c;
                      
                      let path = '';
                      let labelX = (x1c + x2c) / 2;
                      let labelY = (y1c + y2c) / 2;
                      
                      if (mesmaLinha) {
                        if (isConexaoDireta) {
                          // Conexão direta (A→B, esquerda para direita): vai por CIMA com offset
                          const x1 = orig.x + cardW + 5;
                          const y1 = orig.y + cardH / 2 - 12;
                          const x2 = dest.x - 5;
                          const y2 = dest.y + cardH / 2 - 12;
                          path = `M ${x1} ${y1} L ${x2} ${y2}`;
                          labelX = (x1 + x2) / 2;
                          labelY = y1 - 15;
                        } else if (isConexaoReversa) {
                          // Conexão reversa (B→A, direita para esquerda): vai por BAIXO com curva
                          const x1 = orig.x + cardW / 2;
                          const y1 = orig.y + cardH + 5;
                          const x2 = dest.x + cardW / 2;
                          const y2 = dest.y + cardH + 5;
                          const curveY = y1 + 40;
                          path = `M ${x1} ${y1} L ${x1} ${curveY} L ${x2} ${curveY} L ${x2} ${y2}`;
                          labelX = (x1 + x2) / 2;
                          labelY = curveY + 15;
                        } else {
                          // Conexão normal na mesma linha (sem bidirecional)
                          const x1 = orig.x + cardW + 5;
                          const y1 = orig.y + cardH / 2;
                          const x2 = dest.x - 5;
                          const y2 = dest.y + cardH / 2;
                          if (x2 > x1) {
                            path = `M ${x1} ${y1} L ${x2} ${y2}`;
                          } else {
                            path = `M ${x1 - cardW - 10} ${y1} L ${x1 - cardW - 40} ${y1} L ${x1 - cardW - 40} ${y1 - 60} L ${x2 + cardW + 40} ${y1 - 60} L ${x2 + cardW + 40} ${y2} L ${x2 + cardW + 10} ${y2}`;
                          }
                          labelX = (x1 + x2) / 2;
                          labelY = y1 - 15;
                        }
                      } else if (mesmaColuna) {
                        const x1 = orig.x + cardW / 2;
                        const y1 = orig.y + cardH + 5;
                        const x2 = dest.x + cardW / 2;
                        const y2 = dest.y - 5;
                        path = `M ${x1} ${y1} L ${x2} ${y2}`;
                        labelX = x1 + 15;
                        labelY = (y1 + y2) / 2;
                      } else if (x2c > x1c && y2c > y1c) {
                        const x1 = orig.x + cardW + 5;
                        const y1 = orig.y + cardH / 2;
                        const x2 = dest.x + cardW / 2;
                        const y2 = dest.y - 5;
                        path = `M ${x1} ${y1} L ${x2} ${y1} L ${x2} ${y2}`;
                        labelX = x2 + 15;
                        labelY = (y1 + y2) / 2;
                      } else if (x2c < x1c && y2c > y1c) {
                        const x1 = orig.x - 5;
                        const y1 = orig.y + cardH / 2;
                        const x2 = dest.x + cardW / 2;
                        const y2 = dest.y - 5;
                        const off = 30;
                        path = `M ${x1} ${y1} L ${x1 - off} ${y1} L ${x1 - off} ${y2 - off} L ${x2} ${y2 - off} L ${x2} ${y2}`;
                        labelX = x1 - off - 60;
                        labelY = (y1 + y2) / 2;
                      } else if (x2c > x1c && y2c < y1c) {
                        const x1 = orig.x + cardW / 2;
                        const y1 = orig.y - 5;
                        const x2 = dest.x - 5;
                        const y2 = dest.y + cardH / 2;
                        const midY = y1 - 30;
                        path = `M ${x1} ${y1} L ${x1} ${midY} L ${x2 + cardW + 5} ${midY} L ${x2 + cardW + 5} ${y2}`;
                        labelX = (x1 + x2) / 2;
                        labelY = midY - 15;
                      } else {
                        const x1 = orig.x - 5;
                        const y1 = orig.y + cardH / 2;
                        const x2 = dest.x + cardW + 5;
                        const y2 = dest.y + cardH / 2;
                        const off = 30;
                        path = `M ${x1} ${y1} L ${x1 - off} ${y1} L ${x1 - off} ${y2} L ${x2} ${y2}`;
                        labelX = x1 - off - 60;
                        labelY = (y1 + y2) / 2;
                      }
                      
                      const etapaOrigNome = etapasUnificadas.find(e => e.id === conexao.origem)?.nome || 'Etapa';
                      const etapaDestNome = etapasUnificadas.find(e => e.id === conexao.destino)?.nome || 'Etapa';
                      const tooltipCompleto = `${conexao.label}\n${etapaOrigNome} → ${etapaDestNome}`;
                      
                      return (
                        <g key={`conn-${idx}`} opacity={opacidade}>
                          {/* Linha invisível grossa para facilitar hover */}
                          <path 
                            d={path} 
                            fill="none" 
                            stroke="transparent" 
                            strokeWidth="14" 
                            className="pointer-events-auto cursor-pointer"
                            style={{ pointerEvents: 'auto' }}
                          >
                            <title>{tooltipCompleto}</title>
                          </path>
                          {/* Linha visível */}
                          <path d={path} fill="none" stroke={cor} strokeWidth={strokeW} strokeDasharray={dasharray} markerEnd={`url(#${markerId})`} className="pointer-events-none" />
                          {/* Label com tooltip */}
                          <g className="pointer-events-auto cursor-pointer" style={{ pointerEvents: 'auto' }}>
                            <rect x={labelX - 60} y={labelY - 10} width="120" height="20" rx="4" fill="white" stroke={cor} strokeWidth="1" opacity="0.9">
                              <title>{tooltipCompleto}</title>
                            </rect>
                            <text x={labelX} y={labelY + 4} fill="#374151" fontSize="9" fontWeight="600" textAnchor="middle" className="pointer-events-none">
                              {conexao.label.length > 20 ? conexao.label.substring(0, 18) + '...' : conexao.label}
                            </text>
                            <text x={labelX + 55} y={labelY + 5} fill={cor} fontSize="10" textAnchor="middle" className="pointer-events-none">
                              {isPercorrida ? '✅' : isSubprocesso ? '🔗' : conexao.tipo === 'automacao' ? '⚡' : '➜'}
                            </text>
                          </g>
                        </g>
                      );
                    })}
                    

                    
                  </svg>
                  
                  {/* Labels das pools (indicador de processo) */}
                  {temMultiplasPools && pools.map((pool, pIdx) => (
                    <div
                      key={`pool-${pIdx}`}
                      className="absolute flex items-center gap-1.5"
                      style={{
                        left: `${pool.minX}px`,
                        top: `${pool.minY - 28}px`,
                      }}
                    >
                      {pool.isOrigem && (
                        <svg className="w-3 h-3 text-purple-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                        </svg>
                      )}
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-md border ${pool.isOrigem ? 'bg-purple-50 text-purple-600 border-purple-200' : 'bg-blue-50 text-blue-600 border-blue-200'}`}>
                        {pool.label}
                      </span>
                    </div>
                  ))}

                  {/* Cards das etapas unificadas */}
                  {etapasUnificadas.map((et, idx) => {
                    const pos = layoutMap[et.id];
                    if (!pos) return null;
                    
                    const isVisitada = etapasVisitadasIds.has(et.id);
                    const isAtual = !et.isOrigem && et.id === String(etapa?.id);
                    const visitCount = percursoUnificado.filter(p => p.etapaId === et.id).length;
                    const corIdx = idx % coresSeq.length;
                    
                    // Info temporal
                    const primeiraVisita = percursoUnificado.find(p => p.etapaId === et.id);
                    
                    // Detectar mudança de board/fluxo
                    const isInicioFluxo = idx === 0;
                    
                    // Participantes (só fluxo atual)
                    const participantesEtapa = !et.isOrigem
                      ? (et.participantes || []).map((pId: string) =>
                          colaboradores.find((c: any) => c.id === pId)
                        ).filter(Boolean)
                      : [];
                    
                    return (
                      <div
                        key={et.id}
                        className="absolute"
                        style={{ left: `${pos.x}px`, top: `${pos.y}px`, width: `${cardW}px` }}
                      >
                        {/* Indicador de INÍCIO */}
                        {isInicioFluxo && (
                          <div className="absolute -left-20 top-1/2 -translate-y-1/2 flex items-center gap-1 z-10">
                            <div className="px-2 py-1 bg-gradient-to-br from-green-400 to-green-600 rounded-md flex items-center justify-center shadow-lg border border-white/60">
                              <span className="text-white text-[10px] font-bold tracking-wide">INÍCIO</span>
                            </div>
                            <div className="w-4 h-0.5 bg-green-500"></div>
                          </div>
                        )}
                        
                        {/* Número sequencial da visita no percurso */}
                        {isVisitada && ordemVisita[et.id] ? (
                          <div className={`absolute -top-3 -left-3 w-7 h-7 bg-gradient-to-br ${et.isOrigem ? 'from-purple-400 to-purple-600' : et.isDestino ? 'from-green-400 to-green-600' : coresSeq[corIdx]} text-white rounded-full flex items-center justify-center text-[10px] font-bold shadow-lg z-20 border-2 border-white ${isAtual ? 'ring-3 ring-green-400 scale-110' : ''}`}>
                            {ordemVisita[et.id]}
                          </div>
                        ) : (
                          <div className="absolute -top-3 -left-3 w-6 h-6 bg-gray-300 text-gray-500 rounded-full flex items-center justify-center text-[9px] font-medium z-20 border-2 border-white">
                            –
                          </div>
                        )}
                        
                        {/* Badge de contagem de visitas */}
                        {isVisitada && visitCount > 1 && (
                          <div className="absolute -top-2 -right-2 w-6 h-6 bg-amber-500 text-white rounded-full flex items-center justify-center text-[9px] font-bold shadow-lg z-20 border-2 border-white">
                            {visitCount}x
                          </div>
                        )}
                        
                        {/* Card da Etapa - estilo BPMN */}
                        <div className={`relative rounded-2xl shadow-md border-2 transition-all ${
                          isAtual
                            ? 'bg-white border-green-500 shadow-xl shadow-green-100 ring-2 ring-green-300'
                            : isVisitada
                              ? `bg-white border-gray-700 shadow-lg ${et.isOrigem ? 'border-l-purple-500 border-l-4' : ''}`
                              : 'bg-gray-100 border-gray-300 border-dashed opacity-40'
                        }`} style={{ minHeight: `${cardH}px` }}>
                          <div className="p-3">
                            {/* Nome da Etapa */}
                            <h3 className={`font-bold text-sm text-center leading-tight mb-2 ${isVisitada ? 'text-gray-900' : 'text-gray-400'}`}>
                              {et.nome}
                            </h3>
                            
                            {/* Info temporal da visita */}
                            {isVisitada && primeiraVisita && (
                              <div className="text-center mb-2">
                                <span className="text-[9px] text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                                  📅 {primeiraVisita.movedAt.toLocaleDateString('pt-BR')}, {primeiraVisita.movedAt.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                                </span>
                              </div>
                            )}
                            
                            {/* Tag ATUAL */}
                            {isAtual && (
                              <div className="flex items-center justify-center gap-1 mb-1">
                                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                                <span className="text-[10px] text-green-600 font-bold">ETAPA ATUAL</span>
                              </div>
                            )}
                            
                            {/* Avatares dos Participantes */}
                            {participantesEtapa.length > 0 && isVisitada && (
                              <div className="mt-1 flex items-center justify-center -space-x-1.5">
                                {participantesEtapa.slice(0, 4).map((p: any, pIdx: number) => {
                                  const avatarColors = ['bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-pink-500', 'bg-teal-500'];
                                  return (
                                    <div key={p?.id || pIdx} title={p?.nome || 'Participante'}>
                                      {p?.foto ? (
                                        <img src={p.foto} alt={p.nome} className="w-6 h-6 rounded-full border-2 border-white object-cover" />
                                      ) : (
                                        <div className={`w-6 h-6 rounded-full border-2 border-white ${avatarColors[pIdx % avatarColors.length]} flex items-center justify-center text-white text-[8px] font-bold`}>
                                          {(p?.nome || '??').split(' ').map((n: string) => n[0]).join('').substring(0, 2).toUpperCase()}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                                {participantesEtapa.length > 4 && (
                                  <div className="w-6 h-6 rounded-full border-2 border-white bg-gray-400 flex items-center justify-center text-white text-[8px] font-bold">
                                    +{participantesEtapa.length - 4}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              </div>

              {/* Footer */}
              <div className="px-5 py-2.5 bg-gray-50 border-t border-gray-200 flex items-center justify-between flex-shrink-0">
                <div className="text-[10px] text-gray-400">
                  {etapasVisitadasIds.size} de {etapasUnificadas.length} etapas visitadas
                  {percursoUnificado.length > etapasVisitadasIds.size && (
                    <span className="ml-1 text-amber-500 font-medium">• {percursoUnificado.length - etapasVisitadasIds.size} revisita(s)</span>
                  )}
                  {temCadeia && (
                    <span className="ml-2 text-purple-500 font-medium">• 🔗 Inclui {etapasUnificadas.filter(e => e.isOrigem).length} etapas de {cadeiaCompleta.length} fluxo{cadeiaCompleta.length > 1 ? 's' : ''} de origem</span>
                  )}
                  {cadeiaDestinos.length > 0 && (
                    <span className="ml-2 text-green-500 font-medium">• 🔗 Inclui {etapasUnificadas.filter(e => e.isDestino).length} etapas de {cadeiaDestinos.length} fluxo{cadeiaDestinos.length > 1 ? 's' : ''} destino</span>
                  )}
                </div>
                <button
                  onClick={() => { setMostrarFluxoTimeline(false); if (document.fullscreenElement) document.exitFullscreen().catch(() => {}); setFluxoFullScreen(false); }}
                  className="px-4 py-1.5 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg text-sm font-medium transition-colors"
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Modal de Resumo */}
      {mostrarResumo && card.ehCardPai && (
        <ModalResumo
          cardPai={card}
          etapa={etapa}
          todasEtapas={[...etapasAnteriores, etapa]}
          onClose={() => setMostrarResumo(false)}
        />
      )}

      {/* Modal expandido de tabela/gráfico da IA */}
      {iaExpandedContent && iaExpandedContent.type !== 'fullscreen' && createPortal(
        <div
          className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 backdrop-blur-sm p-6"
          onClick={() => setIaExpandedContent(null)}
        >
          <div
            className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-[90vw] max-h-[90vh] overflow-auto p-6 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setIaExpandedContent(null)}
              className="absolute top-3 right-3 p-2 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
              title="Fechar"
            >
              <svg className="w-5 h-5 text-gray-600 dark:text-gray-300" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            {iaExpandedContent.type === 'table' && iaExpandedContent.html && (
              <div
                className="[&_table]:w-full [&_table]:border-collapse [&_table]:text-sm
                  [&_th]:border [&_th]:border-gray-300 [&_th]:bg-violet-50 [&_th]:px-4 [&_th]:py-2.5 [&_th]:text-left [&_th]:font-semibold [&_th]:text-violet-700
                  [&_td]:border [&_td]:border-gray-300 [&_td]:px-4 [&_td]:py-2 [&_td]:text-gray-700
                  min-w-[400px]"
                dangerouslySetInnerHTML={{ __html: iaExpandedContent.html }}
              />
            )}
            {iaExpandedContent.type === 'chart' && iaExpandedContent.chart && (
              <div className="flex items-center justify-center min-w-[500px] min-h-[350px]">
                <AIChart data={iaExpandedContent.chart} compact={false} />
              </div>
            )}
          </div>
        </div>,
        document.body
      )}

      {/* ═══ Modal Fullscreen Chat IA ═══ */}
      {iaAberta && createPortal(
        <div className="fixed inset-0 z-[9999] flex flex-col bg-black/60 backdrop-blur-sm">
          <div className="flex flex-col w-full h-full max-w-4xl mx-auto my-4 bg-white rounded-2xl shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-violet-600 to-purple-600 text-white shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-base font-bold">Assistente IA</h2>
                  <p className="text-xs text-white/70 truncate max-w-[300px]">{card.titulo}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    setIaMensagens([{
                      id: 'welcome',
                      role: 'assistant',
                      content: `Olá! Sou a IA do card **"${card.titulo}"**.\n\nPosso te ajudar a:\n- 📋 Analisar os dados preenchidos\n- 📑 **Ler qualquer anexo** (PDF, Word, Excel, imagens JPG/PNG...)\n- 🖼️ **Interpretar imagens** (notas fiscais, boletos, fotos)\n- ❓ Responder dúvidas sobre este card\n- 💡 Sugerir preenchimentos\n\nO que precisa?`
                    }]);
                    setIaInput('');
                  }}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-white/15 hover:bg-white/25 transition-all"
                  title="Reiniciar conversa"
                >
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182M20.016 4.356v4.992" />
                  </svg>
                  Reiniciar
                </button>
                <button
                  onClick={() => setIaAberta(false)}
                  className="p-2 rounded-lg bg-white/15 hover:bg-white/25 transition-all"
                  title="Fechar"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Mensagens */}
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4 bg-gray-50" style={{ scrollbarWidth: 'thin' }}>
              {iaMensagens.map(msg => (
                <div key={msg.id} className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                  {msg.role === 'assistant' && (
                    <div className="shrink-0 w-8 h-8 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center mt-0.5 shadow-md">
                      <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
                      </svg>
                    </div>
                  )}
                  <div className={`max-w-[80%] rounded-2xl px-5 py-3 text-sm leading-relaxed ${
                    msg.role === 'user'
                      ? 'bg-violet-600 text-white rounded-tr-md shadow-md'
                      : 'bg-white border border-gray-200 text-gray-700 rounded-tl-md shadow-sm'
                  }`}>
                    {msg.loading ? (
                      <div className="flex items-center gap-3 py-1">
                        <span className="w-2 h-2 bg-violet-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                        <span className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                        <span className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                        <span className="text-sm text-gray-400">Analisando dados...</span>
                      </div>
                    ) : (
                      <>
                        <div
                          className="[&_strong]:text-violet-600 [&_strong]:font-semibold [&_table]:my-2 [&_pre]:text-xs [&>p:first-child]:mt-0 [&>p:last-child]:mb-0"
                          dangerouslySetInnerHTML={{ __html: iaRenderMd(msg.content) }}
                          onClick={(e) => {
                            const btn = (e.target as HTMLElement).closest('[data-expand-table]');
                            if (btn) {
                              const tableContainer = btn.closest('[data-table-group]');
                              if (tableContainer) {
                                const tableHtml = tableContainer.querySelector('table')?.outerHTML || '';
                                setIaExpandedContent({ type: 'table', html: tableHtml });
                              }
                            }
                          }}
                        />
                        {msg.role === 'assistant' && extractCharts(msg.content).map((chart, ci) => (
                          <div key={ci} className="relative group/chart inline-block my-2">
                            <button
                              onClick={() => setIaExpandedContent({ type: 'chart', chart })}
                              className="absolute -top-1 -right-1 z-10 opacity-0 group-hover/chart:opacity-100 transition-opacity bg-violet-600 hover:bg-violet-700 text-white rounded-lg p-1 shadow-md"
                              title="Ampliar gráfico"
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                                <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
                              </svg>
                            </button>
                            <AIChart data={chart} compact={false} />
                          </div>
                        ))}
                        {msg.role === 'assistant' && iaExtractCsv(msg.content) && (
                          <button
                            onClick={() => {
                              const csv = iaExtractCsv(msg.content);
                              if (csv) iaDownloadCsv(csv);
                            }}
                            className="flex items-center gap-1.5 mt-2 px-3 py-1.5 text-xs font-medium
                              bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-lg
                              hover:bg-emerald-100 transition-all"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                            </svg>
                            Baixar CSV
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              ))}
              <div ref={iaEndRef} />
            </div>

            {/* Sugestões rápidas */}
            {iaMensagens.length <= 1 && (
              <div className="px-6 py-4 border-t border-gray-100 bg-white shrink-0">
                <div className="flex items-center gap-2 mb-3">
                  <div className="h-px flex-1 bg-gradient-to-r from-violet-200 to-transparent" />
                  <span className="text-xs text-gray-400 font-semibold uppercase tracking-widest">Sugestões</span>
                  <div className="h-px flex-1 bg-gradient-to-l from-violet-200 to-transparent" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { icon: '📋', label: 'Resumo completo', desc: 'Todos os campos e status', prompt: 'Faça um resumo completo e detalhado deste card: título, etapa atual, responsável, todos os campos preenchidos com seus valores, datas, histórico de movimentação entre etapas e tempo em cada uma.' },
                    { icon: '📑', label: 'Ler todos os anexos', desc: 'PDFs, imagens, planilhas e docs', prompt: 'Leia TODOS os arquivos anexados a este card (PDFs, imagens, documentos Word, planilhas Excel) e faça um resumo detalhado do conteúdo de cada um. Liste os pontos principais encontrados em cada documento.' },
                    { icon: '🧾', label: 'Análise de NF/DANFE', desc: 'Chave, tributos, itens e valores', prompt: 'Analise os documentos anexados como Nota Fiscal (NF/DANFE). Extraia e apresente em tabelas: chave de acesso (44 dígitos), número da NF, série, CNPJ do emitente e destinatário, natureza da operação, TODOS os itens/produtos com quantidade e valores, TODOS os tributos (ICMS com base de cálculo, alíquota e valor; IPI; PIS; COFINS; valor total dos tributos), valor total da NF, frete, desconto, forma e prazo de pagamento, datas de emissão e saída.' },
                    { icon: '📄', label: 'Análise de contrato', desc: 'Partes, valores, prazos e riscos', prompt: 'Analise os dados deste card e os documentos anexados como um contrato. Identifique: partes envolvidas, objeto do contrato, valores, prazos, cláusulas importantes, obrigações de cada parte e possíveis riscos ou pontos de atenção.' },
                    { icon: '🔄', label: 'Histórico de movimentação', desc: 'Tempo em cada etapa', prompt: 'Mostre o histórico completo de movimentação deste card: por quais etapas passou, quanto tempo ficou em cada uma, e quando foi movido. Identifique se houve algum atraso ou etapa onde ficou muito tempo parado.' },
                    { icon: '💰', label: 'Resumo financeiro', desc: 'Valores, pagamentos e vencimentos', prompt: 'Analise todos os dados e documentos deste card do ponto de vista financeiro. Identifique: valores totais, formas de pagamento, prazos de vencimento, parcelas, impostos/tributos, e qualquer informação relevante sobre custos e pagamentos.' },
                  ].map((s, i) => (
                    <button
                      key={i}
                      onClick={() => iaEnviar(s.prompt)}
                      disabled={iaLoading}
                      className="flex items-start gap-2.5 text-left px-4 py-3 bg-gradient-to-r from-gray-50 to-white
                        border border-gray-200 rounded-xl
                        hover:from-violet-50 hover:to-purple-50 hover:border-violet-300 hover:shadow-md
                        text-gray-600 hover:text-violet-700
                        disabled:opacity-40 transition-all group"
                    >
                      <span className="text-lg mt-0.5 group-hover:scale-110 transition-transform">{s.icon}</span>
                      <div className="flex flex-col min-w-0">
                        <span className="text-xs font-semibold text-gray-700 group-hover:text-violet-700 transition-colors">{s.label}</span>
                        <span className="text-[10px] text-gray-400 leading-tight mt-0.5">{s.desc}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Input */}
            <div className="px-6 py-4 border-t border-gray-200 bg-white shrink-0">
              <div className="flex items-end gap-3 bg-gray-50 rounded-2xl border border-gray-200 focus-within:border-violet-400 focus-within:ring-2 focus-within:ring-violet-500/20 px-4 py-2">
                <textarea
                  ref={iaInputRef}
                  value={iaInput}
                  onChange={e => { setIaInput(e.target.value); e.target.style.height = 'auto'; e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px'; }}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); iaEnviar(); } }}
                  placeholder="Pergunte sobre este card..."
                  rows={1}
                  className="flex-1 resize-none bg-transparent border-0 text-sm focus:outline-none focus:ring-0
                    placeholder-gray-400 text-gray-800 py-2 leading-5"
                  style={{ minHeight: '36px', maxHeight: '120px' }}
                />
                <button
                  onClick={() => iaEnviar()}
                  disabled={!iaInput.trim() || iaLoading}
                  className={`shrink-0 w-9 h-9 rounded-xl flex items-center justify-center mb-0.5 transition-all ${
                    iaInput.trim() && !iaLoading
                      ? 'bg-violet-600 text-white hover:bg-violet-700 shadow-md cursor-pointer'
                      : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" />
                  </svg>
                </button>
              </div>
              <div className="flex items-center justify-between mt-2 px-1">
                <span className="text-xs text-gray-400">
                  {iaLoading ? '⏳ Analisando dados do card...' : 'Enter para enviar, Shift+Enter para nova linha'}
                </span>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ═══ Modal de Detalhes da OC GIX ═══ */}
      {isGixEnabled && gixOcDetalheAberto && createPortal(
        <div
          className="fixed inset-0 z-[10002] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          onClick={() => setGixOcDetalheAberto(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[88vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-3.5 bg-gradient-to-r from-emerald-50 to-teal-50 border-b border-emerald-200/60">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center shadow-md">
                  <span className="text-white text-sm">🧾</span>
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 text-base tracking-tight">
                    Ordem de Compra #{gixOcStatus?.numero_oc}
                    {gixOcStatus?.empresa && <span className="text-gray-500 font-normal text-sm ml-2">(Empresa {gixOcStatus.empresa})</span>}
                  </h3>
                  <span className="text-xs text-gray-500 font-medium">Dados consultados direto do GIX</span>
                </div>
              </div>
              <button
                onClick={() => setGixOcDetalheAberto(false)}
                className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-xl transition-all"
                title="Fechar"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-5 space-y-5 bg-gray-50/40">
              {gixOcStatus && (
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
                  <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-3">Cabeçalho</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-xs">
                    <div>
                      <div className="text-gray-500 font-medium">Status</div>
                      <div className={`font-semibold ${
                        gixOcStatus.status_liberacao_cor === 'green' ? 'text-green-700'
                          : gixOcStatus.status_liberacao_cor === 'red' ? 'text-red-700' : 'text-gray-700'
                      }`}>
                        {gixOcStatus.status_liberacao_cor === 'green' ? '✅' : gixOcStatus.status_liberacao_cor === 'red' ? '🔒' : '❓'} {gixOcStatus.status_liberacao}
                      </div>
                    </div>
                    {gixOcStatus.nome_fornecedor && (
                      <div className="col-span-2">
                        <div className="text-gray-500 font-medium">Fornecedor</div>
                        <div className="font-semibold text-gray-800">{gixOcStatus.codigo_fornecedor} — {gixOcStatus.nome_fornecedor}</div>
                      </div>
                    )}
                    {gixOcStatus.data_oc && (
                      <div>
                        <div className="text-gray-500 font-medium">Emissão</div>
                        <div className="font-semibold text-gray-800">{String(gixOcStatus.data_oc).slice(0, 10).split('-').reverse().join('/')}</div>
                      </div>
                    )}
                    {gixOcStatus.valor_total_fmt && (
                      <div>
                        <div className="text-gray-500 font-medium">Valor Total</div>
                        <div className="font-semibold text-gray-900">{gixOcStatus.valor_total_fmt}</div>
                      </div>
                    )}
                    {(() => {
                      const liberacao = (cardCompleto as any)?.gixLiberacao || (card as any)?.gixLiberacao;
                      const matchOc = liberacao && Number(liberacao.numeroOc) === Number(gixOcStatus?.numero_oc);
                      const liberadoPorEscalone = matchOc ? liberacao.por : null;
                      const fonteGix = gixOcStatus.quem_liberou;
                      const exibir = liberadoPorEscalone || fonteGix;
                      if (!exibir) return null;
                      return (
                        <div>
                          <div className="text-gray-500 font-medium">Liberado por</div>
                          <div className="font-semibold text-gray-800">{exibir}</div>
                          {liberadoPorEscalone && fonteGix && fonteGix !== liberadoPorEscalone && (
                            <div className="text-[10px] text-gray-400 mt-0.5">via GIX como {fonteGix}</div>
                          )}
                          {matchOc && liberacao?.em && (
                            <div className="text-[10px] text-gray-400">{new Date(liberacao.em).toLocaleString('pt-BR')}</div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              )}

              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
                <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-3">Itens</h4>
                {gixOcDetalheLoading ? (
                  <div className="flex items-center gap-2 text-xs text-gray-500 py-4">
                    <div className="w-3 h-3 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin"></div>
                    Carregando itens...
                  </div>
                ) : gixOcDetalheErro ? (
                  <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">{gixOcDetalheErro}</div>
                ) : gixOcDetalheItens.length === 0 ? (
                  <div className="text-xs text-gray-500 italic py-2">Nenhum item encontrado.</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-50 text-gray-600 uppercase text-[10px]">
                        <tr>
                          <th className="px-3 py-2 text-left font-semibold">Seq</th>
                          <th className="px-3 py-2 text-left font-semibold">Produto</th>
                          <th className="px-3 py-2 text-left font-semibold">Descrição</th>
                          <th className="px-3 py-2 font-semibold text-right">Qtd</th>
                          <th className="px-3 py-2 font-semibold text-right">Entregue</th>
                          <th className="px-3 py-2 font-semibold text-right">Pendente</th>
                          <th className="px-3 py-2 text-left font-semibold">Un</th>
                          <th className="px-3 py-2 font-semibold text-right">Unit</th>
                          <th className="px-3 py-2 font-semibold text-right">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {gixOcDetalheItens.map((it: any, idx: number) => (
                          <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50">
                            <td className="px-3 py-2 text-gray-500">{it.sequencia}</td>
                            <td className="px-3 py-2 font-mono text-gray-600">{String(it.codigo_produto).padStart(7, '0')}</td>
                            <td className="px-3 py-2 font-medium text-gray-800">{it.nome_produto || <span className="text-gray-400 italic">sem descrição</span>}</td>
                            <td className="px-3 py-2 text-right text-gray-700">{it.qtd_pedida}</td>
                            <td className="px-3 py-2 text-right text-gray-700">{it.qtd_entregue}</td>
                            <td className="px-3 py-2 text-right text-gray-700">{it.qtd_pendente}</td>
                            <td className="px-3 py-2 text-gray-600">{it.unidade}</td>
                            <td className="px-3 py-2 text-right text-gray-700">
                              {Number(it.preco_unitario || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                            </td>
                            <td className="px-3 py-2 text-right font-semibold text-gray-900">
                              {Number(it.valor_total || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>

            <div className="px-5 py-3 border-t border-gray-200 bg-white flex items-center justify-between gap-3">
              <div className="text-xs">
                {gixLiberarMsg && (
                  <span className={gixLiberarMsg.startsWith('✅') ? 'text-emerald-700 font-medium' : 'text-red-600 font-medium'}>
                    {gixLiberarMsg}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {gixToggles.liberarOc && userPermissions?.canAdmin && gixOcStatus?.status_liberacao_cor === 'red' && gixOcStatus?.numero_oc && (
                  <button
                    disabled={gixLiberarLoading}
                    onClick={async () => {
                      const numeroOc = gixOcStatus?.numero_oc;
                      const empresa = gixOcStatus?.empresa;
                      if (!numeroOc || !empresa) {
                        setGixLiberarMsg('❌ Faltam dados da OC (número/empresa)');
                        return;
                      }
                      if (!window.confirm(`Liberar a OC ${numeroOc} (empresa ${empresa}) no GIX?`)) return;
                      setGixLiberarLoading(true);
                      setGixLiberarMsg(null);
                      try {
                        const r = await fetch(`/api/gix/oc/${numeroOc}/liberar`, {
                          method: 'POST',
                          headers: {
                            'Content-Type': 'application/json',
                            'X-User-Data': JSON.stringify({
                              id: userPermissions?.currentUser?.id || 'unknown',
                              nome: userPermissions?.currentUser?.nome || userPermissions?.currentUser?.email || 'ESCALONE',
                              role: userPermissions?.canAdmin ? 'admin' : 'user',
                            }),
                            'X-User-Role': userPermissions?.canAdmin ? 'admin' : 'user',
                          },
                          body: JSON.stringify({ empresa, usuario: 'ESCALONE' }),
                        });
                        const j = await r.json();
                        if (j?.status === 'ok') {
                          setGixLiberarMsg(j.ja_liberada ? '✅ OC já estava liberada' : '✅ OC liberada com sucesso');
                          // Atualiza badge localmente
                          setGixOcStatus((prev: any) => prev ? {
                            ...prev,
                            status_liberacao: 'LIBERADA',
                            status_liberacao_cor: 'green',
                            quem_liberou: userPermissions?.currentUser?.nome || userPermissions?.currentUser?.email || 'ESCALONE',
                          } : prev);

                          // 📜 Registrar evento na linha do tempo do card (hierarchyHistory)
                          if (!j.ja_liberada && flowId && card.id) {
                            try {
                              const userId = userPermissions?.currentUser?.id || 'unknown';
                              const userNome = userPermissions?.currentUser?.nome || userPermissions?.currentUser?.email || 'Usuário';
                              const novaEntrada = {
                                event: 'gix_oc_liberada',
                                at: new Date().toISOString(),
                                by: userId,
                                detalhes: `OC #${numeroOc} (empresa ${empresa}) liberada no GIX por ${userNome}`,
                              };
                              const historicoAtual = Array.isArray((cardCompleto as any)?.hierarchyHistory)
                                ? (cardCompleto as any).hierarchyHistory
                                : (Array.isArray((card as any)?.hierarchyHistory) ? (card as any).hierarchyHistory : []);
                              const gixLiberacao = {
                                numeroOc,
                                empresa,
                                por: userNome,
                                porId: userId,
                                em: novaEntrada.at,
                              };
                              // Atualiza estado local pra refletir já no modal aberto
                              try {
                                setCardCompleto((prev: any) => prev ? { ...prev, gixLiberacao, hierarchyHistory: [...historicoAtual, novaEntrada] } : prev);
                              } catch { /* setter pode não existir; ignora */ }
                              await fetch(`/api/flows/${flowId}/cards/${card.id}`, {
                                method: 'PUT',
                                headers: {
                                  'Content-Type': 'application/json',
                                  'X-User-Data': JSON.stringify({ id: userId, nome: userNome }),
                                },
                                body: JSON.stringify({
                                  hierarchyHistory: [...historicoAtual, novaEntrada],
                                  gixLiberacao,
                                }),
                              });
                            } catch (logErr) {
                              console.warn('⚠️ Falha ao registrar evento de liberação na timeline:', logErr);
                            }
                          }
                        } else {
                          setGixLiberarMsg(`❌ ${j?.message || 'Falha ao liberar OC'}`);
                        }
                      } catch (e: any) {
                        setGixLiberarMsg(`❌ ${e?.message || 'Erro de conexão'}`);
                      } finally {
                        setGixLiberarLoading(false);
                      }
                    }}
                    className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium flex items-center gap-2"
                    title="Liberar esta OC no GIX (CORCLIBE='S')"
                  >
                    {gixLiberarLoading ? (
                      <>
                        <div className="w-3 h-3 border-2 border-white/40 border-t-white rounded-full animate-spin"></div>
                        Liberando...
                      </>
                    ) : (
                      <>🔓 Liberar OC</>
                    )}
                  </button>
                )}
                <button
                  onClick={() => setGixOcDetalheAberto(false)}
                  className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 text-sm font-medium"
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ═══ Modal Criar OC no GIX ═══ */}
      {isGixEnabled && gixCriarOcAberto && createPortal(
        <div className="fixed inset-0 z-[10003] bg-black/50 flex items-center justify-center p-4" onClick={() => setGixCriarOcAberto(false)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b bg-gradient-to-r from-emerald-50 to-teal-50">
              <h3 className="font-bold text-gray-900 text-sm">🧾 Criar Ordem de Compra no GIX</h3>
              <button
                onClick={() => setGixCriarOcAberto(false)}
                className="text-gray-500 hover:text-gray-700"
                title="Fechar"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-3">
              <CriarOcPelaNota
                flowId={flowId}
                cardId={String(card.id)}
                onCriada={(numeroOc) => {
                  // O servidor já gravou o número e moveu o card: fecha o card para não
                  // salvar por cima a versão antiga (etapa de antes).
                  success(`OC ${numeroOc} criada no GIX e salva no card!`);
                  setTimeout(() => { setGixCriarOcAberto(false); onClose(); }, 2500);
                }}
              />
              <div className="border-t pt-2">
                <button
                  type="button"
                  onClick={() => setGixCriarOcManual(v => !v)}
                  className="text-[11px] text-gray-500 hover:text-gray-700 underline"
                >
                  {gixCriarOcManual ? 'Esconder criação manual' : 'Criar manualmente (outro produto/valor, 1 item)'}
                </button>
              </div>
              {gixCriarOcManual && (
              <GixCriarOcForm
                config={gixCriarOcPrefill}
                onSucesso={(numeroOc) => {
                  const campoId = etapa.gixCriarOcCampoId;
                  if (campoId) {
                    updateFieldValue(campoId, String(numeroOc));
                    // Auto-save: persiste imediatamente o número da OC no card
                    const camposAtualizados = {
                      ...card.campos,
                      [campoId]: String(numeroOc),
                    };
                    onSave({
                      ...card,
                      campos: camposAtualizados,
                      updatedAt: new Date(),
                    });
                  }
                  success(`OC ${numeroOc} criada no GIX e salva no card!`);
                  setGixCriarOcAberto(false);
                }}
              />
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Modal de Chat com o Solicitante (portal) */}
      {chatPortalModalAberto && submissaoPortal && (
        <PortalMensagensInternas
          submissaoId={submissaoPortal.id}
          portalId={submissaoPortal.portalId}
          nomeExterno={submissaoPortal.nomeExterno}
          emailExterno={submissaoPortal.emailExterno}
          protocolo={submissaoPortal.protocolo}
          currentUser={userPermissions?.currentUser ? {
            id: userPermissions.currentUser.id,
            nome: userPermissions.currentUser.nome
          } : undefined}
          onAtualizou={(naoLidas) => setChatNaoLidas(naoLidas)}
          onClose={() => setChatPortalModalAberto(false)}
        />
      )}

      {/* Modal Linha do Tempo (substitui a seção inline) */}
      {/* 🔪 Modal de fatiar card */}
      {mostrarModalFatiar && flowId && (
        <ModalFatiarCard
          card={cardCompleto || card}
          flowId={flowId}
          etapas={todasEtapas}
          etapaAtual={etapa}
          userEmail={userPermissions?.currentUser?.email}
          empresaId={empresaId || card.empresaId}
          onClose={() => setMostrarModalFatiar(false)}
          onFatiado={(novoCard, camposRestantes) => {
            // O servidor já gravou a fatia, o histórico e os campos restantes da
            // origem. NÃO chamamos onSave aqui: o objeto local do card é anterior
            // ao corte e sobrescreveria fatiasIds/fatiamentoHistory. Só refletimos
            // o resultado na tela aberta e avisamos o board.
            setValores(camposRestantes);
            setCardCompleto(prev => (prev ? { ...prev, campos: camposRestantes } : prev));
            onCardFatiado?.(novoCard, card.id, camposRestantes);
          }}
        />
      )}

      {/* 💰 Modal de pedir cotação */}
      {mostrarModalCotacao && flowId && (
        <ModalPedirCotacao
          card={cardCompleto || card}
          flowId={flowId}
          etapaAtual={etapa}
          // O contexto do pedido costuma estar em etapas anteriores
          etapasFluxo={etapasParaCotacao}
          podeSalvarPadrao={podePedirCotacao}
          userEmail={userPermissions?.currentUser?.email}
          empresaId={empresaId || card.empresaId}
          onClose={() => setMostrarModalCotacao(false)}
          onPadraoSalvo={onCotacaoPadraoSalvo}
        />
      )}

      {mostrarTimelineModal && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-[10001] p-4 backdrop-blur-sm"
          onClick={() => setMostrarTimelineModal(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Cabeçalho */}
            <div className="flex flex-wrap items-center justify-between gap-2 px-3 sm:px-5 py-3 bg-gradient-to-r from-orange-50 via-amber-50 to-orange-50 border-b border-gray-200">
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-orange-400 to-amber-500 flex items-center justify-center shadow-sm flex-shrink-0">
                  <span className="text-white text-base">📜</span>
                </div>
                <div className="min-w-0">
                  <h3 className="font-bold text-gray-900 text-base tracking-tight">Linha do tempo</h3>
                  <span className="text-[11px] text-gray-500 font-medium block truncate">
                    {timelineEntries.length} evento{timelineEntries.length > 1 ? 's' : ''} registrado{timelineEntries.length > 1 ? 's' : ''} • {card.titulo}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {cardCompleto.etapaHistory && cardCompleto.etapaHistory.length > 0 && (
                  <button
                    onClick={() => { setMostrarTimelineModal(false); setFluxoZoom(1); setFluxoPan({ x: 0, y: 0 }); setMostrarFluxoTimeline(true); }}
                    className="flex items-center gap-1.5 px-2 sm:px-3 py-1.5 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-lg hover:from-blue-600 hover:to-indigo-600 transition-all shadow-sm text-xs font-medium whitespace-nowrap"
                    title="Visualizar percurso do card no fluxo"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
                    </svg>
                    <span className="hidden sm:inline">Ver Fluxo</span>
                  </button>
                )}
                <button
                  onClick={() => setMostrarTimelineModal(false)}
                  className="p-1.5 hover:bg-gray-200/70 rounded-lg transition-colors"
                  title="Fechar"
                >
                  <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Conteúdo */}
            <div className="flex-1 overflow-y-auto bg-gray-50/30 p-4">
              {carregandoCardCompleto ? (
                <div className="flex items-center justify-center gap-2 text-sm text-gray-500 py-8">
                  <div className="animate-spin w-5 h-5 border-2 border-gray-300 border-t-blue-600 rounded-full"></div>
                  Carregando histórico...
                </div>
              ) : timelineEntries.length === 0 ? (
                <div className="text-center text-sm text-gray-400 py-8">
                  Nenhum evento registrado
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {timelineEntries.map((item, idx) => (
                    <div key={idx} className="p-3 bg-white border border-gray-200/80 rounded-lg text-xs hover:shadow-sm transition-shadow">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <p className="font-semibold text-gray-900 text-[12px]">
                            {item.kind === 'move' ? '🚚 ' : item.kind === 'hierarchy' ? '' : '📝 '}
                            {item.title}
                          </p>
                          {item.subtitle && (
                            <p className="text-[11px] text-gray-600 mt-0.5">{item.subtitle}</p>
                          )}
                          <div className="flex items-center gap-2 mt-1.5 text-[10px] text-gray-500 flex-wrap">
                            {item.meta?.by && <span>👤 {getUserNameById(item.meta.by)}</span>}
                            <span>📅 {isNaN(item.date.getTime()) ? 'Data não registrada' : item.date.toLocaleString('pt-BR')}</span>
                            {item.kind === 'move' && item.meta?.motivo && (
                              <span className="px-1.5 py-0.5 bg-gray-100 rounded border border-gray-200">Motivo: {item.meta.motivo}</span>
                            )}
                          </div>
                          {item.kind === 'move' && item.meta?.camposLimpos && item.meta.camposLimpos.length > 0 && (
                            <div className="mt-1.5 p-1.5 bg-red-50 border border-red-200 rounded">
                              <p className="text-[10px] font-semibold text-red-700 mb-0.5">⚠️ Campos limpos automaticamente:</p>
                              <ul className="text-[10px] text-red-600 space-y-0.5">
                                {item.meta.camposLimpos.map((campo: any, i: number) => (
                                  <li key={i} className="flex items-start gap-1">
                                    <span className="text-red-400">•</span>
                                    <span>
                                      <strong>{campo.nomeCampo}:</strong>{' '}
                                      <span className="italic">"{String(campo.valorAnterior || '').substring(0, 50)}"</span>
                                    </span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Galeria de Anexos (lightbox para múltiplos arquivos) */}
      {galeriaAnexos && (
        <AnexosGaleria
          itens={galeriaAnexos.itens}
          initialIndex={galeriaAnexos.index}
          flowId={flowId}
          cardId={card.id}
          onClose={() => setGaleriaAnexos(null)}
        />
      )}

      {/* ⚠️ Fornecedor novo digitado no card já criado: o salvamento para aqui até
          a pessoa confirmar. "Voltar e revisar" não salva — é a saída para quem
          percebeu que era erro de grafia. */}
      {pedindoCiencia && (
        // o clique tem que morrer aqui: este modal é filho do overlay do card, que
        // fecha no clique de fora — sem isto, clicar fora da caixa fechava o card
        <div onClick={(e) => e.stopPropagation()}>
        <ModalCienciaFornecedorNovo
          fornecedor={pedindoCiencia.fornecedor}
          parecidos={pedindoCiencia.parecidos}
          onVoltar={() => setPedindoCiencia(null)}
          onUsarNome={(nome) => {
            // trocou pelo nome já conhecido: não é mais fornecedor novo
            if (pedindoCiencia.noHistorico) {
              setCamposEditadosNoHistorico(prev => ({ ...prev, [pedindoCiencia.campoId]: nome }));
            } else {
              updateFieldValue(pedindoCiencia.campoId, nome);
            }
            cienciaRef.current = null;
            setPedindoCiencia(null);
          }}
          onCiente={() => {
            cienciaRef.current = {
              fornecedor: pedindoCiencia.fornecedor,
              campoId: pedindoCiencia.campoId,
              cienteEm: new Date().toISOString(),
              cientePor: userPermissions?.currentUser?.id || null,
              cientePorNome: userPermissions?.currentUser?.nome
                || userPermissions?.currentUser?.email || null,
            };
            setPedindoCiencia(null);
            handleSave();
          }}
        />
        </div>
      )}
    </div>
  );
};

export default DetalhesCartao;